/**
 * Leaf — Dual Mode Content Editor (Visual + Markdown)
 *
 * Standalone vanilla JS. No build step, no npm dependencies.
 * Visual mode uses contenteditable + execCommand.
 * Markdown mode uses a plain textarea with toolbar support.
 * Content syncs between modes via server-side conversion (Earmark) and
 * client-side HTML→Markdown conversion.
 *
 * SETUP: Add the hook to your app.js:
 *
 *   import "../../../deps/leaf/priv/static/assets/leaf.js"
 *
 *   let Hooks = {
 *     Leaf: window.LeafHooks.Leaf,
 *     // ... your other hooks
 *   }
 */
(function () {
  "use strict";

  if (window.LeafEditorLoaded) return;
  window.LeafEditorLoaded = true;

  window.LeafHooks = window.LeafHooks || {};

  // Version of this bundle. Kept in lockstep with mix.exs's @version by
  // test/js_version_test.exs, and compared against the server-rendered
  // `data-leaf-js-version` on mount — a host that VENDORS this file into
  // its own asset pipeline gets a copy that silently stays behind after
  // `mix deps.update leaf`, and a stale bundle looks exactly like a
  // current one until something it doesn't implement quietly no-ops.
  window.LeafHooks.version = "0.8.0";

  // =========================================================================
  // Reveal hidden spoilers on click (works for any .leaf-spoiler on the page,
  // not just inside an editor — so consumer-rendered output works too).
  // =========================================================================

  document.addEventListener("click", function (e) {
    var node = e.target;
    while (node && node !== document.body) {
      if (
        node.nodeType === 1 &&
        node.classList &&
        node.classList.contains("leaf-spoiler") &&
        !node.classList.contains("leaf-spoiler-revealed")
      ) {
        // Inside the editor, the spoiler is always shown for editing —
        // don't intercept clicks (let the cursor land normally).
        if (node.closest && node.closest("[data-editor-visual]")) return;
        e.preventDefault();
        node.classList.add("leaf-spoiler-revealed");
        return;
      }
      node = node.parentNode;
    }
  });

  // =========================================================================
  // Inject CSS styles for the visual editor
  // =========================================================================

  var EDITOR_CSS = [
    // Placeholder
    ".content-editor-visual:empty::before {",
    "  content: attr(data-placeholder);",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 35%, transparent);",
    "  pointer-events: none;",
    "  position: absolute;",
    "}",
    ".content-editor-visual { position: relative; -webkit-user-select: text; user-select: text; }",

    // Typography
    ".content-editor-visual h1 { font-size: 2em; font-weight: 700; margin: 0.67em 0; line-height: 1.2; }",
    ".content-editor-visual h2 { font-size: 1.5em; font-weight: 600; margin: 0.6em 0; line-height: 1.3; }",
    ".content-editor-visual h3 { font-size: 1.25em; font-weight: 600; margin: 0.5em 0; line-height: 1.4; }",
    ".content-editor-visual h4 { font-size: 1.1em; font-weight: 600; margin: 0.4em 0; line-height: 1.4; }",
    ".content-editor-visual p { margin: 0.5em 0; }",
    ".content-editor-visual p:first-child, .content-editor-visual h1:first-child,",
    "  .content-editor-visual h2:first-child, .content-editor-visual h3:first-child { margin-top: 0; }",

    // Inline
    ".content-editor-visual strong, .content-editor-visual b { font-weight: 700; }",
    ".content-editor-visual em, .content-editor-visual i { font-style: italic; }",
    ".content-editor-visual s, .content-editor-visual del, .content-editor-visual strike { text-decoration: line-through; }",
    ".content-editor-visual u { text-decoration: underline; }",
    ".content-editor-visual code {",
    "  background: var(--color-base-200, #e5e7eb); border-radius: 0.25rem;",
    "  padding: 0.1em 0.35em; font-family: monospace; font-size: 0.9em;",
    "}",

    // Code blocks
    ".content-editor-visual pre {",
    "  background: var(--color-base-200, #e5e7eb); border-radius: 0.5rem;",
    "  padding: 0.75rem 1rem; margin: 0.75em 0; overflow-x: auto;",
    "  font-family: monospace; font-size: 0.875rem; line-height: 1.6;",
    "}",
    ".content-editor-visual pre code { background: none; padding: 0; border-radius: 0; font-size: inherit; }",

    // Blockquote
    ".content-editor-visual blockquote {",
    "  border-left: 3px solid color-mix(in oklab, var(--color-base-content, #1f2937) 25%, transparent);",
    "  padding-left: 1rem; margin: 0.75em 0;",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 70%, transparent);",
    "}",

    // Lists
    ".content-editor-visual ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5em 0; }",
    ".content-editor-visual ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.5em 0; }",
    ".content-editor-visual li { margin: 0.2em 0; }",
    ".content-editor-visual li > p { margin: 0; }",

    // Tables
    ".content-editor-visual table { border-collapse: collapse; table-layout: fixed; width: 100%; margin: 0.75em 0; }",
    ".content-editor-visual th, .content-editor-visual td {",
    "  border: 1px solid color-mix(in oklab, var(--color-base-content, #1f2937) 20%, transparent);",
    "  padding: 0.4rem 0.75rem; text-align: left;",
    "  overflow-wrap: anywhere;",
    "}",
    ".content-editor-visual th {",
    "  font-weight: 600;",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 5%, transparent);",
    "}",

    // Links
    ".content-editor-visual a { color: var(--color-primary, #3b82f6); text-decoration: underline; cursor: text; }",
    ".content-editor-visual a:hover { opacity: 0.8; }",

    // Images
    ".content-editor-visual img {",
    "  max-width: 100%; height: auto; border-radius: 0.5rem; margin: 0.75em 0;",
    "  cursor: pointer;",
    "}",
    ".content-editor-visual img.leaf-img-selected {",
    "  outline: 2px solid var(--color-primary, #3b82f6);",
    "  outline-offset: 2px;",
    "}",

    // Atomic preserved blocks (custom/unknown tags).
    //
    // Non-editable, so the underlying XML can't be corrupted in place — the
    // dashed outline is this editor's established "you can't type in here"
    // vocabulary and stays. What changed is the inside: known attributes
    // are typeset by role (title, subtitle, media, call-to-action) in the
    // editor's own prose voice instead of dumped as `title="…"` source.
    // Reading your own document should not mean reading code.
    //
    // Two deliberate restraints. The nameplate sits top-RIGHT, handing the
    // top-left landing spot to the writer's content. And the type scale
    // stays close to prose rather than imitating a real published hero —
    // Leaf has no idea what the host's Hero looks like, and a document with
    // four of them still has to be readable. Monospace survives in exactly
    // two places: the nameplate and the leftover-attribute line, i.e. the
    // machine facts.
    ".content-editor-visual .leaf-atomic {",
    "  user-select: all; cursor: default;",
    "  border-radius: 0.5rem;",
    "  border: 1px dashed color-mix(in oklab, var(--color-base-content, #1f2937) 22%, transparent);",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 3.5%, transparent);",
    "}",
    // Click-selected: the ring says the keyboard now talks to the chip —
    // Backspace deletes it, Enter opens a paragraph after it, arrows
    // step the caret out.
    ".content-editor-visual .leaf-atomic-selected {",
    "  outline: 2px solid var(--color-primary, #3b82f6);",
    "  outline-offset: 2px;",
    "}",

    // Inline: the size of a word, because it interrupts a sentence.
    ".content-editor-visual .leaf-atomic-inline {",
    "  display: inline-flex; align-items: baseline; gap: 0.35em;",
    "  vertical-align: baseline; padding: 0.05em 0.5em; margin: 0 0.05em;",
    "  border-radius: 0.35em;",
    "}",

    ".content-editor-visual .leaf-atomic-block {",
    "  display: block; position: relative; overflow: hidden;",
    "  padding: 0.85rem 1rem 0.9rem; margin: 0.85em 0;",
    "  transition: border-color 120ms ease, background-color 120ms ease;",
    "}",
    ".content-editor-visual .leaf-atomic-block:hover {",
    "  border-color: color-mix(in oklab, var(--color-primary, #3b82f6) 45%, transparent);",
    "  background: color-mix(in oklab, var(--color-primary, #3b82f6) 4%, transparent);",
    "}",

    // Nameplate — the tag's identity, and the only thing here that is a
    // fact about the machine rather than about the content.
    ".content-editor-visual .leaf-atomic-name {",
    "  font: 600 0.68rem/1 ui-monospace, SFMono-Regular, monospace;",
    "  letter-spacing: 0.08em; text-transform: uppercase;",
    "  color: color-mix(in oklab, var(--color-primary, #3b82f6) 75%, var(--color-base-content, #1f2937));",
    "}",
    ".content-editor-visual .leaf-atomic-block .leaf-atomic-name {",
    "  position: absolute; top: 0; right: 0; z-index: 1;",
    "  padding: 0.3rem 0.6rem 0.32rem;",
    "  border-radius: 0 0.5rem 0 0.5rem;",
    "  background: color-mix(in oklab, var(--color-base-100, #fff) 86%, var(--color-primary, #3b82f6));",
    "}",

    // Hover-only affordance: the block is editable, but saying so
    // permanently would be noise on every block in the document.
    ".content-editor-visual .leaf-atomic-hint {",
    "  position: absolute; right: 0.75rem; bottom: 0.5rem;",
    "  font: 400 0.68rem/1 ui-monospace, SFMono-Regular, monospace;",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 45%, transparent);",
    "  opacity: 0; transition: opacity 120ms ease; pointer-events: none;",
    "}",
    ".content-editor-visual .leaf-atomic-block:hover .leaf-atomic-hint { opacity: 1; }",
    "@media (prefers-reduced-motion: reduce) {",
    "  .content-editor-visual .leaf-atomic-block,",
    "  .content-editor-visual .leaf-atomic-hint { transition: none; }",
    "}",

    // Media reads as a banner, not a floating thumbnail: full width,
    // cropped to a calm letterbox so tall and wide art both behave.
    ".content-editor-visual .leaf-atomic-media {",
    "  display: block; width: 100%; height: 7.5rem; object-fit: cover;",
    "  border-radius: 0.3rem; margin: 0 0 0.7rem; cursor: default;",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 6%, transparent);",
    "}",

    ".content-editor-visual .leaf-atomic-preview { display: block; }",
    ".content-editor-visual .leaf-atomic-eyebrow {",
    "  display: block; font-size: 0.72em; font-weight: 600;",
    "  letter-spacing: 0.06em; text-transform: uppercase;",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 55%, transparent);",
    "  margin-bottom: 0.15em;",
    "}",
    ".content-editor-visual .leaf-atomic-title {",
    "  display: block; font-size: 1.15em; font-weight: 650; line-height: 1.25;",
    "}",
    // Reserve room for the nameplate on whatever lands on the first line —
    // which is the title, but only when there is no banner for the plate to
    // sit over instead.
    ".content-editor-visual .leaf-atomic-block:not(.leaf-atomic-has-media)",
    "  .leaf-atomic-preview > :first-child { padding-right: 5.5rem; }",
    ".content-editor-visual .leaf-atomic-subtitle {",
    "  display: block; margin-top: 0.2em; line-height: 1.45;",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 72%, transparent);",
    "}",
    ".content-editor-visual .leaf-atomic-body {",
    "  display: block; margin-top: 0.5em; line-height: 1.5;",
    "}",
    ".content-editor-visual .leaf-atomic-body a {",
    "  text-decoration: underline; text-underline-offset: 0.15em;",
    "  color: var(--color-primary, #3b82f6);",
    "}",
    ".content-editor-visual .leaf-atomic-body img {",
    "  max-height: 6rem; width: auto; margin: 0.4em 0 0; border-radius: 0.25rem;",
    "}",

    // A label with a destination is a button on the published page, so it
    // previews as one — with the destination alongside, since "where does
    // this go" is usually why you looked.
    ".content-editor-visual .leaf-atomic-cta {",
    "  display: inline-flex; align-items: baseline; gap: 0.5em;",
    "  margin-top: 0.65em;",
    "}",
    ".content-editor-visual .leaf-atomic-cta-label {",
    "  display: inline-block; padding: 0.2em 0.7em; border-radius: 999px;",
    "  font-size: 0.85em; font-weight: 600;",
    "  background: color-mix(in oklab, var(--color-primary, #3b82f6) 16%, transparent);",
    "  color: color-mix(in oklab, var(--color-primary, #3b82f6) 90%, var(--color-base-content, #1f2937));",
    "}",
    ".content-editor-visual .leaf-atomic-cta-label::after { content: ' →'; opacity: 0.55; }",
    ".content-editor-visual .leaf-atomic-cta-link {",
    "  font: 400 0.75em ui-monospace, SFMono-Regular, monospace;",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 50%, transparent);",
    "  word-break: break-all;",
    "}",

    // Whatever had no role. Shown as source because for these there is
    // nothing better to say — small and faint so they don't compete.
    ".content-editor-visual .leaf-atomic-rest {",
    "  display: block; margin-top: 0.6em;",
    "  font: 400 0.72em/1.5 ui-monospace, SFMono-Regular, monospace;",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 42%, transparent);",
    "  word-break: break-word;",
    "}",

    // Inline value: whichever single attribute best says which one this is.
    ".content-editor-visual .leaf-atomic-value {",
    "  font-size: 0.9em;",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 70%, transparent);",
    "}",
    // Hashtags. Opt-in — only rendered when the host configured a `#`
    // suggestion trigger, i.e. said that `#` means "tag" here.
    // Wiki links. `data-leaf-wikilink-exists="false"` is set by the host's
    // resolution reply; until an answer arrives a link is styled as ordinary,
    // so a slow round trip does not flash every link as broken.
    ".content-editor-visual .leaf-wikilink {",
    "  color: var(--color-primary, #3b82f6); cursor: pointer;",
    "  text-decoration: underline; text-decoration-style: dotted;",
    "  text-underline-offset: 0.15em;",
    "}",
    ".content-editor-visual .leaf-wikilink[data-leaf-wikilink-exists='false'] {",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 45%, transparent);",
    "  text-decoration-style: dashed;",
    "}",
    // Source-mode chips: the `[[`, target and `|` sit in marker spans that
    // hide while the token is inactive, so the chip shows only its alias.
    // `:not(.leaf-source-active)` rather than a bare hide + reveal pair:
    // the reveal rule lives in the server-emitted stylesheet and this one
    // in the injected sheet, so a specificity tie would resolve by sheet
    // order — fragile. (Lives HERE, not in the server CSS, on purpose:
    // the server-rendered markup must not contain the class name, or every
    // "brackets stay literal" refute in wiki_links_test trips on the
    // stylesheet instead of actual decoration.)
    ".content-editor-visual [data-leaf-source] .leaf-wikilink:not(.leaf-source-active) .leaf-source-marker {",
    "  display: none;",
    "}",

    ".content-editor-visual .leaf-hashtag {",
    "  font-style: italic; font-weight: 500;",
    "  color: color-mix(in oklab, var(--color-primary, #3b82f6) 85%, var(--color-base-content, #1f2937));",
    "  background: color-mix(in oklab, var(--color-primary, #3b82f6) 10%, transparent);",
    "  border-radius: 0.25em; padding: 0 0.2em; margin: 0 -0.05em;",
    "}",

    // GFM callouts / admonitions
    ".content-editor-visual blockquote.leaf-callout {",
    "  border-left: 4px solid var(--cl, #3b82f6); border-radius: 0.375rem;",
    "  background: color-mix(in oklab, var(--cl, #3b82f6) 8%, transparent);",
    "  padding: 0.5rem 0.85rem; margin: 0.75em 0;",
    "}",
    ".content-editor-visual blockquote.leaf-callout .leaf-callout-title {",
    "  font-weight: 700; font-size: 0.85em; text-transform: uppercase;",
    "  letter-spacing: 0.03em; color: var(--cl, #3b82f6); margin: 0 0 0.25em;",
    "  user-select: none;",
    "}",
    ".content-editor-visual blockquote.leaf-callout-note { --cl: #3b82f6; }",
    ".content-editor-visual blockquote.leaf-callout-tip { --cl: #22c55e; }",
    ".content-editor-visual blockquote.leaf-callout-important { --cl: #a855f7; }",
    ".content-editor-visual blockquote.leaf-callout-warning { --cl: #f59e0b; }",
    ".content-editor-visual blockquote.leaf-callout-caution { --cl: #ef4444; }",

    // GFM task lists
    ".content-editor-visual li.leaf-task { list-style: none; margin-left: -1.25em; }",
    ".content-editor-visual .leaf-task-box {",
    "  display: inline-block; width: 1em; height: 1em; vertical-align: -0.15em;",
    "  margin-right: 0.45em; border: 1.5px solid currentColor; border-radius: 0.25em;",
    "  position: relative; cursor: pointer; opacity: 0.65; box-sizing: border-box;",
    "}",
    // A row wholly inside the selection reveals its source, the way
    // Obsidian does: the bullet / number / checkbox steps aside and the
    // literal "- " / "3. " / "- [x] " appears, painted in the selection
    // colours, so the sweep visibly takes the marker and not just the
    // label. The marker text rides in data-leaf-selmarker, computed by the
    // selection mirror — rebuilding the row into real source text mid-drag
    // would destroy the very selection being made. The clipboard writes the
    // same marker the eye sees, so the appearance keeps the promise.
    ".content-editor-visual li.leaf-row-in-selection:not([data-leaf-source]) {",
    "  list-style: none;",
    "}",
    ".content-editor-visual li.leaf-row-in-selection:not([data-leaf-source]) > .leaf-task-box {",
    "  display: none;",
    "}",
    ".content-editor-visual li.leaf-row-in-selection:not([data-leaf-source])::before {",
    "  content: attr(data-leaf-selmarker);",
    "  white-space: pre;",
    "  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;",
    "  font-size: 0.9em;",
    "  background-color: rgba(59, 130, 246, 0.35);",
    "  background-color: Highlight;",
    "  color: HighlightText;",
    "}",
    ".content-editor-visual li.leaf-task[data-checked='true'] .leaf-task-box {",
    "  background: currentColor; opacity: 0.85;",
    "}",
    ".content-editor-visual li.leaf-task[data-checked='true'] .leaf-task-box::after {",
    "  content: ''; position: absolute; left: 0.28em; top: 0.08em;",
    "  width: 0.28em; height: 0.55em; border: solid var(--color-base-100, #fff);",
    "  border-width: 0 2px 2px 0; transform: rotate(45deg);",
    "}",
    // A completed item reads as done at a glance: muted and struck through,
    // the way Obsidian and every other task list renders one.
    //
    // The line is drawn by the `<li>`, but does NOT cross the checkbox: CSS
    // does not propagate text-decoration into atomic inline-level boxes, and
    // `.leaf-task-box` is `display: inline-block`. Striking the box as well as
    // the label would make the tick unreadable.
    ".content-editor-visual li.leaf-task[data-checked='true'] {",
    "  opacity: 0.65; text-decoration: line-through;",
    "  text-decoration-thickness: from-font;",
    "}",
    // Not while the line is being edited. In hybrid the block under the cursor
    // shows its markdown source — `- [x] label` — and striking through the
    // syntax you are editing makes it hard to read.
    ".content-editor-visual li.leaf-task[data-checked='true'][data-leaf-source] {",
    "  text-decoration: none;",
    "}",

    // Collapsible details / accordion blocks
    ".content-editor-visual details {",
    "  border: 1px solid var(--color-base-300, #d1d5db); border-radius: 0.5rem;",
    "  padding: 0.5rem 0.75rem; margin: 0.75em 0;",
    "}",
    ".content-editor-visual summary {",
    "  cursor: pointer; font-weight: 600; outline: none;",
    "}",

    // Image / figure alignment + captions
    ".content-editor-visual figure { margin: 0.75em 0; }",
    ".content-editor-visual figure img { margin: 0; }",
    ".content-editor-visual figcaption {",
    "  font-size: 0.85em; opacity: 0.7; text-align: center; margin-top: 0.3em;",
    "}",
    ".content-editor-visual img[data-align='center'],",
    ".content-editor-visual figure[data-align='center'] {",
    "  display: block; margin-left: auto; margin-right: auto;",
    "}",
    ".content-editor-visual img[data-align='left'],",
    ".content-editor-visual figure[data-align='left'] {",
    "  float: left; margin-right: 1rem; max-width: 50%;",
    "}",
    ".content-editor-visual img[data-align='right'],",
    ".content-editor-visual figure[data-align='right'] {",
    "  float: right; margin-left: 1rem; max-width: 50%;",
    "}",

    // Image resize handles
    ".leaf-resize-handle {",
    "  position: absolute; width: 10px; height: 10px;",
    "  background: var(--color-base-100, #fff);",
    "  border: 2px solid var(--color-primary, #3b82f6);",
    "  border-radius: 2px; z-index: 51;",
    "}",
    ".leaf-resize-handle--nw { cursor: nw-resize; }",
    ".leaf-resize-handle--ne { cursor: ne-resize; }",
    ".leaf-resize-handle--sw { cursor: sw-resize; }",
    ".leaf-resize-handle--se { cursor: se-resize; }",

    // Drag-and-drop indicator
    ".leaf-drop-indicator {",
    "  position: absolute; left: 0; right: 0; height: 3px;",
    "  background: var(--color-primary, #3b82f6);",
    "  border-radius: 2px; pointer-events: none; z-index: 50;",
    "  transition: top 0.05s ease-out;",
    "}",
    ".leaf-dragging {",
    "  opacity: 0.35 !important;",
    "  outline: 2px dashed var(--color-primary, #3b82f6) !important;",
    "  outline-offset: 2px;",
    "}",

    // Block drag handle
    ".leaf-drag-handle {",
    "  position: absolute; z-index: 52;",
    "  display: flex; align-items: center; justify-content: center;",
    "  width: 28px; height: 28px;",
    "  cursor: grab; border-radius: 6px;",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 30%, transparent);",
    "  background: transparent;",
    "  transition: color 0.1s, background 0.1s;",
    "  user-select: none; -webkit-user-select: none;",
    "}",
    ".leaf-drag-handle:hover {",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 60%, transparent);",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 8%, transparent);",
    "}",
    ".leaf-drag-handle:active { cursor: grabbing; }",
    ".leaf-drag-handle svg { width: 18px; height: 18px; pointer-events: none; }",

    // Selection
    ".content-editor-visual ::selection { background-color: Highlight !important; color: HighlightText !important; }",
    ".content-editor-visual *::selection { background-color: Highlight !important; color: HighlightText !important; }",

    // Link popover — floating island
    ".leaf-link-popover {",
    "  position: absolute; z-index: 50;",
    "  display: flex; align-items: center; gap: 0.5rem;",
    "  background: var(--color-base-200, #e5e7eb); color: var(--color-base-content, #1f2937);",
    "  border: 1px solid var(--color-base-300, #d1d5db);",
    "  border-radius: 9999px; padding: 0.4rem 0.5rem 0.4rem 0.75rem;",
    "  box-shadow: 0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08);",
    "  font-size: 0.8125rem; line-height: 1;",
    "  animation: leaf-popover-in 0.15s ease-out;",
    "  white-space: nowrap;",
    "}",
    "@keyframes leaf-popover-in { from { opacity: 0; transform: translateY(6px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }",
    ".leaf-link-popover a {",
    "  color: var(--color-primary, #3b82f6); text-decoration: none; max-width: 220px;",
    "  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: pointer;",
    "}",
    ".leaf-link-popover a:hover { text-decoration: underline; }",
    ".leaf-link-popover .leaf-popover-actions {",
    "  display: flex; align-items: center; gap: 0.125rem;",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 8%, transparent);",
    "  border-radius: 9999px; padding: 0.125rem;",
    "}",
    ".leaf-link-popover button {",
    "  background: none; border: none; cursor: pointer; padding: 0.3rem;",
    "  border-radius: 9999px; color: color-mix(in oklab, var(--color-base-content, #1f2937) 50%, transparent);",
    "  display: flex; align-items: center;",
    "  transition: background 0.1s, color 0.1s;",
    "}",
    ".leaf-link-popover button:hover {",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 12%, transparent);",
    "  color: var(--color-base-content, #1f2937);",
    "}",
    ".leaf-link-popover .leaf-popover-divider {",
    "  width: 1px; height: 0.875rem;",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 15%, transparent);",
    "}",

    // Selection toolbar — floating bubble that appears above a non-
    // collapsed selection on touch devices. Bold / italic / code /
    // link, the four most common inline formats. Hidden by default;
    // positioned via JS, animated on entry.
    "[data-toolbar-layout='floating'] [data-visual-toolbar-buttons] { display: none !important; }",
    ".leaf-selection-toolbar {",
    "  position: absolute; z-index: 60;",
    "  display: flex; align-items: center; gap: 0.125rem;",
    "  background: var(--color-base-content, #1f2937); color: var(--color-base-100, #fff);",
    "  border-radius: 9999px; padding: 0.25rem;",
    "  box-shadow: 0 6px 20px rgba(0,0,0,0.18), 0 1px 4px rgba(0,0,0,0.10);",
    "  animation: leaf-popover-in 0.12s ease-out;",
    "  user-select: none; -webkit-user-select: none;",
    "}",
    ".leaf-selection-toolbar button {",
    "  background: none; border: none; cursor: pointer;",
    "  min-width: 36px; min-height: 36px; padding: 0.4rem 0.6rem;",
    "  border-radius: 9999px; color: inherit;",
    "  display: flex; align-items: center; justify-content: center;",
    "  font-size: 0.875rem; font-weight: 600; line-height: 1;",
    "  transition: background 0.1s;",
    "}",
    ".leaf-selection-toolbar button:hover,",
    ".leaf-selection-toolbar button:active {",
    "  background: rgba(255,255,255,0.15);",
    "}",
    ".leaf-selection-toolbar button.is-active {",
    "  background: rgba(255,255,255,0.22);",
    "}",
    ".leaf-selection-toolbar svg {",
    "  width: 1.05rem; height: 1.05rem;",
    "}",

    // Image URL dialog
    ".leaf-image-url-backdrop {",
    "  position: fixed; inset: 0; z-index: 99999;",
    "  background: rgba(0,0,0,0.15);",
    "}",
    ".leaf-image-url-dialog {",
    "  position: fixed; z-index: 100000;",
    "  background: var(--color-base-200, #e5e7eb); color: var(--color-base-content, #1f2937);",
    "  border: 1px solid var(--color-base-300, #d1d5db);",
    "  border-radius: 0.5rem; padding: 0.75rem;",
    "  box-shadow: 0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08);",
    "  font-size: 0.8125rem; line-height: 1;",
    "  animation: leaf-popover-in 0.15s ease-out;",
    "  display: flex; flex-direction: column; gap: 0.5rem;",
    "  width: 300px;",
    "}",
    ".leaf-image-url-dialog input {",
    "  width: 100%; padding: 0.375rem 0.5rem; font-size: 0.8125rem;",
    "  border: 1px solid var(--color-base-300, #d1d5db); border-radius: 0.375rem;",
    "  background: var(--color-base-100, #fff); color: var(--color-base-content, #1f2937);",
    "  outline: none;",
    "}",
    ".leaf-image-url-dialog input:focus {",
    "  border-color: var(--color-primary, #3b82f6);",
    "  box-shadow: 0 0 0 1px var(--color-primary, #3b82f6);",
    "}",
    ".leaf-image-url-dialog label {",
    "  font-size: 0.75rem; font-weight: 500;",
    "  color: color-mix(in oklab, var(--color-base-content, #1f2937) 70%, transparent);",
    "}",
    ".leaf-image-url-dialog .leaf-image-url-actions {",
    "  display: flex; justify-content: flex-end; gap: 0.375rem; margin-top: 0.25rem;",
    "}",
    ".leaf-image-url-dialog .leaf-image-url-actions button {",
    "  padding: 0.375rem 0.75rem; font-size: 0.75rem; font-weight: 500;",
    "  border-radius: 0.375rem; border: none; cursor: pointer;",
    "  transition: background 0.1s, color 0.1s;",
    "}",
    ".leaf-image-url-dialog .leaf-image-url-cancel {",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 10%, transparent);",
    "  color: var(--color-base-content, #1f2937);",
    "}",
    ".leaf-image-url-dialog .leaf-image-url-cancel:hover {",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 18%, transparent);",
    "}",
    ".leaf-image-url-dialog .leaf-image-url-insert {",
    "  background: var(--color-primary, #3b82f6); color: #fff;",
    "}",
    ".leaf-image-url-dialog .leaf-image-url-insert:hover { opacity: 0.9; }",

    // Spoiler (||text||) — censored block; click to reveal. Hidden by default
    // wherever it's rendered (consumer output, preview panes, etc.).
    ".leaf-spoiler {",
    "  background: var(--color-base-content, #1f2937);",
    "  color: transparent;",
    "  border-radius: 3px;",
    "  padding: 0 2px;",
    "  cursor: pointer;",
    "  user-select: none;",
    "  transition: color 0.15s ease, background 0.15s ease;",
    "}",
    ".leaf-spoiler.leaf-spoiler-revealed {",
    "  color: inherit;",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 12%, transparent);",
    "  user-select: text;",
    "}",
    // Inside the editor, spoilers are always shown so the writer can see and
    // edit what they typed. A subtle background hint keeps the spoiler-ness
    // visible at a glance.
    "[data-editor-visual] .leaf-spoiler {",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 12%, transparent);",
    "  color: inherit;",
    "  user-select: text;",
    "  cursor: text;",
    "}",

    // (Toolbar alignment rules live in the server-side inline <style>
    // block — see loading_state_css/1 in lib/leaf.ex — so they apply on
    // first paint, before this CSS is injected by mounted().)

    // Hybrid source mode: the block's markdown source is plain text in
    // which soft line breaks are literal "\n" chars — pre-wrap renders
    // them as real line breaks (a normal <p> collapses them to a single
    // space, which visually destroyed every Shift+Enter break the moment
    // the block entered source mode).
    "[data-leaf-source] { white-space: pre-wrap; }",

    // Sticky toolbar
    "[data-visual-toolbar].leaf-toolbar-sticky {",
    "  position: fixed;",
    "  z-index: 10000;",
    "  box-sizing: border-box;",
    "}",
    ".leaf-toolbar-placeholder { visibility: hidden; }",

    // Fullscreen mode — triggered via the browser Fullscreen API (see
    // `_toggleFullscreen` in the hook). The host element fills the screen
    // at OS level; these CSS rules then flex the inner layout (toolbar +
    // body + footer) so the editor body absorbs the new height instead of
    // staying at its configured `:height`. The `data-leaf-fullscreen='true'`
    // attribute is reflected from `fullscreenchange`, so F11/Escape/API
    // exit all keep the inner layout in sync.
    "[data-leaf-fullscreen='true'] {",
    "  position: fixed !important; inset: 0 !important;",
    "  width: 100vw !important; height: 100vh !important;",
    "  z-index: 9999;",
    "  background: var(--color-base-100, #ffffff);",
    "  padding: 0.75rem 1rem;",
    "  display: flex !important; flex-direction: column !important;",
    "  align-items: stretch !important;",
    "  overflow: hidden;",
    "  box-sizing: border-box;",
    "}",
    // Force every direct child to take the full row regardless of any
    // ancestor / Tailwind constraints (`min-w-0`, parent transforms, etc.)
    // that might have collapsed them to min-content width.
    "[data-leaf-fullscreen='true'] > * {",
    "  width: 100% !important;",
    "  max-width: 100% !important;",
    "  box-sizing: border-box;",
    "}",
    // Editor body: ignore the configured `:height` inline style and grow
    // to fill whatever the parent flex column hands it. `flex-basis: 0`
    // (instead of `auto`) starts the item at zero so subsequent
    // `flex-grow: 1` decides its size from the remaining space alone —
    // `auto` basis would re-introduce the inline `height: 480px` and
    // collapse the editor back to its baseline.
    "[data-leaf-fullscreen='true'] [data-editor-visual],",
    "[data-leaf-fullscreen='true'] [data-markdown-wrapper] textarea,",
    "[data-leaf-fullscreen='true'] [data-html-wrapper] textarea {",
    "  flex: 1 1 0 !important;",
    "  height: auto !important;",
    "  min-height: 0 !important;",
    "  resize: none !important;",
    "}",
    // The border-wrapper holds the editor body + footer. Tagged with a
    // data attribute so the selector survives template reshuffles and
    // doesn't break if a 4th top-level child gets added to the host.
    // Drop any `height: …` — flex-basis: 0 + flex-grow: 1 owns sizing
    // here, and an explicit height can fight the basis (especially in
    // Safari) and collapse the row.
    "[data-leaf-fullscreen='true'] [data-leaf-body-wrapper] {",
    "  flex: 1 1 0 !important;",
    "  min-height: 0 !important;",
    "  display: flex !important;",
    "  flex-direction: column !important;",
    "}",
    "[data-leaf-fullscreen='true'] [data-leaf-content] {",
    "  flex: 1 1 0 !important;",
    "  min-height: 0 !important;",
    "  display: flex !important;",
    "  flex-direction: column !important;",
    "}",
    "[data-leaf-fullscreen='true'] [data-visual-wrapper]:not(.hidden),",
    "[data-leaf-fullscreen='true'] [data-markdown-wrapper]:not(.hidden),",
    "[data-leaf-fullscreen='true'] [data-html-wrapper]:not(.hidden) {",
    "  flex: 1 1 0 !important;",
    "  min-height: 0 !important;",
    "  display: flex !important;",
    "  flex-direction: column !important;",
    "}",
    // The toolbar shouldn't grow at all — its content height is the
    // whole height it should ever take in fullscreen. Without this it
    // can absorb leftover space from the flex column instead of the
    // editor body.
    "[data-leaf-fullscreen='true'] [data-visual-toolbar] {",
    "  flex: 0 0 auto !important;",
    "}",

    // Resize-grip tooltip (shown when the mouse is over the bottom-right
    // resize grip; hint that double-click auto-fits height to content).
    ".leaf-grip-tooltip {",
    "  position: fixed; pointer-events: none; z-index: 100000;",
    "  background: rgba(17, 24, 39, 0.92); color: #f9fafb;",
    "  padding: 4px 8px; border-radius: 4px;",
    "  font: 500 11px/1.3 ui-sans-serif, system-ui, -apple-system, sans-serif;",
    "  white-space: nowrap;",
    "  opacity: 0; transform: translateY(2px);",
    "  transition: opacity 0.12s ease-out, transform 0.12s ease-out;",
    "  box-shadow: 0 2px 8px rgba(0,0,0,0.18);",
    "}",
    ".leaf-grip-tooltip.leaf-grip-tooltip-visible {",
    "  opacity: 1; transform: translateY(0);",
    "}",

    // Toolbar dropdown rows. Kept here rather than in Tailwind classes on
    // the markup so they don't depend on the host's build picking up
    // arbitrary variants from leaf's lib/ directory.
    //
    // `nowrap` pairs with the `min-w-max` on the menu: the widest row sizes
    // the menu instead of long labels ("Details / Accordion") wrapping to
    // two lines inside a fixed width.
    "[data-leaf-menu] li > * {",
    "  white-space: nowrap;",
    "  gap: 0.5rem;",
    "}",
    // Touch targets. Desktop keeps the compact rows; a coarse pointer gets
    // rows and toolbar buttons near the 44px guidance.
    "@media (pointer: coarse) {",
    "  [data-leaf-menu] li > * { min-height: 2.75rem; }",
    "  [data-mobile-toolbar] .btn,",
    "  [data-mobile-toolbar] summary.btn {",
    "    min-height: 2.75rem; min-width: 2.75rem;",
    "  }",
    "}",
    // The mobile menus are <details>; remove the default disclosure marker
    // so the summary renders as a plain button.
    "[data-mobile-toolbar] summary { list-style: none; }",
    "[data-mobile-toolbar] summary::-webkit-details-marker { display: none; }",

    // Inline-suggestion popup (#tag / @user / :emoji / …). Portaled to
    // <body> while open, so it can't be clipped by a scrollable panel or
    // covered by a sticky admin header. Colors come from the daisyUI CSS
    // variables the rest of the editor uses, with plain-CSS fallbacks, so
    // it themes and dark-modes with the host without a Tailwind build.
    ".leaf-suggest-popup {",
    "  position: fixed; z-index: 100001;",
    "  min-width: 12rem; max-width: 22rem;",
    "  background: var(--color-base-100, #ffffff);",
    "  color: var(--color-base-content, #1f2937);",
    "  border: 1px solid var(--color-base-300, #d1d5db);",
    "  border-radius: 0.5rem;",
    "  box-shadow: 0 8px 24px rgba(0,0,0,0.16), 0 2px 6px rgba(0,0,0,0.08);",
    "  overflow: hidden;",
    "  font: 400 0.8125rem/1.4 ui-sans-serif, system-ui, -apple-system, sans-serif;",
    "}",
    ".leaf-suggest-list { max-height: 14rem; overflow-y: auto; }",
    ".leaf-suggest-heading {",
    "  padding: 0.3rem 0.6rem; font-size: 0.6875rem; font-weight: 600;",
    "  text-transform: uppercase; letter-spacing: 0.04em;",
    "  opacity: 0.55;",
    "  border-bottom: 1px solid var(--color-base-300, #d1d5db);",
    "}",
    ".leaf-suggest-row {",
    "  display: flex; align-items: center; gap: 0.45rem;",
    "  width: 100%; padding: 0.35rem 0.6rem;",
    "  cursor: pointer; text-align: left;",
    "}",
    ".leaf-suggest-row[aria-selected='true'] {",
    "  background: color-mix(in oklab, var(--color-base-content, #1f2937) 10%, transparent);",
    "}",
    ".leaf-suggest-row .leaf-suggest-text {",
    "  flex: 1 1 auto; min-width: 0;",
    "  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;",
    "}",
    ".leaf-suggest-row .leaf-suggest-sub {",
    "  flex: 0 0 auto; margin-left: 0.5rem;",
    "  font-size: 0.6875rem; opacity: 0.55;",
    "}",
    ".leaf-suggest-row .leaf-suggest-icon {",
    "  flex: 0 0 auto; width: 1em; height: 1em;",
    "  background-color: currentColor; opacity: 0.55;",
    "}",
    ".leaf-suggest-create { border-top: 1px solid var(--color-base-300, #d1d5db); }",
    ".leaf-suggest-note {",
    "  display: flex; align-items: center; gap: 0.4rem;",
    "  padding: 0.4rem 0.6rem; opacity: 0.6; font-size: 0.75rem;",
    "}",
    ".leaf-suggest-spinner {",
    "  width: 0.75rem; height: 0.75rem; border-radius: 50%;",
    "  border: 2px solid currentColor; border-right-color: transparent;",
    "  animation: leaf-suggest-spin 0.6s linear infinite;",
    "}",
    "@keyframes leaf-suggest-spin { to { transform: rotate(360deg); } }",
    ".leaf-suggest-sr {",
    "  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;",
    "  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;",
    "}",
    "@media (prefers-reduced-motion: reduce) {",
    "  .leaf-suggest-spinner { animation-duration: 2s; }",
    "}",
  ].join("\n");

  function injectStyles() {
    if (document.getElementById("leaf-content-editor-css")) return;
    var style = document.createElement("style");
    style.id = "leaf-content-editor-css";
    style.textContent = EDITOR_CSS;
    document.head.appendChild(style);
  }

  // =========================================================================
  // HTML → Markdown converter (pure DOM walking)
  // =========================================================================

  function htmlToMarkdown(html) {
    var container = document.createElement("div");
    container.innerHTML = html;
    return nodeToMarkdown(container).replace(/\n{3,}/g, "\n\n").trim();
  }

  function nodeToMarkdown(node) {
    var result = "";
    var prevWasBr = false;
    for (var i = 0; i < node.childNodes.length; i++) {
      var child = node.childNodes[i];
      var emit = convertNode(child);

      // Earmark's HTML output (with breaks: true) puts a literal "\n" after
      // every <br> as pretty-print whitespace. Without stripping, the
      // <br>'s "\n" plus the text node's leading "\n" become "\n\n" — a
      // markdown paragraph break — and the round-trip splits a single
      // paragraph into multiple.
      if (prevWasBr && child.nodeType === Node.TEXT_NODE) {
        emit = emit.replace(/^[\n\r\t]+/, "");
      }

      prevWasBr =
        child.nodeType === Node.ELEMENT_NODE &&
        child.tagName &&
        child.tagName.toLowerCase() === "br";

      result += emit;
    }
    return result;
  }

  // True when `node` holds hybrid source-mode marker spans
  // (`<span class="leaf-source-marker">`) as direct children. Such a node's
  // serialized `inner` already contains its own syntax markers (e.g. an
  // `<a>` whose children are `[` / body / `](url)`), so the wrapping cases
  // below must NOT synthesize the markers again — doing so doubles them
  // (`[[label](url)](url)`). Mirrors `_hasMarkerChildren` on the editor.
  function hasSourceMarkerChild(node) {
    if (!node || !node.childNodes) return false;
    for (var i = 0; i < node.childNodes.length; i++) {
      var c = node.childNodes[i];
      if (
        c.nodeType === Node.ELEMENT_NODE &&
        c.classList &&
        c.classList.contains("leaf-source-marker")
      ) {
        return true;
      }
    }
    return false;
  }

  // Move leading/trailing whitespace outside markers so markdown stays valid
  // e.g. <i>works </i> → "*works* " instead of "*works *"
  function wrapInline(text, marker) {
    if (!text) return marker + marker;
    var leading = text.match(/^(\s*)/)[0];
    var trailing = text.match(/(\s*)$/)[0];
    var trimmed = text.substring(leading.length, text.length - trailing.length);
    if (!trimmed) return leading + trailing;
    return leading + marker + trimmed + marker + trailing;
  }

  // A <br> is a line break only when something comes after it. The one that
  // sits at the end of a block is there so the caret has a visible line to
  // rest on, and an empty paragraph is written <p><br></p> — neither is a
  // break in the text, and treating them as one puts stray markers in the
  // markdown.
  function isHardBreak(node) {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
    if (node.tagName.toLowerCase() !== "br") return false;
    if (node.hasAttribute && node.hasAttribute("data-leaf-filler")) return false;

    var sibling = node.nextSibling;

    while (sibling) {
      var text = sibling.nodeType === Node.TEXT_NODE ? sibling.nodeValue : sibling.textContent;
      if (text && text.replace(/[\s\u200B\uFEFF]/g, "") !== "") return true;
      sibling = sibling.nextSibling;
    }

    return false;
  }

  function convertNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      // Hybrid-mode keystroke redirects insert U+00A0 for trailing spaces
      // (so HTML doesn't collapse them); normalize back to regular spaces
      // when serializing to markdown. Heading-decoration cursor anchoring
      // sometimes leaves U+200B (ZWSP) — strip those too.
      var text = node.textContent.replace(/​/g, "").replace(/\u00a0/g, " ");

      // Markup pretty-printing, not text: MDEx writes "<br />\n" and that
      // newline would otherwise serialize as a second break on the way back.
      if (isHardBreak(node.previousSibling)) text = text.replace(/^\n/, "");

      return text;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    // Atomic preserved block (#3): a server-wrapped custom/unknown tag.
    // Emit the verbatim source it carries and ignore the placeholder label.
    if (node.classList && node.classList.contains("leaf-atomic")) {
      return node.getAttribute("data-leaf-raw") || "";
    }

    var tag = node.tagName.toLowerCase();
    var inner = nodeToMarkdown(node);

    switch (tag) {
      case "h1":
        return "\n# " + inner.trim() + "\n\n";
      case "h2":
        return "\n## " + inner.trim() + "\n\n";
      case "h3":
        return "\n### " + inner.trim() + "\n\n";
      case "h4":
        return "\n#### " + inner.trim() + "\n\n";
      case "h5":
        return "\n##### " + inner.trim() + "\n\n";
      case "h6":
        return "\n###### " + inner.trim() + "\n\n";

      case "p":
        return inner.trim() + "\n\n";

      case "br":
        // The Shift+Enter handler appends a filler <br> at the end of a
        // block so the cursor has a visible empty line to sit on. That
        // filler is for visual presentation only — emitting "\n" for it
        // would cause "<br><br>" sequences to round-trip as paragraph
        // breaks, splitting a single paragraph into multiple.
        if (node.hasAttribute && node.hasAttribute("data-leaf-filler")) {
          return "";
        }

        // A newline, not a backslash-newline. The host renders with hardbreaks
        // on, so a newline already comes back as a <br> — writing an explicit
        // hard break instead would serialize the same document one character
        // longer than the one the host is holding, and the first keystroke
        // after loading would be refused as an edit to a document nobody has.
        return "\n";

      case "strong":
      case "b":
        // Hybrid source mode: the wrapper already contains its literal
        // `**` markers as `.leaf-source-marker` spans — wrapping again
        // would double them (`****bold****`). Same guard as <a> below.
        if (hasSourceMarkerChild(node)) return inner;
        return wrapInline(inner, "**");

      case "em":
      case "i":
        if (hasSourceMarkerChild(node)) return inner;
        return wrapInline(inner, "*");

      case "s":
      case "del":
      case "strike":
        if (hasSourceMarkerChild(node)) return inner;
        return wrapInline(inner, "~~");

      case "sup":
        // Markdown has no superscript syntax — pass through as inline
        // HTML (Earmark/MDEx keep it) so the formatting survives saves
        // and mode switches.
        return "<sup>" + inner + "</sup>";

      case "sub":
        return "<sub>" + inner + "</sub>";

      case "code":
        if (
          node.parentElement &&
          node.parentElement.tagName.toLowerCase() === "pre"
        ) {
          return inner;
        }
        if (hasSourceMarkerChild(node)) return inner;
        return "`" + inner + "`";

      case "pre":
        var preLang = node.getAttribute("data-language") || "";
        if (!preLang) {
          var preCode = node.querySelector("code");
          if (preCode) {
            var langMatch = preCode.className.match(/language-(\S+)/);
            if (langMatch) preLang = langMatch[1];
          }
        }
        return "\n```" + preLang + "\n" + inner.trim() + "\n```\n\n";

      case "a":
        // Hybrid source-mode <a> already contains its `[` / `](url)` marker
        // spans, so `inner` is the full `[label](url)` — return it as-is.
        // Only a bare <a> (rendered/visual mode, Earmark, createLink) needs
        // the markers synthesized. Without this guard, editing a comment/
        // post round-trips `[label](url)` into `[[label](url)](url)`.
        if (hasSourceMarkerChild(node)) {
          return inner;
        }

        var href = node.getAttribute("href") || "";
        return "[" + inner + "](" + href + ")";

      case "img":
        var src = node.getAttribute("src") || "";
        var alt = node.getAttribute("alt") || "";
        var w = node.getAttribute("width");
        var h = node.getAttribute("height");
        var imgAlign = node.getAttribute("data-align");
        if (w || h || imgAlign) {
          var tag = '<img src="' + src + '" alt="' + alt + '"';
          if (w) tag += ' width="' + w + '"';
          if (h) tag += ' height="' + h + '"';
          if (imgAlign) tag += ' data-align="' + imgAlign + '"';
          tag += ' />';
          return tag;
        }
        return "![" + alt + "](" + src + ")";

      case "figure":
        var fimg = node.querySelector("img");
        if (!fimg) return inner;
        var fAlign =
          node.getAttribute("data-align") || fimg.getAttribute("data-align");
        var fcap = node.querySelector("figcaption");
        var capText = fcap ? fcap.textContent : "";
        var out = "\n<figure" + (fAlign ? ' data-align="' + fAlign + '"' : "") + ">";
        out += '<img src="' + (fimg.getAttribute("src") || "") + '"';
        out += ' alt="' + (fimg.getAttribute("alt") || "") + '"';
        if (fimg.getAttribute("width")) out += ' width="' + fimg.getAttribute("width") + '"';
        if (fimg.getAttribute("height")) out += ' height="' + fimg.getAttribute("height") + '"';
        out += " />";
        if (capText) out += "<figcaption>" + capText + "</figcaption>";
        out += "</figure>\n\n";
        return out;

      case "details":
        // Collapsible block. Markdown has no syntax for it, so emit raw
        // HTML — Earmark passes block-level HTML through on the way back.
        return "\n" + node.outerHTML.replace(/\s*contenteditable="[^"]*"/gi, "") + "\n\n";

      case "blockquote":
        var callout = node.getAttribute("data-callout");
        var bqInner;
        if (callout) {
          // Serialize children except the derived title label, prefix the
          // GFM callout marker.
          var coParts = [];
          for (var coi = 0; coi < node.childNodes.length; coi++) {
            var coChild = node.childNodes[coi];
            if (
              coChild.nodeType === Node.ELEMENT_NODE &&
              coChild.classList &&
              coChild.classList.contains("leaf-callout-title")
            ) {
              continue;
            }
            coParts.push(convertNode(coChild));
          }
          bqInner = "[!" + callout.toUpperCase() + "]\n" + coParts.join("").trim();
        } else {
          bqInner = inner.trim();
        }
        return (
          "\n" +
          bqInner
            .split("\n")
            .map(function (line) {
              return "> " + line;
            })
            .join("\n") +
          "\n\n"
        );

      case "ul":
        // Trailing blank line so a following block (paragraph, another
        // list) is separated — otherwise markdown folds it into the last
        // list item as a lazy-continuation line.
        return "\n" + convertList(node, "ul") + "\n\n";

      case "ol":
        return "\n" + convertList(node, "ol") + "\n\n";

      case "li":
        return inner;

      case "hr":
        return "\n---\n\n";

      case "table":
        return "\n" + convertTable(node) + "\n";

      case "thead":
      case "tbody":
      case "tr":
      case "th":
      case "td":
        return inner;

      case "div":
        return inner + "\n";

      case "span":
        // Hybrid-mode syntax decorations are visual only — never serialize.
        if (
          node.classList &&
          node.classList.contains("leaf-syntax-decoration")
        ) {
          return "";
        }
        // A wiki link displays its ALIAS (`[[Ideas|my notes]]` shows "my
        // notes") or `Target › Heading`, so serializing the span's text —
        // which is what an undecorated span does, and what a hashtag relies on
        // — would write the label back and lose the link. Emit the source it
        // carries instead.
        if (node.classList && node.classList.contains("leaf-wikilink")) {
          return node.getAttribute("data-leaf-wikilink-raw") || inner;
        }
        if (
          node.classList &&
          node.classList.contains("leaf-spoiler")
        ) {
          if (hasSourceMarkerChild(node)) return inner;
          return "||" + inner + "||";
        }
        return inner;

      default:
        return inner;
    }
  }

  // True when `li` carries its own literal list marker ("- ", "N. ",
  // "- [ ] ") as a hybrid source-mode `.leaf-list-marker` span. In that
  // state the marker text is already part of the item's content, so
  // convertList must not synthesize a second one ("2. 2.").
  function hasListMarkerChild(li) {
    if (!li || !li.children) return false;
    for (var i = 0; i < li.children.length; i++) {
      var c = li.children[i];
      if (c.classList && c.classList.contains("leaf-list-marker")) {
        return true;
      }
    }
    return false;
  }

  // Where an `<ol>` starts counting. Authors write lists that begin at 19 —
  // continuing one interrupted by a paragraph, or numbering steps that carry on
  // from an earlier section — and CommonMark preserves that: `19.` renders as
  // `<ol start="19">`. Renumbering from 1 on the way back to markdown silently
  // rewrote the document.
  function listStart(listNode) {
    var raw = listNode.getAttribute && listNode.getAttribute("start");
    var parsed = parseInt(raw, 10);

    return isNaN(parsed) ? 1 : parsed;
  }

  function convertList(listNode, type, indent) {
    indent = indent || "";
    // Nested content must be indented past the parent marker ("- " → 2
    // chars, "1. " → 3) for CommonMark to treat it as nested.
    var childIndent = indent + (type === "ol" ? "   " : "  ");
    var items = [];
    var index = type === "ol" ? listStart(listNode) : 1;
    for (var i = 0; i < listNode.children.length; i++) {
      var child = listNode.children[i];
      var childTag = child.tagName.toLowerCase();

      if (childTag === "ul" || childTag === "ol") {
        // execCommand("indent") nests a list as a SIBLING of the item it
        // came from (invalid HTML, but what Chrome produces). Serialize
        // it as nested under the previous item instead of dropping it.
        items.push(convertList(child, childTag, childIndent));
        continue;
      }

      if (childTag !== "li") {
        // Chrome's editing commands sometimes drop other blocks (e.g. a
        // <p>) directly into a list. Serialize them as continuation
        // content of the previous item rather than silently losing them.
        var stray = convertNode(child).trim();
        if (stray) {
          items.push(childIndent + stray.replace(/\n/g, "\n" + childIndent));
        }
        continue;
      }

      // Pull nested lists out of the item and serialize them indented on
      // their own lines — inlining them into the item's content flattens
      // (and previously lost) the nesting.
      var li = child.cloneNode(true);
      var nestedOut = [];
      var liKids = Array.prototype.slice.call(li.children);
      for (var n = 0; n < liKids.length; n++) {
        var kidTag = liKids[n].tagName ? liKids[n].tagName.toLowerCase() : "";
        if (kidTag === "ul" || kidTag === "ol") {
          nestedOut.push(convertList(liKids[n], kidTag, childIndent));
          li.removeChild(liKids[n]);
        }
      }

      var content = nodeToMarkdown(li).trim();
      var prefix;
      if (hasListMarkerChild(child)) {
        prefix = "";
      } else if (child.classList && child.classList.contains("leaf-task")) {
        var checked = child.getAttribute("data-checked") === "true";
        prefix = "- [" + (checked ? "x" : " ") + "] ";
      } else {
        prefix = type === "ol" ? index + ". " : "- ";
      }
      items.push(indent + prefix + content);
      for (var m = 0; m < nestedOut.length; m++) {
        items.push(nestedOut[m]);
      }
      index++;
    }
    return items.join("\n");
  }

  function convertTable(tableNode) {
    var rows = tableNode.querySelectorAll("tr");
    if (!rows.length) return "";
    var lines = [];
    var aligns = [];
    for (var i = 0; i < rows.length; i++) {
      var cells = rows[i].querySelectorAll("th, td");
      var parts = [];
      for (var j = 0; j < cells.length; j++) {
        parts.push(nodeToMarkdown(cells[j]).trim().replace(/\|/g, "\\|"));
        // Capture per-column alignment from the header row (data-align, or
        // an inline text-align style left by Earmark).
        if (i === 0) {
          var a = cells[j].getAttribute("data-align") || "";
          if (!a) {
            var st = cells[j].getAttribute("style") || "";
            var m = st.match(/text-align:\s*(left|center|right)/);
            if (m) a = m[1];
          }
          aligns.push(a);
        }
      }
      lines.push("| " + parts.join(" | ") + " |");
      // Separator row encodes alignment per CommonMark.
      if (i === 0) {
        var sep = [];
        for (var k = 0; k < parts.length; k++) {
          var al = aligns[k];
          if (al === "center") sep.push(":---:");
          else if (al === "left") sep.push(":---");
          else if (al === "right") sep.push("---:");
          else sep.push("---");
        }
        lines.push("| " + sep.join(" | ") + " |");
      }
    }
    return lines.join("\n") + "\n";
  }

  // =========================================================================
  // Clean paste — strips Word/Google Docs junk, keeps structure
  // =========================================================================

  function cleanPastedHtml(html) {
    var container = document.createElement("div");
    container.innerHTML = html;

    container.querySelectorAll("[style]").forEach(function (el) {
      el.removeAttribute("style");
    });

    container.querySelectorAll("[class]").forEach(function (el) {
      el.removeAttribute("class");
    });

    container.querySelectorAll("span").forEach(function (span) {
      var parent = span.parentNode;
      while (span.firstChild) {
        parent.insertBefore(span.firstChild, span);
      }
      parent.removeChild(span);
    });

    container
      .querySelectorAll("meta, style, link, script, title, xml")
      .forEach(function (el) {
        el.remove();
      });

    container.querySelectorAll("[id]").forEach(function (el) {
      el.removeAttribute("id");
    });

    return container.innerHTML;
  }

  // =========================================================================
  // Markdown textarea helpers
  // =========================================================================

  // Replace [start, end) in a textarea WITHOUT destroying the browser's
  // native undo stack.
  //
  // Assigning `textarea.value` (what every helper below used to do) clears
  // the undo history wholesale: after a programmatic write, Ctrl/Cmd+Z
  // restores neither the programmatic edit nor the typing that preceded it.
  // For a toolbar that is fine-ish; for suggestion accepts it is not —
  // using the feature once would silently disable undo for the rest of the
  // session.
  //
  // `document.execCommand("insertText")` inserts at the current selection
  // and pushes a proper undo entry, so the whole stack survives. It is
  // formally deprecated but remains the only cross-browser way to make an
  // undoable programmatic edit to a <textarea> (the InputEvent /
  // beforeinput path has no "apply this edit for me" counterpart). Every
  // engine we target still implements it; when it is missing or refuses
  // (returns false / throws) we fall back to the old value assignment plus
  // a synthetic `input` event, so callers keep working — just without undo.
  function replaceRange(textarea, start, end, text, selStart, selEnd) {
    textarea.focus();
    textarea.setSelectionRange(start, end);

    var ok = false;
    try {
      if (text === "") {
        // insertText with an empty string is a no-op in some engines, so an
        // empty replacement has to go through the delete command instead.
        ok = end > start ? document.execCommand("delete", false, null) : true;
      } else {
        ok = document.execCommand("insertText", false, text);
      }
    } catch (e) {
      ok = false;
    }

    if (!ok) {
      var v = textarea.value;
      textarea.value = v.substring(0, start) + text + v.substring(end);
      textarea.dispatchEvent(new Event("input", { bubbles: true }));
    }

    if (selStart != null) {
      textarea.setSelectionRange(selStart, selEnd == null ? selStart : selEnd);
    }
  }

  function markdownFormat(textarea, before, after, pushFn) {
    var start = textarea.selectionStart;
    var end = textarea.selectionEnd;
    var selected = textarea.value.substring(start, end);

    replaceRange(
      textarea,
      start,
      end,
      before + selected + after,
      start + before.length,
      end + before.length
    );
    if (pushFn) pushFn(textarea.value);
  }

  function markdownLinePrefix(textarea, prefix, pushFn) {
    var start = textarea.selectionStart;
    var text = textarea.value;

    // Find start of current line
    var lineStart = text.lastIndexOf("\n", start - 1) + 1;
    var lineEnd = text.indexOf("\n", start);
    if (lineEnd === -1) lineEnd = text.length;

    var line = text.substring(lineStart, lineEnd);

    // Toggle: if line already starts with prefix, remove it
    if (line.startsWith(prefix)) {
      replaceRange(
        textarea,
        lineStart,
        lineEnd,
        line.substring(prefix.length),
        start - prefix.length
      );
    } else {
      // Remove existing heading prefixes before adding new one
      var cleaned = line.replace(/^#{1,6}\s|^[-*+]\s|^\d+\.\s|^>\s/, "");
      var offset = prefix.length + cleaned.length - line.length;
      replaceRange(textarea, lineStart, lineEnd, prefix + cleaned, start + offset);
    }

    if (pushFn) pushFn(textarea.value);
  }

  function markdownIndent(textarea, direction, pushFn) {
    var start = textarea.selectionStart;
    var end = textarea.selectionEnd;
    var text = textarea.value;

    // Find all lines in selection (or current line if no selection)
    var lineStart = text.lastIndexOf("\n", start - 1) + 1;
    var lineEnd = text.indexOf("\n", end);
    if (lineEnd === -1) lineEnd = text.length;

    var block = text.substring(lineStart, lineEnd);
    var lines = block.split("\n");
    var indent = "  ";
    var delta = 0;
    var firstDelta = 0;

    var result = lines.map(function (line, i) {
      if (direction === "indent") {
        if (i === 0) firstDelta = indent.length;
        delta += indent.length;
        return indent + line;
      } else {
        if (line.startsWith(indent)) {
          if (i === 0) firstDelta = -indent.length;
          delta -= indent.length;
          return line.substring(indent.length);
        } else if (line.startsWith(" ")) {
          if (i === 0) firstDelta = -1;
          delta -= 1;
          return line.substring(1);
        }
        return line;
      }
    });

    replaceRange(
      textarea,
      lineStart,
      lineEnd,
      result.join("\n"),
      Math.max(lineStart, start + firstDelta),
      end + delta
    );
    if (pushFn) pushFn(textarea.value);
  }

  // `url` is supplied by the caller (the hook opens its own inline prompt
  // dialog) — window.prompt blocked the whole page here.
  function markdownLink(textarea, pushFn, url) {
    if (url == null || url === "") return;
    var start = textarea.selectionStart;
    var end = textarea.selectionEnd;
    var text = textarea.value;
    var selected = text.substring(start, end);

    var linkText = selected || "link text";
    var md = "[" + linkText + "](" + url + ")";

    replaceRange(textarea, start, end, md, start + 1, start + 1 + linkText.length);
    if (pushFn) pushFn(textarea.value);
  }

  function markdownInsert(textarea, snippet, pushFn) {
    var start = textarea.selectionStart;

    replaceRange(textarea, start, start, snippet, start + snippet.length);
    if (pushFn) pushFn(textarea.value);
  }

  // =========================================================================
  // Inline suggestions — trigger detection
  // =========================================================================
  //
  // The editor knows nothing about tags, users or components: it detects a
  // configured trigger character, asks the host what matches, renders the
  // list, and inserts the pick. Everything below is trigger-agnostic; the
  // rules come from the `suggestions` configs the server rendered as
  // `[data-leaf-suggest]` nodes.

  // Compile a character-class source (`[\p{L}\p{N}_-]`) into an anchored
  // single-char matcher. Unicode property escapes need the `u` flag; engines
  // without it fall back to a non-unicode compile, and a config that is not a
  // valid pattern at all is treated as "no constraint" rather than crashing
  // the hook.
  function suggestCharClass(source) {
    if (!source) return null;
    try {
      return new RegExp("^" + source + "$", "u");
    } catch (e) {
      try {
        return new RegExp("^" + source + "$");
      } catch (e2) {
        return null;
      }
    }
  }

  function parseSuggestConfig(el) {
    var trigger = el.dataset.trigger || "";
    if (!trigger) return null;
    var suffix = el.dataset.insertSuffix;
    return {
      trigger: trigger,
      boundary: el.dataset.boundary || "word_start",
      token: suggestCharClass(el.dataset.token || "[\\p{L}\\p{N}_-]"),
      firstChar: suggestCharClass(el.dataset.firstChar || ""),
      minChars: parseInt(el.dataset.minChars || "0", 10) || 0,
      maxLength: parseInt(el.dataset.maxLength || "0", 10) || 0,
      debounce: parseInt(el.dataset.debounce || "150", 10) || 0,
      maxResults: parseInt(el.dataset.maxResults || "10", 10) || 10,
      allowCreate: el.dataset.allowCreate === "true",
      // `#tag` keeps its trigger; a `/` command menu consumes it, so `/im`
      // becomes `<Image />` rather than `/<Image />`.
      keepTrigger: el.dataset.keepTrigger !== "false",
      insertSuffix: suffix == null ? " " : suffix,
      label: el.dataset.label || "",
      exclude: (el.dataset.exclude || "").split(",").filter(Boolean),
    };
  }

  // Is the character before the trigger an acceptable opening boundary?
  // `index === 0` counts as a boundary everywhere EXCEPT `not_line_start`:
  // in a textarea it means the very start of the content, and in the
  // contenteditable it means the start of a text node (i.e. right after a
  // block or inline element boundary).
  //
  // `not_line_start` exists for `#`, where the first column is already
  // spoken for: `# ` opens a heading and `#tag` mid-line opens the popup.
  // Excluding column zero leaves no keystroke where both are live.
  function suggestBoundaryOk(boundary, index, prevChar) {
    if (boundary === "not_line_start") {
      if (index === 0) return false;
      return prevChar !== "\n" && /[\s(]/.test(prevChar);
    }
    if (index === 0) return true;
    if (boundary === "any") return true;
    if (boundary === "line_start") return prevChar === "\n";
    return /[\s(]/.test(prevChar);
  }

  function suggestTokenOk(cfg, token) {
    if (cfg.firstChar && token.length > 0 && !cfg.firstChar.test(token.charAt(0))) {
      return false;
    }
    if (!cfg.token) return true;
    for (var i = 0; i < token.length; i++) {
      if (!cfg.token.test(token.charAt(i))) return false;
    }
    return true;
  }

  // Nearest ancestor matching `selector`, searched only up to (and not past)
  // the editor root — `Element.closest` would happily escape into the host page.
  function suggestClosestWithin(node, root, selector) {
    var el = node && node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
    while (el && el !== root) {
      if (el.nodeType === Node.ELEMENT_NODE && el.matches(selector)) return el;
      el = el.parentNode;
    }
    return null;
  }

  // =========================================================================
  // LiveView Hook
  // =========================================================================

  window.LeafHooks.Leaf = {
    mounted() {
      injectStyles();
      this.el.dataset.leafMountState = "ready";

      this._editorId = this.el.dataset.editorId;
      this._mode = this.el.dataset.mode || "visual";
      this._debounceMs = parseInt(this.el.dataset.debounce || "400", 10);
      this._readonly = this.el.dataset.readonly === "true";
      this._hasUpload = this.el.dataset.hasUpload === "true";
      this._syncInputName = this.el.dataset.syncInputName || "";
      this._denyLinks = this.el.dataset.denyLinks === "true";
      this._denyImages = this.el.dataset.denyImages === "true";
      this._denyVideo = this.el.dataset.denyVideo === "true";
      this._denyVisualMode = this.el.dataset.denyVisualMode === "true";
      this._denyHybridMode = this.el.dataset.denyHybridMode === "true";
      this._denyMarkdownMode = this.el.dataset.denyMarkdownMode === "true";
      this._denyHtmlMode = this.el.dataset.denyHtmlMode === "true";
      this._fallbackMode = this.el.dataset.fallbackMode || "hybrid";
      this._hashtags = this.el.dataset.hashtags === "true";
      this._wikiLinks = this.el.dataset.wikilinks === "true";
      this._collabOperations = this.el.dataset.collabOperations === "true";
      this._collabAwareness = this.el.dataset.collabAwareness === "true";
      this._collabDebug = this.el.dataset.collabDebug === "true";
      // Which version of the document we are starting from. Null, not zero,
      // when the host does not say: zero is a claim to have been written
      // against the beginning of time, and a host that keeps versions would
      // rebase the first edit over everything that ever happened.
      this._collabRevision = this.el.dataset.collabRevision
        ? parseInt(this.el.dataset.collabRevision, 10)
        : null;
      // Edits sent but not yet acknowledged. Someone else's operation is
      // written against a document that does not contain these, so it has to
      // be rebased over them before it can be applied here.
      this._pending = [];
      // Operations are not snapshots and must not wait like them. The snapshot
      // is the whole document and is what the host saves, so it can afford to
      // wait for a pause in typing; an operation is a few bytes and is what
      // another person is sitting there waiting to see.
      this._collabIntervalMs = parseInt(this.el.dataset.collabInterval || "40", 10);
      this._wikiLinksResolve = this.el.dataset.wikilinksResolve !== "false";
      this._wikiLinksFollow = this.el.dataset.wikilinksFollow || "click";
      this._warnStaleBundle();
      this._debounceTimer = null;
      this._markdownDebounceTimer = null;
      this._htmlDebounceTimer = null;
      // Host-integration state: flush-on-blur, dirty tracking, and
      // focus/blur/selection forwarding to the LiveView.
      this._flushOnBlur = this.el.dataset.flushOnBlur !== "false";
      // Lifecycle/extra events ({:leaf_focus}, {:leaf_blur},
      // {:leaf_selection_changed}, {:leaf_paste_image}) are opt-in: a host
      // LiveView that exports handle_info/2 (every Leaf host does, for
      // {:leaf_changed}) would crash on an unhandled message, so we only
      // push these when emit_events is set.
      this._emitEvents = this.el.dataset.emitEvents === "true";
      // Lowercased custom-tag names that render as atomic chips, so a
      // freshly inserted preserved tag becomes a chip immediately.
      this._preserveTags = (this.el.dataset.preserveTags || "")
        .split(",")
        .filter(Boolean);
      this._savedContent = this.el.dataset.initialMarkdown || "";
      this._hasEditorFocus = false;
      this._selectionTimer = null;
      // Cheap-wins state: char limit, smart typography, auto-grow, nav guard.
      this._maxlength = parseInt(this.el.dataset.maxlength || "0", 10) || 0;
      this._smartTypography = this.el.dataset.smartTypography === "true";
      this._protectNavigation = this.el.dataset.protectNavigation === "true";
      this._autoGrow = (this.el.dataset.height || "") === "auto";
      // Hybrid source-block engine state: the block currently swapped
      // to its markdown-source `<p data-leaf-source="...">` form. Null
      // when the cursor isn't inside an editable block.
      this._sourceBlock = null;

      this._visualEl = this.el.querySelector("[data-editor-visual]");
      this._visualWrapper = this.el.querySelector("[data-visual-wrapper]");
      this._markdownWrapper = this.el.querySelector(
        "[data-markdown-wrapper]"
      );
      this._htmlWrapper = this.el.querySelector("[data-html-wrapper]");
      this._syncFormInput(this.el.dataset.initialMarkdown || "");

      if (this._visualEl) {
        // Server-rendered content is pretty-printed, so block containers
        // hold whitespace-only text nodes between their block children —
        // a cursor trap in contenteditable (see `_stripInterBlockWhitespace`).
        // Loose lists (`<li><p>…</p></li>`) get the same normalization.
        this._normalizeRenderedHtml(this._visualEl);

        document.execCommand("defaultParagraphSeparator", false, "p");

        this._visualEl.addEventListener(
          "input",
          this._onVisualInput.bind(this)
        );

        this._visualEl.addEventListener(
          "input",
          function () {
            this._historyCapture("input");
          }.bind(this)
        );

        this._visualEl.addEventListener(
          "keydown",
          this._onVisualKeydown.bind(this)
        );

        this._visualEl.addEventListener("paste", this._onPaste.bind(this));
        // Copy and cut carry a task row's checkbox with it. Native copy
        // reads the DOM's text, and the box is a contenteditable=false span
        // with none — so a copied row arrived as a bare label, its state
        // left behind.
        this._visualEl.addEventListener("copy", this._onCopyOrCut.bind(this));
        this._visualEl.addEventListener("cut", this._onCopyOrCut.bind(this));

        this._visualEl.addEventListener(
          "click",
          function (e) {
            this._onWikiLinkClick(e);
          }.bind(this)
        );

        // In `:click` follow mode the caret must not land on a plain
        // mousedown over a link — landing would reveal the row's source, and
        // the click is a follow, not an edit. Every other way in still
        // edits: shift+click extends a selection, a second click of a
        // double-click selects the word, a modifier click is a follow that
        // never needed the caret suppressed.
        this._visualEl.addEventListener(
          "mousedown",
          function (e) {
            this._onWikiLinkMouseDown(e);
          }.bind(this)
        );

        if (
          this._visualEl.innerHTML.trim() === "" ||
          this._visualEl.innerHTML === "<br>"
        ) {
          this._visualEl.innerHTML = "<p><br></p>";
        }
      }

      this._setupToolbar();
      this._setupStickyToolbar();
      this._setupModeSwitcher();
      this._setupResponsiveToolbar();
      this._setupLinkPopover();
      // Floating selection bubble: opt-in per instance via toolbar_layout.
      // "floating" hides the fixed formatting buttons (CSS) and surfaces the
      // bubble; "both" keeps the fixed toolbar and adds the bubble; "fixed"
      // (default) leaves it off.
      this._toolbarLayout = this.el.dataset.toolbarLayout || "fixed";
      if (
        this._toolbarLayout === "floating" ||
        this._toolbarLayout === "both"
      ) {
        this._setupSelectionToolbar();
      }
      this._setupImageDragAndDrop();
      this._setupAtomicBlockEditing();
      this._setupCodeBlockTools();
      this._setupTaskLists();
      this._setupMaxlength();
      this._setupAutoGrow();
      this._setupNavigationGuard();
      this._registerMarkdownHelpers();
      this._setupMarkdownTextarea();
      this._setupHtmlTextarea();

      // The content LiveView rendered into the page came from the server the
      // same way a sync does, so it needs the same normalising — and this is
      // the only path that was missing it. An empty list item arrives as a
      // bare `<li></li>`, which has no height and nothing to click, so after a
      // reload a deliberately blank row was invisible: a click aimed at it
      // landed in a neighbouring item, and the next keystroke acted on that
      // one instead.
      if (this._visualEl) this._ensureListItemPlaceholders(this._visualEl);
      this._resolveWikiLinks();

      // After the surfaces exist, so the opening state is step zero and the
      // first edit has something to undo back to.
      this._historyInit();
      this._setupGripDoubleClick();
      this._setupMenuPlacement();
      // Entirely inert unless the host passed `suggestions` — see
      // `_setupSuggestions`: no configs means no state, no listeners and no
      // server-event registration.
      this._setupSuggestions();

      this._wordCountEl = this.el.querySelector("[data-word-count]");
      this._charCountEl = this.el.querySelector("[data-char-count]");
      this._readingTimeEl = this.el.querySelector("[data-reading-time]");
      this._maxlengthCountEl = this.el.querySelector("[data-maxlength-count]");
      this._updateCounts();

      // Mobile soft keyboard: when the on-screen keyboard opens, the
      // visual viewport shrinks but the document height doesn't, so
      // the caret can end up hidden behind the keyboard. Listen to
      // `visualViewport.resize` (the standard way to detect keyboard
      // open / close) and scroll the caret into view if focus is on
      // the editor at the moment of the resize.
      if (window.visualViewport) {
        this._onVisualViewportResize = function () {
          if (
            document.activeElement === this._visualEl &&
            this._visualEl
          ) {
            this._scrollCaretIntoView();
          }
        }.bind(this);
        window.visualViewport.addEventListener(
          "resize",
          this._onVisualViewportResize
        );
      }

      // Forward focus / blur / selection to the LiveView, and flush
      // pending changes on blur (so a host "Save" doesn't lose the last
      // debounced keystrokes).
      this._setupHostEvents();
      this._announceState();

      // Handle commands from LiveView
      this.handleEvent(
        "leaf-command:" + this._editorId,
        this._handleCommand.bind(this)
      );

      // The host saw this editor's fingerprint differ from its own and wants
      // the text itself, so it can say exactly which character parted company.
      this.handleEvent(
        "leaf-debug-snapshot:" + this._editorId,
        function () {
          var state = this._debugState();
          state.editor_id = this._editorId;
          state.markdown = this._currentMarkdown();
          state.visible =
            this._mode === "markdown" || this._mode === "html"
              ? state.markdown
              : this._visibleText(this._visualEl);
          this.pushEventTo(this.el, "debug_state", state);
        }.bind(this)
      );

      // The version this editor is now working from.
      this.handleEvent(
        "leaf-revision:" + this._editorId,
        function (payload) {
          if (!payload) return;
          if (typeof payload.revision === "number") this._collabRevision = payload.revision;
          this._forgetPendingThrough(payload.seq);
        }.bind(this)
      );

      // Where everyone else's caret is.
      this.handleEvent(
        "leaf-peer-cursors:" + this._editorId,
        function (payload) {
          this._renderPeerCursors((payload && payload.cursors) || []);
        }.bind(this)
      );

      // Someone else's edit, to be applied without moving this user's caret.
      this.handleEvent(
        "leaf-remote-operation:" + this._editorId,
        this._applyRemoteOperation.bind(this)
      );

      // The host's answer to a `resolve_links` request.
      this.handleEvent(
        "leaf-link-targets:" + this._editorId,
        function (payload) {
          this._onWikiLinkTargets(payload || {});
        }.bind(this)
      );

      // Handle HTML content pushed from server (markdown→visual sync)
      this.handleEvent(
        "leaf-set-html:" + this._editorId,
        function (payload) {
          if (this._visualEl && payload.html !== undefined) {
            this._visualEl.innerHTML = payload.html || "<p><br></p>";
            // Pretty-printed server HTML leaves whitespace-only text nodes
            // between block children (e.g. between `<li>`s); strip them so
            // a click can't land the caret in a `<ul>`-child text node,
            // which would make Enter/Backspace unable to act on the item.
            // Also unwrap loose list items (`<li><p>…</p></li>`) — markdown
            // with blank lines between items round-trips through here, and
            // the inner `<p>` otherwise traps Enter inside the item.
            this._normalizeRenderedHtml(this._visualEl);
            this._ensureListItemPlaceholders(this._visualEl);
            this._resolveWikiLinks();
            // DOM was replaced — old block references are stale
            this._dragHandleBlock = null;
          }
        }.bind(this)
      );

      // Handle HTML pushed to the HTML textarea (markdown→html conversion)
      this.handleEvent(
        "leaf-set-html-textarea:" + this._editorId,
        function (payload) {
          var ta = this._getHtmlTextarea();
          if (ta && payload.html !== undefined) {
            ta.value = payload.html;
          }
        }.bind(this)
      );

      // A re-rendered atomic block, answering `render_preserved`. The
      // preview (attributes, thumbnail, formatted children) is built
      // server-side because that is where the markdown renderer lives; the
      // client only knows how to swap the finished chip into place.
      this.handleEvent(
        "leaf-preserved-html:" + this._editorId,
        function (payload) {
          var target =
            this._pendingPreserved &&
            this._pendingPreserved.token === payload.token
              ? this._pendingPreserved.el
              : null;
          this._pendingPreserved = null;
          if (!target || !target.parentNode || !payload.html) return;

          var tmp = document.createElement("div");
          tmp.innerHTML = payload.html;
          var chip = tmp.firstElementChild;
          if (chip) target.parentNode.replaceChild(chip, target);
          this._debouncedPushVisualChange();
          this._updateCounts();
        }.bind(this)
      );

      // Fullscreen API state reflection. The browser owns entering/exiting
      // fullscreen (user click on our button, Escape, F11, programmatic
      // exit); our DOM follows by listening to `fullscreenchange`. Attach
      // both prefixed + unprefixed so Safari fires the handler too.
      this._fullscreenChangeHandler = this._syncFullscreenState.bind(this);
      document.addEventListener(
        "fullscreenchange",
        this._fullscreenChangeHandler
      );
      document.addEventListener(
        "webkitfullscreenchange",
        this._fullscreenChangeHandler
      );
    },

    // The socket dropped and came back. The editor kept on working — its
    // document has moved, and the host's may have moved too, or been restarted
    // out from under it. Neither can assume anything about the other, so say
    // what we are holding and let the host settle it.
    //
    // Without this the two simply carried on disagreeing: every keystroke
    // refused, the caret thrown about, until a snapshot happened to arrive and
    // reconciled it several characters later.
    reconnected() {
      this._announceState();
    },

    // What this editor is holding, and the last version it heard about. Sent
    // on mount as well: markdown that has been through the editor is not
    // always character-for-character what the host stored, and it is better to
    // agree about that before anyone types than to discover it on the first
    // keystroke.
    _announceState() {
      if (!this._collabOperations) return;

      var self = this;

      setTimeout(function () {
        if (!self.el || !self.el.isConnected) return;

        self.pushEventTo(self.el, "ready", {
          editor_id: self._editorId,
          markdown: self._currentMarkdown(),
          revision: typeof self._collabRevision === "number" ? self._collabRevision : null
        });
      }, 0);
    },

    updated() {
      // Server always renders data-leaf-mount-state="loading"; once the hook
      // is mounted we own the state, so keep it pinned to "ready" through
      // any parent-triggered re-render.
      this.el.dataset.leafMountState = "ready";

      // The host can add, change or drop trigger configs at runtime. This
      // returns immediately when the config set is unchanged, so the very
      // common case (a re-render caused by the host answering our own
      // suggest request) never disturbs an open popup.
      this._setupSuggestions();

      if (!this._visualEl) return;
      var newReadonly = this.el.dataset.readonly === "true";
      if (newReadonly !== this._readonly) {
        this._readonly = newReadonly;
        this._visualEl.contentEditable = !newReadonly;
      }
      var newHasUpload = this.el.dataset.hasUpload === "true";
      if (newHasUpload !== this._hasUpload) {
        this._hasUpload = newHasUpload;
      }
      this._denyLinks = this.el.dataset.denyLinks === "true";
      this._denyImages = this.el.dataset.denyImages === "true";
      this._denyVideo = this.el.dataset.denyVideo === "true";
      this._denyVisualMode = this.el.dataset.denyVisualMode === "true";
      this._denyHybridMode = this.el.dataset.denyHybridMode === "true";
      this._denyMarkdownMode = this.el.dataset.denyMarkdownMode === "true";
      this._denyHtmlMode = this.el.dataset.denyHtmlMode === "true";
      this._fallbackMode = this.el.dataset.fallbackMode || "hybrid";
      // A host can add or drop the `#` trigger at runtime, which is what
      // decides whether hashtags are decorated at all.
      this._hashtags = this.el.dataset.hashtags === "true";
      this._wikiLinks = this.el.dataset.wikilinks === "true";
      this._collabOperations = this.el.dataset.collabOperations === "true";
      this._collabAwareness = this.el.dataset.collabAwareness === "true";
      this._collabDebug = this.el.dataset.collabDebug === "true";
      // Which version of the document we are starting from. Null, not zero,
      // when the host does not say: zero is a claim to have been written
      // against the beginning of time, and a host that keeps versions would
      // rebase the first edit over everything that ever happened.
      this._collabRevision = this.el.dataset.collabRevision
        ? parseInt(this.el.dataset.collabRevision, 10)
        : null;
      // Edits sent but not yet acknowledged. Someone else's operation is
      // written against a document that does not contain these, so it has to
      // be rebased over them before it can be applied here.
      this._pending = [];
      // Operations are not snapshots and must not wait like them. The snapshot
      // is the whole document and is what the host saves, so it can afford to
      // wait for a pause in typing; an operation is a few bytes and is what
      // another person is sitting there waiting to see.
      this._collabIntervalMs = parseInt(this.el.dataset.collabInterval || "40", 10);
      this._wikiLinksResolve = this.el.dataset.wikilinksResolve !== "false";
      this._wikiLinksFollow = this.el.dataset.wikilinksFollow || "click";

      // The host can widen or narrow the deny list at runtime. Falling back
      // to a hardcoded "visual" stopped being safe once :visual itself
      // became deniable — the server names the first allowed mode instead.
      if (!this._isModeAllowed(this._mode)) {
        var fallbackTab = this.el.querySelector(
          '[data-mode-tab="' + this._fallbackMode + '"]'
        );
        if (fallbackTab) fallbackTab.click();
      }

      // Re-find drag handle after morphdom patch (element may have been replaced)
      if (this._visualWrapper) {
        var newHandle = this._visualWrapper.querySelector("[data-drag-handle]");
        if (newHandle && newHandle !== this._dragHandle) {
          this._dragHandle = newHandle;
          this._dragHandleBlock = null;
        }
      }

      // Re-show image popover if it was active but removed by morphdom
      if (this._imagePopoverTarget && this._imagePopoverEl && !this._imagePopoverEl.parentNode) {
        var imgTarget = this._imagePopoverTarget;
        this._imagePopoverEl = null;
        this._resizeHandles = null;
        this._showImagePopover(imgTarget);
      }

      // Re-insert sticky placeholder if morphdom removed it
      if (
        this._stickyPlaceholder &&
        !this._stickyPlaceholder.parentNode &&
        this._stickyToolbarEl
      ) {
        this._stickyToolbarEl.parentNode.insertBefore(
          this._stickyPlaceholder,
          this._stickyToolbarEl
        );
      }

      if (this._syncResponsiveToolbar) {
        this._syncResponsiveToolbar();
      }
    },

    destroyed() {
      if (this._closeTextPrompt) {
        this._closeTextPrompt();
      }
      if (this._debounceTimer) {
        clearTimeout(this._debounceTimer);
      }
      if (this._markdownDebounceTimer) {
        clearTimeout(this._markdownDebounceTimer);
      }
      if (this._htmlDebounceTimer) {
        clearTimeout(this._htmlDebounceTimer);
      }
      if (this._selectionTimer) {
        clearTimeout(this._selectionTimer);
        this._selectionTimer = null;
      }
      if (this._wikiLinkResolveTimer) {
        clearTimeout(this._wikiLinkResolveTimer);
        this._wikiLinkResolveTimer = null;
      }
      if (this._onSelectionChange) {
        document.removeEventListener(
          "selectionchange",
          this._onSelectionChange
        );
        this._onSelectionChange = null;
      }
      if (this._onTaskSelectionChange) {
        document.removeEventListener(
          "selectionchange",
          this._onTaskSelectionChange
        );
        this._onTaskSelectionChange = null;
      }
      if (this._onTaskMouseUpBound) {
        document.removeEventListener("mouseup", this._onTaskMouseUpBound);
        this._onTaskMouseUpBound = null;
      }
      if (this._typingDiagObserver) {
        this._typingDiagObserver.disconnect();
        this._typingDiagObserver = null;
      }
      if (this._awarenessTimer) {
        clearTimeout(this._awarenessTimer);
        this._awarenessTimer = null;
      }
      if (this._operationTimer) {
        clearTimeout(this._operationTimer);
        this._operationTimer = null;
      }
      if (this._onAwarenessSelectionChange) {
        document.removeEventListener(
          "selectionchange",
          this._onAwarenessSelectionChange
        );
        this._onAwarenessSelectionChange = null;
      }
      if (this._onAwarenessBlur) {
        this.el.removeEventListener("focusout", this._onAwarenessBlur);
        this._onAwarenessBlur = null;
      }
      if (this._onAwarenessResize) {
        window.removeEventListener("resize", this._onAwarenessResize);
        this._onAwarenessResize = null;
      }
      if (this._beforeUnloadHandler) {
        window.removeEventListener("beforeunload", this._beforeUnloadHandler);
        this._beforeUnloadHandler = null;
      }

      this._cleanupDrag();
      this._closeBlockMenu();
      if (this._imgObserver) {
        this._imgObserver.disconnect();
        this._imgObserver = null;
      }

      this._cleanupStickyToolbar();
      if (this._toolbarResizeObserver) {
        this._toolbarResizeObserver.disconnect();
        this._toolbarResizeObserver = null;
      }
      if (this._toolbarResizeRaf) {
        cancelAnimationFrame(this._toolbarResizeRaf);
        this._toolbarResizeRaf = null;
      }
      if (this._toolbarResizeHandler) {
        window.removeEventListener("resize", this._toolbarResizeHandler);
        this._toolbarResizeHandler = null;
      }
      this._closeEmojiPicker();
      this._closeSymbolPicker();
      this._dismissLinkPopover();
      this._dismissImageUrlDialog();
      this._teardownSelectionToolbar();
      if (this._onVisualViewportResize && window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          this._onVisualViewportResize
        );
        this._onVisualViewportResize = null;
      }
      if (this._gripTooltipCleanup) {
        this._gripTooltipCleanup();
        this._gripTooltipCleanup = null;
      }
      if (this._onAtomicDblClick && this._visualEl) {
        this._visualEl.removeEventListener("dblclick", this._onAtomicDblClick);
        this._onAtomicDblClick = null;
      }
      if (this._onAtomicClick && this._visualEl) {
        this._visualEl.removeEventListener("click", this._onAtomicClick);
        this._onAtomicClick = null;
      }
      this._selectedAtomic = null;
      if (this._onAtomicMediaError && this._visualEl) {
        this._visualEl.removeEventListener(
          "error",
          this._onAtomicMediaError,
          true
        );
        this._onAtomicMediaError = null;
      }
      this._pendingPreserved = null;
      if (this._imageDropdownMenu) {
        this._imageDropdownMenu.remove();
        this._imageDropdownMenu = null;
      }
      if (this._imageDropdownBackdrop) {
        this._imageDropdownBackdrop.remove();
        this._imageDropdownBackdrop = null;
      }
      if (this._onDocClickForPopover) {
        document.removeEventListener("mousedown", this._onDocClickForPopover);
      }
      if (this._fullscreenChangeHandler) {
        document.removeEventListener(
          "fullscreenchange",
          this._fullscreenChangeHandler
        );
        document.removeEventListener(
          "webkitfullscreenchange",
          this._fullscreenChangeHandler
        );
        this._fullscreenChangeHandler = null;
      }

      // Clean up global markdown helper functions
      var gid = this._editorId.replace(/-/g, "_") + "_markdown";
      delete window["markdownFormat_" + gid];
      delete window["markdownLinePrefix_" + gid];
      delete window["markdownLink_" + gid];
      delete window["markdownEditorInsert_" + gid];
      delete window["markdownIndent_" + gid];

      this._teardownSuggestions();
      this._teardownMenuPlacement();
    },

    // -- Markdown textarea setup --

    _registerMarkdownHelpers: function () {
      var self = this;
      var gid = this._editorId.replace(/-/g, "_") + "_markdown";

      var pushFn = function (value) {
        self._debouncedPushMarkdownChange(value);
      };

      window["markdownFormat_" + gid] = function (before, after) {
        var ta = self._getMarkdownTextarea();
        if (ta) markdownFormat(ta, before, after, pushFn);
      };

      window["markdownLinePrefix_" + gid] = function (prefix) {
        var ta = self._getMarkdownTextarea();
        if (ta) markdownLinePrefix(ta, prefix, pushFn);
      };

      window["markdownLink_" + gid] = function (url) {
        var ta = self._getMarkdownTextarea();
        if (ta) markdownLink(ta, pushFn, url);
      };

      window["markdownEditorInsert_" + gid] = function (snippet) {
        var ta = self._getMarkdownTextarea();
        if (ta) markdownInsert(ta, snippet, pushFn);
      };

      window["markdownIndent_" + gid] = function (direction) {
        var ta = self._getMarkdownTextarea();
        if (ta) markdownIndent(ta, direction, pushFn);
      };
    },

    _setupMarkdownTextarea: function () {
      var self = this;
      var textarea = this._getMarkdownTextarea();
      if (!textarea) return;

      this._markdownInputHandler = function () {
        self._debouncedPushMarkdownChange(textarea.value);
        self._updateCounts();
        self._historyCapture("markdown-input");
      };

      textarea.addEventListener("input", this._markdownInputHandler);

      // The textarea keeps a native stack of its own, but it is wiped by every
      // programmatic write and cannot cross a mode switch. Drive ours instead.
      textarea.addEventListener("keydown", function (e) {
        if (self._historyKeydown(e)) return;
        self._maybeSurroundTextarea(e, textarea);
      });
    },

    _debouncedPushMarkdownChange: function (content) {
      this._syncFormInput(content);
      if (this._markdownDebounceTimer)
        clearTimeout(this._markdownDebounceTimer);
      this._scheduleOperation();
      var self = this;
      this._markdownDebounceTimer = setTimeout(function () {
        self.pushEventTo(self.el, "markdown_content_changed", {
          editor_id: self._editorId,
          content: content,
          dirty: self._computeDirty(content),
        });
      }, this._debounceMs);
    },

    // -- HTML textarea setup --

    _setupHtmlTextarea: function () {
      var self = this;
      var textarea = this._getHtmlTextarea();
      if (!textarea) return;

      textarea.addEventListener("input", function () {
        self._debouncedPushHtmlChange(textarea.value);
        self._updateCounts();
        self._historyCapture("html-input");
      });

      textarea.addEventListener("keydown", function (e) {
        if (self._historyKeydown(e)) return;
        self._maybeSurroundTextarea(e, textarea);
      });
    },

    _getHtmlTextarea: function () {
      return document.getElementById(
        this._editorId + "-html-textarea"
      );
    },

    _setupGripDoubleClick: function () {
      // Double-clicking the resize grip on any of the three editors should
      // auto-fit that editor's height to its content. The native grip is UA
      // chrome and doesn't fire its own events, but the underlying element
      // still gets dblclick — we just check that the click landed in the
      // bottom-right corner where the grip lives. While we're here, also
      // show a small tooltip when hovering the grip area so the gesture is
      // discoverable.
      var self = this;
      var minHeight = parseInt(this.el.dataset.height || "480", 10);
      var GRIP_PX = 18;
      var tooltip = null;

      var ensureTooltip = function () {
        if (tooltip) return tooltip;
        tooltip = document.createElement("div");
        tooltip.className = "leaf-grip-tooltip";
        tooltip.textContent = "Drag to resize · Double-click to fit content";
        document.body.appendChild(tooltip);
        return tooltip;
      };

      var hideTooltip = function () {
        if (tooltip) tooltip.classList.remove("leaf-grip-tooltip-visible");
      };

      var showTooltipFor = function (el) {
        var rect = el.getBoundingClientRect();
        var tt = ensureTooltip();
        tt.classList.add("leaf-grip-tooltip-visible");
        // Position above the grip, right-aligned with the editor's right edge.
        var ttRect = tt.getBoundingClientRect();
        tt.style.left = Math.max(4, rect.right - ttRect.width - 4) + "px";
        tt.style.top = rect.bottom - GRIP_PX - ttRect.height - 6 + "px";
      };

      var fitToContent = function (el) {
        var prev = el.style.height;
        el.style.height = "auto";
        var natural = el.scrollHeight;
        if (!natural) {
          el.style.height = prev;
          return;
        }
        el.style.height = Math.max(minHeight, natural) + "px";
      };

      var attach = function (el) {
        if (!el) return;
        el.addEventListener("dblclick", function (e) {
          var rect = el.getBoundingClientRect();
          var inGripArea =
            e.clientX > rect.right - GRIP_PX &&
            e.clientY > rect.bottom - GRIP_PX;
          if (!inGripArea) return;
          e.preventDefault();
          fitToContent(el);
        });
        el.addEventListener("mousemove", function (e) {
          var rect = el.getBoundingClientRect();
          var inGripArea =
            e.clientX > rect.right - GRIP_PX &&
            e.clientY > rect.bottom - GRIP_PX;
          if (inGripArea) {
            showTooltipFor(el);
          } else {
            hideTooltip();
          }
        });
        el.addEventListener("mouseleave", hideTooltip);
      };

      attach(this._visualEl);
      attach(this._getMarkdownTextarea());
      attach(this._getHtmlTextarea());

      // Stash for cleanup in destroyed().
      this._gripTooltipCleanup = function () {
        if (tooltip && tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
        tooltip = null;
      };
    },

    _debouncedPushHtmlChange: function (content) {
      this._syncFormInput(htmlToMarkdown(content || ""));
      if (this._htmlDebounceTimer)
        clearTimeout(this._htmlDebounceTimer);
      var self = this;
      var hmd = htmlToMarkdown(content || "");
      this._htmlDebounceTimer = setTimeout(function () {
        self.pushEventTo(self.el, "html_content_changed", {
          editor_id: self._editorId,
          content: content,
          dirty: self._computeDirty(hmd),
        });
      }, this._debounceMs);
    },

    // -- Footer counts --

    _updateCounts: function () {
      if (!this._wordCountEl || !this._charCountEl) return;

      var text = "";
      if (this._mode === "visual") {
        text = this._visualEl ? this._visualEl.innerText : "";
      } else if (this._mode === "hybrid") {
        // Same contenteditable as visual, but strip the cursor-anchoring
        // `**`/`*`/`~~`/`# ` decoration spans before counting so the
        // numbers don't jitter as the cursor moves in/out of formatted
        // runs (decoration spans are present only while the cursor is
        // inside their wrapper).
        text = this._visualEl ? this._readPlainTextWithoutDecorations() : "";
      } else if (this._mode === "markdown") {
        var ta = this._getMarkdownTextarea();
        text = ta ? ta.value : "";
      } else if (this._mode === "html") {
        var ta = this._getHtmlTextarea();
        text = ta ? ta.value : "";
      }

      var trimmed = text.trim();
      var words = trimmed === "" ? 0 : trimmed.split(/\s+/).length;
      var chars = trimmed.length;

      this._wordCountEl.textContent = words + (words === 1 ? " word" : " words");
      this._charCountEl.textContent = chars + (chars === 1 ? " char" : " chars");

      // Reading time at ~200 wpm — a blank doc reads as "0 min read",
      // anything non-empty rounds up to at least 1.
      if (this._readingTimeEl) {
        var mins = words === 0 ? 0 : Math.max(1, Math.ceil(words / 200));
        this._readingTimeEl.textContent = mins + " min read";
      }

      // Character limit, when set.
      if (this._maxlengthCountEl && this._maxlength) {
        this._maxlengthCountEl.textContent = chars + " / " + this._maxlength;
      }
    },

    // -- Event handlers --

    _onVisualInput: function () {
      if (this._mode !== "visual" && this._mode !== "hybrid") return;
      this._dismissLinkPopover();
      // Lists left touching merge back into one. This is what makes a
      // divided list rejoin when the divider is deleted: 1-3, text, 1-3
      // becomes 1-6 again the moment the text between them is gone.
      this._mergeAdjacentLists();
      if (this._smartTypography) this._maybeSmartTypography();
      // Hybrid mode: re-scan the open source block on every keystroke so
      // the block visually retags itself the moment the user finishes a
      // marker (`# ` → h1, `**bold**` → bold body, etc.) without waiting
      // for the cursor to leave. The full render-to-HTML still happens
      // on cursor-leave (`_exitSourceMode`); this is just a live
      // re-tagging of `data-leaf-source` and a rebuild of the marker
      // span / body-text split inside the block.
      if (this._mode === "hybrid") {
        this._refreshSourceBlock();
        // List auto-format snaps `- ` / `1. ` (etc.) at the start of a
        // paragraph into a real `<ul><li>` / `<ol><li>` immediately, so
        // the user sees the proper bullet / number with normal styling
        // instead of a faded source marker.
        this._maybeAutoFormatList();
        // Blockquote auto-format snaps `> ` at the start of a paragraph
        // into a real `<blockquote><p>…</p></blockquote>`, mirroring the
        // list auto-format. Consecutive blockquote lines merge into the
        // same `<blockquote>`.
        this._maybeAutoFormatBlockquote();
      }
      this._debouncedPushVisualChange();
      this._updateCounts();
      // Scan LAST: the hybrid passes above can rebuild the caret's block,
      // so anything read before them would point at a detached text node.
      this._suggestScan();
    },

    _onVisualKeydown: function (e) {
      if (this._readonly) return;

      // While the suggestion popup is open it owns ↑/↓/Enter/Tab/Esc. This
      // has to come first so Enter can't also run the list auto-continue
      // further down.
      if (this._suggestKeydown(e)) return;

      // Before anything else: undo/redo must win over every shortcut below.
      if (this._historyKeydown(e)) return;

      // A click-selected atomic chip owns the keys that act on it —
      // delete, step out, open a line after — before any other handler.
      if (this._atomicSelectionKeydown(e)) return;

      // Wrapping a selection has to come before the handlers that treat a
      // printable key as replacing it.
      if (this._maybeSurroundSelection(e)) return;

      var mod = e.ctrlKey || e.metaKey;

      // Select-all + Backspace/Delete: Chrome's native deletion leaves
      // empty shells of non-text elements behind (custom preserved-tag
      // elements, task boxes, heading wrappers) that survive further
      // deletes and pollute later serialization. When the selection
      // covers the editor's entire content, reset wholesale instead.
      if (
        (e.key === "Backspace" || e.key === "Delete") &&
        this._selectionCoversAllContent()
      ) {
        e.preventDefault();
        // Capture BEFORE the wholesale reset. This is the case that made undo
        // look most broken: select all, delete, Ctrl+Z, nothing comes back.
        this._historyCaptureNow("select-all-delete");
        this._visualEl.innerHTML = "<p><br></p>";
        this._placeCaretIn(this._visualEl.firstChild);
        this._dragHandleBlock = null;
        this._debouncedPushVisualChange();
        return;
      }

      // Ctrl/Cmd+Shift+V arms a one-shot "paste as plain text" for the
      // paste event that immediately follows (read + cleared in _onPaste).
      if (mod && e.shiftKey && (e.key === "v" || e.key === "V")) {
        this._pasteAsPlainText = true;
      }

      // Hybrid source-mode Backspace / Delete: operate on the block's
      // markdown source string directly instead of trusting Chrome's
      // contenteditable deletion. Chrome's Backspace at the boundary
      // between a `<strong>` (or other inline) wrapper and adjacent
      // text eats a char OUT of the wrapper's hidden marker span
      // (the closing `**`), which silently breaks the match — even
      // though the user expected to delete a normal text char like a
      // space. Source-string deletion is predictable: take one char
      // off either side of the cursor's text-offset, rebuild.
      // Word deletion — Ctrl+Backspace/Delete (Win/Linux), Option on mac.
      // Chrome's native word-delete inside a source block runs its
      // selection through hidden marker spans and non-editable chrome and
      // silently refuses the edit (the "keydown with no beforeinput"
      // abort the typing tracer logs), so the word variant goes through
      // the same source-string surgery the single-char path uses.
      var wordDelete =
        (e.key === "Backspace" || e.key === "Delete") &&
        !e.metaKey &&
        !e.shiftKey &&
        e.ctrlKey !== e.altKey;

      if (
        this._mode === "hybrid" &&
        (!mod || wordDelete) &&
        (e.key === "Backspace" || e.key === "Delete") &&
        this._sourceBlock &&
        this._sourceBlock.isConnected
      ) {
        if (this._maybeHandleSourceDelete(e.key === "Backspace", wordDelete)) {
          e.preventDefault();
          return;
        }
      }

      // Hybrid: a list item's `- ` / `- [ ] ` marker is hidden (CSS) while
      // the cursor is in the body, so a plain Left from the body start
      // (`- [ ] |text`) skips the `display:none` marker span and jumps to
      // the PREVIOUS line — the user can never reach the marker to edit it.
      // Intercept: reveal the marker and step the caret onto it, landing
      // just after the last marker char (`- [ ]| text`).
      if (
        this._mode === "hybrid" &&
        !mod &&
        !e.shiftKey &&
        e.key === "ArrowLeft" &&
        this._sourceBlock &&
        this._sourceBlock.isConnected &&
        this._sourceBlock.tagName &&
        this._sourceBlock.tagName.toLowerCase() === "li"
      ) {
        var alSel = window.getSelection();
        if (alSel.rangeCount && alSel.getRangeAt(0).collapsed) {
          var alRange = alSel.getRangeAt(0);
          if (this._sourceBlock.contains(alRange.startContainer)) {
            var alOffset = this._textOffsetInBlock(
              this._sourceBlock,
              alRange.startContainer,
              alRange.startOffset
            );
            var alSrc = (this._sourceBlock.textContent || "").replace(
              /\u00a0/g,
              " "
            );
            var alM = alSrc.match(/^(- |\d+\. )(\[[ xX]\] )?/);
            var alMarkerLen = alM ? alM[0].length : 0;
            if (
              alMarkerLen > 0 &&
              alOffset === alMarkerLen &&
              !this._sourceBlock.classList.contains("leaf-marker-active")
            ) {
              e.preventDefault();
              this._sourceBlock.classList.add("leaf-marker-active");
              var alMarker =
                this._sourceBlock.querySelector(".leaf-list-marker");
              var alTextNode = alMarker ? alMarker.firstChild : null;
              if (alTextNode && alTextNode.nodeType === Node.TEXT_NODE) {
                var alNewRange = document.createRange();
                alNewRange.setStart(
                  alTextNode,
                  Math.min(alMarkerLen - 1, alTextNode.textContent.length)
                );
                alNewRange.collapse(true);
                alSel.removeAllRanges();
                alSel.addRange(alNewRange);
                this._refreshSourceBlock();
              }
              return;
            }
          }
        }
      }

      // Backspace inside an empty `<li>`: merge back into the
      // previous list item rather than letting Chrome's default exit
      // the list to a sibling `<p>`. The exit behavior leaves the
      // user stuck in a paragraph below the list — subsequent Enters
      // create `<p>`s instead of continuing the list, which the user
      // doesn't expect after just deleting the line they typed.
      if (
        (this._mode === "visual" || this._mode === "hybrid") &&
        !mod &&
        e.key === "Backspace"
      ) {
        var bsBlock = this._getCurrentBlock();
        if (
          bsBlock &&
          bsBlock.tagName &&
          bsBlock.tagName.toLowerCase() === "li"
        ) {
          var bsLiText = (bsBlock.textContent || "")
            .replace(/[​ ]/g, "")
            .trim();
          if (bsLiText === "") {
            e.preventDefault();
            var bsList = bsBlock.parentNode;
            var prevLi = bsBlock.previousElementSibling;
            if (
              prevLi &&
              prevLi.tagName &&
              prevLi.tagName.toLowerCase() === "li"
            ) {
              // Merge: drop the empty `<li>`, place caret at the
              // logical end of the previous item so the next Enter
              // builds a fresh sibling.
              bsList.removeChild(bsBlock);
              this._forgetSourceBlock(bsBlock);
              var bsRange = document.createRange();
              var lastNode = prevLi.lastChild;
              while (
                lastNode &&
                lastNode.nodeType === Node.ELEMENT_NODE &&
                lastNode.firstChild
              ) {
                lastNode = lastNode.lastChild;
              }
              if (lastNode && lastNode.nodeType === Node.TEXT_NODE) {
                bsRange.setStart(lastNode, lastNode.textContent.length);
              } else {
                bsRange.setStart(prevLi, prevLi.childNodes.length);
              }
              bsRange.collapse(true);
              var bsSel = window.getSelection();
              bsSel.removeAllRanges();
              bsSel.addRange(bsRange);
              this._visualEl.dispatchEvent(
                new Event("input", { bubbles: true })
              );
              return;
            }
            // No previous list item — this is the only `<li>` in
            // its list. Replace the whole list with a fresh `<p>`
            // so the user can keep typing without getting stuck in
            // an empty list.
            var bsListParent = bsList && bsList.parentNode;
            if (bsListParent) {
              var bsP = document.createElement("p");
              bsP.appendChild(document.createElement("br"));
              bsListParent.replaceChild(bsP, bsList);
              this._forgetSourceBlock(bsBlock);
              var bsRange2 = document.createRange();
              bsRange2.setStart(bsP, 0);
              bsRange2.collapse(true);
              var bsSel2 = window.getSelection();
              bsSel2.removeAllRanges();
              bsSel2.addRange(bsRange2);
              this._visualEl.dispatchEvent(
                new Event("input", { bubbles: true })
              );
            }
            return;
          }
        }
      }

      // Hybrid source-mode Enter: split the block's source string at
      // the cursor offset, keep the first half in the original block
      // (which then exits source mode and renders), open a new
      // `<p data-leaf-source>` for the second half, and move the
      // cursor there. Chrome's default Enter handling can't reliably
      // split content that includes the hidden marker spans —
      // splitting at `*|*bold**` typically dumps the user into a
      // fresh empty `<p>` and leaves the formatted run untouched.
      if (
        this._mode === "hybrid" &&
        !mod &&
        !e.shiftKey &&
        e.key === "Enter" &&
        this._sourceBlock &&
        this._sourceBlock.isConnected
      ) {
        if (this._maybeHandleSourceEnter()) {
          e.preventDefault();
          return;
        }
      }

      // Hybrid: if the cursor is logically past the bolded body (inside the
      // trailing decoration block, at the wrapper end, etc.) and the user
      // is about to insert a character, intercept and insert the char
      // outside the wrapper ourselves. preventDefault avoids browsers that
      // cache the selection at keydown-fire-time and would otherwise insert
      // at the original (inside-wrapper) position regardless of any mid-
      // handler selection moves.
      if (
        this._mode === "hybrid" &&
        !mod &&
        e.key &&
        e.key.length === 1
      ) {
        if (this._maybeRedirectPastTrailingBlock(e.key)) {
          e.preventDefault();
          return;
        }
      }

      // Arrow up/down crossing an `<hr>` boundary swaps it for an
      // editable `<p data-leaf-hr-source>---</p>` so the user can land
      // ON the rule with their cursor instead of skipping past it.
      if (
        (this._mode === "visual" || this._mode === "hybrid") &&
        !mod &&
        !e.shiftKey &&
        (e.key === "ArrowDown" || e.key === "ArrowUp")
      ) {
        if (this._maybeArrowIntoHr(e.key)) {
          e.preventDefault();
          return;
        }
      }

      if (mod && (e.key === "b" || e.key === "i" || (e.shiftKey && e.key === "x"))) {
        e.preventDefault();
        if (e.key === "b" && this._isInsideHeading()) return;
        var preSnapshot = this._snapshotFormattingElements();
        var cmd =
          e.key === "b"
            ? "bold"
            : e.key === "i"
              ? "italic"
              : "strikeThrough";
        document.execCommand(cmd, false, null);
        this._updateToolbarState();
        if (!this._refreshHybridSourceBlockAfterToolbarAction()) {
          this._deferredSyntaxRefresh(preSnapshot);
        }
        return;
      }
      if (mod && e.key === "u") {
        e.preventDefault();
        document.execCommand("underline", false, null);
        return;
      }
      if (mod && e.key === "k") {
        e.preventDefault();
        if (!this._denyLinks) this._insertLink();
        return;
      }

      if (e.key === "Tab" && !mod) {
        if (
          document.queryCommandState("insertUnorderedList") ||
          document.queryCommandState("insertOrderedList")
        ) {
          e.preventDefault();
          if (e.shiftKey) {
            document.execCommand("outdent", false, null);
          } else {
            document.execCommand("indent", false, null);
          }
          return;
        }
      }

      // Arrow-out-of-formatting: when the cursor is at the start/end of an
      // inline formatting element (bold, italic, strike, code, link,
      // spoiler, etc.) and the user presses ArrowLeft/ArrowRight, move it
      // to a definite position OUTSIDE the element (start of the adjacent
      // text node if there is one, otherwise insert an NBSP so the cursor
      // has a home and typing isn't pulled back inside by contenteditable
      // boundary affinity).
      //
      // Skip in hybrid mode — the user expects to navigate "through" the
      // visible markdown delimiters (e.g. the `**` decorations around a
      // bolded word). Using browser-default arrow behavior naturally gives
      // them an extra press to cross the boundary, which approximates
      // delimiter-by-delimiter movement.
      if (
        (e.key === "ArrowRight" || e.key === "ArrowLeft") &&
        !mod &&
        !e.shiftKey &&
        this._mode !== "hybrid"
      ) {
        var arrowSel = window.getSelection();
        if (
          arrowSel &&
          arrowSel.rangeCount &&
          arrowSel.isCollapsed
        ) {
          var arrowRange = arrowSel.getRangeAt(0);
          var arrowEl = this._inlineFormattingAncestor(arrowRange.endContainer);
          if (arrowEl) {
            var sPrefix = document.createRange();
            sPrefix.selectNodeContents(arrowEl);
            sPrefix.setEnd(arrowRange.endContainer, arrowRange.endOffset);
            var sLen = sPrefix.toString().length;
            var sTotal = arrowEl.textContent.length;

            var visualEl = this._visualEl;
            var landCursor = function (newRange) {
              newRange.collapse(true);
              arrowSel.removeAllRanges();
              arrowSel.addRange(newRange);
              visualEl.dispatchEvent(new Event("input", { bubbles: true }));
            };

            if (e.key === "ArrowRight" && sLen === sTotal) {
              e.preventDefault();
              var nextSib = arrowEl.nextSibling;
              var afterRange = document.createRange();
              if (
                nextSib &&
                nextSib.nodeType === Node.TEXT_NODE &&
                nextSib.textContent.length > 0
              ) {
                // Land 1 char into the next text rather than at offset 0,
                // because (nextSib, 0) is the same visual position as end
                // of the formatting wrapper — Chrome's boundary affinity
                // keeps the cursor styled "inside" until it crosses an
                // actual character. Advancing one char makes the escape
                // visible on a single press.
                afterRange.setStart(
                  nextSib,
                  Math.min(1, nextSib.textContent.length)
                );
              } else {
                // Non-breaking space: a regular trailing space at end of a
                // <p> collapses visually, leaving the cursor stuck at the
                // end of the formatting wrapper. NBSP doesn't collapse.
                var spaceR = document.createTextNode(" ");
                arrowEl.parentNode.insertBefore(
                  spaceR,
                  arrowEl.nextSibling
                );
                afterRange.setStart(spaceR, 1);
              }
              landCursor(afterRange);
              return;
            }
            if (e.key === "ArrowLeft" && sLen === 0) {
              e.preventDefault();
              var prevSib = arrowEl.previousSibling;
              var beforeRange = document.createRange();
              if (
                prevSib &&
                prevSib.nodeType === Node.TEXT_NODE &&
                prevSib.textContent.length > 0
              ) {
                beforeRange.setStart(prevSib, prevSib.textContent.length);
              } else {
                var spaceL = document.createTextNode(" ");
                arrowEl.parentNode.insertBefore(spaceL, arrowEl);
                beforeRange.setStart(spaceL, 0);
              }
              landCursor(beforeRange);
              return;
            }
          }
        }
      }

      if (e.key === "Enter" && !e.shiftKey) {
        // If the cursor is inside an inline formatting element (bold,
        // italic, strike, code, link, spoiler, etc.), break out of it:
        // insert a fresh <p> after the current block and move the cursor
        // there. Otherwise the browser would split the wrapper into the
        // new paragraph, making every subsequent paragraph carry the same
        // formatting.
        var sel = window.getSelection();
        if (sel && sel.rangeCount && sel.isCollapsed) {
          var fmt = this._inlineFormattingAncestor(sel.anchorNode);
          if (fmt) {
            var blockOfFmt = this._getCurrentBlock();
            if (blockOfFmt && blockOfFmt.parentNode) {
              e.preventDefault();
              var newP = document.createElement("p");
              newP.appendChild(document.createElement("br"));
              blockOfFmt.parentNode.insertBefore(
                newP,
                blockOfFmt.nextSibling
              );
              var nr = document.createRange();
              nr.setStart(newP, 0);
              nr.collapse(true);
              sel.removeAllRanges();
              sel.addRange(nr);
              this._visualEl.dispatchEvent(
                new Event("input", { bubbles: true })
              );
              return;
            }
          }
        }

        var block = this._getCurrentBlock();
        // Blockquote: same two-step UX as lists. Enter on a non-empty
        // line splits inside the `<blockquote>` (browser default does
        // this — we don't intercept); Enter on an EMPTY line at the
        // end of the quote exits the quote, with a fresh `<p>` placed
        // after the `<blockquote>`. Shift+Enter (further down) always
        // inserts a `<br>` soft break, regardless of emptiness.
        var bqQuote = null;
        if (
          block &&
          block.tagName &&
          block.tagName.toLowerCase() === "blockquote"
        ) {
          bqQuote = block;
        } else if (
          block &&
          block.parentNode &&
          block.parentNode.tagName &&
          block.parentNode.tagName.toLowerCase() === "blockquote"
        ) {
          bqQuote = block.parentNode;
        }
        if (bqQuote) {
          var bqInnerBlock = bqQuote === block ? bqQuote : block;
          var bqInnerEmpty =
            (bqInnerBlock.textContent || "")
              .replace(/[​ ]/g, "")
              .trim() === "";
          if (bqInnerEmpty) {
            e.preventDefault();
            var bqAfter = document.createElement("p");
            bqAfter.appendChild(document.createElement("br"));
            if (bqQuote.parentNode) {
              bqQuote.parentNode.insertBefore(bqAfter, bqQuote.nextSibling);
            }
            // Drop the empty inner block (or the whole blockquote if
            // that was the only thing left in it).
            if (bqInnerBlock !== bqQuote && bqInnerBlock.parentNode) {
              bqInnerBlock.parentNode.removeChild(bqInnerBlock);
            }
            if (!bqQuote.firstChild) {
              bqQuote.parentNode.removeChild(bqQuote);
            }
            var bqRange = document.createRange();
            bqRange.setStart(bqAfter, 0);
            bqRange.collapse(true);
            var bqSel = window.getSelection();
            bqSel.removeAllRanges();
            bqSel.addRange(bqRange);
            this._visualEl.dispatchEvent(
              new Event("input", { bubbles: true })
            );
            return;
          }
          // Non-empty line in the blockquote — let the browser's
          // default Enter split the line into a new `<p>` inside the
          // quote, matching list-item continuation behavior.
        }

        // Enter inside an `<li>`: Chrome's default usually splits the
        // item into a new sibling `<li>`, but after a prior list-exit
        // (empty-item + Enter → `<p>`) or after editing operations
        // that leave the list in an odd state, the default can fall
        // through to a plain `<p>` instead. Handle the split manually
        // so the list always continues — empty item still exits to a
        // `<p>` below as expected.
        if (block && block.tagName && block.tagName.toLowerCase() === "li") {
          var liParent = block.parentNode;
          var isTaskLi = block.classList && block.classList.contains("leaf-task");
          var liText = (block.textContent || "").replace(/[​ ]/g, "").trim();
          if (liText === "") {
            e.preventDefault();

            // Enter on an empty item leaves the list — at the end of a
            // list and in the middle of one alike, the way Obsidian reads
            // it. Mid-list, the items below move into a fresh list under
            // the exit paragraph, so the list visibly divides in two — and
            // with nothing forcing the tail's numbering, a divided 1-6
            // reads 1-3, text, 1-3. Rejoining is the merge pass's job:
            // delete the divider and the halves become 1-6 again.
            var liGrandparent = liParent && liParent.parentNode;
            if (liGrandparent && liParent) {
              var trailingItems = [];
              var trailSib = block.nextSibling;
              while (trailSib) {
                var trailNext = trailSib.nextSibling;
                trailingItems.push(trailSib);
                trailSib = trailNext;
              }

              liParent.removeChild(block);

              var exitRange = document.createRange();

              // Empty paragraph divider — a real, draggable block. For a
              // middle item the trailing items move into a new list below
              // it, so the list visibly divides in two with an editable
              // empty line between. For the last item it's just the exit
              // paragraph after the list.
              var exitP = document.createElement("p");
              exitP.appendChild(document.createElement("br"));
              liGrandparent.insertBefore(exitP, liParent.nextSibling);

              if (trailingItems.length) {
                var splitList = document.createElement(
                  liParent.tagName.toLowerCase()
                );
                for (var si = 0; si < trailingItems.length; si++) {
                  splitList.appendChild(trailingItems[si]);
                }
                liGrandparent.insertBefore(splitList, exitP.nextSibling);
              }

              exitRange.setStart(exitP, 0);

              if (!liParent.firstChild) {
                liGrandparent.removeChild(liParent);
              }

              exitRange.collapse(true);
              var exitSel = window.getSelection();
              exitSel.removeAllRanges();
              exitSel.addRange(exitRange);
              this._visualEl.dispatchEvent(
                new Event("input", { bubbles: true })
              );
            }
            return;
          }
          // Non-empty `<li>`: split at the cursor so content after the
          // caret moves into a fresh sibling `<li>`.
          e.preventDefault();
          var liSel = window.getSelection();
          var liRange = liSel.getRangeAt(0);
          var newLi = document.createElement("li");
          // Continue the checklist: a new task item carries its own
          // (unchecked) checkbox box; the text after the caret follows it.
          if (isTaskLi) {
            newLi.className = "leaf-task";
            newLi.setAttribute("data-checked", "false");
            var newTaskBox = document.createElement("span");
            newTaskBox.className = "leaf-task-box";
            newTaskBox.setAttribute("contenteditable", "false");
            newLi.appendChild(newTaskBox);
          }
          var afterRange = document.createRange();
          afterRange.setStart(liRange.startContainer, liRange.startOffset);
          afterRange.setEndAfter(block.lastChild || block);
          var afterFragment = afterRange.extractContents();
          newLi.appendChild(afterFragment);
          // ZWSP placeholder — Chrome's caret affinity in an empty
          // `<li>` with just a `<br>` filler tends to hide the cursor
          // until the user types something (and the list-item number
          // / bullet sometimes hides too). A zero-width-space text
          // node gives the cursor a real text-node home so it renders
          // immediately; the ZWSP is stripped from markdown output by
          // the existing `_serializeBlockInline` / `htmlToMarkdown`
          // scrubbers.
          // A task item always has the box as firstChild, so emptiness is
          // measured by the lack of a text node after it.
          this._ensureListItemPlaceholder(newLi);
          liParent.insertBefore(newLi, block.nextSibling);
          // The item we split away from can be left blank too — splitting at
          // the very start of a line moves everything into the new item.
          this._ensureListItemPlaceholder(block);
          var liNewRange = document.createRange();
          var firstText = this._firstTextDescendant(newLi);
          if (firstText) {
            // Land cursor AFTER the ZWSP so the next keystroke types
            // past it (the ZWSP is invisible but acts as the caret
            // anchor until real text replaces the gap).
            var offsetInText =
              firstText.textContent === "​"
                ? 1
                : 0;
            liNewRange.setStart(firstText, offsetInText);
          } else {
            liNewRange.setStart(newLi, 0);
          }
          liNewRange.collapse(true);
          liSel.removeAllRanges();
          liSel.addRange(liNewRange);
          this._visualEl.dispatchEvent(
            new Event("input", { bubbles: true })
          );
          return;
        }
      }

      if (e.key === "Enter" && e.shiftKey) {
        // Force a soft break inside the current block. Some browsers
        // (notably Chromium with defaultParagraphSeparator set to "p")
        // wrap insertHTML output in a new <p> when the cursor is at the
        // end of a block — manually inserting a <br> via the Range API
        // sidesteps that and guarantees the break stays inside the
        // current parent.
        e.preventDefault();
        var sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        var range = sel.getRangeAt(0);
        range.deleteContents();

        // Determine if cursor sits at the end of the current block before
        // we mutate the DOM. Using textContent length (rather than
        // checking br.nextSibling after the insert) avoids false negatives
        // when range.insertNode splits a text node and leaves an empty
        // sibling text node behind.
        var block = this._getCurrentBlock();
        var atEndOfBlock = false;
        if (block) {
          var prefix = document.createRange();
          prefix.selectNodeContents(block);
          prefix.setEnd(range.endContainer, range.endOffset);
          atEndOfBlock = prefix.toString().length === block.textContent.length;
        }

        var br = document.createElement("br");
        range.insertNode(br);

        // At end of a block the inserted <br> alone has no visible height
        // — append a filler <br> so the cursor lands on a visible empty
        // line. This is the standard contenteditable trick. Mark it with
        // data-leaf-filler so htmlToMarkdown emits "" for it and the
        // <br><br> pair doesn't round-trip as a paragraph break.
        if (atEndOfBlock) {
          var filler = document.createElement("br");
          filler.setAttribute("data-leaf-filler", "");
          br.parentNode.insertBefore(filler, br.nextSibling);
        }

        var newRange = document.createRange();
        newRange.setStartAfter(br);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);

        this._visualEl.dispatchEvent(new Event("input", { bubbles: true }));
        return;
      }
    },

    // Copy or cut a checklist row — or a run of them — with the checkboxes.
    //
    // The box holds no text, so native copy gives just the labels: paste the
    // row anywhere and its state is gone. When the selection covers whole
    // task rows and nothing else, the clipboard gets the real thing instead —
    // GFM markdown as text ("- [x] label") for editors that read text, and
    // GFM html (li > input[type=checkbox]) for the html path, which is what
    // our own paste rebuilds a working checkbox from. Anything messier than
    // whole rows — half a row, a row plus a paragraph — stays with the
    // native behaviour rather than guessing.
    // `<li><input type="checkbox"> label` → a working task item. The input
    // is interchange, not furniture: left in place it would be an editable
    // form control inside the content, serialized as nothing.
    _adoptPastedCheckboxes: function (container) {
      var boxes = container.querySelectorAll('li > input[type="checkbox"]');

      for (var i = 0; i < boxes.length; i++) {
        var input = boxes[i];
        var li = input.parentNode;

        li.classList.add("leaf-task");
        li.setAttribute(
          "data-checked",
          input.checked || input.hasAttribute("checked") ? "true" : "false"
        );

        var box = document.createElement("span");
        box.className = "leaf-task-box";
        box.setAttribute("contenteditable", "false");
        li.replaceChild(box, input);

        // The interchange form puts a space between the input and the label;
        // ours puts the label straight after the box.
        var after = box.nextSibling;
        if (after && after.nodeType === Node.TEXT_NODE) {
          after.textContent = after.textContent.replace(/^[\s\u00a0]+/, "");
        }
      }
    },

    _onCopyOrCut: function (e) {
      var clipboardData = e.clipboardData || window.clipboardData;
      if (!clipboardData || !this._visualEl) return;
      if (this._mode !== "visual" && this._mode !== "hybrid") return;

      var sel = window.getSelection();
      if (!sel || !sel.rangeCount || sel.isCollapsed) return;

      var range = sel.getRangeAt(0);
      if (!this._visualEl.contains(range.commonAncestorContainer)) return;

      var covered = this._coveredListItems(range);
      if (!covered.length) return;

      // Whole rows and nothing else: the selection's own text must be the
      // rows' text and no more, or this is a mixed selection native copy
      // understands better. Compared with ALL whitespace removed, against
      // the TOP rows' full text (a nested row's text is inside its
      // parent's); a row mid-edit also carries its marker as span text,
      // which is representation, not extra content.
      var scrub = function (text) {
        return (text || "").replace(/[\u200B\uFEFF\s\u00a0]/g, "");
      };
      var rowText = covered
        .filter(function (item) {
          return item.top;
        })
        .map(function (item) {
          return item.li.textContent;
        })
        .join("");
      if (scrub(range.toString()) !== scrub(rowText)) return;

      var markdown = covered
        .map(function (item) {
          return item.indent + item.marker + item.label;
        })
        .join("\n");

      e.preventDefault();
      clipboardData.setData("text/plain", markdown);
      clipboardData.setData("text/html", this._coveredItemsHtml(covered));

      if (e.type === "cut" && !this._readonly) {
        for (var i = 0; i < covered.length; i++) {
          if (!covered[i].top) continue;
          var li = covered[i].li;
          var list = li.parentNode;
          if (!list) continue;
          this._forgetSourceBlock(li);
          list.removeChild(li);
          // A list with nothing left in it is not a list.
          if (!list.firstElementChild && list.parentNode) {
            list.parentNode.removeChild(list);
          }
        }
        this._visualEl.dispatchEvent(new Event("input", { bubbles: true }));
      }
    },

    // The covered rows as html for the clipboard: their real list structure,
    // cloned, with the editor's furniture swapped for interchange — each
    // task box becomes input[type=checkbox], which is what our own paste
    // (and GitHub's renderer) rebuilds a working checkbox from, and
    // source-marker spans go entirely.
    _coveredItemsHtml: function (covered) {
      var container = document.createElement("div");
      var currentList = null;
      var currentSource = null;

      for (var i = 0; i < covered.length; i++) {
        var item = covered[i];
        if (!item.top) continue;

        var list = item.li.parentNode;
        if (list !== currentSource) {
          currentSource = list;
          currentList = document.createElement(list.tagName.toLowerCase());
          if (currentList.tagName.toLowerCase() === "ol") {
            currentList.setAttribute("start", parseInt(item.marker, 10) || 1);
          }
          container.appendChild(currentList);
        }

        var clone = item.li.cloneNode(true);

        var markers = clone.querySelectorAll(".leaf-source-marker");
        for (var m = 0; m < markers.length; m++) {
          markers[m].parentNode.removeChild(markers[m]);
        }

        // Each box reads its own row's state — a covered parent can carry
        // covered children with states of their own — BEFORE the editor
        // attributes are stripped.
        var boxes = clone.querySelectorAll(".leaf-task-box");
        for (var b = 0; b < boxes.length; b++) {
          var box = boxes[b];
          var row = box.parentNode;
          while (row && row.tagName && row.tagName.toLowerCase() !== "li") {
            row = row.parentNode;
          }

          var input = document.createElement("input");
          input.setAttribute("type", "checkbox");
          if (row && row.getAttribute("data-checked") === "true") {
            input.setAttribute("checked", "");
          }

          box.parentNode.replaceChild(input, box);
          input.parentNode.insertBefore(
            document.createTextNode(" "),
            input.nextSibling
          );
        }

        var rows = [clone].concat(
          Array.prototype.slice.call(clone.querySelectorAll("li"))
        );
        for (var a = 0; a < rows.length; a++) {
          rows[a].removeAttribute("data-leaf-source");
          rows[a].removeAttribute("class");
          rows[a].removeAttribute("data-checked");
        }

        currentList.appendChild(clone);
      }

      return container.innerHTML.replace(/[\u200B\uFEFF]/g, "");
    },

    // The literal marker a row would show in source form: "- ", "3. ",
    // "3. [x] ". One computation for the selection reveal and the clipboard,
    // so what looks selected and what copies never disagree.
    _listItemMarker: function (li) {
      var list = li.parentNode;
      var tag = list && list.tagName ? list.tagName.toLowerCase() : "";
      var marker;

      if (tag === "ol") {
        var startAttr = parseInt(list.getAttribute("start"), 10);
        var start = isNaN(startAttr) ? 1 : startAttr;
        marker = Array.prototype.indexOf.call(list.children, li) + start + ". ";
      } else {
        marker = "- ";
      }

      if (li.classList && li.classList.contains("leaf-task")) {
        marker += li.getAttribute("data-checked") === "true" ? "[x] " : "[ ] ";
      }

      return marker;
    },

    // How deep a row sits, as the serializer's indent: two spaces per ul
    // level, three per ol, matching convertList exactly — clipboard markdown
    // that disagrees with the editor's own serializer would paste back
    // differently than it copied out.
    _listItemIndent: function (li) {
      var indent = "";
      var list = li.parentNode;

      while (list && list !== this._visualEl) {
        var host = list.parentNode;
        if (host && host.tagName && host.tagName.toLowerCase() === "li") {
          indent += list.tagName.toLowerCase() === "ol" ? "   " : "  ";
          list = host.parentNode;
        } else {
          break;
        }
      }

      return indent;
    },

    // The list rows whose visible text the range covers entirely, in
    // document order, with marker, label and depth read off the DOM.
    _coveredListItems: function (range) {
      var items = this._visualEl.querySelectorAll("li");
      var covered = [];

      for (var i = 0; i < items.length; i++) {
        var li = items[i];
        var first = this._firstTextDescendant(li);
        var walker = document.createTreeWalker(li, NodeFilter.SHOW_TEXT, null, false);
        var last = null;
        var node;
        while ((node = walker.nextNode())) last = node;

        if (!first || !last) continue;

        var inRange;
        try {
          inRange =
            range.comparePoint(first, 0) === 0 &&
            range.comparePoint(last, last.textContent.length) === 0;
        } catch (err) {
          inRange = false;
        }
        if (!inRange) continue;

        // The label is the row's OWN text: minus its chrome (the box holds
        // none, but a row mid-edit carries its marker as literal span text)
        // and minus any nested list, whose rows speak for themselves.
        var clone = li.cloneNode(true);
        var chrome = clone.querySelectorAll(
          ".leaf-task-box, .leaf-source-marker, ul, ol"
        );
        for (var c = 0; c < chrome.length; c++) {
          chrome[c].parentNode.removeChild(chrome[c]);
        }

        // A row whose ancestor row is also covered rides along inside it —
        // the clipboard html and cut act only on the top of each covered
        // subtree.
        var top = true;
        for (var t = 0; t < covered.length; t++) {
          if (covered[t].li.contains(li)) {
            top = false;
            break;
          }
        }

        covered.push({
          li: li,
          top: top,
          isTask: li.classList.contains("leaf-task"),
          checked: li.getAttribute("data-checked") === "true",
          marker: this._listItemMarker(li),
          indent: this._listItemIndent(li),
          label: clone.textContent
            .replace(/[\u200B\uFEFF]/g, "")
            .replace(/\u00a0/g, " ")
            .trim()
        });
      }

      return covered;
    },

    _onPaste: function (e) {
      var clipboardData = e.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      // 1. Image on the clipboard (screenshot, copied image). Hand the file
      //    to the host's upload flow rather than baking a giant base64 blob
      //    into the content; if no upload_handler is wired, fall back to an
      //    inline data URL so paste still does something useful.
      var imageFile = this._clipboardImageFile(clipboardData);
      if (imageFile) {
        e.preventDefault();
        this._handlePastedImage(imageFile);
        return;
      }

      // 2. "Paste as plain text" (Ctrl/Cmd+Shift+V armed the flag on keydown):
      //    insert raw text, no HTML cleaning, no autolinking.
      if (this._pasteAsPlainText) {
        this._pasteAsPlainText = false;
        var plain = clipboardData.getData("text/plain");
        if (plain) {
          e.preventDefault();
          document.execCommand("insertText", false, plain);
          return;
        }
      }

      // 3. Rich HTML — clean Word/web junk down to sane markup.
      var html = clipboardData.getData("text/html");
      if (html) {
        e.preventDefault();
        var cleaned = cleanPastedHtml(html);
        var container = document.createElement("div");
        container.innerHTML = cleaned;

        // GFM checklists — from our own copy, GitHub, or anywhere else that
        // writes `li > input[type=checkbox]` — become real task items. The
        // cleaner has already stripped classes, so this is the only form a
        // checkbox can arrive in.
        this._adoptPastedCheckboxes(container);

        if (this._denyLinks) {
          container.querySelectorAll("a").forEach(function (anchor) {
            var parent = anchor.parentNode;
            while (anchor.firstChild) {
              parent.insertBefore(anchor.firstChild, anchor);
            }
            parent.removeChild(anchor);
          });
        }

        if (this._denyImages) {
          container.querySelectorAll("img").forEach(function (img) {
            img.remove();
          });
        }

        document.execCommand("insertHTML", false, container.innerHTML);
        return;
      }

      // 4. Tabular plain text (TSV/CSV) → a table.
      var text = clipboardData.getData("text/plain");
      if (
        text &&
        (this._mode === "visual" || this._mode === "hybrid") &&
        this._maybePasteTabular(text)
      ) {
        e.preventDefault();
        return;
      }

      // 5. A bare URL on the clipboard: if text is selected, turn it into a
      //    link to that URL; otherwise drop an autolinked anchor. Anything
      //    else falls through to the browser's native plain-text paste.
      if (text && /^https?:\/\/\S+$/.test(text.trim())) {
        var url = text.trim();
        e.preventDefault();
        var sel = window.getSelection();
        if (
          sel &&
          !sel.isCollapsed &&
          this._visualEl &&
          this._visualEl.contains(sel.anchorNode)
        ) {
          document.execCommand("createLink", false, url);
        } else {
          var safe = url
            .replace(/&/g, "&amp;")
            .replace(/"/g, "&quot;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;");
          document.execCommand(
            "insertHTML",
            false,
            '<a href="' + safe + '">' + safe + "</a>"
          );
        }
        return;
      }
    },

    // Find an image File on the clipboard, or null. Covers both the
    // `items` (Chrome/Safari screenshots) and `files` access paths.
    _clipboardImageFile: function (clipboardData) {
      if (clipboardData.items) {
        for (var i = 0; i < clipboardData.items.length; i++) {
          var item = clipboardData.items[i];
          if (item.kind === "file" && item.type.indexOf("image/") === 0) {
            var f = item.getAsFile();
            if (f) return f;
          }
        }
      }
      if (clipboardData.files && clipboardData.files.length) {
        for (var j = 0; j < clipboardData.files.length; j++) {
          if (clipboardData.files[j].type.indexOf("image/") === 0) {
            return clipboardData.files[j];
          }
        }
      }
      return null;
    },

    // Read a pasted image to a data URL, then either hand it to the host
    // (so its upload_handler can persist it and swap in a real URL via
    // send_update(:insert_image)) or insert it inline as a fallback.
    _handlePastedImage: function (file) {
      var self = this;
      var reader = new FileReader();
      reader.onload = function () {
        var dataUrl = reader.result;
        if (self._hasUpload && self._emitEvents) {
          self.pushEventTo(self.el, "paste_image", {
            editor_id: self._editorId,
            data_url: dataUrl,
            name: file.name || "pasted-image",
            mime: file.type || "image/png",
          });
        } else if (self._visualEl) {
          self._visualEl.focus();
          document.execCommand(
            "insertHTML",
            false,
            '<img src="' + dataUrl + '" alt="" draggable="true" />'
          );
          self._debouncedPushVisualChange();
        }
      };
      reader.readAsDataURL(file);
    },

    // -- Push content to LiveView --

    _debouncedPushVisualChange: function () {
      if (this._debounceTimer) clearTimeout(this._debounceTimer);
      if (this._visualEl) {
        this._syncFormInput(htmlToMarkdown(this._visualEl.innerHTML));
      }
      this._scheduleOperation();
      this._debounceTimer = setTimeout(
        function () {
          if (!this._visualEl) return;
          var html = this._visualEl.innerHTML;
          var markdown = htmlToMarkdown(html);
          this._syncFormInput(markdown);
          this.pushEventTo(this.el, "content_changed", {
            editor_id: this._editorId,
            html: html,
            markdown: markdown,
            dirty: this._computeDirty(markdown),
          });
        }.bind(this),
        this._debounceMs
      );
    },

    _syncFormInput: function (markdown) {
      if (!this._syncInputName) return;

      var form = this.el.closest("form");
      if (!form) return;

      var input = form.querySelector(
        'input[type="hidden"][data-leaf-sync-input="' + this._editorId + '"]'
      );

      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = this._syncInputName;
        input.dataset.leafSyncInput = this._editorId;
        form.appendChild(input);
      }

      input.value = markdown || "";
    },

    // -- Host integration: focus / blur / selection / flush / dirty --

    _setupHostEvents: function () {
      var self = this;

      // Re-baseline the dirty snapshot from the rendered surface now that
      // all surfaces are set up. For the textarea modes this equals the
      // initial markdown exactly; for visual/hybrid it captures the
      // round-trip-normalized form, so an untouched editor reads clean.
      this._savedContent = this._currentMarkdown();

      // Container-level focusin/focusout so that moving focus between the
      // editing surface and the toolbar (a real focus shift) does NOT read
      // as a blur. We only emit blur once focus has genuinely left the
      // whole component (checked on the next tick via document.activeElement).
      // focusin/focusout stay attached regardless of emit_events because
      // flush-on-blur and focus tracking depend on them; only the host-bound
      // focus/blur *pushes* are gated.
      this._onFocusIn = function () {
        if (!self._hasEditorFocus) {
          self._hasEditorFocus = true;
          if (self._emitEvents) {
            self.pushEventTo(self.el, "focus", { editor_id: self._editorId });
          }
        }
      };

      this._onFocusOut = function () {
        setTimeout(function () {
          if (!self.el || !self.el.isConnected) return;
          if (self.el.contains(document.activeElement)) return;
          if (!self._hasEditorFocus) return;
          self._hasEditorFocus = false;
          // Focus genuinely left the editor — the popup goes with it. Row
          // clicks preventDefault on mousedown, so picking never lands here.
          self._suggestClose();
          if (self._flushOnBlur) self._flushPending();
          if (self._emitEvents) {
            self.pushEventTo(self.el, "blur", { editor_id: self._editorId });
          }
        }, 0);
      };

      this.el.addEventListener("focusin", this._onFocusIn);
      this.el.addEventListener("focusout", this._onFocusOut);

      // selectionchange is a document-level event; only wire it when the
      // host has opted into receiving {:leaf_selection_changed}.
      if (this._emitEvents) {
        this._onSelectionChange = function () {
          if (!self._hasEditorFocus) return;
          if (self._selectionTimer) return;
          self._selectionTimer = setTimeout(function () {
            self._selectionTimer = null;
            self._emitSelectionChanged();
          }, 120);
        };
        document.addEventListener("selectionchange", this._onSelectionChange);
      }

      // Awareness is its own opt-in: a host can want carets without wanting
      // the selection events, and vice versa.
      if (this._collabAwareness) {
        this._onAwarenessSelectionChange = function () {
          if (self._awarenessTimer) return;
          self._awarenessTimer = setTimeout(function () {
            self._awarenessTimer = null;
            self._emitAwareness();
          }, 50);
        };

        document.addEventListener("selectionchange", this._onAwarenessSelectionChange);

        // Leaving tells the others to drop our caret, rather than leaving it
        // parked wherever we last were.
        this._onAwarenessBlur = function () {
          self._emitAwareness(true);
        };
        this.el.addEventListener("focusout", this._onAwarenessBlur);

        // The carets are absolute pixels, so anything that reflows the text
        // leaves them pointing at the wrong character until redrawn.
        this._onAwarenessResize = function () {
          if (self._peerCursors && self._peerCursors.length) {
            self._renderPeerCursors(self._peerCursors);
          }
        };
        window.addEventListener("resize", this._onAwarenessResize);
      }
    },

    // ===================================================================
    // Awareness: where everyone's caret is
    // ===================================================================

    _emitAwareness: function (blurred) {
      if (!this._collabAwareness) return;

      var offset = null;
      // Where the selection started. Equal to the caret when nothing is
      // selected; the two together are what lets a peer draw the range and put
      // the caret on the end the person is actually moving.
      var anchor = null;
      var focused = !blurred;

      if (focused) {
        if (this._mode === "markdown") {
          var ta = this._getMarkdownTextarea();

          if (ta && document.activeElement === ta) {
            offset = ta.selectionEnd;
            anchor = ta.selectionStart;
            // A textarea does not say which end is being dragged, so take the
            // caret to be at the end nearest the last known one.
            if (ta.selectionDirection === "backward") {
              offset = ta.selectionStart;
              anchor = ta.selectionEnd;
            }
          }
        } else if (this._visualEl) {
          var sel = window.getSelection();

          if (sel && sel.rangeCount && this._visualEl.contains(sel.getRangeAt(0).startContainer)) {
            var range = sel.getRangeAt(0);
            offset = this._visibleOffset(range.endContainer, range.endOffset);
            anchor = this._visibleOffset(range.startContainer, range.startOffset);

            // getSelection reports the range in document order; anchorNode says
            // which end the person is holding.
            if (sel.anchorNode && sel.focusNode && !sel.isCollapsed) {
              var anchorAt = this._visibleOffset(sel.anchorNode, sel.anchorOffset);
              var headAt = this._visibleOffset(sel.focusNode, sel.focusOffset);
              anchor = anchorAt;
              offset = headAt;
            }
          }
        }
      }

      if (offset === null) {
        focused = false;
        anchor = null;
      }

      // Nothing changed — do not spend a round trip saying so.
      if (
        focused === this._lastAwarenessFocused &&
        offset === this._lastAwarenessOffset &&
        anchor === this._lastAwarenessAnchor
      ) {
        return;
      }

      this._lastAwarenessFocused = focused;
      this._lastAwarenessOffset = offset;
      this._lastAwarenessAnchor = anchor;

      this.pushEventTo(this.el, "awareness", {
        editor_id: this._editorId,
        offset: offset,
        anchor: anchor,
        focused: focused,
        debug: this._collabDebug ? this._debugState() : null
      });
    },

    // The layer the carets are drawn on. Created lazily and kept out of the
    // editable area, so nothing here can end up in the document.
    _peerCursorLayer: function () {
      if (this._peerLayer && this._peerLayer.parentNode) return this._peerLayer;
      if (!this.el) return null;

      var layer = document.createElement("div");
      layer.className = "leaf-peer-cursors";
      layer.setAttribute("aria-hidden", "true");
      // Set one property at a time rather than via cssText: a single value a
      // CSSOM does not recognise makes it discard the whole declaration.
      this._style(layer, {
        position: "absolute",
        top: "0",
        left: "0",
        right: "0",
        bottom: "0",
        pointerEvents: "none",
        overflow: "hidden",
        zIndex: "5"
      });

      // The carets are positioned against this box, so it has to be one.
      var position = "";
      try {
        position = window.getComputedStyle(this.el).position;
      } catch (e) {
        /* no view — fall through and set it anyway */
      }
      if (!position || position === "static") this.el.style.position = "relative";

      this.el.appendChild(layer);
      this._peerLayer = layer;
      return layer;
    },

    _style: function (el, styles) {
      for (var key in styles) {
        if (Object.prototype.hasOwnProperty.call(styles, key)) {
          try {
            el.style[key] = styles[key];
          } catch (e) {
            /* a value this engine will not take — skip it, keep the rest */
          }
        }
      }
    },

    _renderPeerCursors: function (cursors) {
      this._peerCursors = cursors || [];

      var layer = this._peerCursorLayer();
      if (!layer) return;

      while (layer.firstChild) layer.removeChild(layer.firstChild);
      if (!this._peerCursors.length || !this._visualEl) return;

      var host = this.el.getBoundingClientRect();

      for (var i = 0; i < this._peerCursors.length; i++) {
        var cursor = this._peerCursors[i];

        // Their selection first, so their caret is drawn on top of it.
        if (typeof cursor.anchor === "number" && cursor.anchor !== cursor.offset) {
          this._drawSelection(layer, host, cursor);
        }

        var rect = this._rectAtVisibleOffset(cursor.offset);
        if (!rect) continue;

        var caret = document.createElement("div");
        caret.className = "leaf-peer-caret";
        this._style(caret, {
          position: "absolute",
          width: "2px",
          pointerEvents: "none",
          borderRadius: "1px",
          backgroundColor: cursor.color || "#888",
          left: rect.left - host.left + "px",
          top: rect.top - host.top + "px",
          height: (rect.height || 16) + "px"
        });

        if (cursor.label) {
          var label = document.createElement("span");
          label.className = "leaf-peer-caret-label";
          label.textContent = cursor.label;
          this._style(label, {
            position: "absolute",
            bottom: "100%",
            left: "-1px",
            whiteSpace: "nowrap",
            fontSize: "10px",
            lineHeight: "1.4",
            padding: "0 4px",
            borderRadius: "3px",
            color: "#fff",
            backgroundColor: cursor.color || "#888"
          });
          caret.appendChild(label);
        }

        layer.appendChild(caret);
      }
    },

    // One box per line the selection covers, which is what getClientRects
    // gives for a range: a selection running over three lines is three boxes,
    // not one tall one.
    _drawSelection: function (layer, host, cursor) {
      var rects = this._rectsBetween(cursor.anchor, cursor.offset);

      for (var i = 0; i < rects.length; i++) {
        var rect = rects[i];
        if (!rect.width && !rect.height) continue;

        var band = document.createElement("div");
        band.className = "leaf-peer-selection";

        this._style(band, {
          position: "absolute",
          pointerEvents: "none",
          borderRadius: "2px",
          backgroundColor: cursor.color || "#888",
          // Faint enough to read the words through. This sits behind nothing —
          // it is on the overlay — so the text has to show through it.
          opacity: "0.22",
          left: rect.left - host.left + "px",
          top: rect.top - host.top + "px",
          width: rect.width + "px",
          height: rect.height + "px"
        });

        layer.appendChild(band);
      }
    },

    _rectsBetween: function (from, to) {
      if (!this._visualEl || typeof from !== "number" || typeof to !== "number") return [];

      var start = this._visibleNodeAt(Math.min(from, to));
      var end = this._visibleNodeAt(Math.max(from, to));
      if (!start || !end) return [];

      try {
        var range = document.createRange();
        range.setStart(start.node, start.offset);
        range.setEnd(end.node, end.offset);
        return Array.prototype.slice.call(range.getClientRects());
      } catch (e) {
        return [];
      }
    },

    // A screen rectangle for a marker-free text offset. A collapsed range
    // reports no rects in some engines, so fall back to measuring the
    // character next to it.
    _rectAtVisibleOffset: function (offset) {
      var point = this._visibleNodeAt(offset || 0);
      if (!point) return null;

      try {
        var range = document.createRange();
        range.setStart(point.node, point.offset);
        range.collapse(true);

        var rect = range.getBoundingClientRect();
        if (rect && (rect.height || rect.width)) return rect;

        var length = point.node.nodeValue ? point.node.nodeValue.length : 0;
        if (point.offset < length) {
          range.setEnd(point.node, point.offset + 1);
        } else if (point.offset > 0) {
          range.setStart(point.node, point.offset - 1);
        } else {
          return null;
        }

        rect = range.getBoundingClientRect();
        return rect && (rect.height || rect.width) ? rect : null;
      } catch (e) {
        return null;
      }
    },

    // Inverse of _visibleOffset. `boundary` marks a position sitting on a line
    // break, which belongs to neither line: fine for showing a caret, not for
    // placing text.
    _visibleNodeAt: function (offset) {
      var segments = this._visibleSegments();
      var count = 0;
      var last = null;
      // Whether any line break has gone by. An offset that runs past every
      // line with text in it, having crossed a break, belongs to a line with
      // nothing on it — a bullet just made with Enter — and the end of the
      // line above is not where it goes. Not reset when a line does have text:
      // an offset past the end of everything is out of range whichever line it
      // followed, and declining is the safer answer either way.
      var pastBreak = false;

      for (var i = 0; i < segments.length; i++) {
        var segment = segments[i];

        if (!segment.node) {
          if (count === offset) {
            var next = this._nextTextSegment(segments, i);
            if (next) return { node: next.node, offset: 0, boundary: true };
          }
          count += 1;
          pastBreak = true;
          continue;
        }

        if (count + segment.text.length >= offset) {
          var wanted = offset - count;
          // Past the last visible character, the position is the end of the
          // node — anything after it is not part of the document.
          var raw =
            wanted < segment.map.length ? segment.map[wanted] : segment.raw.length;

          return { node: segment.node, offset: raw, boundary: false };
        }

        count += segment.text.length;
        last = segment.node;
      }

      // Nothing matched, so the offset is past the end of everything with text
      // in it. If a line break got us here it belongs to an empty line — a
      // bullet just made with Enter and not yet typed into — and the end of
      // the line above is emphatically not where it goes. Saying so lets the
      // caller decline rather than write into the wrong item.
      if (last) return { node: last, offset: last.nodeValue.length, boundary: pastBreak };
      return null;
    },

    _emitSelectionChanged: function () {
      var info = this._getSelectionInfo();
      if (!info) return;
      this.pushEventTo(this.el, "selection_changed", {
        editor_id: this._editorId,
        text: info.text,
        range: info.range,
      });
    },

    // Returns { text, range } for the current selection, or null when the
    // selection isn't inside this editor. `range` is { start, end } char
    // offsets for the textarea surfaces and null for the contenteditable
    // (where a flat integer offset isn't meaningful).
    _getSelectionInfo: function () {
      if (this._mode === "markdown" || this._mode === "html") {
        var ta =
          this._mode === "markdown"
            ? this._getMarkdownTextarea()
            : this._getHtmlTextarea();
        if (!ta || document.activeElement !== ta) return null;
        var start = ta.selectionStart;
        var end = ta.selectionEnd;
        return {
          text: ta.value.substring(start, end),
          range: { start: start, end: end },
        };
      }

      if (!this._visualEl) return null;
      var sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return null;
      var range = sel.getRangeAt(0);
      if (!this._visualEl.contains(range.commonAncestorContainer)) return null;
      return { text: sel.toString(), range: null };
    },

    // Force any pending debounced change to fire immediately for the
    // surface that matches the current mode. Pushing only the active
    // surface avoids clobbering server content with a stale inactive one.
    //
    // `ref` is the host's correlation id from `action: :flush, ref: …`. A
    // flush's reply is otherwise an ordinary content event, indistinguish-
    // able from the debounce firing on its own — so a host that wants to
    // *await* the flush (save before navigating away) has nothing to wait
    // for. When a ref is given we push one extra, dedicated event after
    // the content event, and that becomes `{:leaf_flushed, %{ref: …}}`.
    //
    // It is pushed unconditionally — even when the branch above found no
    // surface to read — because a host awaiting a reply that never comes
    // is a worse failure than an empty one.
    _flushPending: function (ref) {
      var id = this._editorId;
      var flushedMarkdown = null;
      var flushedHtml = "";

      if (this._mode === "markdown") {
        if (this._markdownDebounceTimer) {
          clearTimeout(this._markdownDebounceTimer);
          this._markdownDebounceTimer = null;
        }
        var mta = this._getMarkdownTextarea();
        if (mta) {
          this._syncFormInput(mta.value);
          flushedMarkdown = mta.value;
          this.pushEventTo(this.el, "markdown_content_changed", {
            editor_id: id,
            content: mta.value,
            dirty: this._computeDirty(mta.value),
          });
        }
      } else if (this._mode === "html") {
        if (this._htmlDebounceTimer) {
          clearTimeout(this._htmlDebounceTimer);
          this._htmlDebounceTimer = null;
        }
        var hta = this._getHtmlTextarea();
        if (hta) {
          var hmd = htmlToMarkdown(hta.value || "");
          this._syncFormInput(hmd);
          flushedMarkdown = hmd;
          flushedHtml = hta.value;
          this.pushEventTo(this.el, "html_content_changed", {
            editor_id: id,
            content: hta.value,
            dirty: this._computeDirty(hmd),
          });
        }
      } else if (this._visualEl) {
        if (this._debounceTimer) {
          clearTimeout(this._debounceTimer);
          this._debounceTimer = null;
        }
        var html = this._visualEl.innerHTML;
        var markdown = htmlToMarkdown(html);
        this._syncFormInput(markdown);
        flushedMarkdown = markdown;
        flushedHtml = html;
        this.pushEventTo(this.el, "content_changed", {
          editor_id: id,
          html: html,
          markdown: markdown,
          dirty: this._computeDirty(markdown),
        });
      }

      if (ref !== undefined && ref !== null) {
        if (flushedMarkdown === null) flushedMarkdown = this._currentMarkdown();
        this.pushEventTo(this.el, "flushed", {
          editor_id: id,
          ref: ref,
          markdown: flushedMarkdown,
          html: flushedHtml,
        });
      }
    },

    _computeDirty: function (markdown) {
      return (markdown || "") !== (this._savedContent || "");
    },

    // The current content as markdown, read from the active surface.
    _currentMarkdown: function () {
      if (this._mode === "markdown") {
        var mta = this._getMarkdownTextarea();
        return mta ? mta.value : this._savedContent || "";
      }
      if (this._mode === "html") {
        var hta = this._getHtmlTextarea();
        return hta ? htmlToMarkdown(hta.value || "") : this._savedContent || "";
      }
      return this._visualEl
        ? htmlToMarkdown(this._visualEl.innerHTML)
        : this._savedContent || "";
    },

    // Insert text at the caret of a textarea, collapsing any selection.
    // Goes through `replaceRange` so the browser's undo stack survives.
    _insertAtTextarea: function (ta, text) {
      var start = ta.selectionStart;
      replaceRange(ta, start, ta.selectionEnd, text, start + text.length);
    },

    // =====================================================================
    // Toolbar dropdown placement + dismissal
    // =====================================================================
    //
    // The menus are `position: absolute` inside their trigger's wrapper, so
    // nothing stops them running off the right edge or the bottom of the
    // screen — most visible on a phone, where the tools menu is twenty rows
    // tall. This clamps them into the viewport and flips them above the
    // trigger when there's more room up there.
    //
    // Placement is driven by a MutationObserver rather than by hooking each
    // open path: menus are opened in three different ways (the JS dropdowns
    // toggle `hidden`, the mobile menus are `<details>`, and the responsive
    // toolbar reveals rows), and observing the resulting attribute change
    // catches all of them without touching that code.

    _setupMenuPlacement: function () {
      var self = this;

      this._placeMenu = function (menu) {
        if (!menu || menu.classList.contains("hidden")) return;

        // Clear last run's adjustments before measuring, or the clamp
        // compounds every time the menu is reopened.
        menu.style.left = "";
        menu.style.right = "";
        menu.style.top = "";
        menu.style.bottom = "";
        menu.style.maxHeight = "";

        var pad = 8;
        var vw = document.documentElement.clientWidth;
        var vh = window.visualViewport
          ? window.visualViewport.height
          : window.innerHeight;
        var rect = menu.getBoundingClientRect();

        // Horizontal: pull back inside the right edge, then the left. Both
        // are expressed as `left` against the same offset parent the
        // Tailwind `left-0` / `right-0` resolved against.
        if (rect.right > vw - pad) {
          menu.style.left = menu.offsetLeft - (rect.right - (vw - pad)) + "px";
          menu.style.right = "auto";
          rect = menu.getBoundingClientRect();
        }
        if (rect.left < pad) {
          menu.style.left = menu.offsetLeft + (pad - rect.left) + "px";
          menu.style.right = "auto";
          rect = menu.getBoundingClientRect();
        }

        // Vertical: flip above the trigger when that's roomier, otherwise
        // just cap the height so the menu scrolls instead of overflowing.
        if (rect.bottom > vh - pad) {
          var anchor = menu.parentElement.getBoundingClientRect();
          var roomAbove = anchor.top - pad;
          var roomBelow = vh - rect.top - pad;

          if (roomAbove > roomBelow) {
            menu.style.top = "auto";
            menu.style.bottom = "100%";
            menu.style.maxHeight = Math.max(120, roomAbove - 4) + "px";
          } else {
            menu.style.maxHeight = Math.max(120, roomBelow) + "px";
          }
        }
      };

      this._menuObserver = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var target = records[i].target;
          if (!target || target.nodeType !== Node.ELEMENT_NODE) continue;

          if (target.matches("[data-leaf-menu]")) {
            self._placeMenu(target);
          } else if (target.tagName === "DETAILS" && target.open) {
            self._placeMenu(target.querySelector("[data-leaf-menu]"));
          }
        }
      });

      // Inline styles don't touch class/open, so placing a menu can't
      // re-trigger the observer.
      this._menuObserver.observe(this.el, {
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "open"],
      });

      // The <details> menus have no dismissal of their own: without this
      // they stay open until the summary is tapped again, which on a phone
      // means a 20-row menu sitting over the text you're trying to read.
      this._onMenuOutside = function (e) {
        var open = self.el.querySelectorAll("details[open]");
        for (var i = 0; i < open.length; i++) {
          if (!open[i].contains(e.target)) open[i].removeAttribute("open");
        }
      };

      this._onMenuEscape = function (e) {
        if (e.key !== "Escape") return;
        var open = self.el.querySelectorAll("details[open]");
        for (var i = 0; i < open.length; i++) open[i].removeAttribute("open");
      };

      // Picking an item should close the menu it came from.
      this._onMenuItemClick = function (e) {
        var item = e.target.closest(
          "[data-toolbar-action],[data-host-action],[data-mode-tab]"
        );
        if (!item) return;
        var details = item.closest("details[open]");
        if (details) details.removeAttribute("open");
      };

      document.addEventListener("mousedown", this._onMenuOutside, true);
      document.addEventListener("touchstart", this._onMenuOutside, true);
      document.addEventListener("keydown", this._onMenuEscape);
      this.el.addEventListener("click", this._onMenuItemClick);
    },

    _teardownMenuPlacement: function () {
      if (this._menuObserver) {
        this._menuObserver.disconnect();
        this._menuObserver = null;
      }
      if (this._onMenuOutside) {
        document.removeEventListener("mousedown", this._onMenuOutside, true);
        document.removeEventListener("touchstart", this._onMenuOutside, true);
        this._onMenuOutside = null;
      }
      if (this._onMenuEscape) {
        document.removeEventListener("keydown", this._onMenuEscape);
        this._onMenuEscape = null;
      }
      if (this._onMenuItemClick) {
        this.el.removeEventListener("click", this._onMenuItemClick);
        this._onMenuItemClick = null;
      }
    },

    // =====================================================================
    // Inline suggestions (#tag / @user / /component / :emoji / …)
    // =====================================================================
    //
    // Nothing here knows what a tag is. A trigger config says which char
    // opens the popup, which chars continue the token, and where the token
    // may start; the host answers `{:leaf_suggest, …}` with rows and we
    // insert the pick. Two rules outrank the shape of any of it:
    //
    //   * stale replies are dropped — keystrokes routinely outrun a round
    //     trip, so every reply is matched on trigger + query + seq;
    //   * typing is never blocked — if the host never answers we spin
    //     briefly and close quietly. A hand-typed `#tag` is already valid;
    //     this is an enhancement over something that works without it.

    _suggestEnabled: function () {
      return !!(this._suggestConfigs && this._suggestConfigs.length);
    },

    _setupSuggestions: function () {
      var self = this;
      var nodes = this.el.querySelectorAll("[data-leaf-suggest]");

      // `updated()` re-runs this on every server render, including the one
      // caused by the host answering our own suggest request. Bail on an
      // unchanged config set so a re-render never closes an open popup.
      var signature = "";
      for (var s = 0; s < nodes.length; s++) {
        signature += nodes[s].outerHTML + "|";
      }
      if (this._suggestSignature === signature) return;
      this._suggestSignature = signature;

      var configs = [];
      for (var i = 0; i < nodes.length; i++) {
        var cfg = parseSuggestConfig(nodes[i]);
        if (cfg) configs.push(cfg);
      }

      // The config set genuinely changed — anything on screen belongs to
      // the old one.
      if (this._suggestWired) this._suggestClose();
      this._suggestConfigs = configs;

      // No configs → no popup state, no listeners, no server event
      // registration. An editor without `suggestions` behaves exactly as it
      // did before this feature existed.
      if (!configs.length) return;

      // Listeners and the `handleEvent` registration are wired exactly once,
      // however often the config list is swapped afterwards.
      if (this._suggestWired) return;
      this._suggestWired = true;

      var host = this.el.querySelector("[data-leaf-suggestions]");
      this._suggestText = {
        searching: (host && host.dataset.tSearching) || "Searching…",
        noMatches: (host && host.dataset.tNoMatches) || "No matches",
        create: (host && host.dataset.tCreate) || "Create",
        results: (host && host.dataset.tResults) || "results",
      };

      this._suggest = {
        open: false,
        cfg: null,
        query: "",
        start: 0,
        caret: 0,
        kind: null,
        el: null,
        seq: null,
        results: [],
        loading: false,
        index: 0,
      };
      this._suggestCache = {};
      this._suggestSeq = 0;
      this._suggestComposing = false;
      this._suggestBlockedKey = null;

      this._onSuggestInput = function () {
        self._suggestScan();
      };
      // Caret moves only ever CLOSE an open popup (opening is driven by
      // typing), so these do nothing until something is on screen.
      this._onSuggestCaretMove = function () {
        if (self._suggest.open) self._suggestScan();
      };
      this._onSuggestKeydown = function (e) {
        if (self._suggestKeydown(e)) return;
      };
      this._onSuggestCompositionStart = function () {
        self._suggestComposing = true;
        self._suggestClose();
      };
      this._onSuggestCompositionEnd = function () {
        self._suggestComposing = false;
        self._suggestScan();
      };

      this._suggestSurfaces = [];
      [
        this._getMarkdownTextarea(),
        this._getHtmlTextarea(),
        this._visualEl,
      ].forEach(function (surface) {
        if (!surface) return;
        self._suggestSurfaces.push(surface);
        // The contenteditable routes keydown through `_onVisualKeydown`,
        // which calls `_suggestKeydown` itself — a second listener here
        // would fire the handler twice and let the list auto-continue run
        // after an accept. The textareas have no keydown handler of their
        // own, and get one in the CAPTURE phase so Enter is swallowed
        // before it can reach a surrounding form.
        if (surface !== self._visualEl) {
          surface.addEventListener("keydown", self._onSuggestKeydown, true);
        }
        surface.addEventListener("keyup", self._onSuggestCaretMove);
        surface.addEventListener("click", self._onSuggestCaretMove);
        surface.addEventListener(
          "compositionstart",
          self._onSuggestCompositionStart
        );
        surface.addEventListener(
          "compositionend",
          self._onSuggestCompositionEnd
        );
        // The contenteditable scans from `_onVisualInput`, which already
        // runs the hybrid re-tagging passes that can rebuild the caret's
        // text node — scanning before those would read a stale node.
        if (surface !== self._visualEl) {
          surface.addEventListener("input", self._onSuggestInput);
        }
      });

      this.handleEvent(
        "leaf-suggestions:" + this._editorId,
        this._suggestApplyResults.bind(this)
      );
    },

    _teardownSuggestions: function () {
      var self = this;
      this._suggestClose();
      if (this._suggestSurfaces) {
        this._suggestSurfaces.forEach(function (surface) {
          surface.removeEventListener("keydown", self._onSuggestKeydown, true);
          surface.removeEventListener("keyup", self._onSuggestCaretMove);
          surface.removeEventListener("click", self._onSuggestCaretMove);
          surface.removeEventListener(
            "compositionstart",
            self._onSuggestCompositionStart
          );
          surface.removeEventListener(
            "compositionend",
            self._onSuggestCompositionEnd
          );
          surface.removeEventListener("input", self._onSuggestInput);
        });
        this._suggestSurfaces = null;
      }
      if (this._suggestMirror && this._suggestMirror.parentNode) {
        this._suggestMirror.parentNode.removeChild(this._suggestMirror);
      }
      this._suggestMirror = null;
      this._suggestConfigs = null;
    },

    // Where the caret is, and the text in front of it. For the textareas
    // that is the whole value up to the caret; for the contenteditable it
    // is the caret's text node only — a token never spans element
    // boundaries, and staying inside the node keeps the offsets we hand to
    // `Range` valid.
    _suggestContext: function () {
      if (this._readonly) return null;

      if (this._mode === "markdown" || this._mode === "html") {
        var ta =
          this._mode === "markdown"
            ? this._getMarkdownTextarea()
            : this._getHtmlTextarea();
        if (!ta || document.activeElement !== ta) return null;
        if (ta.selectionStart !== ta.selectionEnd) return null;
        return {
          kind: "textarea",
          el: ta,
          caret: ta.selectionStart,
          before: ta.value.substring(0, ta.selectionStart),
        };
      }

      var vis = this._visualEl;
      if (!vis) return null;
      var sel = window.getSelection();
      if (!sel || !sel.rangeCount || !sel.isCollapsed) return null;
      var range = sel.getRangeAt(0);
      if (!vis.contains(range.startContainer)) return null;
      if (range.startContainer.nodeType !== Node.TEXT_NODE) return null;

      return {
        kind: "ce",
        el: vis,
        node: range.startContainer,
        caret: range.startOffset,
        // contenteditable stores trailing spaces as NBSP; normalizing keeps
        // the boundary test honest without changing any offsets.
        before: (range.startContainer.textContent || "")
          .substring(0, range.startOffset)
          .replace(/\u00a0/g, " "),
      };
    },

    // The active trigger, if any. When several configs match (an editor can
    // carry `#` and `/` at once) the one nearest the caret wins.
    _suggestDetect: function (ctx) {
      var best = null;

      for (var i = 0; i < this._suggestConfigs.length; i++) {
        var cfg = this._suggestConfigs[i];
        var index = ctx.before.lastIndexOf(cfg.trigger);
        if (index === -1) continue;
        if (best && index <= best.start) continue;

        var prev = index > 0 ? ctx.before.charAt(index - 1) : "";
        if (!suggestBoundaryOk(cfg.boundary, index, prev)) continue;

        var token = ctx.before.substring(index + cfg.trigger.length);
        if (cfg.maxLength && token.length > cfg.maxLength) continue;
        if (token.length < cfg.minChars) continue;
        if (!suggestTokenOk(cfg, token)) continue;
        if (this._suggestExcluded(cfg, ctx, index)) continue;

        best = { cfg: cfg, start: index, query: token };
      }

      return best;
    },

    // Contexts where a suggestion would insert something the markdown
    // parser then ignores. A client-side approximation: in the
    // contenteditable we can ask the DOM (`<code>` / `<pre>` / `<a>`
    // ancestors), in the textareas we count delimiters. A stray popup is
    // cosmetic; a stray insertion is not, which is why accepting is the
    // only thing that ever writes text.
    _suggestExcluded: function (cfg, ctx, index) {
      var head = ctx.before.substring(0, index);

      if (cfg.exclude.indexOf("code") !== -1) {
        if (ctx.kind === "ce") {
          if (suggestClosestWithin(ctx.node, ctx.el, "code,pre")) return true;
        } else {
          // Odd number of fence openers before the caret → inside a fence.
          var fences = head.match(/^```/gm);
          if (fences && fences.length % 2 === 1) return true;
          // Odd number of backticks earlier on this line → inline code.
          var lineHead = head.substring(head.lastIndexOf("\n") + 1);
          var ticks = lineHead.match(/`/g);
          if (ticks && ticks.length % 2 === 1) return true;
        }
      }

      if (cfg.exclude.indexOf("link") !== -1) {
        if (ctx.kind === "ce" && suggestClosestWithin(ctx.node, ctx.el, "a")) {
          return true;
        }
        // Inside a markdown link/image destination: `[jump](#section)`.
        // The `:word_start` boundary deliberately allows `(` so `(#tag)`
        // works, which is exactly what lets this case through otherwise.
        var lineStart = head.lastIndexOf("\n") + 1;
        var line = head.substring(lineStart);
        var open = line.lastIndexOf("](");
        if (open !== -1 && line.indexOf(")", open) === -1) return true;
      }

      return false;
    },

    _suggestScan: function () {
      if (!this._suggestEnabled()) return;
      if (this._suggestComposing) return;

      var ctx = this._suggestContext();
      var hit = ctx ? this._suggestDetect(ctx) : null;

      if (!hit) {
        this._suggestClose();
        return;
      }

      var key = hit.cfg.trigger + "\u0000" + hit.query;
      // The token we just accepted — don't reopen on top of our own insert.
      if (this._suggestBlockedKey === key) return;
      this._suggestBlockedKey = null;

      var st = this._suggest;
      var same =
        st.open &&
        st.cfg === hit.cfg &&
        st.query === hit.query &&
        st.start === hit.start;

      st.cfg = hit.cfg;
      st.query = hit.query;
      st.start = hit.start;
      st.caret = ctx.caret;
      st.kind = ctx.kind;
      st.el = ctx.el;

      if (same) {
        // Typing scans twice (input, then keyup); only the first moved the
        // caret, and measuring is the expensive half.
        if (ctx.caret !== this._suggestPositionedAt) this._suggestPosition();
        return;
      }

      st.open = true;
      st.index = 0;

      var cached = this._suggestCache[key];
      if (cached) {
        st.results = cached.slice(0, hit.cfg.maxResults);
        st.loading = false;
        this._suggestRender();
        return;
      }

      st.results = [];
      st.loading = true;
      this._suggestRender();
      this._suggestRequest();
    },

    _suggestRequest: function () {
      var self = this;
      var st = this._suggest;

      clearTimeout(this._suggestTimer);
      this._suggestTimer = setTimeout(function () {
        if (!st.open || !st.cfg) return;
        self._suggestSeq += 1;
        st.seq = self._suggestSeq;
        self.pushEventTo(self.el, "suggest", {
          editor_id: self._editorId,
          trigger: st.cfg.trigger,
          query: st.query,
          seq: st.seq,
        });

        // Never block typing: a host that never answers gets a short
        // spinner and then the popup goes away on its own.
        clearTimeout(self._suggestTimeout);
        self._suggestTimeout = setTimeout(function () {
          if (st.open && st.loading) self._suggestClose();
        }, 5000);
      }, st.cfg.debounce);
    },

    _suggestApplyResults: function (payload) {
      if (!payload || !this._suggest) return;
      var results = payload.results || [];

      var key = (payload.trigger || "") + "\u0000" + (payload.query || "");
      // Cache even a reply we're about to drop as stale — a backspace can
      // land right back on this query a moment later.
      var keys = Object.keys(this._suggestCache);
      if (keys.length > 200) this._suggestCache = {};
      this._suggestCache[key] = results;

      var st = this._suggest;
      if (!st.open || !st.cfg) return;
      if ((payload.trigger || "") !== st.cfg.trigger) return;
      if ((payload.query || "") !== st.query) return;
      if (payload.seq != null && st.seq != null && payload.seq !== st.seq) {
        return;
      }

      clearTimeout(this._suggestTimeout);
      st.results = results.slice(0, st.cfg.maxResults);
      st.loading = false;
      st.index = 0;
      this._suggestRender();
    },

    // The accept-able rows: the host's results, plus a "create" row when
    // the config allows one and nothing matched the query exactly.
    _suggestRows: function () {
      var st = this._suggest;
      if (!st.cfg) return [];

      var rows = st.results.map(function (result) {
        return { kind: "result", result: result };
      });

      if (st.cfg.allowCreate && st.query && !st.loading) {
        var query = st.query.toLowerCase();
        var exact = st.results.some(function (result) {
          return String(result.value || "").toLowerCase() === query;
        });
        if (!exact) {
          rows.push({
            kind: "create",
            result: { value: st.query, label: st.cfg.trigger + st.query },
          });
        }
      }

      return rows;
    },

    _suggestRender: function () {
      var self = this;
      var st = this._suggest;
      if (!st.open || !st.cfg) return;

      if (!this._suggestEl) {
        var popup = document.createElement("div");
        popup.className = "leaf-suggest-popup";
        popup.id = this._editorId + "-suggest";
        popup.setAttribute("role", "listbox");
        popup.dataset.leafSuggestPopup = this._editorId;
        // mousedown (not click) + preventDefault: the caret must stay put
        // through the press, otherwise the token we're about to replace is
        // gone by the time we read it.
        popup.addEventListener("mousedown", function (e) {
          e.preventDefault();
          var row = e.target.closest("[data-leaf-suggest-index]");
          if (!row) return;
          var rows = self._suggestRows();
          var picked = rows[parseInt(row.dataset.leafSuggestIndex, 10)];
          if (picked) self._suggestAccept(picked);
        });
        popup.addEventListener("mouseover", function (e) {
          var row = e.target.closest("[data-leaf-suggest-index]");
          if (!row) return;
          st.index = parseInt(row.dataset.leafSuggestIndex, 10);
          self._suggestHighlight();
        });
        document.body.appendChild(popup);
        this._suggestEl = popup;

        var status = document.createElement("div");
        status.className = "leaf-suggest-sr";
        status.setAttribute("aria-live", "polite");
        document.body.appendChild(status);
        this._suggestStatusEl = status;

        // Capture-phase scroll fires for every scrollable ancestor and can
        // arrive dozens of times a frame. Repositioning rebuilds the
        // textarea caret mirror with the whole document and forces layout
        // twice, so coalesce to one measurement per frame.
        this._onSuggestReposition = function () {
          if (self._suggestRaf) return;
          self._suggestRaf = requestAnimationFrame(function () {
            self._suggestRaf = null;
            self._suggestPosition();
          });
        };
        // Anything that isn't the popup or the surface being edited
        // dismisses — including this editor's OWN chrome. Guarding on
        // `self.el` instead would keep the popup open when the user clicks
        // a mode tab or a toolbar button: those take focus without leaving
        // the hook root, so `_onFocusOut` doesn't fire either, and the
        // popup would linger anchored to a surface that just got hidden.
        this._onSuggestOutside = function (e) {
          if (popup.contains(e.target)) return;
          var surface = self._suggest && self._suggest.el;
          if (surface && surface.contains(e.target)) return;
          self._suggestClose();
        };
        document.addEventListener("scroll", this._onSuggestReposition, true);
        window.addEventListener("resize", this._onSuggestReposition);
        document.addEventListener("mousedown", this._onSuggestOutside, true);
      }

      var el = this._suggestEl;
      el.setAttribute("aria-label", st.cfg.label || st.cfg.trigger);
      el.textContent = "";

      if (st.cfg.label) {
        var heading = document.createElement("div");
        heading.className = "leaf-suggest-heading";
        heading.textContent = st.cfg.label;
        el.appendChild(heading);
      }

      var rows = this._suggestRows();
      if (st.index >= rows.length) st.index = Math.max(0, rows.length - 1);

      var list = document.createElement("div");
      list.className = "leaf-suggest-list";
      rows.forEach(function (row, i) {
        list.appendChild(self._suggestRowEl(row, i));
      });
      el.appendChild(list);

      if (st.loading) {
        el.appendChild(
          this._suggestNote(this._suggestText.searching, true)
        );
      } else if (!rows.length) {
        el.appendChild(this._suggestNote(this._suggestText.noMatches, false));
      }

      this._suggestHighlight();
      this._suggestPosition();
      this._suggestAria();

      if (this._suggestStatusEl && !st.loading) {
        this._suggestStatusEl.textContent =
          rows.length + " " + this._suggestText.results;
      }
    },

    _suggestRowEl: function (row, index) {
      var st = this._suggest;
      var el = document.createElement("div");
      el.className =
        "leaf-suggest-row" + (row.kind === "create" ? " leaf-suggest-create" : "");
      el.setAttribute("role", "option");
      el.id = this._editorId + "-suggest-opt-" + index;
      el.dataset.leafSuggestIndex = String(index);
      el.dataset.leafSuggestValue = row.result.value;
      el.dataset.leafSuggestKind = row.kind;

      // Icons are host-supplied CSS class names (the heroicons convention
      // the rest of the stack uses). Rendered only when one is given, so an
      // app without that plugin never gets an empty gutter.
      if (row.kind === "create") {
        var plus = document.createElement("span");
        plus.className = "leaf-suggest-icon";
        plus.textContent = "+";
        plus.style.backgroundColor = "transparent";
        el.appendChild(plus);
      } else if (row.result.icon) {
        var icon = document.createElement("span");
        icon.className = "leaf-suggest-icon " + row.result.icon;
        el.appendChild(icon);
      }

      var text = document.createElement("span");
      text.className = "leaf-suggest-text";
      text.textContent =
        row.kind === "create"
          ? this._suggestText.create + " " + (row.result.label || row.result.value)
          : row.result.label || row.result.value;
      el.appendChild(text);

      if (row.kind !== "create" && row.result.sublabel) {
        var sub = document.createElement("span");
        sub.className = "leaf-suggest-sub";
        sub.textContent = row.result.sublabel;
        el.appendChild(sub);
      }

      el.setAttribute("aria-selected", index === st.index ? "true" : "false");
      return el;
    },

    _suggestNote: function (label, spinner) {
      var note = document.createElement("div");
      note.className = "leaf-suggest-note";
      if (spinner) {
        var dot = document.createElement("span");
        dot.className = "leaf-suggest-spinner";
        note.appendChild(dot);
      }
      var text = document.createElement("span");
      text.textContent = label;
      note.appendChild(text);
      return note;
    },

    _suggestHighlight: function () {
      var st = this._suggest;
      if (!this._suggestEl) return;
      var rows = this._suggestEl.querySelectorAll("[data-leaf-suggest-index]");
      for (var i = 0; i < rows.length; i++) {
        var selected = i === st.index;
        rows[i].setAttribute("aria-selected", selected ? "true" : "false");
        if (selected && rows[i].scrollIntoView) {
          rows[i].scrollIntoView({ block: "nearest" });
        }
      }
      this._suggestAria();
    },

    _suggestMove: function (delta) {
      var st = this._suggest;
      var count = this._suggestRows().length;
      if (!count) return;
      st.index = (st.index + delta + count) % count;
      this._suggestHighlight();
    },

    // Editable-combobox wiring, applied only while the popup is open and
    // removed on close, so a plain editor keeps its native semantics.
    _suggestAria: function () {
      var st = this._suggest;
      var surface = st.el;
      if (!surface || !this._suggestEl) return;
      surface.setAttribute("role", "combobox");
      surface.setAttribute("aria-autocomplete", "list");
      surface.setAttribute("aria-expanded", "true");
      surface.setAttribute("aria-controls", this._suggestEl.id);
      var rows = this._suggestRows();
      if (rows.length) {
        surface.setAttribute(
          "aria-activedescendant",
          this._editorId + "-suggest-opt-" + st.index
        );
      } else {
        surface.removeAttribute("aria-activedescendant");
      }
    },

    _suggestClearAria: function () {
      var surface = this._suggest && this._suggest.el;
      if (!surface) return;
      surface.removeAttribute("role");
      surface.removeAttribute("aria-autocomplete");
      surface.removeAttribute("aria-expanded");
      surface.removeAttribute("aria-controls");
      surface.removeAttribute("aria-activedescendant");
    },

    _suggestAccept: function (row) {
      var st = this._suggest;
      if (!st.open || !st.cfg || !row) return;

      var cfg = st.cfg;
      var value = String(row.result.value || "");
      if (!value) return;
      var replacement =
        (cfg.keepTrigger ? cfg.trigger : "") + value + cfg.insertSuffix;

      // Re-derive the token span from the LIVE caret. Between opening the
      // popup and this click the hybrid engine may have rebuilt the block,
      // or another keystroke may have landed; the stored offsets can be a
      // frame old and replacing the wrong range is unrecoverable.
      var ctx = this._suggestContext();
      var hit = ctx ? this._suggestDetect(ctx) : null;
      if (!hit || hit.cfg !== cfg) {
        this._suggestClose();
        return;
      }

      // Set BEFORE the edit: the insert fires `input` synchronously, which
      // re-scans, and with an empty `insert_suffix` the fresh token would
      // match again and reopen the popup on top of our own write.
      this._suggestBlockedKey = cfg.trigger + "\u0000" + value;

      if (ctx.kind === "textarea") {
        replaceRange(
          ctx.el,
          hit.start,
          ctx.caret,
          replacement,
          hit.start + replacement.length
        );
        this._updateCounts();
      } else {
        var range = document.createRange();
        range.setStart(ctx.node, hit.start);
        range.setEnd(ctx.node, ctx.caret);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        ctx.el.focus();

        var ok = false;
        try {
          ok = document.execCommand("insertText", false, replacement);
        } catch (e) {
          ok = false;
        }
        if (!ok) {
          range.deleteContents();
          var node = document.createTextNode(replacement);
          range.insertNode(node);
          range.setStartAfter(node);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
        }

        this._debouncedPushVisualChange();
        this._updateCounts();
      }

      this._suggestClose();
    },

    _suggestKeydown: function (e) {
      if (!this._suggestEnabled()) return false;
      var st = this._suggest;
      if (!st.open) return false;
      // Mid-composition the keydown is the IME's, not the user's.
      if (this._suggestComposing || e.isComposing || e.keyCode === 229) {
        return false;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        this._suggestClose();
        return true;
      }

      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (!this._suggestRows().length) return false;
        e.preventDefault();
        e.stopPropagation();
        this._suggestMove(e.key === "ArrowDown" ? 1 : -1);
        return true;
      }

      if (e.key === "Enter" || e.key === "Tab") {
        var rows = this._suggestRows();
        // Nothing to accept (still loading, or no matches): let the key do
        // its normal job rather than swallowing a keystroke.
        if (!rows.length) {
          this._suggestClose();
          return false;
        }
        // stopPropagation as well as preventDefault: Enter must not reach
        // the list auto-continue, and must not submit a surrounding form.
        e.preventDefault();
        e.stopPropagation();
        this._suggestAccept(rows[st.index]);
        return true;
      }

      return false;
    },

    _suggestClose: function () {
      clearTimeout(this._suggestTimer);
      clearTimeout(this._suggestTimeout);

      var st = this._suggest;
      if (st) {
        this._suggestClearAria();
        st.open = false;
        st.cfg = null;
        st.query = "";
        st.results = [];
        st.loading = false;
        st.index = 0;
        st.seq = null;
        st.el = null;
      }

      if (this._suggestEl && this._suggestEl.parentNode) {
        this._suggestEl.parentNode.removeChild(this._suggestEl);
      }
      this._suggestEl = null;
      if (this._suggestStatusEl && this._suggestStatusEl.parentNode) {
        this._suggestStatusEl.parentNode.removeChild(this._suggestStatusEl);
      }
      this._suggestStatusEl = null;

      if (this._onSuggestReposition) {
        document.removeEventListener("scroll", this._onSuggestReposition, true);
        window.removeEventListener("resize", this._onSuggestReposition);
        this._onSuggestReposition = null;
      }
      if (this._suggestRaf) {
        cancelAnimationFrame(this._suggestRaf);
        this._suggestRaf = null;
      }
      this._suggestPositionedAt = null;
      if (this._onSuggestOutside) {
        document.removeEventListener("mousedown", this._onSuggestOutside, true);
        this._onSuggestOutside = null;
      }
    },

    _suggestPosition: function () {
      var el = this._suggestEl;
      if (!el) return;
      var rect = this._suggestCaretRect();
      if (!rect) return;

      var margin = 6;
      el.style.visibility = "hidden";
      el.style.left = "0px";
      el.style.top = "0px";
      var width = el.offsetWidth;
      var height = el.offsetHeight;

      var vw = window.innerWidth;
      // visualViewport height shrinks when the mobile keyboard opens, so
      // this is what makes the popup flip above the caret on phones.
      var vh = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;

      var left = Math.max(margin, Math.min(rect.left, vw - width - margin));
      var top = rect.bottom + 4;
      if (top + height > vh - margin) {
        var above = rect.top - height - 4;
        top = above >= margin ? above : Math.max(margin, vh - height - margin);
      }

      el.style.left = left + "px";
      el.style.top = top + "px";
      el.style.visibility = "";
      this._suggestPositionedAt = this._suggest.caret;
    },

    _suggestCaretRect: function () {
      var st = this._suggest;
      if (!st.el) return null;

      if (st.kind === "textarea") {
        return this._textareaCaretRect(st.el, st.caret);
      }

      var sel = window.getSelection();
      if (!sel || !sel.rangeCount) return null;
      var range = sel.getRangeAt(0).cloneRange();
      range.collapse(true);
      var rects = range.getClientRects();
      if (rects.length) return rects[0];
      var box = range.getBoundingClientRect();
      if (box && (box.top || box.left || box.height)) return box;
      // Collapsed range in an empty element: no marker-node insertion
      // here — mutating the document to measure it would dirty the buffer
      // and fire an input event. The block's own rect is close enough.
      var block = suggestClosestWithin(
        range.startContainer,
        st.el,
        "p,li,h1,h2,h3,h4,h5,h6,blockquote,pre,div"
      );
      return (block || st.el).getBoundingClientRect();
    },

    // Caret coordinates inside a <textarea>, via the standard hidden-mirror
    // technique: a div that copies every metric affecting text layout, is
    // filled with the text up to the caret, and carries a marker span whose
    // offset within the mirror is the caret's offset within the textarea.
    // Survives soft wrapping and textarea scrolling; recomputed on every
    // reposition so resize / zoom stay correct.
    _textareaCaretRect: function (ta, index) {
      var mirror = this._suggestMirror;
      if (!mirror) {
        mirror = document.createElement("div");
        mirror.setAttribute("aria-hidden", "true");
        document.body.appendChild(mirror);
        this._suggestMirror = mirror;
      }

      var cs = window.getComputedStyle(ta);
      [
        "boxSizing",
        "width",
        "borderTopWidth",
        "borderRightWidth",
        "borderBottomWidth",
        "borderLeftWidth",
        "paddingTop",
        "paddingRight",
        "paddingBottom",
        "paddingLeft",
        "fontStyle",
        "fontVariant",
        "fontWeight",
        "fontStretch",
        "fontSize",
        "fontFamily",
        "lineHeight",
        "letterSpacing",
        "wordSpacing",
        "textTransform",
        "textIndent",
        "textAlign",
        "tabSize",
      ].forEach(function (prop) {
        mirror.style[prop] = cs[prop];
      });
      mirror.style.position = "absolute";
      mirror.style.top = "0";
      mirror.style.left = "-9999px";
      mirror.style.height = "auto";
      mirror.style.visibility = "hidden";
      mirror.style.whiteSpace = "pre-wrap";
      mirror.style.wordWrap = "break-word";
      mirror.style.overflowWrap = "break-word";

      mirror.textContent = ta.value.substring(0, index);
      var marker = document.createElement("span");
      // Non-empty content so the span has a box; the remaining text keeps
      // the wrap point identical to the real textarea.
      marker.textContent = ta.value.substring(index) || ".";
      mirror.appendChild(marker);

      var taRect = ta.getBoundingClientRect();
      var lineHeight =
        parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.25 || 16;
      var top = taRect.top + marker.offsetTop - ta.scrollTop;
      var left = taRect.left + marker.offsetLeft - ta.scrollLeft;

      return {
        top: top,
        left: left,
        bottom: top + lineHeight,
        height: lineHeight,
      };
    },

    // If `text` is exactly one preserved custom tag (<Hero .../> or
    // <CTA>…</CTA> whose name is in preserve_tags), return the atomic-chip
    // HTML so it can be inserted as a finished chip in visual/hybrid mode;
    // otherwise null (insert as plain text).
    _maybeAtomicChip: function (text) {
      if (!this._preserveTags || this._preserveTags.length === 0) return null;
      var trimmed = (text || "").trim();
      var m = trimmed.match(/^<([A-Za-z][\w-]*)\b/);
      if (!m) return null;
      var name = m[1];
      if (this._preserveTags.indexOf(name.toLowerCase()) === -1) return null;

      var whole = new RegExp(
        "^<" + name + "\\b[^>]*?(?:/>|>[\\s\\S]*</" + name + ">)$"
      );
      if (!whole.test(trimmed)) return null;

      return this._buildAtomicChip(trimmed, name);
    },

    _escapeHtml: function (s) {
      return String(s == null ? "" : s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },

    // Attribute-name → display-role map. Kept in step with the same lists
    // in lib/leaf.ex, so a chip inserted at the caret looks like the one
    // the server sends back rather than flickering between two designs.
    _ATOMIC_ROLES: {
      title: ["title", "heading", "headline", "name"],
      eyebrow: ["kicker", "eyebrow", "overline", "badge", "category"],
      subtitle: [
        "subtitle", "subheading", "tagline", "description",
        "summary", "caption", "blurb", "text", "body",
      ],
      link: ["href", "url", "link", "to"],
      cta: ["label", "cta", "button", "action"],
      image: [
        "image", "img", "poster", "thumbnail", "thumb", "cover",
        "background", "bg", "avatar", "photo", "banner",
      ],
    },

    // A chip built on the client — for a tag inserted at the caret, and for
    // the optimistic redraw after an in-place source edit. Same shape as
    // the server's, minus the children: the client has no markdown
    // renderer, so the rich version arrives a round trip later.
    _buildAtomicChip: function (raw, name) {
      var attrs = this._parseTagAttrs(raw);
      var roles = this._atomicRoles(attrs);
      var esc = this._escapeHtml.bind(this);
      var preview = [];

      function role(value, cls) {
        if (value) preview.push('<span class="' + cls + '">' + esc(value) + "</span>");
      }

      role(roles.eyebrow, "leaf-atomic-eyebrow");
      role(roles.title, "leaf-atomic-title");
      role(roles.subtitle, "leaf-atomic-subtitle");

      if (roles.ctaLabel || roles.link) {
        preview.push(
          '<span class="leaf-atomic-cta"><span class="leaf-atomic-cta-label">' +
          esc(roles.ctaLabel || roles.link) + "</span>" +
          (roles.link && roles.ctaLabel
            ? '<span class="leaf-atomic-cta-link">' + esc(roles.link) + "</span>"
            : "") +
          "</span>"
        );
      }

      if (roles.rest.length) {
        preview.push(
          '<span class="leaf-atomic-rest">' +
          esc(roles.rest.slice(0, 6).map(function (a) {
            return a[0] + '="' + a[1] + '"';
          }).join(" ")) +
          "</span>"
        );
      }

      var media = roles.media
        ? '<img class="leaf-atomic-media" src="' + esc(roles.media) + '" alt="" loading="lazy">'
        : "";

      var content =
        preview.length || media
          ? media + '<span class="leaf-atomic-preview">' + preview.join("") + "</span>"
          : "";

      return (
        '<span class="leaf-atomic leaf-atomic-block" contenteditable="false"' +
        ' data-leaf-raw="' + esc(raw) + '"' +
        ' data-leaf-tag="' + esc(name) + '"' +
        ' title="' + esc(raw.replace(/\s+/g, " ").slice(0, 200)) + '">' +
        '<span class="leaf-atomic-name">' + esc(name) + "</span>" +
        '<span class="leaf-atomic-hint">Double-click to edit</span>' +
        content +
        "</span>"
      );
    },

    _atomicRoles: function (attrs) {
      var map = this._ATOMIC_ROLES;
      var urlish = /^(?:https?:\/\/|\/|\.\/|data:)\S*$/i;
      var imagey = /^(?:data:image\/|.*\.(?:png|jpe?g|gif|webp|avif|svg)(?:[?#]|$))/i;

      function take(names) {
        for (var i = 0; i < attrs.length; i++) {
          var k = attrs[i][0].toLowerCase();
          if (names.indexOf(k) !== -1 && String(attrs[i][1]).trim() !== "") return attrs[i][1];
        }
        return "";
      }

      var media = "";
      for (var i = 0; i < attrs.length; i++) {
        var key = attrs[i][0].toLowerCase();
        var value = attrs[i][1];
        if (!urlish.test(value)) continue;
        if (map.image.indexOf(key) !== -1 || (key === "src" && imagey.test(value))) {
          media = value;
          break;
        }
      }

      var link = take(map.link);
      var cta = take(map.cta);
      var title = take(map.title);
      var ctaLabel = "";

      // `label` is a title on its own and a button caption next to a
      // destination — same word, two jobs, told apart by whether there is
      // anywhere to go.
      if (!title && !link) {
        title = cta;
      } else if (!title) {
        ctaLabel = cta;
      } else {
        ctaLabel = cta;
      }

      var eyebrow = take(map.eyebrow);
      var subtitle = take(map.subtitle);
      var claimed = [title, ctaLabel, link, media, eyebrow, subtitle].filter(Boolean);

      return {
        title: title,
        eyebrow: eyebrow,
        subtitle: subtitle,
        media: media,
        link: link,
        ctaLabel: ctaLabel,
        rest: attrs.filter(function (a) {
          return claimed.indexOf(a[1]) === -1;
        }),
      };
    },

    // [name, value] pairs from the OPENING tag only — attributes on nested
    // tags belong to those.
    _parseTagAttrs: function (raw) {
      var open = raw.match(/^\s*<[^>]*>/);
      if (!open) return [];
      var re = /([A-Za-z_:][\w.:-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/g;
      var out = [];
      var m;
      while ((m = re.exec(open[0])) !== null) {
        out.push([m[1], m[2] !== undefined ? m[2] : m[3] !== undefined ? m[3] : m[4]]);
      }
      return out;
    },

    // -- Editing an atomic block in place --
    //
    // A preserved tag is non-editable on purpose — that is what stops the
    // HTML round trip from corrupting it. But "non-editable" used to mean
    // the only way to change a `<Showcase>` was to switch to markdown mode
    // and find it by hand, which makes the visual surfaces useless for
    // component-heavy documents. Double-click opens a raw-source editor
    // for just that one block: still no in-place DOM editing of the tag,
    // still `data-leaf-raw` as the single source of truth, but reachable.

    _setupAtomicBlockEditing: function () {
      if (!this._visualEl) return;
      var self = this;

      // The chip's thumbnail is a guess from an attribute name — a wrong
      // guess should leave no thumbnail, not a broken-image icon sitting
      // in the writer's document. `error` doesn't bubble, hence capture.
      this._onAtomicMediaError = function (e) {
        var img = e.target;
        if (img && img.classList && img.classList.contains("leaf-atomic-media")) {
          img.remove();
        }
      };
      this._visualEl.addEventListener("error", this._onAtomicMediaError, true);

      if (this._readonly) return;

      this._onAtomicDblClick = function (e) {
        var chip = e.target && e.target.closest && e.target.closest(".leaf-atomic");
        if (!chip || !self._visualEl.contains(chip)) return;
        e.preventDefault();
        e.stopPropagation();
        self._openAtomicEditor(chip);
      };

      this._visualEl.addEventListener("dblclick", this._onAtomicDblClick);

      // Single click SELECTS a chip: visible ring, and the keyboard now
      // acts on the chip (see _atomicSelectionKeydown). A chip is
      // contenteditable=false, so without this the only way to delete one
      // was the markdown tab, and a chip ending the document was a wall
      // the caret couldn't step past. Clicking a lone chip's host
      // paragraph counts as clicking the chip (the host has no text to
      // seat a caret in anyway); clicking the editor's own padding below
      // the last block opens a fresh paragraph there.
      this._onAtomicClick = function (e) {
        var chip = e.target && e.target.closest && e.target.closest(".leaf-atomic");
        if (chip && !self._visualEl.contains(chip)) chip = null;

        if (!chip && e.target && e.target !== self._visualEl && e.target.closest) {
          var host = e.target.closest("p");
          if (
            host &&
            self._visualEl.contains(host) &&
            host.childElementCount === 1 &&
            host.firstElementChild.classList &&
            host.firstElementChild.classList.contains("leaf-atomic") &&
            (host.textContent || "").trim() ===
              (host.firstElementChild.textContent || "").trim()
          ) {
            chip = host.firstElementChild;
          }
        }

        if (!chip && e.target === self._visualEl) {
          self._maybeOpenParagraphBelow(e);
        }

        self._selectAtomicChip(chip);
      };
      this._visualEl.addEventListener("click", this._onAtomicClick);
    },

    _selectAtomicChip: function (chip) {
      if (this._selectedAtomic && this._selectedAtomic !== chip) {
        this._selectedAtomic.classList.remove("leaf-atomic-selected");
      }
      this._selectedAtomic = chip || null;
      if (!chip) return;

      chip.classList.add("leaf-atomic-selected");
      // Park the DOM selection ON the chip so the keyboard talks to it,
      // not to wherever the caret happened to be.
      var range = document.createRange();
      range.selectNode(chip);
      var sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    },

    // Clicking the editor's padding below the last block: the writer is
    // saying "there is text after this" — give them a paragraph. Reuses a
    // trailing empty one rather than stacking new ones per click.
    _maybeOpenParagraphBelow: function (e) {
      var last = this._visualEl.lastElementChild;
      if (!last) return;
      if (e.clientY <= last.getBoundingClientRect().bottom) return;

      if (
        last.tagName &&
        last.tagName.toLowerCase() === "p" &&
        !last.querySelector(".leaf-atomic") &&
        (last.textContent || "").replace(/[\u200B\uFEFF]/g, "").trim() === ""
      ) {
        this._placeCaretIn(last);
        return;
      }

      var p = document.createElement("p");
      p.innerHTML = "<br>";
      this._visualEl.appendChild(p);
      this._placeCaretIn(p);
      this._debouncedPushVisualChange();
    },

    // Keys while a chip is selected. Returns true when the key was
    // handled; anything unhandled drops the selection first so typing
    // never silently replaces a component.
    _atomicSelectionKeydown: function (e) {
      var chip = this._selectedAtomic;
      if (!chip) return false;
      if (!chip.isConnected) {
        this._selectedAtomic = null;
        return false;
      }

      var host =
        (chip.closest && chip.closest("p, li, h1, h2, h3, h4, h5, h6")) ||
        chip.parentNode;

      if (e.key === "Backspace" || e.key === "Delete") {
        e.preventDefault();
        this._historyCaptureNow("atomic-delete");
        this._selectAtomicChip(null);
        chip.remove();
        if (host && this._visualEl.contains(host)) {
          if (
            !host.firstElementChild &&
            (host.textContent || "").replace(/[\u200B\uFEFF]/g, "").trim() === ""
          ) {
            host.innerHTML = "<br>";
          }
          this._placeCaretIn(host);
        }
        this._debouncedPushVisualChange();
        this._updateCounts();
        return true;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        this._selectAtomicChip(null);
        var after = document.createElement("p");
        after.innerHTML = "<br>";
        host.parentNode.insertBefore(after, host.nextSibling);
        this._placeCaretIn(after);
        this._debouncedPushVisualChange();
        return true;
      }

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        this._selectAtomicChip(null);
        var next = host.nextElementSibling;
        if (!next) {
          next = document.createElement("p");
          next.innerHTML = "<br>";
          host.parentNode.appendChild(next);
          this._debouncedPushVisualChange();
        }
        var startRange = document.createRange();
        startRange.selectNodeContents(next);
        startRange.collapse(true);
        var startSel = window.getSelection();
        startSel.removeAllRanges();
        startSel.addRange(startRange);
        return true;
      }

      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        this._selectAtomicChip(null);
        var prev = host.previousElementSibling;
        if (!prev) {
          prev = document.createElement("p");
          prev.innerHTML = "<br>";
          host.parentNode.insertBefore(prev, host);
          this._debouncedPushVisualChange();
        }
        this._placeCaretIn(prev);
        return true;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        this._selectAtomicChip(null);
        return true;
      }

      // Modifier chords pass through untouched (undo already ran above).
      // Any other key drops the selection and seats the caret after the
      // chip's block, so typing lands somewhere visible instead of
      // replacing the component.
      if (e.ctrlKey || e.metaKey || e.altKey) return false;
      this._selectAtomicChip(null);
      if (host && this._visualEl.contains(host)) this._placeCaretIn(host);
      return false;
    },

    _openAtomicEditor: function (chip) {
      var self = this;
      var raw = chip.getAttribute("data-leaf-raw") || "";

      this._openTextPrompt({
        label: "Edit <" + (chip.getAttribute("data-leaf-tag") || "block") + "> source",
        value: raw,
        multiline: true,
        placeholder: '<Component attr="value" />',
        confirmLabel: "Save",
        anchor: chip,
        onSubmit: function (next) {
          var trimmed = (next || "").trim();
          if (!trimmed || trimmed === raw) return;

          var name = (trimmed.match(/^<([A-Za-z][\w-]*)\b/) || [])[1];
          if (!name) return;

          // Redraw immediately from what the client can work out, then
          // ask the server for the rich preview (attributes are cheap,
          // rendered children need a markdown renderer we don't have).
          var tmp = document.createElement("div");
          tmp.innerHTML = self._buildAtomicChip(trimmed, name);
          var replacement = tmp.firstElementChild;
          if (!replacement || !chip.parentNode) return;
          chip.parentNode.replaceChild(replacement, chip);

          var token = "atomic-" + Date.now() + "-" + Math.floor(Math.random() * 1e6);
          self._pendingPreserved = { token: token, el: replacement };
          self.pushEventTo(self.el, "render_preserved", {
            editor_id: self._editorId,
            token: token,
            raw: trimmed,
          });

          self._debouncedPushVisualChange();
          self._updateCounts();
        },
      });
    },

    // A vendored copy of leaf.js drifts silently: the editor renders
    // identically whether the bundle matches the library or is six
    // releases behind, and the only symptom is that something quietly
    // doesn't happen. Compare the two and say so once.
    _warnStaleBundle: function () {
      if (window.__leafVersionWarned) return;
      var served = this.el.dataset.leafJsVersion;
      var loaded = window.LeafHooks && window.LeafHooks.version;
      if (!served || !loaded || served === loaded) return;

      window.__leafVersionWarned = true;
      console.warn(
        "[leaf] JS bundle version " + loaded + " does not match the leaf " +
        "library version " + served + ". If you vendored " +
        "priv/static/assets/leaf.js into your app's assets, that copy is " +
        "stale — re-copy it (mix phoenix_kit.update, or your own asset " +
        "step) after updating the dependency."
      );
    },

    // -- Character limit (maxlength) --

    _setupMaxlength: function () {
      if (!this._maxlength || !this._visualEl) return;
      var self = this;
      // Textareas enforce maxlength natively (server-rendered attr); the
      // contenteditable needs a beforeinput guard that blocks length-adding
      // edits once the cap is reached, while still allowing deletions.
      this._visualEl.addEventListener("beforeinput", function (e) {
        if (!self._maxlength) return;
        if (e.inputType && e.inputType.indexOf("delete") === 0) return;
        var len = (self._visualEl.innerText || "").length;
        if (
          len >= self._maxlength &&
          /^(insertText|insertParagraph|insertFromPaste|insertCompositionText|insertLineBreak)$/.test(
            e.inputType || ""
          )
        ) {
          e.preventDefault();
        }
      });
    },

    // -- Auto-grow (height="auto") --

    _setupAutoGrow: function () {
      if (!this._autoGrow) return;
      var self = this;
      var attach = function (ta) {
        if (!ta) return;
        ta.style.resize = "none";
        ta.addEventListener("input", function () {
          self._growTextarea(ta);
        });
        self._growTextarea(ta);
      };
      attach(this._getMarkdownTextarea());
      attach(this._getHtmlTextarea());
    },

    _growTextarea: function (ta) {
      if (!ta) return;
      ta.style.height = "auto";
      ta.style.height = ta.scrollHeight + "px";
    },

    // -- Unsaved-changes navigation guard --

    _setupNavigationGuard: function () {
      if (!this._protectNavigation) return;
      var self = this;
      this._beforeUnloadHandler = function (e) {
        if (self._computeDirty(self._currentMarkdown())) {
          e.preventDefault();
          e.returnValue = "";
          return "";
        }
      };
      window.addEventListener("beforeunload", this._beforeUnloadHandler);
    },

    // -- Smart typography (opt-in) --

    // Replace the just-typed sequence before the caret with its typographic
    // form (-- → em dash, ... → ellipsis, straight → curly quotes). Skips
    // code/pre so samples stay literal. Called from _onVisualInput.
    _maybeSmartTypography: function () {
      if (!this._smartTypography) return;
      var sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      var range = sel.getRangeAt(0);
      var node = range.startContainer;
      if (node.nodeType !== Node.TEXT_NODE) return;
      if (node.parentElement && node.parentElement.closest("code, pre")) return;

      var offset = range.startOffset;
      var text = node.textContent;
      var before = text.slice(0, offset);
      var repl = null;
      var cut = 0;

      if (/\.\.\.$/.test(before)) {
        repl = "…";
        cut = 3;
      } else if (/--$/.test(before)) {
        repl = "—";
        cut = 2;
      } else if (before.charAt(offset - 1) === '"') {
        var p = before.charAt(offset - 2);
        repl = p === "" || /\s/.test(p) ? "“" : "”";
        cut = 1;
      } else if (before.charAt(offset - 1) === "'") {
        var p2 = before.charAt(offset - 2);
        repl = p2 === "" || /\s/.test(p2) ? "‘" : "’";
        cut = 1;
      }
      if (!repl) return;

      var newBefore = before.slice(0, offset - cut) + repl;
      node.textContent = newBefore + text.slice(offset);
      var r = document.createRange();
      r.setStart(node, newBefore.length);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    },

    // -- Export / copy (#54) --

    _currentHtml: function () {
      if (this._mode === "html") {
        var hta = this._getHtmlTextarea();
        return hta ? hta.value : "";
      }
      return this._visualEl ? this._visualEl.innerHTML : "";
    },

    _downloadMarkdown: function () {
      var md = this._currentMarkdown();
      var blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = (this._editorId || "document") + ".md";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () {
        URL.revokeObjectURL(url);
      }, 1000);
    },

    // -- Mode switching --

    _setupResponsiveToolbar: function () {
      var toolbar = this.el.querySelector("[data-visual-toolbar]");
      var inlineModes = this.el.querySelector('[data-mode-switcher="inline"]');
      var compactModes = this.el.querySelector("[data-mode-switcher-compact]");
      var fullscreenSection = this.el.querySelector('[data-toolbar-section="fullscreen"]');
      if (!toolbar || !inlineModes || !compactModes) return;

      var self = this;
      this._toolbarResizeRaf = null;

      this._syncResponsiveToolbar = function () {
        if (self._toolbarResizeRaf) {
          cancelAnimationFrame(self._toolbarResizeRaf);
        }

        self._toolbarResizeRaf = requestAnimationFrame(function () {
          self._toolbarResizeRaf = null;
          if (!toolbar.isConnected) return;

          var toolbarWidth = toolbar.getBoundingClientRect().width;
          var previousWidth = self._responsiveToolbarWidth;
          self._responsiveToolbarWidth = toolbarWidth;
          var isShrinking =
            previousWidth !== undefined && toolbarWidth < previousWidth;

          var shouldCompactModes = toolbarWidth <= 640;

          if (toolbar.dataset.compactModes === "true") {
            if (isShrinking || toolbarWidth <= 640) {
              shouldCompactModes = true;
            } else if (
              previousWidth !== undefined &&
              Math.abs(toolbarWidth - previousWidth) < 1
            ) {
              return;
            } else {
              // Only when growing do we briefly restore the inline switcher
              // to test whether it fits again. Shrinking keeps compact mode
              // pinned to avoid one-frame mode-button flicker.
              delete toolbar.dataset.compactModes;
            }
          }

          var rowTop = null;
          var maxTop = null;
          toolbar.querySelectorAll("button, summary").forEach(function (el) {
            // Skip buttons inside the toolbar's dropdown menus — an open
            // menu positions its items below the row and would otherwise
            // read as a wrap.
            var inMenu = el.closest("ul");
            if (
              inlineModes.contains(el) ||
              compactModes.contains(el) ||
              (fullscreenSection && fullscreenSection.contains(el)) ||
              (inMenu && toolbar.contains(inMenu)) ||
              el.offsetParent === null
            ) {
              return;
            }
            var top = el.getBoundingClientRect().top;
            if (rowTop === null || top < rowTop) rowTop = top;
            if (maxTop === null || top > maxTop) maxTop = top;
          });

          if (rowTop === null) rowTop = toolbar.getBoundingClientRect().top;

          var modeTop = inlineModes.getBoundingClientRect().top;
          var fullscreenTop =
            fullscreenSection && fullscreenSection.offsetParent !== null
              ? fullscreenSection.getBoundingClientRect().top
              : rowTop;

          // Compact when the mode switcher / fullscreen wrap OR when ANY
          // primary button (including host toolbar_extra + export tools)
          // has spilled onto a second row — so the extra tools are part of
          // the width calculation instead of silently wrapping.
          if (
            modeTop > rowTop + 2 ||
            fullscreenTop > rowTop + 2 ||
            (maxTop !== null && maxTop > rowTop + 2)
          ) {
            shouldCompactModes = true;
          }

          if (!shouldCompactModes) {
            delete toolbar.dataset.compactModes;
            delete toolbar.dataset.toolbarOverflowLevel;
            self._toolbarCompactStartWidth = null;
            return;
          }

          toolbar.dataset.compactModes = "true";

          if (
            self._toolbarCompactStartWidth === null ||
            self._toolbarCompactStartWidth === undefined ||
            toolbarWidth > self._toolbarCompactStartWidth
          ) {
            self._toolbarCompactStartWidth = toolbarWidth;
          }

          // Keep overflow gradual. Modes collapse first; after that, every
          // additional ~30px of lost width moves only the next priority tier
          // into the tools menu instead of collapsing several at once.
          var overflowStep = 30;
          var overflowLevel = Math.floor(
            (self._toolbarCompactStartWidth - toolbarWidth) / overflowStep
          );
          if (overflowLevel <= 0) {
            delete toolbar.dataset.toolbarOverflowLevel;
          } else {
            toolbar.dataset.toolbarOverflowLevel = String(
              Math.min(overflowLevel, 10)
            );
          }
        });
      };

      if (window.ResizeObserver) {
        this._toolbarResizeObserver = new ResizeObserver(function () {
          self._syncResponsiveToolbar();
        });
        this._toolbarResizeObserver.observe(toolbar);
      } else {
        this._toolbarResizeHandler = function () {
          self._syncResponsiveToolbar();
        };
        window.addEventListener("resize", this._toolbarResizeHandler);
      }

      this._syncResponsiveToolbar();
    },

    _setupModeSwitcher: function () {
      var self = this;
      var tabs = this.el.querySelectorAll("[data-mode-tab]");

      tabs.forEach(function (tab) {
        tab.addEventListener("click", function (e) {
          e.preventDefault();
          var newMode = tab.dataset.modeTab;
          if (!self._isModeAllowed(newMode)) return;
          if (newMode === self._mode) return;

          self._dismissLinkPopover();
          // The popup is anchored to the surface we're about to hide, and
          // its ARIA lives on that element. (`set_mode` from the server
          // routes through this same click, so this covers it too.)
          self._suggestClose();

          var oldMode = self._mode;
          self._syncModes(oldMode, newMode);

          self._mode = newMode;
          self._applyModeVisibility(newMode);

          tabs.forEach(function (t) {
            if (t.dataset.modeTab === newMode) {
              t.classList.add("btn-active");
              t.classList.remove("btn-ghost");
            } else {
              t.classList.remove("btn-active");
              t.classList.add("btn-ghost");
            }
          });
          var compactMenu = tab.closest("[data-mode-switcher-compact]");
          if (compactMenu) compactMenu.removeAttribute("open");

          var currentMarkdown = "";
          var ta = self._getMarkdownTextarea();
          if (ta) currentMarkdown = ta.value;
          self.pushEventTo(self.el, "mode_changed", {
            editor_id: self._editorId,
            mode: newMode,
            content: currentMarkdown,
          });

          self._updateCounts();
          // Record the newly-active surface, unless we are here BECAUSE of a
          // restore — in that case the caller is about to write the content.
          if (!self._historyRestoring) self._historyCaptureNow("mode:" + newMode);
        });
      });
    },

    _isModeAllowed: function (mode) {
      if (mode === "visual" && this._denyVisualMode) return false;
      if (mode === "hybrid" && this._denyHybridMode) return false;
      if (mode === "markdown" && this._denyMarkdownMode) return false;
      if (mode === "html" && this._denyHtmlMode) return false;
      return true;
    },

    _applyModeVisibility: function (mode) {
      var wrappers = [
        { el: this._visualWrapper, mode: "visual" },
        { el: this._markdownWrapper, mode: "markdown" },
        { el: this._htmlWrapper, mode: "html" },
      ];
      wrappers.forEach(function (w) {
        if (!w.el) return;
        // Hybrid mode reuses the visual wrapper — same DOM, same toolbar,
        // just an extra `data-mode="hybrid"` selector for syntax decorations.
        var visible = w.mode === mode || (w.mode === "visual" && mode === "hybrid");
        if (visible) {
          w.el.classList.remove("hidden");
        } else {
          w.el.classList.add("hidden");
        }
      });

      // Hide formatting toolbar in html mode (raw editing)
      var toolbarButtons = this.el.querySelector("[data-visual-toolbar-buttons]");
      if (toolbarButtons) {
        if (mode === "html") {
          toolbarButtons.classList.add("hidden");
          toolbarButtons.classList.remove("contents");
        } else {
          toolbarButtons.classList.remove("hidden");
          toolbarButtons.classList.add("contents");
        }
      }

      // Auto-grow: a textarea reports scrollHeight 0 while hidden, so size
      // it once it becomes visible for this mode.
      if (this._autoGrow) {
        if (mode === "markdown") this._growTextarea(this._getMarkdownTextarea());
        else if (mode === "html") this._growTextarea(this._getHtmlTextarea());
      }
    },

    _syncModes: function (from, to) {
      var self = this;

      if (from === "visual" || from === "hybrid") {
        // Re-render any open source-mode block before serializing — the
        // outgoing markdown / html / visual representations all expect
        // the rendered form, not the literal-source `<p data-leaf-source>`
        // intermediate. Cheap no-op when no block is open.
        if (from === "hybrid") this._exitAllSourceMode();

        var visualHtml = this._visualEl ? this._visualEl.innerHTML : "";

        if (to === "markdown") {
          var mdTa = this._getMarkdownTextarea();
          if (mdTa) mdTa.value = htmlToMarkdown(visualHtml);
        } else if (to === "html") {
          var htmlTa = this._getHtmlTextarea();
          if (htmlTa) {
            htmlTa.value = this._stripDecorationSpans(visualHtml);
          }
        }

      } else if (from === "markdown") {
        var mdTa = this._getMarkdownTextarea();
        var markdown = mdTa ? mdTa.value : "";

        // Hybrid shares the visual contenteditable, so the markdown→hybrid
        // path needs the same server-side markdown→html sync as
        // markdown→visual; without this branch, switching from markdown to
        // hybrid leaves the visual DOM showing whatever it had before.
        if (to === "visual" || to === "hybrid") {
          this.pushEventTo(this.el, "sync_markdown_to_visual", {
            editor_id: this._editorId,
            markdown: markdown,
          });
        } else if (to === "html") {
          // Server converts markdown→html, pushes to html textarea
          this.pushEventTo(this.el, "convert_markdown_to_html", {
            editor_id: this._editorId,
            markdown: markdown,
          });
        }

      } else if (from === "html") {
        var htmlTa = this._getHtmlTextarea();
        var rawHtml = htmlTa ? htmlTa.value : "";

        if (to === "visual" || to === "hybrid") {
          // Set innerHTML directly; hybrid uses the same contenteditable.
          if (this._visualEl) {
            this._visualEl.innerHTML = rawHtml || "<p><br></p>";
            this._normalizeRenderedHtml(this._visualEl);
            this._ensureListItemPlaceholders(this._visualEl);
          }
        } else if (to === "markdown") {
          // Client-side HTML→markdown conversion
          var mdTa = this._getMarkdownTextarea();
          if (mdTa) mdTa.value = htmlToMarkdown(rawHtml);
        }
      }
    },

    _stripDecorationSpans: function (html) {
      // Returns `html` with all `.leaf-syntax-decoration` spans removed.
      // Used when handing hybrid-mode content to the html textarea so the
      // raw-html view doesn't get cluttered with cursor-only delimiter
      // markup.
      var tmp = document.createElement("div");
      tmp.innerHTML = html;
      var spans = tmp.querySelectorAll(".leaf-syntax-decoration");
      for (var i = 0; i < spans.length; i++) {
        if (spans[i].parentNode) {
          spans[i].parentNode.removeChild(spans[i]);
        }
      }
      // Strip cursor-anchoring ZWSPs introduced by heading decoration.
      return tmp.innerHTML.replace(/​/g, "");
    },

    // Hybrid → visual transition: remove every cursor-anchoring decoration
    // span from the live contenteditable in-place. Resets the tracking
    // state so a later switch back to hybrid starts clean.
    _stripDecorationSpansFromVisualEl: function () {
      if (!this._visualEl) return;
      this._syntaxMutating = true;
      try {
        var spans = this._visualEl.querySelectorAll(
          ".leaf-syntax-decoration"
        );
        for (var i = 0; i < spans.length; i++) {
          if (spans[i].parentNode) {
            spans[i].parentNode.removeChild(spans[i]);
          }
        }
        // Heading decoration leaves cursor-anchoring ZWSP text nodes when
        // it adds the `# ` markers; clean those up too so they don't
        // surface as invisible characters in plain visual mode.
        var walker = document.createTreeWalker(
          this._visualEl,
          NodeFilter.SHOW_TEXT,
          null
        );
        var text;
        while ((text = walker.nextNode())) {
          if (text.nodeValue && text.nodeValue.indexOf("​") !== -1) {
            text.nodeValue = text.nodeValue.replace(/​/g, "");
          }
        }
      } finally {
        this._syntaxMutating = false;
      }
      this._decoratedAncestors = [];
      this._decoratedHeading = null;
    },

    // Non-destructive read of the contenteditable's text with hybrid-only
    // decoration spans (and their cursor-anchoring ZWSPs) filtered out.
    // Used for footer word/char counting so the numbers reflect the
    // user-perceived content, independent of where the cursor sits.
    _readPlainTextWithoutDecorations: function () {
      if (!this._visualEl) return "";
      var clone = this._visualEl.cloneNode(true);
      var spans = clone.querySelectorAll(".leaf-syntax-decoration");
      for (var i = 0; i < spans.length; i++) {
        if (spans[i].parentNode) {
          spans[i].parentNode.removeChild(spans[i]);
        }
      }
      var text = clone.innerText || clone.textContent || "";
      return text.replace(/​/g, "");
    },

    _getMarkdownTextarea: function () {
      return document.getElementById(
        this._editorId + "-markdown-textarea"
      );
    },

    // -- Toolbar --

    _setupToolbar: function () {
      var self = this;

      // Preserve the contenteditable's selection on miss-clicks anywhere
      // in the editor's chrome (toolbar gaps, mode tabs, footer, border,
      // dividers, bg-base-200 background, etc.). preventDefault on
      // mousedown blocks the focus shift but still lets the click event
      // through, so buttons / dropdown triggers / mode switcher all keep
      // working. Carve out the contenteditable itself and form controls
      // so cursor placement and text-input focus aren't broken.
      this.el.addEventListener("mousedown", function (e) {
        var target = e.target;
        if (self._visualEl && self._visualEl.contains(target)) return;
        if (target && target.tagName) {
          var tag = target.tagName.toLowerCase();
          if (tag === "input" || tag === "textarea" || tag === "select") {
            return;
          }
        }
        e.preventDefault();
      });

      var buttons = this.el.querySelectorAll("[data-toolbar-action]");

      buttons.forEach(function (btn) {
        btn.addEventListener("mousedown", function (e) {
          e.preventDefault();
        });

        btn.addEventListener("click", function (e) {
          e.preventDefault();
          var action = btn.dataset.toolbarAction;
          self._execToolbarAction(action);
        });
      });

      // Host-defined toolbar buttons (toolbar_extra). Each emits a
      // "toolbar_action" event carrying the button id and the current
      // selection, leaving what-to-do entirely to the host LiveView
      // (typically a send_update(..., action: :insert_markdown, ...)).
      var hostButtons = this.el.querySelectorAll("[data-host-action]");
      hostButtons.forEach(function (btn) {
        btn.addEventListener("mousedown", function (e) {
          e.preventDefault();
        });
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          var info = self._getSelectionInfo() || { text: "", range: null };
          self.pushEventTo(self.el, "toolbar_action", {
            editor_id: self._editorId,
            id: btn.dataset.hostAction,
            text: info.text,
            range: info.range,
          });
        });
      });

      // Toolbar dropdowns: toggle menus without stealing editor focus
      var dropdowns = [
        { trigger: "[data-heading-trigger]", menu: "[data-heading-menu]" },
        { trigger: "[data-inline-more-trigger]", menu: "[data-inline-more-menu]" },
        { trigger: "[data-table-trigger]", menu: "[data-table-menu]" },
        { trigger: "[data-insert-more-trigger]", menu: "[data-insert-more-menu]" },
      ];
      var compactMenu = self.el.querySelector("[data-mode-switcher-compact]");
      var compactMenuSummary = compactMenu ? compactMenu.querySelector("summary") : null;
      var extraDetailsMenus = Array.prototype.slice.call(
        self.el.querySelectorAll("[data-mobile-tools-menu], [data-mobile-options-menu]")
      );
      var closeImageDropdown = function () {
        if (self._imageDropdownMenu) {
          self._imageDropdownMenu.classList.add("hidden");
        }
        if (self._imageDropdownBackdrop) {
          self._imageDropdownBackdrop.remove();
          self._imageDropdownBackdrop = null;
        }
      };
      var closeToolbarMenus = function (exceptMenu, keepImageDropdown) {
        dropdowns.forEach(function (other) {
          var otherMenu = self.el.querySelector(other.menu);
          if (otherMenu && otherMenu !== exceptMenu) {
            otherMenu.classList.add("hidden");
          }
        });
        if (exceptMenu !== compactMenu && compactMenu) {
          compactMenu.removeAttribute("open");
        }
        extraDetailsMenus.forEach(function (details) {
          if (details !== exceptMenu) details.removeAttribute("open");
        });
        if (!keepImageDropdown) closeImageDropdown();
      };
      dropdowns.forEach(function (cfg) {
        var trigger = self.el.querySelector(cfg.trigger);
        var menu = self.el.querySelector(cfg.menu);
        if (!trigger || !menu) return;
        trigger.addEventListener("mousedown", function (e) { e.preventDefault(); });
        menu.addEventListener("mousedown", function (e) { e.preventDefault(); });
        trigger.addEventListener("click", function (e) {
          e.preventDefault();
          closeToolbarMenus(menu);
          menu.classList.toggle("hidden");
        });
        menu.querySelectorAll("[data-toolbar-action]").forEach(function (btn) {
          btn.addEventListener("click", function () { menu.classList.add("hidden"); });
        });
        document.addEventListener("mousedown", function (e) {
          if (!trigger.contains(e.target) && !menu.contains(e.target)) {
            menu.classList.add("hidden");
          }
        });
      });

      if (compactMenu && compactMenuSummary) {
        compactMenuSummary.addEventListener("mousedown", function (e) {
          e.preventDefault();
        });
        compactMenu.addEventListener("mousedown", function (e) {
          e.preventDefault();
        });
        compactMenuSummary.addEventListener("click", function (e) {
          e.preventDefault();
          var willOpen = !compactMenu.hasAttribute("open");
          closeToolbarMenus(compactMenu);
          if (willOpen) compactMenu.setAttribute("open", "");
        });
        compactMenu.querySelectorAll("[data-toolbar-action], [data-mode-tab]").forEach(function (btn) {
          btn.addEventListener("click", function () {
            compactMenu.removeAttribute("open");
          });
        });
        document.addEventListener("mousedown", function (e) {
          if (!compactMenu.contains(e.target)) {
            compactMenu.removeAttribute("open");
          }
        });
      }

      extraDetailsMenus.forEach(function (details) {
        var summary = details.querySelector("summary");
        if (!summary) return;
        summary.addEventListener("mousedown", function (e) {
          e.preventDefault();
        });
        details.addEventListener("mousedown", function (e) {
          e.preventDefault();
        });
        summary.addEventListener("click", function (e) {
          e.preventDefault();
          var willOpen = !details.hasAttribute("open");
          closeToolbarMenus(details);
          if (willOpen) details.setAttribute("open", "");
        });
        details.querySelectorAll("[data-toolbar-action], [data-mode-tab]").forEach(function (btn) {
          btn.addEventListener("click", function () {
            details.removeAttribute("open");
          });
        });
        document.addEventListener("mousedown", function (e) {
          if (!details.contains(e.target)) {
            details.removeAttribute("open");
          }
        });
      });

      // Image dropdown: rendered on body with a backdrop to sit above navbars
      var imgTrigger = self.el.querySelector("[data-image-dropdown-trigger]");
      var imgMenu = self.el.querySelector("[data-image-dropdown-menu]");
      if (imgTrigger && imgMenu) {
        imgMenu.remove();
        imgMenu.style.position = "fixed";
        imgMenu.style.zIndex = "100000";
        document.body.appendChild(imgMenu);
        this._imageDropdownMenu = imgMenu;

        imgTrigger.addEventListener("mousedown", function (e) { e.preventDefault(); });
        imgMenu.addEventListener("mousedown", function (e) { e.preventDefault(); });

        imgTrigger.addEventListener("click", function (e) {
          e.preventDefault();
          closeToolbarMenus(null, true);

          if (imgMenu.classList.contains("hidden")) {
            // Show backdrop + menu
            var backdrop = document.createElement("div");
            backdrop.className = "leaf-image-url-backdrop";
            backdrop.addEventListener("click", function () {
              imgMenu.classList.add("hidden");
              backdrop.remove();
              self._imageDropdownBackdrop = null;
            });
            document.body.appendChild(backdrop);
            self._imageDropdownBackdrop = backdrop;

            var rect = imgTrigger.getBoundingClientRect();
            imgMenu.style.left = rect.left + "px";
            imgMenu.style.top = (rect.bottom + 2) + "px";
            imgMenu.classList.remove("hidden");
          } else {
            imgMenu.classList.add("hidden");
            if (self._imageDropdownBackdrop) {
              self._imageDropdownBackdrop.remove();
              self._imageDropdownBackdrop = null;
            }
          }
        });

        imgMenu.querySelectorAll("[data-toolbar-action]").forEach(function (btn) {
          btn.addEventListener("click", function () {
            imgMenu.classList.add("hidden");
            if (self._imageDropdownBackdrop) {
              self._imageDropdownBackdrop.remove();
              self._imageDropdownBackdrop = null;
            }
          });
        });
      }

      // Mousedown on a rendered `<hr>` swaps it for an editable
      // `<p data-leaf-hr-source>---</p>` so the user can adjust or
      // delete the rule. We use `mousedown` (not `click`) and
      // `preventDefault` so the browser doesn't first place the cursor
      // in an adjacent paragraph and shift `e.target` away from the
      // thin HR line. The `selectionchange` listener swaps back to a
      // real `<hr>` once the cursor leaves the source paragraph.
      if (this._visualEl) {
        this._visualEl.addEventListener("mousedown", function (e) {
          if (self._mode !== "visual" && self._mode !== "hybrid") return;
          var hr = self._hrAtClick(e);
          if (hr) {
            e.preventDefault();
            self._showHrSource(hr);
          }
        });
      }

      // DEFERRED, never synchronous, for the same reason the selection
      // mirror is: typing over a selection is one editing command — delete,
      // then insert — with a selectionchange in the middle. This listener is
      // the source machinery's only driver, and entering or refreshing
      // source mode REBUILDS the block's children; done mid-command, the
      // insertion finds its target gone and dies. Double-click the only word
      // of a checkbox row and type: the first click had put the row into
      // source form, the delete phase changed its text, the refresh rebuilt
      // it, and the keystroke never landed.
      document.addEventListener("selectionchange", function () {
        if (self._sourceUpdateScheduled) return;
        self._sourceUpdateScheduled = true;

        var run = function () {
          self._sourceUpdateScheduled = false;
          if (
            (self._mode === "visual" || self._mode === "hybrid") &&
            self._visualEl
          ) {
            var sel = window.getSelection();
            if (
              sel.rangeCount > 0 &&
              self._visualEl.contains(sel.anchorNode)
            ) {
              self._updateToolbarState();
              self._updateSourceBlock();
            } else if (document.activeElement !== self._visualEl) {
              // Cursor genuinely left the editor — re-render any open source
              // block so the user sees the rendered form. If focus is still
              // on the editor (e.g., right after a toolbar click), leave the
              // source block alone so it survives until focus actually moves.
              self._exitAllSourceMode();
            }
          } else {
            self._exitAllSourceMode();
          }
        };

        self._afterEditingCommand(run);
      });

    },

    _updateSyntaxDecorations: function () {
      // In hybrid mode, when the cursor enters one or more inline-formatting
      // elements, insert real text-bearing spans for each level's markdown
      // delimiters so the cursor can step through every char with arrow keys.
      // For nested formatting (e.g. `<strong><em>hello</em></strong>`), all
      // wrappers in the chain are decorated independently — the rendered
      // result is `***hello***` with the outer `**` as direct children of
      // `<strong>` and the inner `*` as direct children of `<em>`.
      if (this._mode !== "hybrid") {
        this._clearSyntaxDecoration();
        return;
      }
      if (this._syntaxMutating) return;

      var sel = window.getSelection();
      if (!sel.rangeCount) {
        this._clearSyntaxDecoration();
        return;
      }
      // Same freeze as _updateSourceBlock: adding/removing delimiter
      // spans under a live non-collapsed selection collapses it.
      if (!sel.isCollapsed) return;
      var newChain = this._findFormattingChainForCursor(sel);
      var oldChain = this._decoratedAncestors || [];
      if (this._chainsEqual(oldChain, newChain)) return;

      var savedRange = sel.getRangeAt(0).cloneRange();
      this._syntaxMutating = true;
      try {
        for (var i = 0; i < oldChain.length; i++) {
          if (newChain.indexOf(oldChain[i]) === -1) {
            this._removeDelimitersFrom(oldChain[i]);
          }
        }
        for (var j = 0; j < newChain.length; j++) {
          if (oldChain.indexOf(newChain[j]) === -1) {
            this._addDelimitersTo(newChain[j]);
          }
        }
        this._decoratedAncestors = newChain;

        // Cursor placement. If the cursor was outside the innermost
        // wrapper (boundary case — Chrome lands the anchor in the parent
        // when arrowing in), jump past the innermost's leading or trailing
        // decorations to the content edge.
        var newRange = null;
        if (newChain.length > 0) {
          var innermost = newChain[0];
          var delim = this._delimiterFor(innermost);
          if (
            delim &&
            !innermost.contains(savedRange.startContainer) &&
            innermost !== savedRange.startContainer
          ) {
            var ancRange = document.createRange();
            ancRange.selectNode(innermost);
            var fromLeft =
              savedRange.compareBoundaryPoints(
                Range.START_TO_START,
                ancRange,
              ) <= 0;
            newRange = document.createRange();
            if (fromLeft) {
              newRange.setStart(innermost, delim.length);
            } else {
              newRange.setStart(
                innermost,
                innermost.childNodes.length - delim.length,
              );
            }
            newRange.collapse(true);
          }
        }
        try {
          sel.removeAllRanges();
          sel.addRange(newRange || savedRange);
        } catch (_e) {
          /* range no longer valid */
        }
      } finally {
        this._syntaxMutating = false;
      }
    },

    _findFormattingChainForCursor: function (sel) {
      if (!sel || !sel.rangeCount) return [];
      var range = sel.getRangeAt(0);

      // Find the outermost formatting wrapper relevant to the cursor —
      // walk up from anchor first, then fall back to probing the cursor's
      // boundary neighbors if anchor isn't inside any formatting.
      var walkUp = this._collectFormattingAncestors(sel.anchorNode);
      var outermost = walkUp.length > 0 ? walkUp[walkUp.length - 1] : null;

      if (!outermost) {
        var candidate = this._findBoundaryFormattingCandidate(range);
        if (candidate) {
          var ups = this._collectFormattingAncestors(candidate);
          if (ups.length > 0) outermost = ups[ups.length - 1];
        }
      }

      if (!outermost) return [];

      // Descend the outermost's subtree so every nested formatting layer
      // gets decorated together — `***hello***` shows all three markers
      // when the cursor is anywhere on the bolded word, not just when it
      // reaches the inner content.
      return this._descendChainAll(outermost);
    },

    _findBoundaryFormattingCandidate: function (range) {
      var node = range.endContainer;
      var offset = range.endOffset;
      var candidate = null;
      var fromLeft = true;
      if (node.nodeType === Node.TEXT_NODE) {
        if (offset === node.textContent.length && node.nextSibling) {
          candidate = node.nextSibling;
          fromLeft = true;
        } else if (offset === 0 && node.previousSibling) {
          candidate = node.previousSibling;
          fromLeft = false;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.childNodes[offset]) {
          candidate = node.childNodes[offset];
          fromLeft = true;
        } else if (offset > 0 && node.childNodes[offset - 1]) {
          candidate = node.childNodes[offset - 1];
          fromLeft = false;
        }
      }
      while (candidate && this._isDecorationSpan(candidate)) {
        candidate = fromLeft ? candidate.nextSibling : candidate.previousSibling;
      }
      if (
        candidate &&
        candidate.nodeType === Node.ELEMENT_NODE &&
        this._isFormattingElement(candidate)
      ) {
        return candidate;
      }
      return null;
    },

    _descendChainAll: function (root) {
      // Returns every formatting element in `root`'s subtree, depth-first,
      // innermost first, including `root` itself. Decoration spans are
      // skipped during traversal so we don't recurse into them.
      var result = [];
      var self = this;
      function walk(node) {
        var children = node.children;
        for (var i = 0; i < children.length; i++) {
          var c = children[i];
          if (
            c.classList &&
            c.classList.contains("leaf-syntax-decoration")
          ) {
            continue;
          }
          walk(c);
        }
        if (self._isFormattingElement(node)) {
          result.push(node);
        }
      }
      walk(root);
      return result;
    },

    _collectFormattingAncestors: function (node) {
      // Walk up from `node`, returning every formatting wrapper encountered
      // up to (but not including) `_visualEl`. Innermost first.
      var chain = [];
      var n = node;
      while (n && n !== this._visualEl) {
        if (this._isFormattingElement(n)) {
          chain.push(n);
        }
        n = n.parentNode;
      }
      return chain;
    },

    _chainsEqual: function (a, b) {
      if (a === b) return true;
      if (!a || !b || a.length !== b.length) return false;
      for (var i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    },

    _isFormattingElement: function (node) {
      if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
      var tag = node.tagName ? node.tagName.toLowerCase() : "";
      var tags = [
        "b",
        "strong",
        "i",
        "em",
        "s",
        "del",
        "strike",
        "code",
        "u",
        "sub",
        "sup",
        "mark",
        "a",
      ];
      if (tags.indexOf(tag) !== -1) return true;
      if (node.classList && node.classList.contains("leaf-spoiler")) return true;
      return false;
    },

    _formattingSelector:
      "b, strong, i, em, s, del, strike, code, u, sub, sup, mark, a, .leaf-spoiler",

    _snapshotFormattingElements: function () {
      if (!this._visualEl) return null;
      var nodes = this._visualEl.querySelectorAll(this._formattingSelector);
      var set = new Set();
      for (var i = 0; i < nodes.length; i++) set.add(nodes[i]);
      return set;
    },

    // Re-run decoration on the next tick. Chrome leaves the selection in a
    // transient state immediately after execCommand, and any selectionchange
    // events queued by the DOM mutation fire async — running on the next
    // tick lets all of that settle before we look up the formatting ancestor.
    // `preSnapshot` is a Set of formatting elements present before the
    // action; we compare against the post-action set to find the newly
    // inserted wrapper, which is the most reliable signal — selection state
    // after execCommand can't always be trusted across browsers.
    _deferredSyntaxRefresh: function (preSnapshot) {
      // Decoration spans are a hybrid-mode feature only. In plain visual
      // mode, inserting `**`/`*`/`~~` text spans inside the new wrapper
      // would surface the literal markdown delimiters next to the bold /
      // italic / strike text, which the user never asked to see.
      if (this._mode !== "hybrid") return;
      var self = this;
      setTimeout(function () {
        self._clearSyntaxDecoration();

        // Prefer the cursor-based chain — it captures the full nest stack
        // (e.g., italicizing already-bold text → [em, strong]).
        var sel = window.getSelection();
        var chain =
          sel.rangeCount > 0
            ? self._findFormattingChainForCursor(sel)
            : [];

        // Fallback: if the post-action selection isn't trustworthy,
        // diff against the snapshot to find the new wrapper, then walk up.
        if (chain.length === 0 && preSnapshot) {
          var newFormatting = self._findNewFormatting(preSnapshot);
          if (newFormatting) {
            chain = self._collectFormattingAncestors(newFormatting);
          }
        }
        if (chain.length === 0) return;

        self._syntaxMutating = true;
        try {
          for (var i = 0; i < chain.length; i++) {
            self._addDelimitersTo(chain[i]);
          }
          self._decoratedAncestors = chain;
        } finally {
          self._syntaxMutating = false;
        }
      }, 0);
    },

    _refreshHybridSourceBlockAfterToolbarAction: function () {
      if (this._mode !== "hybrid") return false;
      if (!this._sourceBlock || !this._sourceBlock.isConnected) return false;

      var sel = window.getSelection();
      if (!sel.rangeCount) return false;
      var range = sel.getRangeAt(0);
      if (
        !this._sourceBlock.contains(range.startContainer) &&
        !this._sourceBlock.contains(range.endContainer)
      ) {
        return false;
      }

      // Source-mode blocks already render real markdown markers via
      // `.leaf-source-marker`. The older deferred decoration path would add
      // another visible delimiter pair around toolbar-created wrappers, e.g.
      // `****bold****` until the block was rebuilt. Force the source-block
      // serializer/rebuilder instead so toolbar formatting follows the same
      // path as typed markdown.
      this._clearSyntaxDecoration();
      this._lastSourceStateKey = null;
      this._refreshSourceBlock();
      return true;
    },

    _findNewFormatting: function (preSnapshot) {
      if (!this._visualEl) return null;

      // Diff the post-action set against the pre-action snapshot. A newly
      // inserted wrapper is the one that wasn't there before.
      if (preSnapshot) {
        var post = this._visualEl.querySelectorAll(this._formattingSelector);
        for (var i = 0; i < post.length; i++) {
          if (!preSnapshot.has(post[i])) return post[i];
        }
      }

      // Fallback: standard cursor-based lookup. Used when no snapshot was
      // captured (e.g., the toolbar action was a no-op or we're being
      // called from a non-toolbar path).
      var sel = window.getSelection();
      if (sel.rangeCount > 0) {
        var byCursor = this._activeFormattingForCursor(sel);
        if (byCursor) return byCursor;
      }
      return null;
    },

    // After a contenteditable input event in hybrid mode, scan the text node
    // around the cursor for a completed markdown-delimiter pattern that ends
    // exactly at the cursor (e.g. the user just typed the closing `**` of
    // `**hello**`). If one matches, splice the text node and replace the
    // matched run with the corresponding wrapper element, then decorate
    // it inline so the existing chain logic takes over.
    _maybeAutoFormat: function () {
      var sel = window.getSelection();
      if (!sel.rangeCount) return;
      var range = sel.getRangeAt(0);
      if (!range.collapsed) return;
      var node = range.startContainer;
      if (node.nodeType !== Node.TEXT_NODE) return;
      var parent = node.parentNode;
      if (!parent) return;

      // Skip auto-format when the cursor sits INSIDE a currently-decorated
      // wrapper — auto-format inside an active wrapper would fight with
      // the normalize/unwrap pipeline. Cursor in adjacent text past a
      // wrapper is fine (and necessary so a second `**word**` after the
      // first one still triggers).
      if (this._decoratedAncestors && this._decoratedAncestors.length > 0) {
        for (var di = 0; di < this._decoratedAncestors.length; di++) {
          if (this._decoratedAncestors[di].contains(node)) return;
        }
      }

      // Chrome sometimes inserts a sibling text node mid-sequence rather
      // than extending the existing one — so the "before-cursor" text we
      // need to scan can span more than one text node. Track the cursor's
      // offset within the running combined text, and accumulate text from
      // every text sibling in the contiguous run up to the cursor's node
      // (an element sibling resets the run since auto-format only operates
      // on contiguous text).
      var offset = range.startOffset;
      var before = "";
      var runNodes = [];
      var cursorGlobalOffset = -1;
      var c = parent.firstChild;
      while (c) {
        if (c === node) {
          cursorGlobalOffset = before.length + offset;
          before += node.textContent.slice(0, offset);
          runNodes.push(node);
          break;
        }
        if (c.nodeType === Node.TEXT_NODE) {
          before += c.textContent;
          runNodes.push(c);
        } else {
          before = "";
          runNodes = [];
        }
        c = c.nextSibling;
      }
      if (cursorGlobalOffset < 0) return;

      var patterns = this._autoFormatPatterns;
      for (var i = 0; i < patterns.length; i++) {
        var m = before.match(patterns[i].regexAt);
        if (m) {
          // Defer when an outer delimiter is still open — typing
          // `~~**word**` shouldn't auto-format the inner `**word**` while
          // the outer `~~` hasn't closed yet. Once the closing `~~` lands
          // the outer pattern fires and we recursively wrap the inner.
          var beforeMatch = before.slice(0, before.length - m[0].length);
          if (this._hasUnclosedOuterDelim(beforeMatch, patterns[i])) {
            return;
          }
          this._applyAutoFormat(
            runNodes,
            node,
            offset,
            cursorGlobalOffset,
            m,
            patterns[i],
          );
          return;
        }
      }
    },

    // Order: 3-char first (`***...***`), then 2-char delimiters, then
    // 1-char. Italic uses lookbehind/lookahead to avoid matching either
    // half of `**bold**`. `[^DELIM\n]+?` keeps each pattern self-contained.
    // `regexAt` is anchored at end-of-string for the cursor-end check;
    // `regexAny` is the same pattern without `$` so the recursive inner
    // builder can find it anywhere in the matched content.
    _autoFormatPatterns: [
      {
        regexAt: /\*\*\*([^*\n]+?)\*\*\*$/,
        regexAny: /\*\*\*([^*\n]+?)\*\*\*/,
        type: "boldItalic",
      },
      {
        // Lookbehind/lookahead so `**...**` doesn't match the inner part
        // of `***...***` (triple → bold-italic) before the user finishes
        // the closing pair. `(?<!\*)` keeps the opening `**` from being
        // adjacent to another `*`; `(?!\*)` keeps the closing `**` from
        // being followed by one.
        regexAt: /(?<!\*)\*\*([^*\n]+?)\*\*(?!\*)$/,
        regexAny: /(?<!\*)\*\*([^*\n]+?)\*\*(?!\*)/,
        tag: "strong",
        delim: "**",
      },
      {
        regexAt: /~~([^~\n]+?)~~$/,
        regexAny: /~~([^~\n]+?)~~/,
        tag: "del",
        delim: "~~",
      },
      {
        regexAt: /\|\|([^|\n]+?)\|\|$/,
        regexAny: /\|\|([^|\n]+?)\|\|/,
        tag: "span",
        delim: "||",
        isSpoiler: true,
      },
      {
        regexAt: /(?<!\*)\*([^*\n]+?)\*$/,
        regexAny: /(?<!\*)\*([^*\n]+?)\*(?!\*)/,
        tag: "em",
        delim: "*",
      },
      {
        regexAt: /`([^`\n]+?)`$/,
        regexAny: /`([^`\n]+?)`/,
        tag: "code",
        delim: "`",
      },
      {
        // Link: `[text](url)`. Asymmetric — opening marker is just `[`
        // (1 char), closing marker is `](url)` (variable length).
        // `delim` is left unset so the symmetric-delim code paths skip
        // this pattern; the dedicated link branches in
        // `_scanInlineMatches`, `_buildSourceFragment`, and
        // `_buildFormattedFragment` populate `<a href="...">` instead.
        //
        // The text part rejects `[` so the match starts at the LAST `[`
        // before its `]`. With `[` allowed, typing `[[` (a wiki link in
        // the making) earlier in a row that already held `[text](url)`
        // matched from the typed bracket through the existing link's
        // `](url)` — one bogus link swallowing the row, whose `<a>`
        // wrapper then put the caret in link context and suppressed the
        // `[[` suggestion popup on top of it. The cost is balanced
        // brackets inside link text (`[a [b] c](url)`) rendering as
        // source until the cursor leaves — the same trade every other
        // inline pattern makes.
        regexAt: /\[([^\[\]\n]+?)\]\(([^)\s\n]+)\)$/,
        regexAny: /\[([^\[\]\n]+?)\]\(([^)\s\n]+)\)/,
        type: "link",
      },
    ],

    _hasUnclosedOuterDelim: function (textBeforeMatch, currentPattern) {
      // Single `*` is intentionally skipped — italic and bold share a
      // character so simple counting is ambiguous across mixed runs.
      var checks = [
        { delim: "**", regex: /\*\*/g },
        { delim: "~~", regex: /~~/g },
        { delim: "||", regex: /\|\|/g },
        { delim: "`", regex: /`/g },
      ];
      for (var i = 0; i < checks.length; i++) {
        if (checks[i].delim === currentPattern.delim) continue;
        var matches = textBeforeMatch.match(checks[i].regex);
        if (matches && matches.length % 2 === 1) return true;
      }
      return false;
    },

    _createAutoFormatWrapper: function (pattern) {
      if (pattern.type === "boldItalic") {
        var emEl = document.createElement("em");
        var strongEl = document.createElement("strong");
        strongEl.appendChild(emEl);
        return strongEl;
      }
      if (pattern.isSpoiler) {
        var span = document.createElement("span");
        span.classList.add("leaf-spoiler");
        return span;
      }
      return document.createElement(pattern.tag);
    },

    // Plain text → nodes, turning literal "\n" into real <br> elements.
    // Rendered blocks collapse newline characters to a single space, so
    // leaving them as text silently destroyed soft line breaks whenever
    // a block round-tripped through source mode.
    _textWithLineBreaks: function (text) {
      if (text.indexOf("\n") === -1) {
        return this._textWithDecorations(text);
      }
      var out = [];
      var parts = text.split("\n");
      for (var i = 0; i < parts.length; i++) {
        if (i > 0) out.push(document.createElement("br"));
        if (parts[i]) out = out.concat(this._textWithDecorations(parts[i]));
      }
      return out;
    },

    // Both inline decorations, applied in turn. Wiki links first: their
    // brackets can contain a `#`, and running the hashtag pass first would
    // tokenise the heading part of `[[Note#Section]]` as a tag and leave the
    // link unmatched.
    _textWithDecorations: function (text) {
      var nodes = this._textWithWikiLinks(text);
      var out = [];

      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].nodeType === Node.TEXT_NODE) {
          out = out.concat(this._textWithHashtags(nodes[i].textContent));
        } else {
          out.push(nodes[i]);
        }
      }

      return out;
    },

    // `#tag` → a decoration span, so hashtags read as tags rather than
    // prose in the rendered (non-source) form of a hybrid block. Mirrors
    // what the server does on first render; both are gated on the host
    // having configured a `#` suggestion trigger, so an editor that uses
    // `#` for something else is untouched.
    //
    // Safe by construction: `convertNode` serializes an unknown span as
    // its own text, and `_serializeBlockInline` recurses into it without
    // wrapping — so the source stays `#tag` and offsets stay correct.
    _textWithHashtags: function (text) {
      if (!this._hashtags || !text || text.indexOf("#") === -1) {
        return text ? [document.createTextNode(text)] : [];
      }

      var re = /(^|[\s(\[])#(\p{L}[\p{L}\p{N}_-]*)/gu;
      var out = [];
      var last = 0;
      var m;
      while ((m = re.exec(text)) !== null) {
        var start = m.index + m[1].length;
        if (start > last) out.push(document.createTextNode(text.slice(last, start)));
        var span = document.createElement("span");
        span.className = "leaf-hashtag";
        span.textContent = "#" + m[2];
        out.push(span);
        last = start + 1 + m[2].length;
      }
      if (last < text.length) out.push(document.createTextNode(text.slice(last)));
      return out.length ? out : [document.createTextNode(text)];
    },

    // `[[Target]]` → a decoration span, so a wiki link reads as a link rather
    // than literal brackets in the rendered form of a hybrid block. Mirrors
    // `decorate_wiki_links/1` on the server; both are gated on the host having
    // opted in, so a document using `[[…]]` for something else is untouched.
    //
    // A span, never an `<a>`: `convertNode` serializes an unknown span as its
    // own text, so the source stays `[[Target]]`. An `<a>` would serialize as
    // `[label](href)` and quietly rewrite every wiki link into an ordinary
    // markdown link.
    _WIKI_LINK_RE: /\[\[([^\[\]|#]+?)(?:#([^\[\]|]+?))?(?:\|([^\[\]]+?))?\]\]/g,

    _textWithWikiLinks: function (text) {
      if (!this._wikiLinks || !text || text.indexOf("[[") === -1) {
        return text ? [document.createTextNode(text)] : [];
      }

      var re = new RegExp(this._WIKI_LINK_RE.source, "g");
      var out = [];
      var last = 0;
      var m;

      while ((m = re.exec(text)) !== null) {
        if (m.index > last) out.push(document.createTextNode(text.slice(last, m.index)));

        var target = (m[1] || "").trim();
        var heading = (m[2] || "").trim();
        var alias = (m[3] || "").trim();

        var span = document.createElement("span");
        span.className = "leaf-wikilink";
        // A freshly typed link needs resolving too — mount and server pushes
        // already resolve, but a span minted right here mid-edit is exactly
        // the one they both miss.
        this._scheduleWikiLinkResolve();
        span.setAttribute("data-leaf-wikilink", target);
        if (heading) span.setAttribute("data-leaf-wikilink-heading", heading);
        span.setAttribute("data-leaf-wikilink-raw", m[0]);
        span.textContent = alias || (heading ? target + " \u203a " + heading : target);

        out.push(span);
        last = m.index + m[0].length;
      }

      if (last < text.length) out.push(document.createTextNode(text.slice(last)));
      return out.length ? out : [document.createTextNode(text)];
    },

    // ---- wiki-link resolution ------------------------------------------

    // Debounced: decoration runs per text node mid-render, and one resolve
    // after the dust settles covers every span the pass created. Repeat
    // resolves are already cheap — only unresolved targets are asked about,
    // and a stale answer is dropped by seq.
    _scheduleWikiLinkResolve: function () {
      if (!this._wikiLinks || !this._wikiLinksResolve) return;
      var self = this;
      clearTimeout(this._wikiLinkResolveTimer);
      this._wikiLinkResolveTimer = setTimeout(function () {
        self._resolveWikiLinks();
      }, 150);
    },
    //
    // Only the host knows which notes exist, so the client collects the targets
    // it has no answer for and asks. Answers are cached for the life of the
    // editor: the same target usually appears many times in a note, and a
    // document full of links should not produce a request per occurrence.

    _resolveWikiLinks: function () {
      if (!this._wikiLinks || !this._wikiLinksResolve || !this._visualEl) return;

      this._wikiLinkTargets = this._wikiLinkTargets || {};

      var spans = this._visualEl.querySelectorAll("[data-leaf-wikilink]");
      var pending = [];
      var seen = {};

      for (var i = 0; i < spans.length; i++) {
        var target = spans[i].getAttribute("data-leaf-wikilink");
        if (!target) continue;

        if (Object.prototype.hasOwnProperty.call(this._wikiLinkTargets, target)) {
          this._applyWikiLinkTarget(spans[i], this._wikiLinkTargets[target]);
        } else if (!seen[target]) {
          seen[target] = true;
          pending.push(target);
        }
      }

      if (!pending.length) return;

      this._wikiLinkSeq = (this._wikiLinkSeq || 0) + 1;
      this.pushEventTo(this.el, "resolve_links", {
        editor_id: this._editorId,
        targets: pending,
        seq: this._wikiLinkSeq
      });
    },

    _applyWikiLinkTarget: function (span, info) {
      if (!span || !info) return;

      span.setAttribute("data-leaf-wikilink-exists", info.exists ? "true" : "false");
      if (info.href) span.setAttribute("data-leaf-wikilink-href", info.href);

      // Only what the host supplied. Leaf ships no wording for this: a label
      // like "Not found" belongs to the host, in the host's language, about the
      // host's own idea of what a target is. Falls back to the href, which is
      // data rather than prose.
      var title = info.title || (info.exists ? info.href : null);
      if (title) {
        span.setAttribute("title", title);
      } else {
        span.removeAttribute("title");
      }
    },

    _onWikiLinkTargets: function (payload) {
      // Drop an answer a later edit already superseded.
      if (payload.seq && this._wikiLinkSeq && payload.seq < this._wikiLinkSeq) return;

      this._wikiLinkTargets = this._wikiLinkTargets || {};

      var targets = payload.targets || {};
      for (var key in targets) {
        if (Object.prototype.hasOwnProperty.call(targets, key)) {
          this._wikiLinkTargets[key] = targets[key];
        }
      }

      if (!this._visualEl) return;

      var spans = this._visualEl.querySelectorAll("[data-leaf-wikilink]");
      for (var i = 0; i < spans.length; i++) {
        var t = spans[i].getAttribute("data-leaf-wikilink");
        if (t && this._wikiLinkTargets[t]) {
          this._applyWikiLinkTarget(spans[i], this._wikiLinkTargets[t]);
        }
      }
    },

    // Leaf reports the click and does nothing else. Where a target lives is the
    // host's question — it may want a modal, a new tab, or a "create this note"
    // flow for one that does not exist yet.
    _onWikiLinkMouseDown: function (e) {
      if (!this._wikiLinks) return;

      var span = e.target && e.target.closest
        ? e.target.closest("[data-leaf-wikilink]")
        : null;
      if (!span || !this._visualEl.contains(span)) {
        this._wikiLinkPress = null;
        return;
      }

      // Inside the OPEN source block the token is being edited: the press
      // must seat the caret (revealing the raw form), never follow the link.
      if (span.closest && span.closest("[data-leaf-source]")) {
        this._wikiLinkPress = null;
        return;
      }

      // Captured before the caret moves. In hybrid mode, when the press is
      // allowed to seat the caret (the :modifier gesture), the
      // selectionchange that follows swaps the block to its source form and
      // takes the span with it — so by the time click fires, the span the
      // click handler finds is detached and the follow silently dies. The
      // click handler falls back to this capture, which makes the follow
      // survive the swap in every follow mode.
      this._wikiLinkPress = {
        target: span.getAttribute("data-leaf-wikilink"),
        heading: span.getAttribute("data-leaf-wikilink-heading"),
        href: span.getAttribute("data-leaf-wikilink-href")
      };

      if (e.detail > 1 || e.shiftKey || e.altKey) return;
      if (!(this._readonly || this._wikiLinksFollow === "click")) return;

      e.preventDefault();
    },

    _onWikiLinkClick: function (e) {
      if (!this._wikiLinks) return;

      var span = e.target && e.target.closest
        ? e.target.closest("[data-leaf-wikilink]")
        : null;
      if (span && !this._visualEl.contains(span)) span = null;
      // Same source-block exemption as the press: an edited token never
      // follows on click.
      if (span && span.closest && span.closest("[data-leaf-source]")) {
        this._wikiLinkPress = null;
        return;
      }

      // One press, one click: the capture never outlives its gesture.
      var press = this._wikiLinkPress;
      this._wikiLinkPress = null;

      if (!span && !press) return;

      // Which gesture follows a link is the host's call, declared as
      // `follow: :click | :modifier`.
      //
      // `:click` is the default — Obsidian's live-preview gesture. A bare
      // click on the rendered link is an ACTION, reported to the host; it
      // never places the caret (the mousedown guard below saw to that), so
      // the row's source stays hidden. Editing is entered the other ways in:
      // arrow keys, shift+click, double-click — each of which reveals the raw
      // `[[target]]`. A modifier click follows too, and the modifier is
      // reported so a host can mean something extra by it (a new tab, say).
      //
      // `:modifier` keeps a bare click for the caret and asks Ctrl/Cmd to
      // follow. A read-only surface is treated as `:click` regardless; there
      // is no caret competing for the click, and requiring Ctrl to follow a
      // link in a viewer would be strange.
      var withModifier = e.ctrlKey || e.metaKey;
      var plainClick = !withModifier && !e.altKey && !e.shiftKey;
      var clickMode = this._readonly || this._wikiLinksFollow === "click";
      var follows = clickMode ? plainClick || withModifier : withModifier;

      if (!follows) return;
      if (e.detail > 1) return; // a double-click means editing, not following

      e.preventDefault();
      var link = span
        ? {
            target: span.getAttribute("data-leaf-wikilink"),
            heading: span.getAttribute("data-leaf-wikilink-heading"),
            href: span.getAttribute("data-leaf-wikilink-href")
          }
        : press;
      this.pushEventTo(this.el, "link_clicked", {
        editor_id: this._editorId,
        target: link.target,
        heading: link.heading,
        href: link.href,
        modifier: withModifier
      });
    },

    _buildFormattedFragment: function (text) {
      // Returns an array of DOM nodes representing `text` with every
      // matched delimiter pair wrapped in its corresponding element.
      // Recursive — supports arbitrary depth (e.g., `~~**`code`**~~`).
      if (!text) return [];

      var earliest = null;
      var earliestPattern = null;
      var patterns = this._autoFormatPatterns;
      for (var i = 0; i < patterns.length; i++) {
        var re = new RegExp(
          patterns[i].regexAny.source,
          patterns[i].regexAny.flags,
        );
        var m = re.exec(text);
        if (m && (earliest === null || m.index < earliest.index)) {
          earliest = m;
          earliestPattern = patterns[i];
        }
      }
      if (!earliest) {
        return this._textWithLineBreaks(text);
      }

      var beforeText = text.slice(0, earliest.index);
      var afterText = text.slice(earliest.index + earliest[0].length);
      var innerText = earliest[1];

      var wrapper;
      if (earliestPattern.type === "link") {
        wrapper = document.createElement("a");
        wrapper.setAttribute("href", earliest[2] || "");
      } else {
        wrapper = this._createAutoFormatWrapper(earliestPattern);
      }
      var innerHost =
        earliestPattern.type === "boldItalic" ? wrapper.firstChild : wrapper;
      var innerNodes = this._buildFormattedFragment(innerText);
      for (var j = 0; j < innerNodes.length; j++) {
        innerHost.appendChild(innerNodes[j]);
      }

      var result = [];
      if (beforeText) {
        result = result.concat(this._buildFormattedFragment(beforeText));
      }
      result.push(wrapper);
      if (afterText) {
        result = result.concat(this._buildFormattedFragment(afterText));
      }
      return result;
    },

    // Convert a plain `<p>` to the matching `<h1>`–`<h6>` when its text
    // starts with 1–6 `#` chars followed by a space. The `# ` prefix is
    // consumed so the heading begins empty (or with whatever the user
    // typed after the space, if it was pasted as a chunk).
    _maybeAutoFormatHeading: function () {
      var sel = window.getSelection();
      if (!sel.rangeCount) return;
      var range = sel.getRangeAt(0);
      if (!range.collapsed) return;

      var block = this._getCurrentBlock();
      if (!block || !block.tagName) return;
      if (block.tagName.toLowerCase() !== "p") return;

      var text = block.textContent;
      // Trigger separator allows either a regular space or ` `
      // — Chrome auto-converts a trailing space in a `<p>` to `&nbsp;`,
      // which used to slip past a strict ` ` match.
      var m = text.match(/^(#{1,6})[  ]/);
      if (!m) return;

      var newTag = "h" + m[1].length;
      var prefixLen = m[0].length;

      this._syntaxMutating = true;
      try {
        var heading = document.createElement(newTag);
        while (block.firstChild) {
          heading.appendChild(block.firstChild);
        }
        block.parentNode.replaceChild(heading, block);

        // Strip the `# ` prefix walking text descendants in document
        // order — covers cases where Chrome split the typed chars
        // across `<br>` placeholders or nested wrappers.
        this._trimLeadingTextChars(heading, prefixLen);

        // If the heading ended up empty (typical case: user typed `# `
        // into a fresh paragraph, which had only a placeholder `<br>`),
        // add a `<br>` so Chrome renders the empty heading with height.
        if (
          !heading.firstChild ||
          (heading.childNodes.length === 1 &&
            heading.firstChild.nodeType === Node.TEXT_NODE &&
            heading.firstChild.textContent === "")
        ) {
          heading.innerHTML = "<br>";
        }

        // Place cursor at the start of the heading. The user just typed
        // the trigger space, so they expect their next keystroke to land
        // at the heading's beginning rather than wherever the original
        // `# ` cursor offset would have mapped to.
        var caret = document.createRange();
        var firstText = this._firstTextDescendant(heading);
        if (firstText) {
          caret.setStart(firstText, 0);
        } else {
          caret.setStart(heading, 0);
        }
        caret.collapse(true);
        try {
          sel.removeAllRanges();
          sel.addRange(caret);
        } catch (_e) {
          /* range invalidated mid-frame — skip */
        }
      } finally {
        this._syntaxMutating = false;
      }
      // Reveal the `# ` markers right at conversion. Click-driven flow
      // takes over for subsequent shows/hides.
      this._updateHeadingDecoration();
    },

    // Toolbar HR insert. We build the DOM by hand instead of using
    // `document.execCommand("insertHorizontalRule")` because the latter
    // produces inconsistent structure across browsers (HR sometimes left
    // wrapped inside the original `<p>`, no trailing paragraph created,
    // stray `<br>` siblings) which then fails to round-trip through
    // `htmlToMarkdown` cleanly. This is the same shape `_maybeAutoFormatHr`
    // builds in hybrid mode, which we know serializes correctly.
    _insertHorizontalRule: function () {
      if (!this._visualEl) return;
      var sel = window.getSelection();
      if (!sel.rangeCount) return;

      var block = this._getCurrentBlock();
      if (!block || !block.parentNode) {
        block = this._visualEl.lastElementChild;
        if (!block) {
          block = document.createElement("p");
          block.innerHTML = "<br>";
          this._visualEl.appendChild(block);
        }
      }

      var parent = block.parentNode;
      var hr = document.createElement("hr");
      var trailing = document.createElement("p");
      trailing.innerHTML = "<br>";

      parent.insertBefore(hr, block.nextSibling);
      parent.insertBefore(trailing, hr.nextSibling);

      var caret = document.createRange();
      caret.setStart(trailing, 0);
      caret.collapse(true);
      try {
        sel.removeAllRanges();
        sel.addRange(caret);
      } catch (_e) {
        /* range invalidated mid-frame — skip */
      }
    },

    // `---` (3+ dashes) on its own line auto-formats to a real `<hr>`,
    // mirroring the toolbar's "Insert horizontal rule" button. A fresh
    // `<p>` is created right after the rule and the cursor is moved
    // there so the user can keep typing.
    _maybeAutoFormatHr: function () {
      if (this._syntaxMutating) return;
      var sel = window.getSelection();
      if (!sel.rangeCount) return;
      var range = sel.getRangeAt(0);
      if (!range.collapsed) return;

      var block = this._getCurrentBlock();
      if (!block || !block.tagName) return;
      if (block.tagName.toLowerCase() !== "p") return;
      // Skip the `<p data-leaf-hr-source>` placeholder — that paragraph
      // is in source-edit mode for an existing rule and shouldn't be
      // re-converted while the cursor is inside it.
      if (block.hasAttribute("data-leaf-hr-source")) return;

      var text = block.textContent.replace(/ /g, "");
      if (!/^-{3,}$/.test(text)) return;

      var parent = block.parentNode;
      if (!parent) return;

      this._syntaxMutating = true;
      try {
        var hr = document.createElement("hr");
        var nextP = document.createElement("p");
        nextP.innerHTML = "<br>";
        parent.insertBefore(hr, block);
        parent.insertBefore(nextP, block);
        parent.removeChild(block);

        var caret = document.createRange();
        caret.setStart(nextP, 0);
        caret.collapse(true);
        try {
          sel.removeAllRanges();
          sel.addRange(caret);
        } catch (_e) {
          /* range invalidated mid-frame — skip */
        }
      } finally {
        this._syntaxMutating = false;
      }
    },

    // Typing `- ` / `* ` / `+ ` at the start of an empty (or just-started)
    // paragraph swaps the `<p>` for a `<ul><li>…</li></ul>`; `1. ` / `5. ` /
    // etc. swap to `<ol><li>…</li></ol>`. Mirrors the heading auto-format
    // path: trigger on the space, strip the marker prefix, drop the cursor
    // at the start of the new `<li>`. Consecutive list paragraphs get
    // merged into a single list rather than producing one list per item.
    _maybeAutoFormatList: function () {
      if (this._syntaxMutating) return;
      var sel = window.getSelection();
      if (!sel.rangeCount) return;
      var range = sel.getRangeAt(0);
      if (!range.collapsed) return;

      var block = this._getCurrentBlock();
      if (!block || !block.tagName) return;
      if (block.tagName.toLowerCase() !== "p") return;
      if (block.hasAttribute("data-leaf-hr-source")) return;

      // Chrome auto-converts a typed trailing space inside a `<p>` to NBSP,
      // so normalize before regex-matching the marker.
      var text = block.textContent.replace(/\u00a0/g, " ");
      var um = text.match(/^([-*+]) /);
      var om = !um && text.match(/^(\d+)\. /);
      if (!um && !om) return;

      var listTag = um ? "ul" : "ol";
      var prefixLen = um ? 2 : om[1].length + 2;
      var startNum = om ? parseInt(om[1], 10) : 1;

      var parent = block.parentNode;
      if (!parent) return;

      this._syntaxMutating = true;
      try {
        var li = document.createElement("li");
        while (block.firstChild) li.appendChild(block.firstChild);
        this._trimLeadingTextChars(li, prefixLen);

        // Re-form a task checkbox when the un-prefixed item starts with a
        // `[ ] ` / `[x] ` marker, so re-typing `- ` in front of a broken-out
        // `[ ] foo` rebuilds the real checkbox flat in the list immediately
        // — instead of leaving literal brackets that only become a checkbox
        // on a later source-mode enter/leave cycle (which read as a stray,
        // mis-indented item in the meantime).
        var afFirst = this._firstTextDescendant(li);
        if (afFirst) {
          var afTask = afFirst.textContent
            .replace(/\u00a0/g, " ")
            .match(/^\[([ xX]?)\] /);
          if (afTask) {
            li.classList.add("leaf-task");
            li.setAttribute(
              "data-checked",
              afTask[1] === "x" || afTask[1] === "X" ? "true" : "false"
            );
            afFirst.textContent = afFirst.textContent.slice(afTask[0].length);
            var afBox = document.createElement("span");
            afBox.className = "leaf-task-box";
            afBox.setAttribute("contenteditable", "false");
            li.insertBefore(afBox, li.firstChild);
          }
        }

        if (
          !li.firstChild ||
          (li.childNodes.length === 1 &&
            li.firstChild.nodeType === Node.TEXT_NODE &&
            li.firstChild.textContent === "")
        ) {
          li.innerHTML = "<br>";
        }

        var prev = block.previousElementSibling;
        var list;
        var appending = false;
        if (
          prev &&
          prev.tagName &&
          prev.tagName.toLowerCase() === listTag
        ) {
          list = prev;
          appending = true;
        } else {
          list = document.createElement(listTag);
          if (om && startNum !== 1) {
            list.setAttribute("start", String(startNum));
          }
        }
        list.appendChild(li);
        if (!appending) parent.insertBefore(list, block);
        parent.removeChild(block);

        // The just-removed `<p>` was the active source block; the new
        // `<li>` is rendered (not source-mode), so clear the reference
        // so subsequent input/keydown handlers don't operate on a
        // detached node.
        if (this._sourceBlock === block) {
          this._sourceBlock = null;
          this._lastSourceStateKey = null;
          this._activeMatchKey = null;
        }

        var caret = document.createRange();
        var firstText = this._firstTextDescendant(li);
        if (firstText) {
          caret.setStart(firstText, 0);
        } else {
          caret.setStart(li, 0);
        }
        caret.collapse(true);
        try {
          sel.removeAllRanges();
          sel.addRange(caret);
        } catch (_e) {
          /* range invalidated mid-frame — skip */
        }
      } finally {
        this._syntaxMutating = false;
      }

      // Hybrid mode: go STRAIGHT into source mode on the new item so the
      // user sees a single `<p>- ` → `<li>- ` transition with the caret
      // landing right after the revealed marker. Without this, the item
      // would render as a bare bullet first and `_updateSourceBlock`
      // would re-enter source mode on the next tick — two competing caret
      // placements that drifted the cursor one char right per transform.
      // (The caret was set to the item's start just above, so
      // `_enterSourceMode` traces from the right position.)
      if (this._mode === "hybrid" && li.isConnected) {
        var enteredLi = this._enterSourceMode(li);
        if (enteredLi) {
          this._sourceBlock = enteredLi;
          // The cascade of replaceChild()s during auto-format + source-mode
          // entry can drop the selection (the caret appears to lose focus).
          // Re-focus the editor and force the caret to the END of the new
          // item so the user can start typing immediately.
          try {
            if (
              this._visualEl &&
              document.activeElement !== this._visualEl
            ) {
              this._visualEl.focus();
            }
            var afEndRange = document.createRange();
            afEndRange.selectNodeContents(enteredLi);
            afEndRange.collapse(false);
            var afSel = window.getSelection();
            afSel.removeAllRanges();
            afSel.addRange(afEndRange);
          } catch (_afErr) {
            /* selection invalidated mid-frame — skip */
          }
        }
      }
    },

    // Typing `> ` at the start of a paragraph swaps the `<p>` for a
    // `<blockquote><p>…</p></blockquote>`. Mirrors `_maybeAutoFormatList`
    // — strip the marker prefix, drop the cursor at the start of the
    // new inner `<p>`. Consecutive blockquote paragraphs merge into a
    // single `<blockquote>` instead of producing one wrapper per line.
    _maybeAutoFormatBlockquote: function () {
      if (this._syntaxMutating) return;
      var sel = window.getSelection();
      if (!sel.rangeCount) return;
      var range = sel.getRangeAt(0);
      if (!range.collapsed) return;

      var block = this._getCurrentBlock();
      if (!block || !block.tagName) return;
      if (block.tagName.toLowerCase() !== "p") return;
      if (block.hasAttribute("data-leaf-hr-source")) return;
      // Don't recurse: skip if the `<p>` is already inside a blockquote.
      if (
        block.parentNode &&
        block.parentNode.tagName &&
        block.parentNode.tagName.toLowerCase() === "blockquote"
      ) {
        return;
      }

      var text = block.textContent.replace(/\u00a0/g, " ");
      var bm = text.match(/^> /);
      if (!bm) return;

      var parent = block.parentNode;
      if (!parent) return;

      this._syntaxMutating = true;
      try {
        var inner = document.createElement("p");
        while (block.firstChild) inner.appendChild(block.firstChild);
        this._trimLeadingTextChars(inner, 2);
        if (
          !inner.firstChild ||
          (inner.childNodes.length === 1 &&
            inner.firstChild.nodeType === Node.TEXT_NODE &&
            inner.firstChild.textContent === "")
        ) {
          inner.innerHTML = "<br>";
        }

        // Unlike lists, blockquotes are multi-line by default (Enter
        // inside an existing `<blockquote>` adds a new `<p>` to the
        // same quote), so we always create a fresh `<blockquote>` for
        // a typed `> ` — merging into the previous quote would undo
        // an explicit "Enter-on-empty-line exits" the user just did.
        var quote = document.createElement("blockquote");
        quote.appendChild(inner);
        parent.insertBefore(quote, block);
        parent.removeChild(block);

        if (this._sourceBlock === block) {
          this._sourceBlock = null;
          this._lastSourceStateKey = null;
          this._activeMatchKey = null;
        }

        var caret = document.createRange();
        var firstText = this._firstTextDescendant(inner);
        if (firstText) {
          caret.setStart(firstText, 0);
        } else {
          caret.setStart(inner, 0);
        }
        caret.collapse(true);
        try {
          sel.removeAllRanges();
          sel.addRange(caret);
        } catch (_e) {
          /* range invalidated mid-frame — skip */
        }
      } finally {
        this._syntaxMutating = false;
      }
    },

    // Detect an Arrow Down/Up keystroke about to cross an `<hr>`
    // boundary. If the next/previous block sibling is an `<hr>`, swap
    // it to source-edit mode and place the cursor inside; the
    // selectionchange listener swaps it back when the cursor leaves.
    _maybeArrowIntoHr: function (key) {
      var sel = window.getSelection();
      if (!sel.rangeCount) return false;
      var range = sel.getRangeAt(0);
      if (!range.collapsed) return false;
      var block = this._getCurrentBlock();
      if (!block || !block.parentNode) return false;

      if (key === "ArrowDown") {
        var next = block.nextElementSibling;
        if (!next || !next.tagName || next.tagName.toLowerCase() !== "hr") {
          return false;
        }
        if (!this._isCursorOnLastLineOfBlock(range, block)) return false;
        this._showHrSource(next);
        return true;
      }
      if (key === "ArrowUp") {
        var prev = block.previousElementSibling;
        if (!prev || !prev.tagName || prev.tagName.toLowerCase() !== "hr") {
          return false;
        }
        if (!this._isCursorOnFirstLineOfBlock(range, block)) return false;
        this._showHrSource(prev);
        return true;
      }
      return false;
    },

    // Rect-based "cursor is on the first/last visual line of this block"
    // checks. We can't use `Range.compareBoundaryPoints` against a
    // `selectNodeContents`-collapsed range because (block, 0) and (firstChild,
    // 0) aren't considered equal even though they're at the same visual spot,
    // and trailing `<br>` fillers throw the end-side comparison off too.
    // Comparing client rects sidesteps both problems and naturally handles
    // multi-line wrapping.
    _isCursorOnFirstLineOfBlock: function (range, block) {
      // Structural fast path: cursor at character-offset 0 of the
      // block is always on the first line. Catches the `<p><br></p>`
      // filler case where the collapsed range returns a zero rect.
      if (this._cursorTextOffsetInBlock(range, block) === 0) return true;
      // Single-line block fast path: if the block is no taller than
      // roughly one line, every cursor position is on its only line.
      // Saves us from rect-math fragility in the most common case
      // (a heading or short paragraph next to an HR).
      var lineHeight =
        parseFloat(getComputedStyle(block).lineHeight) || 20;
      if (block.getBoundingClientRect().height < lineHeight * 1.6) {
        return true;
      }
      var caretRect = this._caretRect(range);
      if (!caretRect) return false;
      var blockRect = block.getBoundingClientRect();
      return caretRect.top - blockRect.top < lineHeight * 0.6;
    },

    _isCursorOnLastLineOfBlock: function (range, block) {
      // Symmetric to `_isCursorOnFirstLineOfBlock`: cursor-at-end and
      // single-line-block fast paths first, rect math as the multi-
      // line fallback.
      var totalText = (block.textContent || "").length;
      if (this._cursorTextOffsetInBlock(range, block) === totalText) {
        return true;
      }
      var lineHeight =
        parseFloat(getComputedStyle(block).lineHeight) || 20;
      if (block.getBoundingClientRect().height < lineHeight * 1.6) {
        return true;
      }
      var caretRect = this._caretRect(range);
      if (!caretRect) return false;
      var blockRect = block.getBoundingClientRect();
      return blockRect.bottom - caretRect.bottom < lineHeight * 0.6;
    },

    // Count text characters between the start of `block` and the
    // collapsed `range`'s start position. Element-offset cursors map
    // to the position right before the indexed child. Returns -1 if
    // the range start isn't inside `block`.
    _cursorTextOffsetInBlock: function (range, block) {
      if (!block.contains(range.startContainer) && range.startContainer !== block) {
        return -1;
      }
      var offset = 0;
      var target = range.startContainer;
      var targetOffset = range.startOffset;
      var found = false;
      function walk(node) {
        if (found) return;
        if (node.nodeType === Node.TEXT_NODE) {
          if (node === target) {
            offset += targetOffset;
            found = true;
          } else {
            offset += node.textContent.length;
          }
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        var children = node.childNodes;
        for (var i = 0; i < children.length; i++) {
          if (node === target && targetOffset === i) {
            found = true;
            return;
          }
          walk(children[i]);
          if (found) return;
        }
        if (node === target && targetOffset === children.length) {
          found = true;
        }
      }
      walk(block);
      return found ? offset : -1;
    },

    // A collapsed range often returns a zero-sized rect — especially in
    // `<p><br></p>` or right next to a `<br>`. Fall back to inserting a
    // temporary zero-width-space span, measuring it, and removing it. The
    // `_syntaxMutating` flag is asserted around the mutation so the input
    // pipeline can't re-enter while we're probing.
    _caretRect: function (range) {
      var rect = range.getBoundingClientRect();
      if (rect && (rect.top || rect.bottom || rect.left || rect.right)) {
        return rect;
      }
      var marker = document.createElement("span");
      marker.appendChild(document.createTextNode("​"));
      var probe = range.cloneRange();
      var prevMutating = this._syntaxMutating;
      this._syntaxMutating = true;
      try {
        probe.insertNode(marker);
        rect = marker.getBoundingClientRect();
      } catch (_e) {
        rect = null;
      } finally {
        if (marker.parentNode) marker.parentNode.removeChild(marker);
        var sel = window.getSelection();
        try {
          sel.removeAllRanges();
          sel.addRange(range);
        } catch (_e) {
          /* selection invalidated — best effort only */
        }
        this._syntaxMutating = prevMutating;
      }
      return rect;
    },

    // Mobile soft-keyboard helper: scroll the editor (and the page,
    // if needed) so the caret sits above the on-screen keyboard. Uses
    // the cheap `range.getBoundingClientRect()` first; falls back to
    // the temp-marker `_caretRect` if the range itself reports zero.
    // Called from the `visualViewport.resize` handler in `mounted()`.
    _scrollCaretIntoView: function () {
      if (!this._visualEl) return;
      var sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;
      var range = sel.getRangeAt(0);
      if (!this._visualEl.contains(range.startContainer)) return;
      var rect = this._caretRect(range);
      if (!rect) return;
      var vv = window.visualViewport;
      var viewBottom = vv ? vv.height + vv.offsetTop : window.innerHeight;
      var viewTop = vv ? vv.offsetTop : 0;
      // Margin so the caret isn't flush against the keyboard / top.
      var margin = 32;
      if (rect.bottom > viewBottom - margin) {
        // Caret hidden by (or near) the keyboard — scroll up.
        var delta = rect.bottom - (viewBottom - margin);
        // Scroll the editor's own scrollable parent first (the body
        // wrapper has `overflow: auto`); if that doesn't cover the
        // delta, scroll the page too.
        this._visualEl.scrollBy({ top: delta, behavior: "smooth" });
        window.scrollBy({ top: delta, behavior: "smooth" });
      } else if (rect.top < viewTop + margin) {
        var deltaUp = viewTop + margin - rect.top;
        this._visualEl.scrollBy({ top: -deltaUp, behavior: "smooth" });
        window.scrollBy({ top: -deltaUp, behavior: "smooth" });
      }
    },

    // Returns the `<hr>` at the mousedown coordinates if any. Checks
    // `e.target` first, then falls back to the topmost few elements via
    // `elementsFromPoint` so a near-miss on the thin rule line still
    // counts. Without this, a tiny HR is essentially unclickable.
    _hrAtClick: function (e) {
      if (e.target && e.target.tagName && e.target.tagName.toLowerCase() === "hr") {
        return e.target;
      }
      if (
        typeof document.elementsFromPoint === "function" &&
        typeof e.clientX === "number" &&
        typeof e.clientY === "number"
      ) {
        var stack = document.elementsFromPoint(e.clientX, e.clientY);
        for (var i = 0; i < stack.length && i < 6; i++) {
          if (stack[i].tagName && stack[i].tagName.toLowerCase() === "hr") {
            return stack[i];
          }
        }
      }
      return null;
    },

    // Replace a rendered `<hr>` with a `<p data-leaf-hr-source>---</p>`
    // and put the cursor inside, so the user can edit (or delete) the
    // rule. The selectionchange listener calls `_maybeRestoreHrFromSource`
    // when the cursor leaves to swap the placeholder back to an `<hr>`.
    _showHrSource: function (hr) {
      if (this._syntaxMutating) return;
      this._syntaxMutating = true;
      try {
        var p = document.createElement("p");
        p.setAttribute("data-leaf-hr-source", "");
        p.textContent = "---";
        hr.parentNode.replaceChild(p, hr);

        var sel = window.getSelection();
        var caret = document.createRange();
        if (p.firstChild && p.firstChild.nodeType === Node.TEXT_NODE) {
          caret.setStart(p.firstChild, p.firstChild.textContent.length);
        } else {
          caret.setStart(p, 0);
        }
        caret.collapse(true);
        try {
          sel.removeAllRanges();
          sel.addRange(caret);
        } catch (_e) {
          /* range invalidated mid-frame — skip */
        }
      } finally {
        this._syntaxMutating = false;
      }
    },

    _maybeRestoreHrFromSource: function () {
      if (!this._visualEl) return;
      var sources = this._visualEl.querySelectorAll(
        "p[data-leaf-hr-source]",
      );
      if (!sources.length) return;
      var sel = window.getSelection();
      var anchor = sel.rangeCount > 0 ? sel.anchorNode : null;
      for (var i = 0; i < sources.length; i++) {
        var p = sources[i];
        // Cursor still inside this source paragraph? Leave it alone.
        if (anchor && p.contains(anchor)) continue;
        // Otherwise either swap back to a real `<hr>` (still valid HR
        // syntax) or just drop the marker attribute (user edited the
        // dashes into something else).
        var text = p.textContent.replace(/ /g, "");
        this._syntaxMutating = true;
        try {
          if (/^-{3,}$/.test(text)) {
            var hr = document.createElement("hr");
            p.parentNode.replaceChild(hr, p);
          } else {
            p.removeAttribute("data-leaf-hr-source");
          }
        } finally {
          this._syntaxMutating = false;
        }
      }
    },

    _trimLeadingTextChars: function (root, count) {
      while (count > 0) {
        var first = this._firstTextDescendant(root);
        if (!first) return;
        var len = first.textContent.length;
        if (len === 0) {
          if (first.parentNode) first.parentNode.removeChild(first);
          continue;
        }
        if (len <= count) {
          count -= len;
          if (first.parentNode) first.parentNode.removeChild(first);
        } else {
          first.textContent = first.textContent.slice(count);
          return;
        }
      }
    },

    // Remove whitespace-only text nodes that sit between the block
    // children of structural containers (the editor root, `<ul>`/`<ol>`,
    // table groups, blockquotes). Server-rendered markdown→HTML is
    // pretty-printed, so these stray nodes appear between every `<li>`,
    // `<tr>`, etc. In a contenteditable they're a cursor trap: a click
    // near a list item can land the caret in a text node that's a direct
    // child of the `<ul>`, so `_getCurrentBlock` resolves to the `<ul>`
    // (not an `<li>`) and Enter/Backspace can't act on the item — the
    // bullet looks permanently stuck. These nodes carry no meaning inside
    // these containers (only element children are valid there), so
    // dropping them is safe and leaves the markdown unchanged.
    // Everything that has to be done to html the host rendered, in one place.
    //
    // It used to be a list of calls repeated at each of the three points where
    // rendered html arrives, and one of them was missing a step. That is not a
    // small bug: a session that loaded the page counted the newline after a
    // <br /> while a session that received the same document had it stripped,
    // so the two disagreed about how many characters the text had and every
    // caret and every edit after that point was one place out.
    _normalizeRenderedHtml: function (el) {
      if (!el) return;

      this._stripInterBlockWhitespace(el);
      this._stripBreakWhitespace(el);
      this._unwrapLooseListItems(el);
    },

    // MDEx pretty-prints a newline after every <br />. It is punctuation of
    // the markup, not of the text: left in place it reads as a second line
    // break here while the editor that sent it has only one, and the two stop
    // agreeing about where every later character is.
    _stripBreakWhitespace: function (root) {
      if (!root || !root.querySelectorAll) return;

      var breaks = root.querySelectorAll("br");

      for (var i = 0; i < breaks.length; i++) {
        var next = breaks[i].nextSibling;
        if (!next || next.nodeType !== 3) continue;

        next.nodeValue = next.nodeValue.replace(/^\n/, "");
        if (next.nodeValue === "") next.parentNode.removeChild(next);
      }
    },

    _stripInterBlockWhitespace: function (root) {
      if (!root) return;
      var containers = [root];
      var nested = root.querySelectorAll(
        "ul, ol, menu, table, thead, tbody, tfoot, tr, blockquote"
      );
      for (var i = 0; i < nested.length; i++) containers.push(nested[i]);
      for (var c = 0; c < containers.length; c++) {
        var parent = containers[c];
        var child = parent.firstChild;
        while (child) {
          var next = child.nextSibling;
          if (
            child.nodeType === Node.TEXT_NODE &&
            !/\S/.test(child.textContent)
          ) {
            parent.removeChild(child);
          }
          child = next;
        }
      }
    },

    // Drop the source-block tracking when `block` is being removed from the
    // document.
    //
    // `_sourceBlock` is a live node reference. Detaching the node it points at
    // without clearing it leaves the editor believing it is still editing a
    // block that is no longer in the document — and the stale
    // `_lastSourceStateKey` / `_activeMatchKey` alongside it make the next
    // refresh of a genuinely different block compare against the dead one's
    // state. Every other path that removes a source block does this; the
    // Backspace merge did not, which is why list handling went wrong only
    // AFTER deleting an empty row.
    _forgetSourceBlock: function (block) {
      if (!block || this._sourceBlock !== block) return;

      this._sourceBlock = null;
      this._lastSourceStateKey = null;
      this._activeMatchKey = null;
    },

    // Typing an opening bracket or quote with text selected wraps the
    // selection instead of replacing it: select `hello`, press `(`, get
    // `(hello)` with `hello` still selected.
    //
    // Only openers. A closing character is left alone so it can still be typed
    // literally, and so pressing `)` after a wrap does not nest again.
    // `*` and `_` are symmetric like the quotes, and wrap into markdown
    // emphasis: `*hello*` reads as italic once serialized, `**hello**` as bold
    // if the wrap is repeated. Included because reaching for them around a
    // selection is the same gesture as reaching for a bracket.
    _SURROUND_PAIRS: {
      "(": ")",
      "[": "]",
      "{": "}",
      '"': '"',
      "'": "'",
      "`": "`",
      "*": "*",
      _: "_"
    },

    // Returns true when the keystroke was consumed.
    _maybeSurroundSelection: function (e) {
      if (e.ctrlKey || e.metaKey || e.altKey) return false;
      if (this._readonly) return false;

      var close = this._SURROUND_PAIRS[e.key];
      if (!close) return false;

      var sel = window.getSelection();
      if (!sel || !sel.rangeCount || sel.isCollapsed) return false;

      var range = sel.getRangeAt(0);
      if (!this._visualEl || !this._visualEl.contains(range.commonAncestorContainer)) {
        return false;
      }
      if (range.toString().length === 0) return false;

      e.preventDefault();
      this._historyCaptureNow("surround");

      // Insert the CLOSING character first: putting the opener in first would
      // shift every offset after it and invalidate the range's end.
      //
      // Inserting around the selection rather than replacing its text keeps
      // whatever it contains — bold runs, links, a whole nested structure —
      // which `execCommand("insertText")` would flatten to plain text.
      var closeNode = document.createTextNode(close);
      var closeAt = range.cloneRange();
      closeAt.collapse(false);
      closeAt.insertNode(closeNode);

      var openNode = document.createTextNode(e.key);
      var openAt = range.cloneRange();
      openAt.collapse(true);
      openAt.insertNode(openNode);

      // Leave the original text selected between the two, so the wrap can be
      // repeated — `(`, `[`, `"` in sequence nests as the user expects.
      try {
        var kept = document.createRange();
        kept.setStartAfter(openNode);
        kept.setEndBefore(closeNode);
        sel.removeAllRanges();
        sel.addRange(kept);
      } catch (_e) {
        /* content shifted — leave the caret where the browser put it */
      }

      this._visualEl.dispatchEvent(new Event("input", { bubbles: true }));
      this._historyCapture("surround");
      return true;
    },

    // The textarea counterpart, for markdown and html modes. `markdownFormat`
    // already wraps a selection through `replaceRange`, which keeps the
    // browser's own undo stack intact.
    _maybeSurroundTextarea: function (e, textarea) {
      if (e.ctrlKey || e.metaKey || e.altKey) return false;
      if (this._readonly || !textarea) return false;

      var close = this._SURROUND_PAIRS[e.key];
      if (!close) return false;
      if (textarea.selectionStart === textarea.selectionEnd) return false;

      e.preventDefault();
      this._historyCaptureNow("surround");

      var gid = this._editorId.replace(/-/g, "_") + "_markdown";
      var fmt = window["markdownFormat_" + gid];

      if (fmt && textarea === this._getMarkdownTextarea()) {
        fmt(e.key, close);
      } else {
        var start = textarea.selectionStart;
        var end = textarea.selectionEnd;
        var value = textarea.value;

        textarea.value =
          value.slice(0, start) + e.key + value.slice(start, end) + close + value.slice(end);
        textarea.setSelectionRange(start + 1, end + 1);
        textarea.dispatchEvent(new Event("input", { bubbles: true }));
      }

      this._historyCapture("surround");
      return true;
    },

    // =====================================================================
    // Operation-shaped changes
    // =====================================================================
    //
    // Leaf's wire contract is a snapshot: `content_changed` carries the whole
    // markdown after a debounce. Two concurrent snapshots cannot be merged —
    // from a before and an after you cannot tell an insert from a delete from
    // a rewrite — which is the first thing that has to change for more than one
    // person to be in a document.
    //
    // So alongside the snapshot, emit what actually changed: one splice against
    // the last state we sent. The host applies it to whatever it keeps the
    // truth in (a `Y.Text` under `y_ex`, say). Leaf holds no CRDT and takes no
    // dependency for this; it just stops throwing away what it already knows.
    //
    // Entirely additive and opt-in. A host that only wants the markdown sees
    // exactly the traffic it always did.

    // The minimal single splice turning `before` into `after`: trim the common
    // prefix, then the common suffix, and whatever is left in the middle is the
    // edit. Typing, pasting, deleting a selection — all one splice. A change
    // touching two distant places collapses into one splice spanning both,
    // which is coarser but never wrong.
    //
    // Null when nothing changed, so an idle re-render emits nothing.
    _spliceBetween: function (before, after) {
      if (before === after) return null;

      before = before || "";
      after = after || "";

      var start = 0;
      var limit = Math.min(before.length, after.length);

      while (start < limit && before.charCodeAt(start) === after.charCodeAt(start)) {
        start++;
      }

      var endBefore = before.length;
      var endAfter = after.length;

      while (
        endBefore > start &&
        endAfter > start &&
        before.charCodeAt(endBefore - 1) === after.charCodeAt(endAfter - 1)
      ) {
        endBefore--;
        endAfter--;
      }

      return {
        at: start,
        remove: endBefore - start,
        insert: after.slice(start, endAfter)
      };
    },

    // Diff against the last state SENT, not the last state seen. That is what
    // makes the debounce harmless: however many keystrokes it swallowed, the
    // splice still describes the whole distance from what the host last heard.
    // ===================================================================
    // Applying someone else's operation
    // ===================================================================
    //
    // The hard part is not the text — it is the caret. Replacing the content
    // puts the caret back at the top of the document, which in a shared editor
    // means every keystroke by anyone else yanks you out of your sentence.
    //
    // So the caret is measured before the swap, and put back afterwards at the
    // position the peer's edit moved it to.

    _applyRemoteOperation: function (payload) {
      if (!payload) return;

      var self = this;

      // Anything typed here in the last few milliseconds has not been sent yet
      // and so is not in the host's copy either. Send it first, so it is
      // counted among the edits this incoming one has to be rebased over —
      // otherwise it is invisible to that calculation and the incoming edit
      // lands one place off for every character of it.
      if (this._collabOperations && !this._applyingRemote && !payload.resync) {
        if (this._operationTimer) {
          clearTimeout(this._operationTimer);
          this._operationTimer = null;
        }
        this._emitOperation(this._currentMarkdown());
      }

      var rendered = payload.resync ? null : this._rebaseOverPending(payload.rendered);

      // Can this be written into the text as it stands, or does it need the
      // host's html? Asked before anything is changed, because the answer
      // decides whether our own unsent work is about to be thrown away.
      var placeable =
        !payload.resync &&
        rendered &&
        this._mode !== "markdown" &&
        this._mode !== "html" &&
        this._canFastPath(rendered);

      // Our own edits are still in the air, and this one needs the host's copy
      // — which does not contain them. Taking it would throw them away, so
      // wait until ours are acknowledged and settle it from the host then.
      if (!payload.resync && !placeable && this._pending && this._pending.length) {
        this._needsResync = true;
        if (typeof payload.revision === "number") this._collabRevision = payload.revision;
        return;
      }

      // Swapping content fires input events. Emitting an operation for a change
      // we did not make would send the peer's edit back to them as ours, and
      // recording it in the undo stack would let this user undo someone else's
      // typing.
      this._applyingRemote = true;
      this._historyRestoring = true;

      if (payload.resync) this._pending = [];
      if (typeof payload.revision === "number") this._collabRevision = payload.revision;

      try {
        var visual = null;
        var local = { content: payload.content, html: payload.html, rendered: rendered };

        if (this._mode === "markdown") {
          this._applyRemoteToTextarea(this._getMarkdownTextarea(), payload);
        } else if (this._mode !== "html") {
          if (placeable && this._applyRemoteFastPath(local)) {
            // The caret moved itself: replaceData adjusts live ranges, so this
            // user's selection is already where it should be.
            visual = rendered;
          } else {
            visual = this._applyRemoteToVisual(payload);
          }
        }

        // The peers' carets are offsets into the text that just changed. The
        // shift is already known — it is the same one this user's caret took —
        // so move them now rather than showing them in the wrong place until
        // their owners next touch the keyboard.
        if (visual && this._peerCursors && this._peerCursors.length) {
          var shifted = [];
          for (var i = 0; i < this._peerCursors.length; i++) {
            var peer = this._peerCursors[i];
            shifted.push(
              Object.assign({}, peer, {
                offset: this._shiftOffset(peer.offset || 0, visual),
                anchor:
                  typeof peer.anchor === "number"
                    ? this._shiftOffset(peer.anchor, visual)
                    : peer.anchor
              })
            );
          }
          this._peerCursors = shifted;
        }

        // Measure the next local splice from what we now actually hold. Not
        // from payload.content: the HTML round-trip can differ from it in
        // whitespace, and that difference would otherwise be re-sent as this
        // user's edit.
        this._lastSentMarkdown = this._currentMarkdown();
        this._lastVisibleText =
          this._mode === "markdown" || this._mode === "html"
            ? this._lastSentMarkdown
            : this._visibleText(this._visualEl);
        this._syncFormInput(this._lastSentMarkdown);
        this._updateCounts();

        // Every character just moved; the carets are drawn at pixel positions.
        if (this._peerCursors && this._peerCursors.length) {
          this._renderPeerCursors(this._peerCursors);
        }
      } finally {
        // Cleared on a later tick, for the same reason _historyApply does it:
        // input events from replacing content arrive asynchronously.
        setTimeout(function () {
          self._applyingRemote = false;
          self._historyRestoring = false;
        }, 0);
      }
    },

    // Markdown mode is the easy case: the textarea's offsets are the same
    // markdown character offsets the operation is written in.
    _applyRemoteToTextarea: function (ta, payload) {
      if (!ta) return;

      var start = ta.selectionStart;
      var end = ta.selectionEnd;
      var focused = document.activeElement === ta;

      ta.value = payload.content || "";

      if (!focused) return;
      try {
        ta.setSelectionRange(
          this._shiftOffset(start, payload),
          this._shiftOffset(end, payload)
        );
      } catch (e) {
        /* detached or hidden — nothing to place */
      }
    },

    // Rendered text with hybrid mode's raw-markdown markers left out.
    //
    // In hybrid the block under the caret shows its own markdown — the "- " of
    // a list item, a heading's "#", the "**" around bold — in
    // leaf-source-marker spans. Server HTML never contains those, and which
    // block is showing them depends on where each person's caret is. So the
    // markers are in the DOM we compare against but not in the DOM we compare
    // to, and a straight textContent diff reports them as a deletion nobody
    // made, dragging the caret backwards through untouched text.
    //
    // Excluding them also gives every session the same coordinates for the
    // same document, which is what makes a peer's caret position meaningful
    // here rather than only in the editor it came from.
    // The document as a run of characters every session agrees on, built once
    // and reused by the three functions below.
    //
    // Line breaks between blocks are part of it, and they come from the
    // ELEMENTS rather than the text. Without a position of their own, the end
    // of one line and the start of the next are the same offset, and an edit
    // sent at that offset lands in whichever of the two the receiver happens
    // to resolve it to — which is how "a body" and "peach tree" became
    // "a bodych tree" in one tab and not the other. And they cannot be
    // inferred from text nodes, because an empty list item holds none at all:
    // a session that has put a placeholder in one would then see a line where
    // a session that has not sees nothing.
    _visibleSegments: function (root) {
      var scope = root || this._visualEl;
      if (!scope) return [];

      var walker = document.createTreeWalker(
        scope,
        NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
        null,
        false
      );

      var segments = [];
      var node;

      while ((node = walker.nextNode())) {
        if (node.nodeType === 1) {
          if (isHardBreak(node) || (segments.length > 0 && this._BLOCK_TAGS.test(node.tagName))) {
            segments.push({ node: null, text: "\n", raw: "\n", map: [0] });
          }
          continue;
        }

        if (this._inSourceMarker(node, scope)) continue;

        segments.push({ node: node, raw: node.nodeValue || "" });
      }

      this._markVisible(segments);

      // Whether a block opening is a break between two lines depends on there
      // being a line before it, and that is only known once it is settled which
      // characters count. An item holding nothing but a placeholder has no
      // line in it, and a session that has put one there must not read a break
      // where a session that has not reads none.
      var firstLine = segments.length;
      for (var f = 0; f < segments.length; f++) {
        if (segments[f].node && segments[f].text !== "") {
          firstLine = f;
          break;
        }
      }

      var kept = [];
      for (var k = 0; k < segments.length; k++) {
        // A break before the first line is not between two lines.
        if (k < firstLine && !segments[k].node) continue;
        kept.push(segments[k]);
      }

      return kept;
    },

    // Decide which characters of each segment are part of the shared document,
    // and record where each one really sits so an offset can be turned back
    // into a DOM position.
    //
    // Two kinds are not part of it. Zero-width placeholders, which are
    // scaffolding for the caret. And whitespace at the end of a line, which
    // the editor shows — Leaf substitutes a non-breaking space so a trailing
    // space stays visible — but which markdown trims away. A character the
    // host never stores cannot be in the coordinates, or the session that
    // typed it counts one more character than every session that received the
    // document, and every later edit is placed one position out.
    _markVisible: function (segments) {
      var trailing = true;

      for (var i = segments.length - 1; i >= 0; i--) {
        var segment = segments[i];

        if (!segment.node) {
          trailing = true;
          continue;
        }

        var raw = segment.raw;
        var keep = [];

        for (var j = raw.length - 1; j >= 0; j--) {
          var ch = raw.charAt(j);

          if (ch === "\u200B" || ch === "\uFEFF") continue;

          if (trailing && (ch === " " || ch === "\u00A0" || ch === "\t")) continue;

          trailing = false;
          keep.push(j);
        }

        keep.reverse();
        segment.map = keep;
        segment.text = keep.map(function (index) {
          return raw.charAt(index);
        }).join("");
      }
    },

    // Tag-driven rather than computed: this runs against detached and
    // still-loading DOM, where getComputedStyle has nothing useful to say.
    _BLOCK_TAGS: /^(?:P|DIV|LI|H[1-6]|BLOCKQUOTE|PRE|TD|TH|FIGCAPTION|DT|DD|SECTION|ARTICLE|ASIDE|HEADER|FOOTER|MAIN|NAV|ADDRESS|FIELDSET|TABLE|CAPTION|HR)$/,

    _nextTextSegment: function (segments, from) {
      for (var i = from; i < segments.length; i++) {
        if (segments[i].node) return segments[i];
      }
      return null;
    },

    // True when every character of this node is part of the document, so real
    // offsets and shared offsets mean the same thing inside it.
    _fullyVisible: function (node) {
      var segments = this._visibleSegments();

      for (var i = 0; i < segments.length; i++) {
        if (segments[i].node === node) {
          return segments[i].map.length === (segments[i].raw || "").length;
        }
      }

      return false;
    },

    _visibleText: function (root) {
      var segments = this._visibleSegments(root);
      var out = "";

      for (var i = 0; i < segments.length; i++) out += segments[i].text;

      return out;
    },

    // Zero-width placeholders are scaffolding, not text. An empty list item
    // gets one so the caret has somewhere to sit, and whether a given item has
    // one depends on what that session has been doing — so counting them makes
    // two people looking at the same document disagree about where a character
    // is. That misplaces their carets and, worse, makes an edit apply at the
    // wrong offset on the other side.
    _visibleChars: function (text) {
      if (!text) return "";
      return text.replace(/[\u200B\uFEFF]/g, "");
    },

    _inSourceMarker: function (node, root) {
      var el = node.parentNode;
      var stop = root || this._visualEl;

      while (el && el !== stop) {
        if (el.classList && el.classList.contains("leaf-source-marker")) return true;
        el = el.parentNode;
      }

      return false;
    },

    // A DOM position as an offset into _visibleText.
    _visibleOffset: function (container, offset) {
      if (!container || !this._visualEl) return 0;

      var boundary;
      try {
        boundary = document.createRange();
        boundary.setStart(this._visualEl, 0);
        boundary.setEnd(container, offset);
      } catch (e) {
        return 0;
      }

      var segments = this._visibleSegments();
      var count = 0;
      var pendingBreaks = 0;

      for (var i = 0; i < segments.length; i++) {
        var segment = segments[i];

        // A line break only counts once something after it does.
        if (!segment.node) {
          pendingBreaks += 1;
          continue;
        }

        var take;
        var reached = false;

        if (segment.node === container) {
          take = offset;
          reached = true;
        } else {
          try {
            if (boundary.comparePoint(segment.node, segment.raw.length) <= 0) {
              take = segment.raw.length;
            } else if (boundary.comparePoint(segment.node, 0) > 0) {
              break;
            } else {
              take = segment.raw.length;
            }
          } catch (e) {
            break;
          }
        }

        count += pendingBreaks;
        pendingBreaks = 0;

        // How many of this segment's visible characters lie before the caret.
        var seen = 0;
        for (var m = 0; m < segment.map.length; m++) {
          if (segment.map[m] < take) seen++;
        }
        count += seen;

        if (reached) break;
      }

      return count;
    },

    // Plain typing, applied straight into the text node it belongs to.
    //
    // The slow path replaces the whole document with server-rendered HTML,
    // which for a single character is a great deal of work to watch: the
    // source block closes, wiki links re-resolve, drag handles go stale, and
    // every caret on screen has to be recomputed. Editing the text node
    // instead leaves all of that alone, and the browser moves live ranges —
    // including this user's caret — as part of the mutation.
    //
    // Deliberately narrow. Anything that could be structure (a newline, an
    // edit spanning more than the one text node) falls through to the slow
    // path, where the server's HTML is the authority.
    // Whether an edit can be written straight into the text, asked separately
    // from doing it: the caller has to know before it changes anything,
    // because the alternative replaces the document with the host's copy and
    // that copy does not contain work of ours still in flight.
    _canFastPath: function (rendered) {
      if (!rendered || !this._visualEl) return false;
      if (this._mode === "markdown" || this._mode === "html") return false;

      var insert = rendered.insert || "";
      var remove = rendered.remove || 0;
      if (!insert && !remove) return false;
      // A newline is structure, and structure is the host's to describe.
      if (insert.indexOf("\n") !== -1) return false;

      var point = this._visibleNodeAt(rendered.at || 0);
      if (!point || !point.node || point.node.nodeType !== 3) return false;
      // On a line break, belonging to neither line — including an empty line
      // with nothing to write into, such as a bullet just made with Enter.
      if (point.boundary) return false;
      if (this._inSourceMarker(point.node)) return false;

      // The whole edit has to sit inside this one text node. Past its end we
      // would be guessing about structure a character offset cannot describe.
      if (point.offset + remove > point.node.nodeValue.length) return false;

      // `remove` counts visible characters and replaceData counts real ones.
      // They part company wherever this node holds something the document does
      // not — a placeholder, or whitespace at the end of a line — so those are
      // left to the slow path rather than writing over the wrong span.
      return this._fullyVisible(point.node);
    },

    _applyRemoteFastPath: function (payload) {
      var rendered = payload && payload.rendered;
      if (!this._canFastPath(rendered)) return false;

      var insert = rendered.insert || "";
      var remove = rendered.remove || 0;
      var point = this._visibleNodeAt(rendered.at || 0);

      try {
        point.node.replaceData(point.offset, remove, insert);
      } catch (e) {
        return false;
      }

      return true;
    },

    _applyRemoteToVisual: function (payload) {
      var el = this._visualEl;
      if (!el) return;

      var sel = window.getSelection();
      var hadCaret =
        sel && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).startContainer);
      var caretStart = 0;
      var caretEnd = 0;

      if (hadCaret) {
        var range = sel.getRangeAt(0);
        caretStart = this._visibleOffset(range.startContainer, range.startOffset);
        caretEnd = this._visibleOffset(range.endContainer, range.endOffset);
      }

      var before = this._visibleText(el);

      el.innerHTML = payload.html || "<p><br></p>";
      // Same cleanup leaf-set-html does — server HTML arrives pretty-printed,
      // and the stray whitespace nodes break caret placement in lists.
      this._normalizeRenderedHtml(el);
      this._ensureListItemPlaceholders(el);
      this._resolveWikiLinks();
      this._sourceBlock = null;
      this._dragHandleBlock = null;

      // The operation is in markdown offsets, but the caret lives in rendered
      // text, and the two differ by every marker markdown does not render —
      // `#`, `- `, the brackets around a link. Rather than map between the two
      // coordinate systems, derive the shift from what actually changed on
      // screen.
      var visual = this._spliceBetween(before, this._visibleText(el));

      if (hadCaret) {
        this._restoreVisualCaret(
          this._shiftOffset(caretStart, visual),
          this._shiftOffset(caretEnd, visual)
        );
      }

      return visual;
    },

    // Where a position lands after a splice is applied in front of it.
    _shiftOffset: function (offset, splice) {
      if (!splice) return offset;

      var at = splice.at || 0;
      var remove = splice.remove || 0;
      var insert = (splice.insert || "").length;

      // Edited after us: we do not move.
      if (offset <= at) return offset;
      // Edited entirely before us: we slide by the net change.
      if (offset >= at + remove) return offset + insert - remove;
      // We were sitting inside text the peer replaced. The end of what they
      // wrote is the only position that still means anything.
      return at + insert;
    },

    _restoreVisualCaret: function (start, end) {
      var startPoint = this._historyNodeAt(start);
      var endPoint = end === start ? startPoint : this._historyNodeAt(end);
      if (!startPoint) return;

      try {
        var range = document.createRange();
        range.setStart(startPoint.node, startPoint.offset);
        range.setEnd((endPoint || startPoint).node, (endPoint || startPoint).offset);

        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (e) {
        /* content shifted under us — leave the caret where the browser put it */
      }
    },

    // Coalesces a burst of keystrokes without ever holding one back: the timer
    // is left alone once set rather than pushed forward on each change, so a
    // continuous typist emits steadily instead of emitting nothing until they
    // stop.
    _scheduleOperation: function () {
      if (!this._collabOperations || this._applyingRemote) return;
      if (this._operationTimer) return;

      var self = this;
      this._operationTimer = setTimeout(function () {
        self._operationTimer = null;
        self._emitOperation(self._currentMarkdown());
      }, this._collabIntervalMs);
    },

    // The current edit in marker-free rendered coordinates, or null when that
    // cannot be established. Tracked separately from the markdown baseline
    // because the two do not move in step: "**" appearing in the markdown is
    // no change on screen at all.
    _renderedSplice: function (markdown) {
      var current =
        this._mode === "markdown" || this._mode === "html"
          ? markdown
          : this._visibleText(this._visualEl);

      var previous = this._lastVisibleText;
      this._lastVisibleText = current;

      if (typeof previous !== "string") return null;
      return this._spliceBetween(previous, current);
    },

    // What `op` means once `applied` has happened. The same rule the host uses
    // — the two have to agree or an edit ends up in a different place in each
    // session.
    // FNV-1a, 32-bit. Cheap, and the host computes it the same way, so two
    // sessions holding the same text produce the same eight characters and a
    // disagreement is visible without shipping the document anywhere.
    _digest: function (text) {
      var hash = 0x811c9dc5;

      for (var i = 0; i < text.length; i++) {
        hash = Math.imul(hash ^ text.charCodeAt(i), 0x01000193) >>> 0;
      }

      return ("0000000" + hash.toString(16)).slice(-8);
    },

    // What this session believes it is holding, for the host's log.
    _debugState: function () {
      var markdown = this._currentMarkdown();
      var visible =
        this._mode === "markdown" || this._mode === "html"
          ? markdown
          : this._visibleText(this._visualEl);

      var caret = null;
      var sel = window.getSelection();

      if (
        sel &&
        sel.rangeCount &&
        this._visualEl &&
        this._visualEl.contains(sel.getRangeAt(0).startContainer)
      ) {
        var range = sel.getRangeAt(0);
        caret = this._visibleOffset(range.startContainer, range.startOffset);
      }

      return {
        digest: this._digest(markdown),
        length: markdown.length,
        // The coordinates, fingerprinted separately. Two sessions can hold the
        // same document and still disagree about how many characters are in
        // it — which is the disagreement that misplaces a caret, and it is
        // invisible to a comparison of the documents alone.
        visible_digest: this._digest(visible),
        visible_length: visible.length,
        caret: caret,
        pending: this._pending ? this._pending.length : 0,
        revision: typeof this._collabRevision === "number" ? this._collabRevision : null
      };
    },

    _rebaseSplice: function (op, applied) {
      var appliedEnd = applied.at + applied.remove;
      var shift = (applied.insert || "").length - applied.remove;
      var opEnd = op.at + op.remove;

      if (appliedEnd <= op.at) {
        return { at: op.at + shift, remove: op.remove, insert: op.insert };
      }

      if (opEnd <= applied.at) return op;

      var before = Math.max(applied.at - op.at, 0);
      var rest = Math.max(opEnd - appliedEnd, 0);

      return {
        at: Math.min(op.at, applied.at),
        remove: before + rest,
        insert: op.insert
      };
    },

    // An incoming edit, expressed against this session's document rather than
    // the one the sender was looking at.
    _rebaseOverPending: function (rendered) {
      if (!rendered || !this._pending || !this._pending.length) return rendered;

      var carried = rendered;

      for (var i = 0; i < this._pending.length; i++) {
        var mine = this._pending[i].rendered;
        // One of our own edits has no rendered form, so there is nothing to
        // rebase over and no honest answer. The caller falls back.
        if (!mine) return null;
        carried = this._rebaseSplice(carried, mine);
      }

      return carried;
    },

    _forgetPendingThrough: function (seq) {
      if (!this._pending || typeof seq !== "number") return;

      var kept = [];
      for (var i = 0; i < this._pending.length; i++) {
        if (this._pending[i].seq > seq) kept.push(this._pending[i]);
      }
      this._pending = kept;

      // Whatever we could not apply while our own edits were in the air can be
      // settled now, from the host's copy.
      if (!this._pending.length && this._needsResync) {
        this._needsResync = false;
        this.pushEventTo(this.el, "resync", { editor_id: this._editorId });
      }
    },

    _emitOperation: function (markdown) {
      if (!this._collabOperations || this._applyingRemote) return;

      var previous = this._lastSentMarkdown;

      // First call establishes the baseline. There is no operation from
      // "nothing known" to the opening document — the host already has that.
      if (typeof previous !== "string") {
        this._lastSentMarkdown = markdown;
        return;
      }

      var splice = this._spliceBetween(previous, markdown);
      if (!splice) return;

      this._lastSentMarkdown = markdown;
      this._operationSeq = (this._operationSeq || 0) + 1;

      // The same edit expressed in the rendered text every session shares.
      // Markdown offsets are the document of record, but they cannot be
      // applied to a peer's DOM without re-rendering it; these can.
      var rendered = this._renderedSplice(markdown);

      this.pushEventTo(this.el, "operation", {
        editor_id: this._editorId,
        at: splice.at,
        remove: splice.remove,
        insert: splice.insert,
        rendered: rendered,
        // The version this was written against. Two people typing at once each
        // describe their edit against the text they can see, and the host needs
        // to know which text that was to put both of them where they were meant
        // to go.
        revision: typeof this._collabRevision === "number" ? this._collabRevision : null,
        seq: this._operationSeq,
        // Length of the text this splice applies to. A host holding a document
        // of a different length knows it has diverged, rather than applying an
        // operation at an offset that means something else now.
        base_length: previous.length,
        // What this editor holds now that it has applied its own edit. The
        // host compares it with what the same edit produced there.
        debug: this._collabDebug ? this._debugState() : null
      });

      if (!this._pending) this._pending = [];
      this._pending.push({ seq: this._operationSeq, rendered: rendered });
    },

    // The next real item after `li`, or null at the end of the list.
    //
    // Element-wise on purpose: a text node between items (pretty-printed
    // server HTML leaves them, and `_stripInterBlockWhitespace` only runs on
    // synced content) would otherwise read as "something follows" and turn the
    // last item in a list into a middle one.
    // Adjacent lists of the same kind are one list that something used to
    // separate. Markdown already reads them that way — two touching <ol>s
    // round-trip to one — so the DOM merging eagerly just keeps what the
    // person sees in step with what the document says. Numbering follows
    // for free: the swallowed list's start is dropped, so 1-3 + 1-3
    // renders 1-6.
    _mergeAdjacentLists: function () {
      if (!this._visualEl || this._syntaxMutating) return false;

      var merged = false;
      var lists = this._visualEl.querySelectorAll("ol, ul");

      for (var i = 0; i < lists.length; i++) {
        var list = lists[i];
        if (!list.parentNode) continue; // already swallowed this pass

        for (;;) {
          // Whitespace and ZWSP crumbs between blocks do not keep two
          // lists apart.
          var between = [];
          var next = list.nextSibling;
          while (
            next &&
            next.nodeType === Node.TEXT_NODE &&
            !next.textContent.replace(/[\s\u200B\uFEFF]/g, "")
          ) {
            between.push(next);
            next = next.nextSibling;
          }
          if (!next || next.tagName !== list.tagName) break;

          this._syntaxMutating = true;
          try {
            for (var b = 0; b < between.length; b++) {
              between[b].parentNode.removeChild(between[b]);
            }
            while (next.firstChild) list.appendChild(next.firstChild);
            next.parentNode.removeChild(next);
          } finally {
            this._syntaxMutating = false;
          }
          merged = true;
        }
      }

      return merged;
    },

    _nextListSibling: function (li) {
      var sib = li && li.nextSibling;

      while (sib) {
        if (sib.nodeType === Node.ELEMENT_NODE) return sib;
        if (sib.nodeType === Node.TEXT_NODE && /\S/.test(sib.textContent)) return sib;
        sib = sib.nextSibling;
      }

      // Nothing further in this list — but a list DIRECTLY followed by another
      // list is one list as far as the reader is concerned, and editing has
      // several ways of producing that pair. Deleting an item's `- ` marker
      // breaks it out to a `<p>` and moves the items below it into a second
      // list; delete that paragraph and the two lists close up against each
      // other, looking exactly like the single list they render as.
      //
      // Without this, a blank row at the end of the first list counted as the
      // last item of the document's list while bullets were plainly visible
      // under it, and the tidy-up removed it. Anything else in between — a
      // paragraph, a heading — means the author really did end the list, so
      // only an immediately adjacent one counts.
      var list = li && li.parentNode;
      var after = list && list.nextElementSibling;
      var afterTag = after && after.tagName ? after.tagName.toLowerCase() : "";

      if (afterTag === "ul" || afterTag === "ol") {
        return after.firstElementChild || after;
      }

      return null;
    },

    // Give every empty `<li>` a caret home.
    //
    // An item with no content renders at zero height and offers the caret
    // nowhere to sit, so it looks deleted even though it is still in the
    // document. Chrome also tends to hide the bullet/number for such an item.
    //
    // The Enter-split path already knows this — it drops a ZWSP into a newly
    // created item for exactly this reason. The problem was that the
    // placeholder is a client-side artifact: it is stripped on the way out to
    // markdown (correctly — it must not become content), so every empty item
    // that comes BACK from the server arrives bare. Deliberately leaving a
    // bullet empty and then clicking away made it vanish, because the sync
    // round-trip dropped the only thing making it visible.
    //
    // Applied wherever content is assigned wholesale, so the two paths that
    // can produce an empty item agree about how it renders. The ZWSP is
    // stripped again by the markdown scrubbers, so this changes nothing about
    // what the document serializes to.
    _ensureListItemPlaceholders: function (root) {
      if (!root) return;

      var items = root.querySelectorAll("li");
      for (var i = 0; i < items.length; i++) {
        this._ensureListItemPlaceholder(items[i]);
      }
    },

    // The single-item form, and the one every caller should reach for.
    //
    // Emptiness is measured by TEXT, not by `firstChild`. That distinction is
    // the whole bug: `Range.extractContents()` hands back a fragment holding an
    // empty text node when the caret sat at the end of a line, so a freshly
    // split item had a `firstChild` and looked occupied while rendering at zero
    // height. Splitting at the end of a bullet — the ordinary way to make the
    // next one — therefore produced an item that was invisible from birth.
    _ensureListItemPlaceholder: function (li) {
      if (!li) return false;

      var raw = li.textContent || "";

      // Already carries a placeholder. Checked BEFORE the emptiness test,
      // which ignores zero-width characters: without this the item collects a
      // fresh one on every pass, growing an invisible run of them.
      if (/[\u200B\uFEFF]/.test(raw)) return false;

      // Real text means there is nothing to fix.
      if (/\S/.test(raw)) return false;

      // Elements that give the item height on their own. A task box is NOT
      // one: an empty task item still needs somewhere for the caret to sit and
      // the label to be typed.
      if (li.querySelector("ul, ol, img, br, input")) return false;

      li.appendChild(document.createTextNode("\u200B"));
      return true;
    },

    // Unwrap "loose" list items. CommonMark renders a list whose items
    // are separated by blank lines as `<li><p>text</p></li>` instead of
    // `<li>text</li>`. The hybrid editor expects inline content directly
    // in the `<li>`: with a `<p>` inside, a click lands the caret in that
    // `<p>`, so `_getCurrentBlock` returns the `<p>` (not the `<li>`) and
    // Enter inserts a nested paragraph inside the item instead of starting
    // a new list item — the bullet gets stuck and can't be left. Unwrap
    // the common single-paragraph case so list items behave tightly; a
    // genuinely multi-paragraph item is left as-is.
    _unwrapLooseListItems: function (root) {
      if (!root) return;
      var items = root.querySelectorAll("li");
      for (var i = 0; i < items.length; i++) {
        var li = items[i];
        var directPs = [];
        for (var k = 0; k < li.children.length; k++) {
          if (li.children[k].tagName.toLowerCase() === "p") {
            directPs.push(li.children[k]);
          }
        }
        if (directPs.length === 1) {
          var p = directPs[0];
          while (p.firstChild) li.insertBefore(p.firstChild, p);
          li.removeChild(p);
        }
        // Drop the pretty-printer's leading/trailing whitespace-only text
        // nodes left around the (former) `<p>` so the item reads cleanly.
        var child = li.firstChild;
        while (child) {
          var next = child.nextSibling;
          if (
            child.nodeType === Node.TEXT_NODE &&
            !/\S/.test(child.textContent) &&
            (child === li.firstChild || child === li.lastChild)
          ) {
            li.removeChild(child);
          }
          child = next;
        }
      }
    },

    _firstTextDescendant: function (root) {
      var c = root.firstChild;
      while (c) {
        if (c.nodeType === Node.TEXT_NODE) return c;
        if (c.nodeType === Node.ELEMENT_NODE) {
          var f = this._firstTextDescendant(c);
          if (f) return f;
        }
        c = c.nextSibling;
      }
      return null;
    },

    _lastTextDescendant: function (root) {
      var c = root.lastChild;
      while (c) {
        if (c.nodeType === Node.TEXT_NODE) return c;
        if (c.nodeType === Node.ELEMENT_NODE) {
          var f = this._lastTextDescendant(c);
          if (f) return f;
        }
        c = c.previousSibling;
      }
      return null;
    },

    _applyAutoFormat: function (
      runNodes,
      cursorNode,
      cursorOffset,
      cursorGlobalOffset,
      match,
      pattern,
    ) {
      var inner = match[1];
      if (!inner) return;
      var matchLen = match[0].length;
      var matchStartGlobal = cursorGlobalOffset - matchLen;

      // Find which run node contains the match's start position, and the
      // offset within that node. The match's end is the cursor itself.
      var startNode = null;
      var startOffset = 0;
      var acc = 0;
      for (var k = 0; k < runNodes.length; k++) {
        var rn = runNodes[k];
        var rlen =
          rn === cursorNode ? cursorOffset : rn.textContent.length;
        if (acc + rlen >= matchStartGlobal) {
          startNode = rn;
          startOffset = matchStartGlobal - acc;
          break;
        }
        acc += rlen;
      }
      if (!startNode) return;

      // Build the wrapper with inner content recursively formatted, so
      // a deferred outer that finally closes (e.g. `~~**word**~~`)
      // produces nested `<del><strong>word</strong></del>` rather than
      // a single-level wrapper containing raw markdown text.
      var wrapper = this._createAutoFormatWrapper(pattern);
      var innerHost =
        pattern.type === "boldItalic" ? wrapper.firstChild : wrapper;
      var innerNodes = this._buildFormattedFragment(inner);
      for (var bf = 0; bf < innerNodes.length; bf++) {
        innerHost.appendChild(innerNodes[bf]);
      }

      this._syntaxMutating = true;
      try {
        // Clear any previously-decorated wrappers — they're not the
        // current focus and their decoration spans should fade.
        if (this._decoratedAncestors) {
          for (var p = 0; p < this._decoratedAncestors.length; p++) {
            if (this._decoratedAncestors[p].isConnected) {
              this._removeDelimitersFrom(this._decoratedAncestors[p]);
            }
          }
        }

        // Splice via Range — handles multi-text-node spans cleanly.
        var spliceRange = document.createRange();
        spliceRange.setStart(startNode, startOffset);
        spliceRange.setEnd(cursorNode, cursorOffset);
        spliceRange.deleteContents();
        spliceRange.insertNode(wrapper);

        // Range.insertNode leaves an empty `""` text node on either side
        // of the wrapper when the split position lands at a text edge.
        // Clean those up so boundary detection finds the wrapper directly
        // (an empty text sibling otherwise gets picked as the candidate
        // and the listener clears the decoration).
        if (
          wrapper.previousSibling &&
          wrapper.previousSibling.nodeType === Node.TEXT_NODE &&
          wrapper.previousSibling.textContent === ""
        ) {
          wrapper.previousSibling.parentNode.removeChild(
            wrapper.previousSibling,
          );
        }
        if (
          wrapper.nextSibling &&
          wrapper.nextSibling.nodeType === Node.TEXT_NODE &&
          wrapper.nextSibling.textContent === ""
        ) {
          wrapper.nextSibling.parentNode.removeChild(wrapper.nextSibling);
        }

        // Decorate every formatting element in the wrapper's subtree
        // (innermost first) AND prime the chain so the listener-driven
        // `_updateSyntaxDecorations` sees `oldChain == newChain` and
        // skips its cursor-relocate logic.
        var chain = this._descendChainAll(wrapper);
        for (var j = 0; j < chain.length; j++) {
          this._addDelimitersTo(chain[j]);
        }
        this._decoratedAncestors = chain;

        // Place the caret INSIDE the innermost wrapper, immediately AFTER
        // the last trailing decoration span. That puts the visible caret
        // past the closing `**` (so the user sees `**bold**|` rather than
        // `**bold|**`) while still anchoring inside the wrapper, so chain
        // detection on the next selectionchange walks up to the wrapper
        // and decoration markers stay visible. Typing the next char still
        // escapes the wrapper via `_maybeRedirectPastTrailingBlock` route 1
        // (which fires for any cursor position past the inner body).
        var caret = document.createRange();
        var innermost = chain[0];
        var innerDelim = innermost
          ? this._delimiterFor(innermost)
          : null;
        var lastTrailing = null;
        if (innerDelim) {
          var spans = Array.prototype.filter.call(
            innermost.children,
            function (c) {
              return (
                c.classList &&
                c.classList.contains("leaf-syntax-decoration")
              );
            }
          );
          if (spans.length === 2 * innerDelim.length) {
            lastTrailing = spans[spans.length - 1];
          }
        }
        if (lastTrailing) {
          caret.setStartAfter(lastTrailing);
        } else {
          caret.setStartAfter(wrapper);
        }
        caret.collapse(true);
        var sel = window.getSelection();
        try {
          sel.removeAllRanges();
          sel.addRange(caret);
        } catch (_e) {
          /* range invalidated mid-frame — skip */
        }
      } finally {
        this._syntaxMutating = false;
      }
    },

    // After a contenteditable input event in hybrid mode, verify the active
    // decorated wrapper still represents valid formatting:
    //   - if the leading or trailing delimiter run is broken (chars edited
    //     or deleted), the wrapper is unwrapped entirely;
    //   - if there are stragglers BEFORE the leading run or AFTER the
    //     trailing run (the user typed at an edge position that landed
    //     inside the wrapper), those nodes are relocated outside the
    //     wrapper so the formatting boundary stays in sync with the
    //     decoration spans.
    _maybeUnwrapTamperedFormatting: function () {
      if (!this._decoratedAncestors || this._decoratedAncestors.length === 0) {
        return;
      }
      // Snapshot — the per-wrapper integrity check may unwrap entries via
      // _unwrapTamperedFormatting which mutates the live array.
      var snapshot = this._decoratedAncestors.slice();
      for (var i = 0; i < snapshot.length; i++) {
        var wrapper = snapshot[i];
        if (!wrapper.isConnected) continue;
        this._processWrapperIntegrity(wrapper);
      }
      // Cursor may have shifted out of the inner wrapper as part of
      // straggler relocation; re-evaluate the chain.
      this._updateSyntaxDecorations();
    },

    _processWrapperIntegrity: function (wrapper) {
      var delim = this._delimiterFor(wrapper);
      if (!delim) return;

      this._syntaxMutating = true;
      var normalized;
      try {
        normalized = this._normalizeDecorationSpans(wrapper, delim);
      } finally {
        this._syntaxMutating = false;
      }
      if (!normalized) {
        this._unwrapTamperedFormatting(wrapper);
        return;
      }

      var n = delim.length;
      // Direct decoration-span children only — querying descendants would
      // wrongly include nested wrappers' decoration spans.
      var allSpans = Array.prototype.filter.call(
        wrapper.children,
        function (c) {
          return (
            c.classList && c.classList.contains("leaf-syntax-decoration")
          );
        },
      );
      if (allSpans.length !== 2 * n) {
        this._unwrapTamperedFormatting(wrapper);
        return;
      }
      for (var i = 0; i < 2 * n; i++) {
        var expected = i < n ? delim[i] : delim[i - n];
        if (allSpans[i].textContent !== expected) {
          this._unwrapTamperedFormatting(wrapper);
          return;
        }
      }

      var children = Array.prototype.slice.call(wrapper.childNodes);
      var idxFirstLeading = children.indexOf(allSpans[0]);
      var idxLastLeading = children.indexOf(allSpans[n - 1]);
      var idxFirstTrailing = children.indexOf(allSpans[n]);
      var idxLastTrailing = children.indexOf(allSpans[2 * n - 1]);

      var beforeStragglers = [];
      var afterStragglers = [];
      for (var k = 0; k < idxFirstLeading; k++) {
        beforeStragglers.push(children[k]);
      }
      for (var k2 = idxFirstLeading + 1; k2 < idxLastLeading; k2++) {
        if (!this._isDecorationSpan(children[k2])) {
          beforeStragglers.push(children[k2]);
        }
      }
      for (var k3 = idxFirstTrailing + 1; k3 < idxLastTrailing; k3++) {
        if (!this._isDecorationSpan(children[k3])) {
          afterStragglers.push(children[k3]);
        }
      }
      for (var k4 = idxLastTrailing + 1; k4 < children.length; k4++) {
        afterStragglers.push(children[k4]);
      }

      if (beforeStragglers.length === 0 && afterStragglers.length === 0) {
        return;
      }

      var parent = wrapper.parentNode;
      if (!parent) return;
      this._syntaxMutating = true;
      try {
        for (var b = 0; b < beforeStragglers.length; b++) {
          parent.insertBefore(beforeStragglers[b], wrapper);
        }
        var afterAnchor = wrapper.nextSibling;
        for (var a = 0; a < afterStragglers.length; a++) {
          if (afterAnchor) {
            parent.insertBefore(afterStragglers[a], afterAnchor);
          } else {
            parent.appendChild(afterStragglers[a]);
          }
        }
      } finally {
        this._syntaxMutating = false;
      }
    },

    _isDecorationSpan: function (node) {
      return (
        node &&
        node.nodeType === Node.ELEMENT_NODE &&
        node.classList &&
        node.classList.contains("leaf-syntax-decoration")
      );
    },

    // Called from keydown for printable-character keys in hybrid mode.
    // If the cursor is past the bolded body (inside the trailing decoration
    // block or at the wrapper's tail), insert the typed character outside
    // the wrapper ourselves and tell the caller to preventDefault. Returns
    // true if it did the insertion, false otherwise.
    _maybeRedirectPastTrailingBlock: function (key) {
      var sel = window.getSelection();
      if (!sel.rangeCount) return false;
      var range = sel.getRangeAt(0);
      if (!range.collapsed) return false;

      var wrapper = this._findRedirectWrapper(range);
      if (!wrapper || !wrapper.isConnected) return false;

      var parent = wrapper.parentNode;
      if (!parent) return false;
      // Substitute regular spaces with U+00A0 so HTML doesn't collapse a
      // trailing space at the end of the block (`<p>hello </p>` would
      // otherwise render with no visible trailing whitespace until a
      // non-space character follows). htmlToMarkdown maps NBSPs back to
      // regular spaces on serialization so the markdown stays clean.
      var insertedChar = key === " " ? " " : key;
      var afterAnchor = wrapper.nextSibling;
      this._syntaxMutating = true;
      try {
        var textNode;
        if (afterAnchor && afterAnchor.nodeType === Node.TEXT_NODE) {
          // Insert at the START of the existing trailing text node — the
          // cursor is logically right after the wrapper, not at the end of
          // whatever text already lives further down the line. Extending
          // here keeps subsequent characters in one contiguous text run
          // (the next keystroke uses native browser insertion at offset >0
          // so this redirect path doesn't fire again).
          textNode = afterAnchor;
          textNode.insertData(0, insertedChar);
        } else {
          textNode = document.createTextNode(insertedChar);
          if (afterAnchor) {
            parent.insertBefore(textNode, afterAnchor);
          } else {
            parent.appendChild(textNode);
          }
        }
        var caret = document.createRange();
        caret.setStart(textNode, insertedChar.length);
        caret.collapse(true);
        try {
          sel.removeAllRanges();
          sel.addRange(caret);
        } catch (_e) {
          /* selection invalidated mid-frame — skip */
        }
      } finally {
        this._syntaxMutating = false;
      }
      // The browser would normally fire `input` here, but our
      // `preventDefault` suppressed it — drive the same hybrid-mode
      // post-input handling manually so auto-format still fires for
      // patterns typed past an existing wrapper (`**bold** **another**`).
      this._maybeAutoFormat();
      this._debouncedPushVisualChange();
      this._updateCounts();
      // NOTE: NOT calling `_updateSyntaxDecorations()` — selectionchange
      // will clean up decorations naturally once the cursor moves
      // somewhere unambiguously outside the wrapper.
      return true;
    },

    _findRedirectWrapper: function (range) {
      // Returns the formatting wrapper to redirect typing past, or null
      // if the cursor's current position doesn't warrant a redirect.
      // Two routes:
      //   1. Cursor is inside a currently-decorated wrapper, past the
      //      innermost wrapper's body (`**hello*|*` / `**hello**|`).
      //   2. Cursor is in the wrapper's immediately-following sibling
      //      text node — redirect remains armed so subsequent keystrokes
      //      keep flowing past the wrapper instead of getting pulled
      //      back inside by Chrome's caret affinity.
      // Only the first route requires decorations to currently be active,
      // so route 2 keeps working after they've cleared.

      // Route 1: inside a decorated wrapper, past inner body.
      if (this._decoratedAncestors && this._decoratedAncestors.length > 0) {
        var innermost = this._decoratedAncestors[0];
        var outermost =
          this._decoratedAncestors[this._decoratedAncestors.length - 1];
        if (innermost.isConnected && outermost.isConnected) {
          var delim = this._delimiterFor(innermost);
          if (delim) {
            var allSpans = Array.prototype.filter.call(
              innermost.children,
              function (c) {
                return (
                  c.classList &&
                  c.classList.contains("leaf-syntax-decoration")
                );
              },
            );
            var n = delim.length;
            if (allSpans.length === 2 * n) {
              var firstTrailing = allSpans[n];
              var probe = document.createRange();
              probe.setStartBefore(firstTrailing);
              probe.collapse(true);
              var pastBody =
                range.compareBoundaryPoints(
                  Range.START_TO_START,
                  probe,
                ) > 0;
              if (pastBody) {
                var afterOutermost = document.createRange();
                afterOutermost.setStartAfter(outermost);
                afterOutermost.collapse(true);
                var pastWrapper =
                  range.compareBoundaryPoints(
                    Range.START_TO_START,
                    afterOutermost,
                  ) > 0;
                if (!pastWrapper) return outermost;
                // Past the wrapper but possibly in its adjacent text —
                // fall through to route 2.
              }
            }
          }
        }
      }

      // Route 2: cursor at offset 0 of the wrapper's immediately-following
      // text node. Only the boundary position is ambiguous — Chrome's caret
      // affinity can pull the cursor back inside the wrapper, so we redirect
      // manually. Once the user has moved deeper into the text, typing
      // belongs in-place and a redirect would silently send the keystroke
      // to the end of the line.
      var startContainer = range.startContainer;
      if (
        startContainer.nodeType === Node.TEXT_NODE &&
        range.startOffset === 0 &&
        startContainer.previousSibling &&
        this._isFormattingElement(startContainer.previousSibling)
      ) {
        return startContainer.previousSibling;
      }
      return null;
    },

    _normalizeDecorationSpans: function (wrapper, delim) {
      // Each `.leaf-syntax-decoration` span should hold exactly one delim
      // character. If a span got extended (typing at its edge), split off
      // the extra into a sibling text node, preserving the cursor.
      // Returns false if any span no longer contains the delim character
      // (the user really did break it — caller should unwrap).
      // All our delimiters are repeating-char strings (`**`, `*`, `~~`,
      // `||`, `` ` ``), so a single delim char identifies them all.
      var delimChar = delim[0];
      var sel = window.getSelection();
      var rangeContainer = null;
      var rangeOffset = 0;
      if (sel.rangeCount > 0) {
        var r = sel.getRangeAt(0);
        rangeContainer = r.startContainer;
        rangeOffset = r.startOffset;
      }
      var rangeMoved = false;

      // Direct children only — querySelectorAll would also pick up nested
      // wrappers' decoration spans.
      var spans = Array.prototype.filter.call(
        wrapper.children,
        function (c) {
          return (
            c.classList && c.classList.contains("leaf-syntax-decoration")
          );
        },
      );
      for (var i = 0; i < spans.length; i++) {
        var span = spans[i];
        var text = span.textContent;
        if (text === delimChar) continue;
        if (text.length === 0) return false;

        var startsWithDelim = text[0] === delimChar;
        var endsWithDelim = text[text.length - 1] === delimChar;
        if (!startsWithDelim && !endsWithDelim) return false;

        var cursorInSpan = rangeContainer === span;
        var cursorOff = cursorInSpan ? rangeOffset : -1;

        if (startsWithDelim) {
          var extraTextStart = text.slice(1);
          span.textContent = delimChar;
          var afterNode = document.createTextNode(extraTextStart);
          if (span.nextSibling) {
            span.parentNode.insertBefore(afterNode, span.nextSibling);
          } else {
            span.parentNode.appendChild(afterNode);
          }
          if (cursorInSpan && cursorOff > 1) {
            rangeContainer = afterNode;
            rangeOffset = Math.min(cursorOff - 1, extraTextStart.length);
            rangeMoved = true;
          } else if (cursorInSpan) {
            rangeOffset = Math.min(cursorOff, 1);
          }
        } else {
          var extraTextEnd = text.slice(0, -1);
          span.textContent = delimChar;
          var beforeNode = document.createTextNode(extraTextEnd);
          span.parentNode.insertBefore(beforeNode, span);
          if (cursorInSpan) {
            if (cursorOff <= extraTextEnd.length) {
              rangeContainer = beforeNode;
              rangeOffset = cursorOff;
            } else {
              rangeContainer = span;
              rangeOffset = 1;
            }
            rangeMoved = true;
          }
        }
      }

      if (rangeMoved && rangeContainer) {
        try {
          var newRange = document.createRange();
          newRange.setStart(rangeContainer, rangeOffset);
          newRange.collapse(true);
          sel.removeAllRanges();
          sel.addRange(newRange);
        } catch (_e) {
          /* range invalidated by mutation — leave selection alone */
        }
      }
      return true;
    },

    _unwrapTamperedFormatting: function (wrapper) {
      var parent = wrapper.parentNode;
      if (!parent) return;
      this._syntaxMutating = true;
      try {
        var child = wrapper.firstChild;
        while (child) {
          var next = child.nextSibling;
          if (
            !(
              child.nodeType === Node.ELEMENT_NODE &&
              child.classList &&
              child.classList.contains("leaf-syntax-decoration")
            )
          ) {
            parent.insertBefore(child, wrapper);
          }
          child = next;
        }
        parent.removeChild(wrapper);
        if (this._decoratedAncestors) {
          var idx = this._decoratedAncestors.indexOf(wrapper);
          if (idx !== -1) this._decoratedAncestors.splice(idx, 1);
        }
      } finally {
        this._syntaxMutating = false;
      }
    },

    _clearSyntaxDecoration: function () {
      if (!this._decoratedAncestors || this._decoratedAncestors.length === 0) {
        return;
      }
      this._syntaxMutating = true;
      try {
        for (var i = 0; i < this._decoratedAncestors.length; i++) {
          this._removeDelimitersFrom(this._decoratedAncestors[i]);
        }
      } finally {
        this._syntaxMutating = false;
      }
      this._decoratedAncestors = [];
    },

    // Heading decoration: when the cursor sits inside a `<h1>`–`<h6>`,
    // prepend leading `# `…`###### ` decoration spans so the markdown
    // shorthand is visible the same way `**bold**` markers are. Only the
    // leading delimiter is shown — markdown headings have no closing.
    _updateHeadingDecoration: function () {
      // The `# ` / `## ` / etc. markers are a hybrid-mode feature only.
      // In plain visual mode they'd surface as literal characters at
      // the start of every heading the cursor enters.
      if (this._mode !== "hybrid") {
        this._clearHeadingDecoration();
        return;
      }
      var sel = window.getSelection();
      if (!sel.rangeCount) {
        this._clearHeadingDecoration();
        return;
      }
      if (this._syntaxMutating) return;

      var node = sel.anchorNode;
      var heading = null;
      while (node && node !== this._visualEl) {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          node.tagName &&
          /^h[1-6]$/i.test(node.tagName)
        ) {
          heading = node;
          break;
        }
        node = node.parentNode;
      }

      if (this._decoratedHeading === heading) return;

      this._syntaxMutating = true;
      try {
        if (this._decoratedHeading && this._decoratedHeading.isConnected) {
          this._removeHeadingDecorationFrom(this._decoratedHeading);
        }
        if (heading) {
          this._addHeadingDecorationTo(heading);
        }
        this._decoratedHeading = heading || null;
      } finally {
        this._syntaxMutating = false;
      }
    },

    _clearHeadingDecoration: function () {
      if (!this._decoratedHeading) return;
      this._syntaxMutating = true;
      try {
        if (this._decoratedHeading.isConnected) {
          this._removeHeadingDecorationFrom(this._decoratedHeading);
        }
      } finally {
        this._syntaxMutating = false;
      }
      this._decoratedHeading = null;
    },


    _addHeadingDecorationTo: function (heading) {
      var level = parseInt(heading.tagName.slice(1), 10);
      if (!level || level < 1 || level > 6) return;
      var prefix = "";
      for (var n = 0; n < level; n++) prefix += "#";
      prefix += " ";
      // Insert one span per char so the cursor can step through each
      // glyph the same way the inline `**`/`~~` markers work. Reverse so
      // the final order is `# space` (or `## space`, etc.) at the start.
      for (var i = prefix.length - 1; i >= 0; i--) {
        var span = document.createElement("span");
        span.className = "leaf-syntax-decoration";
        span.setAttribute("data-leaf-syntax-decoration", "");
        span.textContent = prefix[i];
        heading.insertBefore(span, heading.firstChild);
      }

      // If the cursor was at heading element-offset 0 (the post-
      // conversion empty case), it's now BEFORE the spans we just
      // prepended. Element-offset cursors are also vulnerable to
      // caret-affinity snapping into the trailing decoration span, which
      // would make typed chars extend that span (and inherit its
      // grayed-out decoration styling). Insert a fresh empty text node
      // right after the decoration spans and anchor the cursor INSIDE
      // it, so typing extends THIS text (heading-styled) instead.
      var sel = window.getSelection();
      if (sel.rangeCount > 0) {
        var range = sel.getRangeAt(0);
        if (
          range.collapsed &&
          range.startContainer === heading &&
          range.startOffset === 0
        ) {
          var anchorNode = heading.childNodes[prefix.length] || null;
          // Zero-width space — Chrome's caret affinity at an EMPTY text
          // node tends to snap into the adjacent decoration span (so the
          // typed letters extend that gray span, then get stripped on
          // sync). A ZWSP gives the text node real content so the cursor
          // sticks; htmlToMarkdown / mode-sync filter it back out.
          var placeholder = document.createTextNode("​");
          if (anchorNode) {
            heading.insertBefore(placeholder, anchorNode);
          } else {
            heading.appendChild(placeholder);
          }
          var newRange = document.createRange();
          newRange.setStart(placeholder, 1);
          newRange.collapse(true);
          try {
            sel.removeAllRanges();
            sel.addRange(newRange);
          } catch (_e) {
            /* range invalidated mid-frame — skip */
          }
        }
      }
    },

    _removeHeadingDecorationFrom: function (heading) {
      // Only remove DIRECT-child decoration spans — descendant ones come
      // from inline formatting (e.g., `**bold**` inside the heading) and
      // are owned by the inline decoration system.
      var children = Array.prototype.slice.call(heading.children);
      for (var i = 0; i < children.length; i++) {
        var c = children[i];
        if (c.classList && c.classList.contains("leaf-syntax-decoration")) {
          heading.removeChild(c);
        }
      }
    },

    // When the user edits the `# ` decoration block of a heading (adds
    // or removes hash chars), retag the heading to the matching level.
    // Counts `#` chars in the leading sequence (decoration spans + any
    // typed text) up to the first space; that count becomes the new
    // level (clamped 1–6).
    _maybeAdjustHeadingLevel: function () {
      if (!this._decoratedHeading || !this._decoratedHeading.isConnected) {
        return;
      }
      if (this._syntaxMutating) return;
      var heading = this._decoratedHeading;
      var match = heading.tagName
        ? heading.tagName.toLowerCase().match(/^h([1-6])$/)
        : null;
      if (!match) return;
      var oldLevel = parseInt(match[1], 10);

      var hashCount = 0;
      var leadingChildren = [];
      var done = false;
      var c = heading.firstChild;
      while (c && !done) {
        var nodeText = null;
        if (c.nodeType === Node.TEXT_NODE) {
          nodeText = c.textContent;
        } else if (
          c.nodeType === Node.ELEMENT_NODE &&
          this._isDecorationSpan(c)
        ) {
          nodeText = c.textContent;
        }
        if (nodeText === null) break;

        var allConsumed = true;
        for (var i = 0; i < nodeText.length; i++) {
          var ch = nodeText[i];
          if (ch === "#") {
            hashCount++;
          } else if (ch === " ") {
            done = true;
            break;
          } else {
            allConsumed = false;
            break;
          }
        }

        if (allConsumed || done) {
          leadingChildren.push(c);
          c = c.nextSibling;
        } else {
          break;
        }
      }

      if (hashCount === 0) return;
      var newLevel = Math.min(hashCount, 6);
      if (newLevel === oldLevel) return;

      this._retagHeading(heading, "h" + newLevel, leadingChildren);
    },

    _retagHeading: function (oldHeading, newTag, leadingChildren) {
      this._syntaxMutating = true;
      try {
        var newHeading = document.createElement(newTag);
        var newLevel = parseInt(newTag.slice(1), 10) || 1;

        // Drop the old leading section (decorations + any user-typed
        // hash/space text). The new decoration set is added fresh below.
        for (var i = 0; i < leadingChildren.length; i++) {
          if (leadingChildren[i].parentNode === oldHeading) {
            oldHeading.removeChild(leadingChildren[i]);
          }
        }

        while (oldHeading.firstChild) {
          newHeading.appendChild(oldHeading.firstChild);
        }
        oldHeading.parentNode.replaceChild(newHeading, oldHeading);
        this._decoratedHeading = newHeading;

        this._addHeadingDecorationTo(newHeading);

        // Park the cursor at the end of the last `#` decoration span
        // (i.e., `###|` for h3) so further `#`/Backspace edits keep
        // adjusting the level naturally without bouncing the cursor to
        // the heading's content area.
        var sel = window.getSelection();
        var lastHashSpan = newHeading.children[newLevel - 1];
        if (
          lastHashSpan &&
          lastHashSpan.firstChild &&
          lastHashSpan.firstChild.nodeType === Node.TEXT_NODE
        ) {
          var caret = document.createRange();
          caret.setStart(
            lastHashSpan.firstChild,
            lastHashSpan.firstChild.textContent.length,
          );
          caret.collapse(true);
          try {
            sel.removeAllRanges();
            sel.addRange(caret);
          } catch (_e) {
            /* range invalidated mid-frame — skip */
          }
        }
      } finally {
        this._syntaxMutating = false;
      }
    },

    _delimiterFor: function (el) {
      if (!el || !el.tagName) return null;
      var tag = el.tagName.toLowerCase();
      if (tag === "strong" || tag === "b") return "**";
      if (tag === "em" || tag === "i") return "*";
      if (tag === "s" || tag === "del" || tag === "strike") return "~~";
      if (tag === "code") return "`";
      if (
        tag === "span" &&
        el.classList &&
        el.classList.contains("leaf-spoiler")
      ) {
        return "||";
      }
      return null;
    },

    // True when `el` has at least one direct child carrying the
    // `.leaf-source-marker` class — i.e. it's a wrapper built by
    // `_buildSourceFragment` whose marker text is already in the DOM
    // and shouldn't be synthesized again by the inline serializer.
    _hasMarkerChildren: function (el) {
      if (!el || !el.childNodes) return false;
      for (var i = 0; i < el.childNodes.length; i++) {
        var c = el.childNodes[i];
        if (
          c.nodeType === Node.ELEMENT_NODE &&
          c.classList &&
          c.classList.contains("leaf-source-marker")
        ) {
          return true;
        }
      }
      return false;
    },

    // -- Hybrid source-block engine --
    //
    // Replaces the old decoration-span approach. The model now is:
    //   - The DOM stores rendered HTML for every block EXCEPT the one
    //     currently containing the cursor (in hybrid mode).
    //   - The cursor's block carries its markdown source as literal text
    //     inside a `<p data-leaf-source="origTag">` wrapper. The user
    //     edits the source freely; broken syntax (`#$ header`, half-
    //     stripped `*test**`) stays as text and round-trips through
    //     `htmlToMarkdown` as a plain `<p>`.
    //   - On cursor-leave, the source text is parsed client-side and the
    //     block is swapped back to its rendered form.
    //
    // Only one block at a time is in source mode (`this._sourceBlock`).
    // Called from selectionchange.

    // Editable blocks: paragraphs and headings carry text the user can
    // edit as markdown source. Lists / blockquotes / pre / table cells
    // are deferred for now and stay in rendered form even when the
    // cursor is inside them.
    _isSourceModeBlock: function (block) {
      if (!block || !block.tagName) return false;
      // A block holding a preserved-tag chip (.leaf-atomic) must stay
      // rendered — swapping it to source turns the chip into raw <Hero/>
      // text the client can't re-wrap, so it never comes back. (Task items
      // DO enter source mode: `_enterSourceMode` shows their `[ ] ` marker
      // and `_exitSourceMode` re-parses it back into a checkbox.)
      if (block.querySelector && block.querySelector(".leaf-atomic")) return false;
      var tag = block.tagName.toLowerCase();
      return (
        tag === "p" || tag === "h1" || tag === "h2" || tag === "h3" ||
        tag === "h4" || tag === "h5" || tag === "h6" || tag === "li"
      );
    },

    _updateSourceBlock: function () {
      if (this._mode !== "hybrid") {
        this._exitAllSourceMode();
        return;
      }
      if (this._syntaxMutating) return;

      var sel = window.getSelection();
      if (!sel.rangeCount) {
        this._exitAllSourceMode();
        return;
      }

      // An active (non-collapsed) selection must never be disturbed:
      // entering, exiting, or refreshing source mode replaces the
      // block's text nodes, which collapses a selection the user is
      // mid-way through dragging (selecting across a formatted run made
      // its markers pop in and reset the selection). Freeze every
      // source-mode transition until the selection collapses again —
      // markers only reveal for a caret, never during a selection.
      if (!sel.isCollapsed) return;

      var anchor = sel.anchorNode;
      if (!anchor || !this._visualEl.contains(anchor)) return;

      var block = this._getCurrentBlock();
      // Cursor in the same block we already opened — re-run the
      // refresh so the active-inline-match toggle tracks the cursor
      // as it moves between formatted runs (`**bold**`, `*italic*`, …)
      // and plain text in the same paragraph.
      if (block && block === this._sourceBlock) {
        this._refreshSourceBlock();
        return;
      }

      // Cursor moved to a different block; close out the old one first.
      if (this._sourceBlock && this._sourceBlock.isConnected) {
        this._exitSourceMode(this._sourceBlock);
      }
      this._sourceBlock = null;
      this._lastSourceStateKey = null;
      this._activeMatchKey = null;

      if (block && this._isSourceModeBlock(block)) {
        if (block.hasAttribute("data-leaf-source")) {
          // Already a source block (its `_sourceBlock` tracking was lost,
          // e.g. across a re-render or split). Adopt it and refresh —
          // re-running `_enterSourceMode` would prepend a SECOND `- ` /
          // `# ` marker and accumulate them on every cursor entry.
          this._sourceBlock = block;
          this._lastSourceStateKey = null;
          this._activeMatchKey = null;
          this._refreshSourceBlock();
        } else {
          var newBlock = this._enterSourceMode(block);
          if (newBlock) this._sourceBlock = newBlock;
        }
      }
    },

    // Source-mode Backspace / Delete: delete one char of the block's
    // source string at the cursor's text-offset, then let
    // `_refreshSourceBlock` rebuild the marker / wrapper structure from
    // the new source. Returns true if it consumed the keystroke.
    // Non-collapsed selections fall through so the browser's default
    // selection delete still works (covers most click+drag delete).
    // The span a word-delete removes from `text` at `offset`, as
    // [start, end). Editor-standard semantics: skip the whitespace touching
    // the caret, then take the run of word characters OR the run of symbols
    // — whichever the first non-space character belongs to. ZWSP/FEFF
    // placeholders count as skippable space so a checkbox's caret anchor
    // can't make the word immortal.
    _wordDeleteSpan: function (text, offset, backward) {
      var space = /[\s\u00a0\u200B\uFEFF]/;
      var wordChar = /[\p{L}\p{N}_]/u;

      if (backward) {
        var start = offset;
        while (start > 0 && space.test(text.charAt(start - 1))) start--;
        if (start > 0) {
          var backIsWord = wordChar.test(text.charAt(start - 1));
          while (
            start > 0 &&
            !space.test(text.charAt(start - 1)) &&
            wordChar.test(text.charAt(start - 1)) === backIsWord
          ) {
            start--;
          }
        }
        return [start, offset];
      }

      var end = offset;
      while (end < text.length && space.test(text.charAt(end))) end++;
      if (end < text.length) {
        var fwdIsWord = wordChar.test(text.charAt(end));
        while (
          end < text.length &&
          !space.test(text.charAt(end)) &&
          wordChar.test(text.charAt(end)) === fwdIsWord
        ) {
          end++;
        }
      }
      return [offset, end];
    },

    _maybeHandleSourceDelete: function (backspace, word) {
      var sel = window.getSelection();
      if (!sel.rangeCount) return false;
      var range = sel.getRangeAt(0);
      if (!range.collapsed) return false;
      if (!this._sourceBlock.contains(range.startContainer)) return false;

      var cursorOffset = this._textOffsetInBlock(
        this._sourceBlock,
        range.startContainer,
        range.startOffset
      );
      var sourceText = this._sourceBlock.textContent || "";

      if (
        sourceText.length === 0 &&
        this._maybeRemoveEmptySourceListOrQuoteBlock(backspace)
      ) {
        return true;
      }

      var newSource;
      var newCursor;
      if (backspace) {
        // Step past placeholders to the nearest visible character. Deleting
        // only a ZWSP changes nothing the user can see, and the rebuild
        // seats a fresh one — Backspace pressed forever, checkbox immortal.
        var visibleCut = cursorOffset;
        while (
          visibleCut > 0 &&
          /[\u200B\uFEFF]/.test(sourceText.charAt(visibleCut - 1))
        ) {
          visibleCut--;
        }

        if (visibleCut <= 0) {
          // Backspace at the very start of the editor's only block
          // would have Chrome delete the `<p>` itself, leaving the
          // contenteditable with no block wrapper — the drag handle
          // disappears and subsequent typing lands in a bare text
          // node. Swallow the keystroke in that case; everywhere else
          // (block has a previous sibling to merge into), let the
          // browser's default merge behavior run.
          if (
            sourceText.length === 0 &&
            !this._sourceBlock.previousElementSibling &&
            !this._sourceBlock.nextElementSibling
          ) {
            return true;
          }
          return false;
        }
        if (word) {
          var backSpan = this._wordDeleteSpan(sourceText, visibleCut, true);
          newSource =
            sourceText.slice(0, backSpan[0]) + sourceText.slice(cursorOffset);
          newCursor = backSpan[0];
        } else {
          newSource =
            sourceText.slice(0, visibleCut - 1) +
            sourceText.slice(cursorOffset);
          newCursor = visibleCut - 1;
        }
      } else {
        if (cursorOffset >= sourceText.length) {
          // Delete at the end of a source-mode block. Chrome's default
          // tries to merge with the next block but doesn't always
          // succeed for source-mode `<li>` and blockquote-`<p>` (the
          // marker spans and `data-leaf-source` attributes confuse its
          // default merge), so do the merge explicitly when there's a
          // sibling worth merging.
          var delHostBlock = this._sourceBlock;
          var delContextParent = delHostBlock.parentNode;
          var delHostTag = delHostBlock.tagName.toLowerCase();
          var delInList = delHostTag === "li";
          var delInBlockquote =
            delHostTag === "p" &&
            delContextParent &&
            delContextParent.tagName &&
            delContextParent.tagName.toLowerCase() === "blockquote";
          if (delInList || delInBlockquote) {
            var delNext = delHostBlock.nextElementSibling;
            if (
              delNext &&
              delNext.tagName &&
              delNext.tagName.toLowerCase() === delHostTag
            ) {
              // Merge the next sibling's content into the current
              // source block, then drop the now-empty sibling.
              this._syntaxMutating = true;
              try {
                var delMergedSource =
                  sourceText + (delNext.textContent || "");
                while (this._sourceBlock.firstChild) {
                  this._sourceBlock.removeChild(this._sourceBlock.firstChild);
                }
                if (delMergedSource.length === 0) {
                  this._sourceBlock.innerHTML = "<br>";
                } else {
                  this._sourceBlock.appendChild(
                    document.createTextNode(delMergedSource)
                  );
                }
                delNext.parentNode.removeChild(delNext);
                var delMergedRange = document.createRange();
                if (
                  this._sourceBlock.firstChild &&
                  this._sourceBlock.firstChild.nodeType === Node.TEXT_NODE
                ) {
                  var delClamped = Math.max(
                    0,
                    Math.min(
                      cursorOffset,
                      this._sourceBlock.firstChild.textContent.length
                    )
                  );
                  delMergedRange.setStart(
                    this._sourceBlock.firstChild,
                    delClamped
                  );
                } else {
                  delMergedRange.setStart(this._sourceBlock, 0);
                }
                delMergedRange.collapse(true);
                sel.removeAllRanges();
                sel.addRange(delMergedRange);
              } finally {
                this._syntaxMutating = false;
              }
              this._lastSourceStateKey = null;
              this._activeMatchKey = null;
              this._refreshSourceBlock();
              this._debouncedPushVisualChange();
              this._updateCounts();
              return true;
            }
          }
          return false;
        }
        if (word) {
          var fwdSpan = this._wordDeleteSpan(sourceText, cursorOffset, false);
          newSource =
            sourceText.slice(0, cursorOffset) + sourceText.slice(fwdSpan[1]);
        } else {
          newSource =
            sourceText.slice(0, cursorOffset) +
            sourceText.slice(cursorOffset + 1);
        }
        newCursor = cursorOffset;
      }

      this._syntaxMutating = true;
      try {
        while (this._sourceBlock.firstChild) {
          this._sourceBlock.removeChild(this._sourceBlock.firstChild);
        }
        if (newSource.length === 0) {
          this._sourceBlock.innerHTML = "<br>";
        } else {
          this._sourceBlock.appendChild(document.createTextNode(newSource));
        }
        var newRange = document.createRange();
        if (
          this._sourceBlock.firstChild &&
          this._sourceBlock.firstChild.nodeType === Node.TEXT_NODE
        ) {
          var clamped = Math.max(
            0,
            Math.min(newCursor, this._sourceBlock.firstChild.textContent.length)
          );
          newRange.setStart(this._sourceBlock.firstChild, clamped);
        } else {
          newRange.setStart(this._sourceBlock, 0);
        }
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      } finally {
        this._syntaxMutating = false;
      }

      // Force a rebuild — the manual mutation above didn't fire an input
      // event, so the dedupe key isn't naturally invalidated. Also fire
      // the same pushVisualChange / count-update path `_onVisualInput`
      // would have triggered.
      this._lastSourceStateKey = null;
      this._activeMatchKey = null;
      this._refreshSourceBlock();
      this._debouncedPushVisualChange();
      this._updateCounts();
      return true;
    },

    _maybeRemoveEmptySourceListOrQuoteBlock: function (backspace) {
      var block = this._sourceBlock;
      if (!block || !block.parentNode) return false;

      var tag = block.tagName ? block.tagName.toLowerCase() : "";
      var parent = block.parentNode;
      var parentTag = parent.tagName ? parent.tagName.toLowerCase() : "";
      var inList = tag === "li" && (parentTag === "ul" || parentTag === "ol");
      var inBlockquote = tag === "p" && parentTag === "blockquote";
      if (!inList && !inBlockquote) return false;

      var prev = block.previousElementSibling;
      var next = block.nextElementSibling;
      var container = parent;
      var grandparent = container.parentNode;
      if (!grandparent) return false;

      var target = null;
      var placeAtEnd = false;
      if (backspace && prev) {
        target = prev;
        placeAtEnd = true;
      } else if (next) {
        target = next;
      } else if (prev) {
        target = prev;
        placeAtEnd = true;
      }

      this._syntaxMutating = true;
      try {
        container.removeChild(block);

        if (!target) {
          target = document.createElement("p");
          target.appendChild(document.createElement("br"));
          grandparent.replaceChild(target, container);
        } else if (!container.firstChild) {
          grandparent.removeChild(container);
        }

        var range = document.createRange();
        var textNode = placeAtEnd
          ? this._lastTextDescendant(target)
          : this._firstTextDescendant(target);
        if (textNode) {
          range.setStart(
            textNode,
            placeAtEnd ? textNode.textContent.length : 0
          );
        } else {
          range.setStart(target, placeAtEnd ? target.childNodes.length : 0);
        }
        range.collapse(true);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);

        this._sourceBlock = null;
        this._lastSourceStateKey = null;
        this._activeMatchKey = null;
      } finally {
        this._syntaxMutating = false;
      }

      this._debouncedPushVisualChange();
      this._updateCounts();
      return true;
    },

    // Source-mode Enter: split the block's source string at the cursor
    // offset. The first half stays in the original block which then
    // exits source mode (rendered to HTML); the second half goes into
    // a new `<p data-leaf-source>` inserted right after, which becomes
    // the new active source block. Cursor lands at the start of the
    // new block.
    _maybeHandleSourceEnter: function () {
      var sel = window.getSelection();
      if (!sel.rangeCount) return false;
      var range = sel.getRangeAt(0);
      if (!range.collapsed) return false;
      if (!this._sourceBlock.contains(range.startContainer)) return false;

      var cursorOffset = this._textOffsetInBlock(
        this._sourceBlock,
        range.startContainer,
        range.startOffset
      );
      var sourceText = this._sourceBlock.textContent || "";
      var firstHalf = sourceText.slice(0, cursorOffset);
      var secondHalf = sourceText.slice(cursorOffset);

      var oldBlock = this._sourceBlock;
      var parent = oldBlock.parentNode;
      if (!parent) return false;

      var isLi = oldBlock.tagName.toLowerCase() === "li";

      // A source-mode `<li>` always carries its revealed `- ` / `N. `
      // marker (plus an optional `[ ] ` / `[x] ` checkbox), so "the item
      // is empty" means "only the marker remains", not "textContent is
      // empty". Derive the marker the new split-off item should carry
      // (next number for `ol`, a fresh unchecked `[ ] ` for tasks) and
      // whether the current item has no content past the marker.
      var liMarkerPrefix = "";
      var liContentEmpty = false;
      if (isLi) {
        var liNormFirst = firstHalf.replace(/\u00a0/g, " ");
        var liNormFull = sourceText.replace(/\u00a0/g, " ");
        var markerM = liNormFirst.match(/^(- |(\d+)\. )/);
        if (markerM) {
          liMarkerPrefix = markerM[2]
            ? parseInt(markerM[2], 10) + 1 + ". "
            : "- ";
          if (/^\[[ xX]?\] /.test(liNormFirst.slice(markerM[0].length))) {
            liMarkerPrefix += "[ ] ";
          }
        } else {
          liMarkerPrefix = "- ";
        }
        var liContentRest = liNormFull
          .replace(/^(- |\d+\. )/, "")
          .replace(/^\[([ xX]?)\] /, "")
          // The caret-home placeholder is scaffolding, not content. Counted,
          // it made every "empty" task row read as occupied: Enter split
          // instead of exiting, minting a fresh item each press.
          .replace(/[\u200B\uFEFF]/g, "");
        liContentEmpty = liContentRest.length === 0;
      }

      // List item: an empty source-mode `<li>` + Enter exits the list, but
      // as the keyboard escape from a list — at the end of one and in the
      // middle of one alike, the way Obsidian reads it. Drop the empty item
      // and place a fresh `<p>` where it stood; items still below move into
      // a new list of the same kind, so a divided list visibly becomes
      // list / paragraph / list.
      if (
        isLi &&
        liContentEmpty &&
        parent.tagName &&
        (parent.tagName.toLowerCase() === "ul" ||
          parent.tagName.toLowerCase() === "ol")
      ) {
        var liExitList = parent;
        var liExitGrandparent = liExitList.parentNode;
        if (liExitGrandparent) {
          this._syntaxMutating = true;
          try {
            // Items after the empty `<li>` move into a new list of
            // the same kind so we get list/<p>/list, not list/<p>.
            var liTrailing = [];
            var liNext = oldBlock.nextElementSibling;
            while (liNext) {
              var liAfterNext = liNext.nextElementSibling;
              liTrailing.push(liNext);
              liNext = liAfterNext;
            }

            liExitList.removeChild(oldBlock);

            var liExitP = document.createElement("p");
            liExitP.appendChild(document.createElement("br"));
            liExitGrandparent.insertBefore(liExitP, liExitList.nextSibling);

            if (liTrailing.length > 0) {
              var liSecondList = document.createElement(liExitList.tagName);
              for (var lt = 0; lt < liTrailing.length; lt++) {
                liSecondList.appendChild(liTrailing[lt]);
              }
              liExitGrandparent.insertBefore(
                liSecondList,
                liExitP.nextSibling
              );
            }

            if (!liExitList.firstChild) {
              liExitGrandparent.removeChild(liExitList);
            }

            var liExitRange = document.createRange();
            liExitRange.setStart(liExitP, 0);
            liExitRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(liExitRange);
            this._sourceBlock = null;
            this._lastSourceStateKey = null;
            this._activeMatchKey = null;
          } finally {
            this._syntaxMutating = false;
          }
          this._debouncedPushVisualChange();
          this._updateCounts();
          return true;
        }
      }

      // Blockquote: empty `<p>` source block inside `<blockquote>` +
      // Enter exits the quote. Mirrors the list-item UX — Enter on a
      // non-empty line splits in place. If the empty line sits BETWEEN
      // siblings (mid-quote), split the `<blockquote>` into two so the
      // user gets `<blockquote>before</blockquote><p/><blockquote>after
      // </blockquote>` instead of leaking the "after" lines into a
      // sibling-less paragraph stuck below the original quote.
      if (
        sourceText.length === 0 &&
        oldBlock.tagName.toLowerCase() === "p" &&
        parent.tagName &&
        parent.tagName.toLowerCase() === "blockquote"
      ) {
        var bqExitQuote = parent;
        var bqExitGrandparent = bqExitQuote.parentNode;
        if (bqExitGrandparent) {
          this._syntaxMutating = true;
          try {
            // Collect anything after the empty `<p>` inside the quote
            // — those siblings become the contents of a new
            // `<blockquote>` placed below the exit paragraph.
            var bqTrailing = [];
            var bqNext = oldBlock.nextSibling;
            while (bqNext) {
              var bqAfterNext = bqNext.nextSibling;
              bqTrailing.push(bqNext);
              bqNext = bqAfterNext;
            }

            // Drop the empty `<p>` that triggered the exit.
            bqExitQuote.removeChild(oldBlock);

            // The exit paragraph itself.
            var bqExitP = document.createElement("p");
            bqExitP.appendChild(document.createElement("br"));
            bqExitGrandparent.insertBefore(
              bqExitP,
              bqExitQuote.nextSibling
            );

            // If we collected trailing nodes, move them into a fresh
            // `<blockquote>` placed AFTER the exit paragraph.
            if (bqTrailing.length > 0) {
              var bqSecond = document.createElement("blockquote");
              for (var bt = 0; bt < bqTrailing.length; bt++) {
                bqSecond.appendChild(bqTrailing[bt]);
              }
              bqExitGrandparent.insertBefore(
                bqSecond,
                bqExitP.nextSibling
              );
            }

            // Drop the original quote if the exit emptied it.
            if (!bqExitQuote.firstChild) {
              bqExitGrandparent.removeChild(bqExitQuote);
            }

            var bqExitRange = document.createRange();
            bqExitRange.setStart(bqExitP, 0);
            bqExitRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(bqExitRange);
            this._sourceBlock = null;
            this._lastSourceStateKey = null;
            this._activeMatchKey = null;
          } finally {
            this._syntaxMutating = false;
          }
          this._debouncedPushVisualChange();
          this._updateCounts();
          return true;
        }
      }

      this._syntaxMutating = true;
      try {
        // Render the old block's first-half source. For `<li>` source
        // blocks the rendered form is a `<li>` wrapping the inline
        // body (block-level patterns don't apply); for everything
        // else we route through `_renderBlockFromSource` which handles
        // heading / HR / list-block detection.
        var renderedOld;
        if (isLi) {
          // Strip the revealed `- ` / `N. ` marker (and re-form a checkbox)
          // via the shared helper so the split-off rendered <li> never
          // keeps a literal marker that would double on the next entry.
          renderedOld = this._renderListItemFromSource(firstHalf);
        } else {
          renderedOld = this._renderBlockFromSource(firstHalf);
        }
        parent.replaceChild(renderedOld, oldBlock);

        // Create the new source block. `<li>` source items beget
        // another `<li>` so the list keeps its structure; everything
        // else opens a fresh `<p>` regardless of what tag the old
        // block was (the user can type `# ` etc. to retag).
        var newBlock = document.createElement(isLi ? "li" : "p");
        newBlock.setAttribute("data-leaf-source", isLi ? "li" : "p");
        // A new `<li>` half re-reveals its own `- ` / `N. ` marker so it
        // reads the same as any other cursored list line; the caret lands
        // right after the marker, at the start of the carried-over content.
        var newSourceText = isLi ? liMarkerPrefix + secondHalf : secondHalf;
        var newCaretOffset = isLi ? liMarkerPrefix.length : 0;
        if (newSourceText.length === 0) {
          newBlock.innerHTML = "<br>";
        } else {
          newBlock.appendChild(document.createTextNode(newSourceText));
        }
        parent.insertBefore(newBlock, renderedOld.nextSibling);

        // Cursor at the start of the new block's content (past the marker).
        var newRange = document.createRange();
        if (
          newBlock.firstChild &&
          newBlock.firstChild.nodeType === Node.TEXT_NODE
        ) {
          newRange.setStart(
            newBlock.firstChild,
            Math.min(newCaretOffset, newBlock.firstChild.textContent.length)
          );
        } else {
          newRange.setStart(newBlock, 0);
        }
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);

        this._sourceBlock = newBlock;
        this._lastSourceStateKey = null;
        this._activeMatchKey = null;
      } finally {
        this._syntaxMutating = false;
      }

      this._refreshSourceBlock();
      this._debouncedPushVisualChange();
      this._updateCounts();
      return true;
    },

    _exitAllSourceMode: function () {
      if (this._sourceBlock && this._sourceBlock.isConnected) {
        this._exitSourceMode(this._sourceBlock);
      }
      this._sourceBlock = null;
      this._lastSourceStateKey = null;
      this._activeMatchKey = null;
    },

    // Convert `block` (rendered HTML) → `<p data-leaf-source="origTag">`
    // containing its markdown source as plain text. Preserves the
    // cursor's logical position by walking the block, accumulating
    // source text, and recording the source-offset when we pass the
    // cursor node.
    _enterSourceMode: function (block) {
      if (this._syntaxMutating) return null;
      var sel = window.getSelection();
      if (!sel.rangeCount) return null;
      var range = sel.getRangeAt(0);

      var origTag = block.tagName.toLowerCase();
      var blockPrefix = this._blockSourcePrefix(origTag);
      // List item: surface its FULL markdown source — the `- ` / `N. ` list
      // marker (plus a `[ ] ` / `[x] ` checkbox for task items) — so the
      // whole line is editable and breakable like any other source.
      var isTaskLi =
        origTag === "li" &&
        block.classList &&
        block.classList.contains("leaf-task");
      var liMarker = "";
      if (origTag === "li") {
        var listParent = block.parentNode;
        if (
          listParent &&
          listParent.tagName &&
          listParent.tagName.toLowerCase() === "ol"
        ) {
          // Position within the list PLUS where the list starts counting —
          // revealing `1. ` for the first item of an `<ol start="19">` both
          // lied about what the item shows and, once serialized, renumbered
          // the document.
          var liOffset = Array.prototype.indexOf.call(listParent.children, block);
          var liStartAttr = parseInt(listParent.getAttribute("start"), 10);
          var liFirst = isNaN(liStartAttr) ? 1 : liStartAttr;

          liMarker = liOffset + liFirst + ". ";
        } else {
          liMarker = "- ";
        }
        if (isTaskLi) {
          liMarker +=
            block.getAttribute("data-checked") === "true" ? "[x] " : "[ ] ";
        }
      }
      var trace = this._serializeBlockInline(
        block,
        range.startContainer,
        range.startOffset
      );

      var sourceText = blockPrefix + liMarker + trace.source;
      var caretOffset = blockPrefix.length + liMarker.length + trace.cursorOffset;

      // Drop a `<p data-leaf-source>` with just the raw source text as
      // a single text node; `_refreshSourceBlock` (called immediately
      // below) does the hybrid active/inactive split based on the new
      // cursor position. For `<li>` we keep the `<li>` tag so it stays
      // a valid child of its `<ul>` / `<ol>` parent and the list
      // bullet / number keeps rendering.
      var sourceBlock = document.createElement(origTag === "li" ? "li" : "p");
      sourceBlock.setAttribute("data-leaf-source", origTag);
      if (isTaskLi) {
        // Keep the task class so the bullet stays hidden while the `[ ] `
        // marker is shown as editable source.
        sourceBlock.classList.add("leaf-task");
        sourceBlock.setAttribute(
          "data-checked",
          block.getAttribute("data-checked") || "false"
        );
      }
      if (sourceText.length === 0) {
        sourceBlock.innerHTML = "<br>";
      } else {
        sourceBlock.appendChild(document.createTextNode(sourceText));
      }

      this._syntaxMutating = true;
      try {
        block.parentNode.replaceChild(sourceBlock, block);

        // Park the cursor at the text-content offset; `_refreshSourceBlock`
        // will then convert that to the correct DOM target after it
        // rebuilds with marker spans + rendered inactive matches.
        var newRange = document.createRange();
        if (
          sourceBlock.firstChild &&
          sourceBlock.firstChild.nodeType === Node.TEXT_NODE
        ) {
          var clamped = Math.max(
            0,
            Math.min(caretOffset, sourceBlock.firstChild.textContent.length)
          );
          newRange.setStart(sourceBlock.firstChild, clamped);
        } else {
          newRange.setStart(sourceBlock, 0);
        }
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      } finally {
        this._syntaxMutating = false;
      }

      this._sourceBlock = sourceBlock;
      this._lastSourceStateKey = null;
      this._refreshSourceBlock();

      return sourceBlock;
    },

    // Build the children of a source block from a flat source string
    // plus marker ranges. Plain runs become text nodes; marker runs
    // become `<span class="leaf-source-marker">` so CSS can fade them.
    // Returns a `{ node, offset }` pointing at the DOM position that
    // corresponds to `caretOffset` in the source string.
    // Build the source-block's children from a source string + scan
    // info + the index of the "active" inline match (cursor is inside
    // it). The DOM structure is identical for active and inactive
    // inline matches: each match becomes its formatted element
    // (`<strong>`, `<em>`, …) wrapping `<span class="leaf-source-marker">`
    // opening marker + body text + closing marker. CSS keys on the
    // `.leaf-source-active` class to fade-in the markers for the
    // active match and hide them for the inactive ones — so the
    // source text (including `**`) is always present in textContent,
    // and the formatting (`<strong>`) is always present in the DOM.
    // Returns the caret target inside the new DOM.
    _buildSourceFragment: function (
      parent,
      sourceText,
      scan,
      caretOffset
    ) {
      // Chrome writes a typed trailing space as NBSP, and the serializer
      // preserves it so this rebuild can't eat the space under the caret.
      // But the moment typing continues past it the NBSP is INTERIOR — and
      // an NBSP is precisely the space a browser refuses to wrap at, so a
      // line of them grew past the box edge (horizontal scrollbar, breaks
      // landing on the few real spaces) until a mode switch or blur
      // re-rendered the block. Interior NBSPs become regular spaces here —
      // same length, so every scan offset and the caret offset stay valid —
      // and the trailing run is re-pinned as NBSP at the bottom of this
      // function, which is the one place Chrome genuinely needs it.
      sourceText = sourceText.replace(/\u00a0(?![\u0020\u00a0]*$)/g, "\u0020");

      var caretTarget = null;
      var self = this;

      function noteCaret(node, offset) {
        if (caretTarget === null) caretTarget = { node: node, offset: offset };
      }

      function appendPlain(host, text, sourceStart) {
        if (text.length === 0) return;
        var node = document.createTextNode(text);
        host.appendChild(node);
        if (
          caretTarget === null &&
          caretOffset >= sourceStart &&
          caretOffset <= sourceStart + text.length
        ) {
          noteCaret(node, caretOffset - sourceStart);
        }
      }

      function appendMarker(host, text, sourceStart, extraClass) {
        if (text.length === 0) return;
        var span = document.createElement("span");
        span.className =
          "leaf-source-marker" + (extraClass ? " " + extraClass : "");
        var textNode = document.createTextNode(text);
        span.appendChild(textNode);
        host.appendChild(span);
        if (
          caretTarget === null &&
          caretOffset >= sourceStart &&
          caretOffset <= sourceStart + text.length
        ) {
          noteCaret(textNode, caretOffset - sourceStart);
        }
      }

      // Block prefix marker. For headings it's the `# ` (always faded but
      // visible). For a list item it's the `- ` / `N. ` / `- [ ] ` marker,
      // tagged `.leaf-list-marker` so CSS can hide it behind the bullet /
      // checkbox until the cursor lands on it. Task items also get a
      // (contenteditable-false) checkbox box that shows while the marker
      // is hidden.
      if (scan.blockPrefixLen > 0) {
        if (scan.listMarker) {
          if (scan.isTask) {
            var taskBox = document.createElement("span");
            taskBox.className = "leaf-task-box";
            taskBox.setAttribute("contenteditable", "false");
            parent.appendChild(taskBox);
          }
          appendMarker(
            parent,
            sourceText.slice(0, scan.blockPrefixLen),
            0,
            "leaf-list-marker"
          );
        } else {
          appendMarker(parent, sourceText.slice(0, scan.blockPrefixLen), 0);
        }
      }

      // Render the rest of the source text recursively so nested
      // matches (e.g. `**bold**` inside `~~..~~`) build the right
      // ancestor chain. A wrapper is "active" (markers shown) when
      // the cursor is in its own range OR when any ancestor wrapper
      // is active — that way a cursor anywhere inside `~~..~~` keeps
      // both its `~~` markers AND the nested `**` markers visible,
      // so arrow-key navigation can step through every delimiter
      // char without the inner markers blinking out as the cursor
      // crosses an outer marker boundary.
      function appendSegment(host, segText, segOffset, ancestorActive) {
        var matches = self._scanInlineMatches(segText);
        var pos = 0;
        for (var i = 0; i < matches.length; i++) {
          var match = matches[i];
          if (match.start > pos) {
            appendPlain(host, segText.slice(pos, match.start), segOffset + pos);
          }

          var p = match.pattern;
          // Bold-italic — `***body***` — is the only pattern whose
          // markers split across two nested wrappers (`**` on strong,
          // `*` on em). Build it explicitly so each wrapper carries
          // its own `<span class="leaf-source-marker">` children; the
          // serializer's `_hasMarkerChildren` check then walks them
          // verbatim instead of synthesizing extra `*`s around the
          // em — which would otherwise compound `***body***` into
          // `****body****` and onward on every refresh.
          if (p.type === "boldItalic") {
            var biAbsStart = segOffset + match.start;
            var biAbsEnd = segOffset + match.end;
            var biActive =
              ancestorActive ||
              (caretOffset >= biAbsStart && caretOffset <= biAbsEnd);
            var biStrong = document.createElement("strong");
            var biEm = document.createElement("em");
            if (biActive) {
              biStrong.classList.add("leaf-source-active");
              biEm.classList.add("leaf-source-active");
            }
            // strong: opening `**` (chars 0–2 of the `***` delim).
            appendMarker(
              biStrong,
              segText.slice(match.start, match.start + 2),
              segOffset + match.start
            );
            biStrong.appendChild(biEm);
            // em: opening `*` (3rd char of `***`).
            appendMarker(
              biEm,
              segText.slice(match.start + 2, match.start + 3),
              segOffset + match.start + 2
            );
            // Body recursed into em.
            appendSegment(
              biEm,
              segText.slice(match.start + 3, match.end - 3),
              segOffset + match.start + 3,
              biActive
            );
            // em: closing `*`.
            appendMarker(
              biEm,
              segText.slice(match.end - 3, match.end - 2),
              segOffset + match.end - 3
            );
            // strong: closing `**`.
            appendMarker(
              biStrong,
              segText.slice(match.end - 2, match.end),
              segOffset + match.end - 2
            );
            host.appendChild(biStrong);
            pos = match.end;
            continue;
          }

          // Wiki link — `[[Target|Alias]]` shows only the alias while
          // inactive: `[[`, the target and the `|` sit in marker spans the
          // CSS hides, the alias is plain text, and the whole thing lives
          // in the same `.leaf-wikilink` span visual mode uses (so target
          // resolution decorates it, and the serializer's wikilink branch
          // emits `data-leaf-wikilink-raw` verbatim). Caret inside →
          // `.leaf-source-active` reveals every marker for raw editing.
          if (p.type === "wikilink") {
            var wAbsStart = segOffset + match.start;
            var wAbsEnd = segOffset + match.end;
            var wActive =
              ancestorActive ||
              (caretOffset >= wAbsStart && caretOffset <= wAbsEnd);
            var wSpan = document.createElement("span");
            wSpan.className = "leaf-wikilink";
            wSpan.setAttribute("data-leaf-wikilink", match.wikiTarget);
            if (match.wikiHeading) {
              wSpan.setAttribute("data-leaf-wikilink-heading", match.wikiHeading);
            }
            wSpan.setAttribute("data-leaf-wikilink-raw", match.raw);
            if (wActive) wSpan.classList.add("leaf-source-active");

            appendMarker(
              wSpan,
              segText.slice(match.start, match.start + 2),
              segOffset + match.start
            );
            if (match.wikiAlias) {
              var wAliasStart = match.end - 2 - match.wikiAlias.length;
              appendMarker(
                wSpan,
                segText.slice(match.start + 2, wAliasStart),
                segOffset + match.start + 2
              );
              appendPlain(
                wSpan,
                segText.slice(wAliasStart, match.end - 2),
                segOffset + wAliasStart
              );
            } else {
              appendPlain(
                wSpan,
                segText.slice(match.start + 2, match.end - 2),
                segOffset + match.start + 2
              );
            }
            appendMarker(
              wSpan,
              segText.slice(match.end - 2, match.end),
              segOffset + match.end - 2
            );

            host.appendChild(wSpan);
            self._scheduleWikiLinkResolve();
            pos = match.end;
            continue;
          }

          var wrapper;
          var bodyHost;
          if (p.type === "link") {
            wrapper = document.createElement("a");
            wrapper.setAttribute("href", match.href || "");
            bodyHost = wrapper;
          } else if (p.isSpoiler) {
            wrapper = document.createElement("span");
            wrapper.classList.add("leaf-spoiler");
            bodyHost = wrapper;
          } else {
            wrapper = document.createElement(p.tag);
            bodyHost = wrapper;
          }

          var absStart = segOffset + match.start;
          var absEnd = segOffset + match.end;
          var isActive =
            ancestorActive ||
            (caretOffset >= absStart && caretOffset <= absEnd);
          if (isActive) {
            wrapper.classList.add("leaf-source-active");
          }

          var openLen = match.openLen != null ? match.openLen : match.delim.length;
          var closeLen = match.closeLen != null ? match.closeLen : match.delim.length;
          var openEnd = match.start + openLen;
          var closeStart = match.end - closeLen;

          // Opening marker — direct child of the outer wrapper so
          // CSS `.leaf-source-active > .leaf-source-marker` matches.
          appendMarker(
            wrapper,
            segText.slice(match.start, openEnd),
            segOffset + match.start
          );

          // Body — recurse so a nested match like `~~**bold**~~`
          // builds the `<strong>` (and its markers) inside the
          // outer `<del>`. Pass `isActive` so descendants light up
          // alongside their active ancestor.
          if (bodyHost !== wrapper) wrapper.appendChild(bodyHost);
          appendSegment(
            bodyHost,
            segText.slice(openEnd, closeStart),
            segOffset + openEnd,
            isActive
          );

          // Closing marker.
          appendMarker(
            wrapper,
            segText.slice(closeStart, match.end),
            segOffset + closeStart
          );

          host.appendChild(wrapper);
          pos = match.end;
        }
        if (pos < segText.length) {
          appendPlain(host, segText.slice(pos), segOffset + pos);
        }
      }

      appendSegment(
        parent,
        sourceText.slice(scan.blockPrefixLen),
        scan.blockPrefixLen,
        false
      );

      // Cursor past everything we placed (typically end of block).
      if (caretTarget === null && parent.lastChild) {
        var last = parent.lastChild;
        var textNode =
          last.nodeType === Node.TEXT_NODE
            ? last
            : last.firstChild && last.firstChild.nodeType === Node.TEXT_NODE
              ? last.firstChild
              : null;
        if (textNode) {
          caretTarget = {
            node: textNode,
            offset: textNode.textContent.length
          };
        }
      }

      // A regular space at the very end of the block collapses in
      // contenteditable (Chrome renders it zero-width), so the caret
      // parks on top of it and the next keystroke overwrites it — e.g. a
      // freshly-revealed `- ` marker (empty body) loses its space and the
      // next char yields `-x` instead of `- x`. Pin trailing spaces as
      // NBSP so they hold width and the caret sits AFTER them. NBSP is
      // normalized back to a regular space on serialize / exit, so the
      // markdown stays clean. Same length, so the recorded caret offset
      // stays valid.
      var lastTail = this._lastTextDescendant(parent);
      if (lastTail && /\u0020$/.test(lastTail.textContent)) {
        lastTail.textContent = lastTail.textContent.replace(
          /\u0020+$/,
          function (run) {
            return run.replace(/\u0020/g, "\u00a0");
          }
        );
      }

      return caretTarget;
    },

    // Build a rendered `<li>` from a list-item source string. Strips the
    // revealed `- ` / `N. ` list marker (the <li>'s position in its
    // <ul>/<ol> already carries it — a rendered <li> must NEVER hold the
    // literal marker, or each re-entry into source mode would prepend
    // another one and accumulate `- - - …`). A `[ ] ` / `[x] ` marker
    // re-forms the checkbox box; an edited (broken) marker falls through
    // to plain text so the user can break a checkbox by editing its
    // source. Used by both `_exitSourceMode` and the Enter-split path so
    // the strip happens on every source→rendered transition.
    _renderListItemFromSource: function (sourceText) {
      var li = document.createElement("li");
      var src = sourceText || "";
      // Normalize NBSP to a regular space for MARKER detection only \u2014 the
      // body is sliced from the original so the user's spaces survive.
      var normalized = src.replace(/\u00a0/g, " ");
      var bodyStart = 0;
      var markerM = normalized.match(/^(- |\d+\. )/);
      if (markerM) {
        bodyStart = markerM[0].length;
        // A `[ ] ` / `[x] ` checkbox is only valid immediately after the
        // list marker. If the user broke the marker (deleted the `- `),
        // the brackets fall through as literal text. (`_exitSourceMode`
        // lifts a fully marker-less `<li>` out to a `<p>`; this helper is
        // reached with the marker present.)
        var taskM = normalized.slice(bodyStart).match(/^\[([ xX]?)\] /);
        if (taskM) {
          li.className = "leaf-task";
          li.setAttribute(
            "data-checked",
            taskM[1] === "x" || taskM[1] === "X" ? "true" : "false"
          );
          var box = document.createElement("span");
          box.className = "leaf-task-box";
          box.setAttribute("contenteditable", "false");
          li.appendChild(box);
          bodyStart += taskM[0].length;
        }
      }
      // Keep the body's NBSP and pin any leading / repeated regular spaces
      // the user added as NBSP, so HTML doesn't collapse them on render \u2014
      // i.e. spaces typed before/inside the item content stick.
      var text = this._preserveSpaces(src.slice(bodyStart));
      var nodes = this._buildFormattedFragment(text);
      for (var i = 0; i < nodes.length; i++) {
        li.appendChild(nodes[i]);
      }
      if (!li.firstChild) {
        li.innerHTML = "<br>";
      }
      return li;
    },

    // Pin spaces that HTML would otherwise collapse \u2014 a leading run, and any
    // run of two or more \u2014 as NBSP, so they render at their typed width.
    // Single inter-word spaces stay regular so normal line-wrapping works.
    _preserveSpaces: function (text) {
      return text
        .replace(/^[\u0020\u00a0]+/, function (run) {
          return run.replace(/\u0020/g, "\u00a0");
        })
        .replace(/[\u0020\u00a0]{2,}/g, function (run) {
          return run.replace(/\u0020/g, "\u00a0");
        });
    },

    _exitSourceMode: function (sourceBlock) {
      if (!sourceBlock || !sourceBlock.parentNode) return;
      if (this._syntaxMutating) return;

      var sourceText = sourceBlock.textContent || "";
      // Skip the placeholder `<br>` filler ZWSP that we'd left behind for
      // a never-touched empty source block.
      sourceText = sourceText.replace(/​/g, "");

      var origTag = sourceBlock.getAttribute("data-leaf-source") || "";

      // A source `<li>` whose `- ` / `N. ` marker the user deleted is no
      // longer a list item — break it out of the list into a `<p>` (like
      // Obsidian), splitting the surrounding `<ul>`/`<ol>` if it sat
      // mid-list. The user has already moved their cursor to another
      // block, so we restructure the DOM without touching the selection.
      if (origTag === "li") {
        var liNormalized = sourceText.replace(/\u00a0/g, " ");
        var hasMarker = /^(- |\d+\. )/.test(liNormalized);
        var liList = sourceBlock.parentNode;
        var liListTag =
          liList && liList.tagName ? liList.tagName.toLowerCase() : "";
        if (
          !hasMarker &&
          (liListTag === "ul" || liListTag === "ol") &&
          liList.parentNode
        ) {
          this._exitListItemToParagraph(sourceBlock, liNormalized, liList);
          return;
        }

        // An empty list item the cursor LEAVES is dropped — but only when it
        // is the last thing in the list.
        //
        // A trailing empty bullet is the residue of starting an item and
        // changing your mind, so clearing it keeps the document tidy. One with
        // items BELOW it is the opposite: a blank line somebody deliberately
        // left inside a list, and deleting it on blur meant a row could not be
        // left empty at all — it vanished the moment the caret moved away.
        //
        // Same rule as Enter (see `_nextListSibling` in the keydown handler):
        // the tidy-up applies at the end of a list, never in the middle of one.
        // A task item is exempt. An empty bullet is the residue of starting a
        // line and changing your mind, but an empty CHECKBOX is a thing in its
        // own right — it renders as a tickable box, and it is what the toolbar
        // button produces. Clearing it on blur made that button look broken:
        // clicking it appeared to do nothing unless text was selected first,
        // because the item it created was removed the moment the caret left.
        var isTaskSource = /^(- |\d+\. )\[[ xX]?\] /.test(liNormalized);

        if (hasMarker && !isTaskSource && !this._nextListSibling(sourceBlock)) {
          var liContent = liNormalized
            .replace(/^(- |\d+\. )/, "")
            .replace(/^\[([ xX]?)\] /, "");

          // A third exemption, same reasoning as the task one: the ONLY item
          // in a list IS the list. The bullet and numbered buttons create
          // exactly this — one empty item to start typing into — and tidying
          // it away on blur undid the click that made it. A trailing empty
          // among other items is still residue and still goes.
          var loneItem = liList && liList.children && liList.children.length === 1;

          if (
            liContent.trim() === "" &&
            (liListTag === "ul" || liListTag === "ol") &&
            !loneItem
          ) {
            this._syntaxMutating = true;
            try {
              liList.removeChild(sourceBlock);
              if (!liList.firstElementChild && liList.parentNode) {
                liList.parentNode.removeChild(liList);
              }
            } finally {
              this._syntaxMutating = false;
            }
            return;
          }
        }
      }

      // Editing the FIRST item's number is how a list gets renumbered by hand:
      // in CommonMark only that number counts, and the rest follow from it. It
      // is carried on the `<ol>` rather than the item, so without this the
      // number typed in source mode was stripped with the marker and the list
      // snapped back to where it started — the "it reverts the numbers"
      // complaint. Later items are deliberately ignored: their numbers are not
      // meaningful in markdown and renumbering from one of them would move
      // every item above it.
      if (origTag === "li") {
        var numList = sourceBlock.parentNode;

        if (
          numList &&
          numList.tagName &&
          numList.tagName.toLowerCase() === "ol" &&
          numList.firstElementChild === sourceBlock
        ) {
          var typedNum = sourceText.replace(/\u00a0/g, " ").match(/^(\d+)\. /);

          if (typedNum) {
            var typedStart = parseInt(typedNum[1], 10);

            if (typedStart === 1) {
              numList.removeAttribute("start");
            } else {
              numList.setAttribute("start", String(typedStart));
            }
          }
        }
      }

      var rendered;
      if (origTag === "li") {
        // List items render back as `<li>` — block-level patterns
        // (heading, HR, list) don't apply inside a list item. The shared
        // helper strips the revealed `- ` / `N. ` marker and re-forms a
        // checkbox.
        rendered = this._renderListItemFromSource(sourceText);
        // A kept-empty item renders with no content at all, which is invisible
        // and un-clickable — give it a caret home the same way every other
        // path does.
        this._ensureListItemPlaceholder(rendered);
      } else {
        rendered = this._renderBlockFromSource(sourceText);
      }

      this._syntaxMutating = true;
      try {
        sourceBlock.parentNode.replaceChild(rendered, sourceBlock);
      } finally {
        this._syntaxMutating = false;
      }
    },

    // Live counterpart to `_exitListItemToParagraph`: the user deleted a
    // list item's marker WHILE editing it. Convert the source `<li>` to a
    // source `<p>` in place — splitting the `<ul>`/`<ol>` if mid-list — and
    // keep the caret, so the bullet and list indent vanish immediately and
    // editing continues in the paragraph. The refresh at the end re-scans
    // the `<p>` and rebuilds its inline formatting.
    _breakSourceListItemToParagraph: function (sourceText, cursorOffset) {
      var li = this._sourceBlock;
      var list = li.parentNode;
      var grandparent = list ? list.parentNode : null;
      if (!grandparent) return;

      var p = document.createElement("p");
      p.setAttribute("data-leaf-source", "p");

      this._syntaxMutating = true;
      try {
        // Items after this one move into a fresh list below the `<p>`.
        var trailing = [];
        var next = li.nextElementSibling;
        while (next) {
          var after = next.nextElementSibling;
          trailing.push(next);
          next = after;
        }
        list.removeChild(li);
        grandparent.insertBefore(p, list.nextSibling);
        if (trailing.length > 0) {
          var second = document.createElement(list.tagName);
          for (var i = 0; i < trailing.length; i++) {
            second.appendChild(trailing[i]);
          }
          grandparent.insertBefore(second, p.nextSibling);
        }
        if (!list.firstChild) {
          grandparent.removeChild(list);
        }

        // Seed the `<p>` with the raw source + caret; the refresh below
        // re-scans it as a paragraph and rebuilds inline formatting.
        if (sourceText.length === 0) {
          p.innerHTML = "<br>";
        } else {
          p.appendChild(document.createTextNode(sourceText));
        }
        var range = document.createRange();
        if (p.firstChild && p.firstChild.nodeType === Node.TEXT_NODE) {
          range.setStart(
            p.firstChild,
            Math.min(cursorOffset, p.firstChild.textContent.length)
          );
        } else {
          range.setStart(p, 0);
        }
        range.collapse(true);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } finally {
        this._syntaxMutating = false;
      }

      this._sourceBlock = p;
      this._lastSourceStateKey = null;
      this._activeMatchKey = null;
      this._refreshSourceBlock();
      this._debouncedPushVisualChange();
    },

    // Lift a marker-less `<li>` out of its `<ul>`/`<ol>` into a sibling
    // `<p>` carrying the item's (marker-less) content. Items that sat
    // AFTER it move into a fresh list of the same kind placed below the
    // paragraph, so the result is list / `<p>` / list rather than leaking
    // the tail into a sibling-less paragraph. Does not move the cursor.
    _exitListItemToParagraph: function (li, contentText, list) {
      var grandparent = list.parentNode;

      var p = document.createElement("p");
      var pNodes = this._buildFormattedFragment(contentText);
      for (var i = 0; i < pNodes.length; i++) {
        p.appendChild(pNodes[i]);
      }
      if (!p.firstChild) {
        p.innerHTML = "<br>";
      }

      this._syntaxMutating = true;
      try {
        // Trailing siblings → a new list of the same kind below the `<p>`.
        var trailing = [];
        var next = li.nextElementSibling;
        while (next) {
          var afterNext = next.nextElementSibling;
          trailing.push(next);
          next = afterNext;
        }

        list.removeChild(li);
        grandparent.insertBefore(p, list.nextSibling);

        if (trailing.length > 0) {
          var secondList = document.createElement(list.tagName);
          for (var t = 0; t < trailing.length; t++) {
            secondList.appendChild(trailing[t]);
          }
          grandparent.insertBefore(secondList, p.nextSibling);
        }

        if (!list.firstChild) {
          grandparent.removeChild(list);
        }
      } finally {
        this._syntaxMutating = false;
      }
    },

    // Live re-evaluation of the currently-open source block. Called on
    // every input AND on cursor moves within the block, so the block:
    //   1. Retags itself (`data-leaf-source="h2"` etc.) the instant the
    //      leading `#`s change — heading styles update live.
    //   2. Shows source markers (`**`, `*`, …) ONLY for the inline range
    //      the cursor is currently inside; other inline ranges render
    //      as their formatted element (`<strong>`, `<em>`, …) just
    //      like they will after exit.
    // Skips the rebuild when nothing user-visible would change (same
    // text, same active range) so cursor moves within a single
    // text-segment don't churn the DOM.
    _refreshSourceBlock: function () {
      if (this._syntaxMutating) return;
      if (!this._sourceBlock || !this._sourceBlock.isConnected) return;

      var sel = window.getSelection();
      if (!sel.rangeCount) return;
      var range = sel.getRangeAt(0);
      if (!this._sourceBlock.contains(range.startContainer)) return;

      // Derive source from a DOM walk rather than `textContent`. The
      // contenteditable can hold an `<a>` (from `execCommand
      // ("createLink")`) or any other inline element that doesn't
      // round-trip through plain text — `_serializeBlockInline`
      // re-emits the markdown form (`[text](url)`, `**bold**`, etc.).
      // For source-mode blocks the block-prefix marker (`#`, `# `,
      // `## `, …) lives in a `<span class="leaf-source-marker">`
      // child whose text content IS the marker; walking it emits the
      // prefix exactly once, which is also why we don't prepend
      // `_blockSourcePrefix` here — that'd double the `#`s.
      var trace = this._serializeBlockInline(
        this._sourceBlock,
        range.startContainer,
        range.startOffset
      );
      var sourceText = trace.source;
      var cursorOffset = trace.cursorOffset;
      var scan;
      if (this._sourceBlock.tagName.toLowerCase() === "li") {
        // The leading `- ` / `N. ` (plus an optional `[ ] ` / `[x] `
        // checkbox) is the list marker. Treat it as a block-prefix marker
        // so `_buildSourceFragment` renders it as a `.leaf-list-marker`
        // span that CSS hides — showing the bullet / checkbox in its place
        // — UNLESS the cursor is on it (`.leaf-marker-active`, set below).
        // That mirrors how inline markers (`**`, `*`) only reveal when the
        // cursor is inside them, instead of the whole row going to source.
        // (Block-level patterns like `# ` are literal text inside a list
        // item, so we run only the inline scan on the body.)
        var liM = sourceText
          .replace(/\u00a0/g, " ")
          .match(/^(- |\d+\. )(\[([ xX])\] )?/);
        var liMarkerLen = liM ? liM[0].length : 0;
        var liIsTask = !!(liM && liM[2]);
        var liChecked = liIsTask && (liM[3] === "x" || liM[3] === "X");
        var liBodyMatches = this._scanInlineMatches(
          sourceText.slice(liMarkerLen)
        );
        for (var lim = 0; lim < liBodyMatches.length; lim++) {
          liBodyMatches[lim].start += liMarkerLen;
          liBodyMatches[lim].end += liMarkerLen;
        }
        scan = {
          kind: "li",
          blockPrefixLen: liMarkerLen,
          inlineMatches: liBodyMatches,
          listMarker: liMarkerLen > 0,
          isTask: liIsTask,
          checked: liChecked,
        };
      } else {
        scan = this._scanSource(sourceText);
      }

      // Marker fully deleted — the item is no longer a list item. Break it
      // out of its `<ul>`/`<ol>` into a source `<p>` RIGHT NOW (caret
      // preserved) so the bullet AND the list indent vanish immediately,
      // instead of lingering until the cursor leaves the line.
      if (
        scan.kind === "li" &&
        !scan.listMarker &&
        this._sourceBlock.parentNode &&
        this._sourceBlock.parentNode.tagName &&
        /^(ul|ol)$/i.test(this._sourceBlock.parentNode.tagName)
      ) {
        this._breakSourceListItemToParagraph(sourceText, cursorOffset);
        return;
      }

      // Walk every enclosing inline match (nesting-aware) so the active
      // path can drive both the marker reveal and the cursor-snap on
      // arrow-key entry.
      var activePath = this._findActiveInlinePath(
        sourceText.slice(scan.blockPrefixLen),
        scan.blockPrefixLen,
        cursorOffset
      );

      // Symmetric-entry snap: when arrow-key navigation crosses into an
      // inactive nest of matches the browser skips over every hidden
      // marker layer and lands the cursor exactly at the INNERMOST
      // body boundary. One keypress eats every `*` / `~~` stop. Detect
      // it (active group just changed AND cursor sits at innermost
      // body boundary) and snap to JUST OUTSIDE the OUTERMOST wrapper.
      // Active groups are keyed by their outermost match — every
      // wrapper nested inside an active outermost is rendered active
      // too (so a cursor anywhere in `~~..~~` shows both `~~` and the
      // inner `**`), which means cursor moves within the same group
      // don't churn the DOM.
      var outermost = activePath.length > 0 ? activePath[0] : null;
      var activeKey = outermost ? outermost.start + "-" + outermost.end : "";
      if (activePath.length > 0 && activeKey !== this._activeMatchKey) {
        var innermost = activePath[activePath.length - 1];
        var innerOpen = innermost.openLen != null
          ? innermost.openLen
          : innermost.delim.length;
        var innerClose = innermost.closeLen != null
          ? innermost.closeLen
          : innermost.delim.length;
        var bs = innermost.start + innerOpen;
        var be = innermost.end - innerClose;
        if (cursorOffset === bs) {
          cursorOffset = outermost.start;
        } else if (cursorOffset === be) {
          cursorOffset = outermost.end;
        }
      }
      this._activeMatchKey = activeKey;

      var stateKey = scan.kind + "|" + activeKey + "|" + sourceText;
      var willRebuild = stateKey !== this._lastSourceStateKey;

      // List marker reveal: show the `- ` / `N. ` / `- [ ] ` source only
      // while the cursor is on the marker's non-space chars — strictly
      // BEFORE the trailing space that separates the marker from the body.
      // So `- [ ]| text` reveals (touching `]`), but `- [ ] |text` (past the
      // space, at the body) shows the bullet / checkbox. This is a class
      // flip — no rebuild — so it runs even when the dedupe skips the
      // rebuild (the marker span / checkbox box live in the DOM the whole
      // time; only their visibility changes).
      if (scan.kind === "li") {
        this._sourceBlock.classList.toggle("leaf-task", !!scan.isTask);
        if (scan.isTask) {
          this._sourceBlock.setAttribute(
            "data-checked",
            scan.checked ? "true" : "false"
          );
        }
        // Also keep it active while the body is still empty — i.e. you're
        // typing the marker itself (`- `, `- [ ] `). Without that the marker
        // would hide the instant you finish typing it and strand the caret
        // in the now-`display:none` span, corrupting the next keystrokes.
        //
        // Task items are exempt from the empty-body half. Every Enter on a
        // checklist creates an empty item, so the rule made every new line
        // open as raw `- [ ] ` instead of the checkbox — the marker text is
        // for editing the marker, not the face of a freshly made row. The
        // stranding the rule guards against is handled for task items by
        // seating the caret in a placeholder AFTER the hidden marker (see
        // the rebuild below); revealing the marker deliberately is the
        // click in the gutter left of the box.
        var markerActive =
          !!scan.listMarker &&
          (cursorOffset < scan.blockPrefixLen ||
            (!scan.isTask && sourceText.length <= scan.blockPrefixLen));
        this._sourceBlock.classList.toggle("leaf-marker-active", markerActive);

        // The marker just hid with the caret still inside it — display:none
        // means the engine parks the visible caret before the span, so the
        // next keystroke prepends to the row, the serialization loses its
        // leading marker, and the item breaks out to a paragraph: typing
        // "- [ ] h" produced "h- [ ] " in a plain <p>. Seat the caret in a
        // placeholder AFTER the marker instead. Done here, not only in the
        // rebuild, because a class flip happens without one.
        if (scan.isTask && !markerActive) {
          var hiddenMarker = this._sourceBlock.querySelector(".leaf-source-marker");
          var homeSel = window.getSelection();

          if (
            hiddenMarker &&
            homeSel.rangeCount &&
            hiddenMarker.contains(homeSel.getRangeAt(0).startContainer)
          ) {
            var home = hiddenMarker.nextSibling;

            if (!home || home.nodeType !== 3) {
              home = document.createTextNode("\u200B");
              this._syntaxMutating = true;
              try {
                this._sourceBlock.insertBefore(home, hiddenMarker.nextSibling);
              } finally {
                this._syntaxMutating = false;
              }
            }

            var homeRange = document.createRange();
            homeRange.setStart(home, home.nodeValue ? Math.min(1, home.nodeValue.length) : 0);
            homeRange.collapse(true);
            homeSel.removeAllRanges();
            homeSel.addRange(homeRange);
          }
        }
        // Marker just deleted (`- ` / `N. ` gone) — the item is no longer a
        // list item (it breaks out to a `<p>` on cursor-leave). Hide the
        // bullet / number NOW so the broken formatting shows immediately,
        // instead of a stray bullet lingering until the line is left.
        this._sourceBlock.classList.toggle(
          "leaf-marker-broken",
          !scan.listMarker
        );
      }

      // Cheap dedupe: same kind + same active path + same source text
      // means the DOM we'd build is identical to what's already there.
      // Cursor moves within a single segment land here every time.
      if (!willRebuild) return;
      this._lastSourceStateKey = stateKey;

      this._syntaxMutating = true;
      try {
        this._sourceBlock.setAttribute("data-leaf-source", scan.kind);
        while (this._sourceBlock.firstChild) {
          this._sourceBlock.removeChild(this._sourceBlock.firstChild);
        }
        if (sourceText.length === 0) {
          this._sourceBlock.innerHTML = "<br>";
        } else {
          var caretTarget = this._buildSourceFragment(
            this._sourceBlock,
            sourceText,
            scan,
            cursorOffset
          );

          // A task item whose body is empty seats its caret AFTER the hidden
          // marker, in a placeholder of its own. Left where the build put it
          // — inside the marker span — the caret sits in a display:none node:
          // invisible, and the next keystrokes go somewhere the user cannot
          // see. This is what lets the marker stay hidden on a fresh
          // checklist row at all.
          if (
            scan.kind === "li" &&
            scan.isTask &&
            cursorOffset <= scan.blockPrefixLen &&
            sourceText.length <= scan.blockPrefixLen &&
            !this._sourceBlock.classList.contains("leaf-marker-active")
          ) {
            var home = document.createTextNode("\u200B");
            this._sourceBlock.appendChild(home);
            caretTarget = { node: home, offset: 1 };
          }

          if (caretTarget && caretTarget.node) {
            var newRange = document.createRange();
            newRange.setStart(caretTarget.node, caretTarget.offset);
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);
          }
        }
      } finally {
        this._syntaxMutating = false;
      }
    },

    // Compute the character offset within a block element that
    // corresponds to (cursorNode, cursorOffset). Counts text characters
    // depth-first; element-offset cursors map to the position right
    // before the indexed child.
    _textOffsetInBlock: function (block, cursorNode, cursorOffset) {
      var offset = 0;
      var found = false;

      function walk(node) {
        if (found) return;
        if (node.nodeType === Node.TEXT_NODE) {
          if (node === cursorNode) {
            offset += cursorOffset;
            found = true;
          } else {
            offset += node.textContent.length;
          }
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        var children = node.childNodes;
        for (var i = 0; i < children.length; i++) {
          if (node === cursorNode && cursorOffset === i) {
            found = true;
            return;
          }
          walk(children[i]);
          if (found) return;
        }
        if (node === cursorNode && cursorOffset === children.length) {
          found = true;
        }
      }

      walk(block);
      return offset;
    },

    // Scan a source string and return `{ kind, markers }`. `kind` is the
    // block-level tag ("p", "h1"–"h6"); `markers` is an array of
    // `[start, end)` ranges marking the source-text positions that
    // should be rendered as faded markers (block prefix + balanced
    // inline delimiters).
    _scanSource: function (text) {
      // Normalize NBSP → space first. Chrome converts the typed trailing
      // space after `## ` to U+00A0 inside the contenteditable; the
      // heading regex below uses a literal space, so without the
      // normalization the prefix only matches until the user happens
      // to type a non-space character that flips NBSP back.
      text = text.replace(/\u00a0/g, " ");
      var kind = "p";
      var blockPrefixLen = 0;

      // Detect leading `#`s (1-6, not followed by a 7th) followed by a
      // REQUIRED space or tab. The negative lookahead `(?!#)` makes the
      // count exact — a 7-hash prefix leaves the block as a plain
      // paragraph.
      //
      // The separator being required is what makes `#hashtag` work at the
      // start of a line. This used to treat the space as optional, so
      // `#hashtag` rendered as `<h1>hashtag</h1>` on the client while the
      // server stored a plain paragraph — and exiting the block
      // serialized it back out as a real `# hashtag` heading, silently
      // destroying the tag.
      //
      // A bare `#` (or `##`) with nothing after it is deliberately NOT
      // treated as a heading either, even though CommonMark counts it as
      // an empty one. Accepting it made the block flash into h1 styling
      // on the first keystroke of every hashtag and then drop back out on
      // the second — the formatting should only appear once the space
      // that actually commits to a heading has been typed. The cost is
      // that a line holding nothing but hashes previews as a paragraph
      // while MDEx would render an (invisible, empty) heading.
      var headingMatch = text.match(/^(#{1,6})(?!#)[ \t]/);
      if (headingMatch) {
        kind = "h" + headingMatch[1].length;
        blockPrefixLen = headingMatch[0].length;
      }

      var inlineMatches = this._scanInlineMatches(text.slice(blockPrefixLen));
      // Shift positions to absolute (within the block's full source).
      for (var i = 0; i < inlineMatches.length; i++) {
        inlineMatches[i].start += blockPrefixLen;
        inlineMatches[i].end += blockPrefixLen;
      }

      return {
        kind: kind,
        blockPrefixLen: blockPrefixLen,
        inlineMatches: inlineMatches
      };
    },

    // Walk the inline-match tree the cursor sits in, outermost-first.
    // For nesting like `~~**bold**~~` with the cursor on "bold" this
    // returns [{del…}, {strong…}] so callers can build the active key,
    // snap to the outermost boundary on arrow-key entry, etc. Each
    // path entry carries absolute `start`/`end` positions (inside the
    // full source string) plus `delim` and `pattern` from the scan.
    _findActiveInlinePath: function (text, startOffset, caretOffset) {
      var path = [];
      var self = this;
      function recurse(segText, segOffset) {
        var matches = self._scanInlineMatches(segText);
        for (var i = 0; i < matches.length; i++) {
          var m = matches[i];
          var absStart = segOffset + m.start;
          var absEnd = segOffset + m.end;
          if (caretOffset >= absStart && caretOffset <= absEnd) {
            var openLen = m.openLen != null ? m.openLen : m.delim.length;
            var closeLen = m.closeLen != null ? m.closeLen : m.delim.length;
            path.push({
              start: absStart,
              end: absEnd,
              delim: m.delim,
              openLen: openLen,
              closeLen: closeLen,
              pattern: m.pattern
            });
            var bodyStart = segOffset + m.start + openLen;
            var bodyText = segText.slice(
              m.start + openLen,
              m.end - closeLen
            );
            recurse(bodyText, bodyStart);
            return;
          }
        }
      }
      recurse(text, startOffset);
      return path;
    },

    // Find every balanced inline-delimiter match (`**bold**`, `*it*`,
    // `~~strike~~`, `||spoiler||`, `` `code` ``, `***bi***`) inside
    // `text`. Returns non-overlapping match records sorted by start
    // position. Each record carries enough info (delim, pattern, body)
    // to render the match either as source-with-markers (when active)
    // or as the formatted element (when inactive).
    _scanInlineMatches: function (text) {
      var candidates = [];
      var patterns = this._autoFormatPatterns;
      // Wiki links first. In hybrid source mode a `[[Target|Alias]]` token
      // renders as its alias chip (the `[[`, target and `|` hidden in
      // marker spans) unless the caret is inside it — the same reveal
      // grammar `**bold**` follows. Without this the whole raw token —
      // easily 50+ chars when the target is an id — exploded into view the
      // moment the block entered source mode.
      if (this._wikiLinks && text.indexOf("[[") !== -1) {
        var wre = /\[\[([^\[\]|#]+?)(?:#([^\[\]|]+?))?(?:\|([^\[\]]+?))?\]\]/g;
        var wm;
        while ((wm = wre.exec(text)) !== null) {
          candidates.push({
            start: wm.index,
            end: wm.index + wm[0].length,
            delim: "[[",
            openLen: 2,
            closeLen: 2,
            pattern: { type: "wikilink" },
            body: wm[1],
            wikiTarget: wm[1],
            wikiHeading: wm[2] || "",
            wikiAlias: wm[3] || "",
            raw: wm[0]
          });
        }
      }
      for (var i = 0; i < patterns.length; i++) {
        var p = patterns[i];
        if (p.type === "link") {
          // Asymmetric — opening is `[` (1 char), closing is `](url)`.
          var lFlags = p.regexAny.flags;
          if (lFlags.indexOf("g") === -1) lFlags += "g";
          var lre = new RegExp(p.regexAny.source, lFlags);
          var lm;
          while ((lm = lre.exec(text)) !== null) {
            if (lm[0].length === 0) {
              lre.lastIndex++;
              continue;
            }
            var closeLen = lm[0].length - 1 - lm[1].length; // ](url)
            candidates.push({
              start: lm.index,
              end: lm.index + lm[0].length,
              delim: "[",
              openLen: 1,
              closeLen: closeLen,
              pattern: p,
              body: lm[1],
              href: lm[2]
            });
          }
          continue;
        }
        var delim = p.type === "boldItalic" ? "***" : p.delim;
        if (!delim) continue;
        var flags = p.regexAny.flags;
        if (flags.indexOf("g") === -1) flags += "g";
        var re = new RegExp(p.regexAny.source, flags);
        var m;
        while ((m = re.exec(text)) !== null) {
          if (m[0].length === 0) {
            re.lastIndex++;
            continue;
          }
          candidates.push({
            start: m.index,
            end: m.index + m[0].length,
            delim: delim,
            openLen: delim.length,
            closeLen: delim.length,
            pattern: p,
            body: m[1]
          });
        }
      }
      // Sort by start, prefer longer delims at the same start so
      // boldItalic (`***`) wins over strong (`**`).
      candidates.sort(function (a, b) {
        if (a.start !== b.start) return a.start - b.start;
        return b.delim.length - a.delim.length;
      });
      // Drop overlapping matches (first-found wins).
      var out = [];
      var lastEnd = 0;
      for (var k = 0; k < candidates.length; k++) {
        if (candidates[k].start >= lastEnd) {
          out.push(candidates[k]);
          lastEnd = candidates[k].end;
        }
      }
      return out;
    },

    // Markdown prefix for a block-level tag — heading hashes, etc. Empty
    // for paragraphs since they have no leading marker.
    _blockSourcePrefix: function (tag) {
      if (tag === "h1") return "# ";
      if (tag === "h2") return "## ";
      if (tag === "h3") return "### ";
      if (tag === "h4") return "#### ";
      if (tag === "h5") return "##### ";
      if (tag === "h6") return "###### ";
      return "";
    },

    // Walk a block's children depth-first, emitting markdown source for
    // inline content (text, `**bold**`, `*italic*`, `~~strike~~`,
    // `` `code` ``, `||spoiler||`, `[link](url)`, soft-break `<br>` as
    // `\n`). Tracks a cursor location: when we pass `cursorNode` at
    // `cursorOffset`, record the corresponding source-text offset.
    // Returns `{ source, cursorOffset }`. cursorOffset is the source
    // offset (or `source.length` if the cursor wasn't found).
    _serializeBlockInline: function (block, cursorNode, cursorOffset) {
      var source = "";
      var markers = [];
      var cursorAt = -1;
      var self = this;

      function noteCursor(absOffset) {
        if (cursorAt < 0) cursorAt = absOffset;
      }

      function pushMarker(text) {
        var start = source.length;
        source += text;
        markers.push([start, source.length]);
      }

      function walk(node) {
        var isElementCursor =
          node === cursorNode && node.nodeType === Node.ELEMENT_NODE;
        var children = node.childNodes;
        var prevWasBr = false;
        for (var i = 0; i < children.length; i++) {
          // Element-offset cursor lives BEFORE the child at this index.
          // Record source.length right before we serialize that child.
          if (isElementCursor && cursorOffset === i) {
            noteCursor(source.length);
          }
          var c = children[i];

          if (c.nodeType === Node.TEXT_NODE) {
            if (c === cursorNode) {
              noteCursor(source.length + cursorOffset);
            }
            // Strip cursor-anchoring ZWSPs but preserve NBSPs verbatim —
            // `_refreshSourceBlock` rebuilds text nodes from this source
            // and Chrome needs NBSPs (not regular spaces) at trailing
            // positions, otherwise the trailing space collapses and the
            // next keystroke lands at the wrong offset (effectively
            // eating the space). `_scanSource` normalizes NBSP→space
            // for heading detection, and `_renderBlockFromSource` does
            // the same on exit, so the markdown output stays clean.
            var textPiece = c.textContent.replace(/​/g, "");
            // Earmark pretty-prints a literal "\n" text node after every
            // <br>; the <br> itself already emitted "\n", so keeping this
            // one doubles the break ("\n\n" splits the paragraph on
            // round-trip). Same guard as nodeToMarkdown's.
            if (prevWasBr) {
              textPiece = textPiece.replace(/^[\n\r\t]+/, "");
            }
            source += textPiece;
            prevWasBr = false;
            continue;
          }

          if (c.nodeType !== Node.ELEMENT_NODE) continue;

          var tag = c.tagName.toLowerCase();

          // Skip decoration-span debris from any old DOM state.
          if (
            c.classList &&
            c.classList.contains("leaf-syntax-decoration")
          ) {
            continue;
          }

          if (tag === "br") {
            // A solo `<br>` is the layout placeholder for an empty
            // block; emitting `\n` here would seed the source with a
            // stray newline and break round-tripping. Skip it. Real
            // soft-break `<br>`s inside non-empty paragraphs still
            // emit `\n` so multi-line content survives.
            var isPlaceholder =
              node === block && children.length === 1;
            if (isPlaceholder) {
              continue;
            }
            if (
              c.hasAttribute &&
              c.hasAttribute("data-leaf-filler")
            ) {
              continue;
            }
            source += "\n";
            prevWasBr = true;
            continue;
          }

          prevWasBr = false;

          if (tag === "a") {
            var href = c.getAttribute("href") || "";
            // If the `<a>` already carries source-mode marker spans
            // (built by `_buildSourceFragment`), walking will emit
            // their text directly — synthesizing extra `[` / `](url)`
            // via pushMarker would double up. Bare `<a>` (Earmark, or
            // `execCommand("createLink")`) has no marker children, so
            // we synthesize them.
            if (self._hasMarkerChildren(c)) {
              walk(c);
            } else {
              pushMarker("[");
              walk(c);
              pushMarker("](" + href + ")");
            }
            continue;
          }

          // A wiki link's SOURCE is `[[Target|Alias]]`, while its text is the
          // label. Walking it would put the label into the source and lose the
          // link — the same trap as in `convertNode`. Emit what it carries.
          if (c.classList && c.classList.contains("leaf-wikilink")) {
            // A caret inside the chip (a click on a source-mode alias) maps
            // to the token's START — the next rebuild then renders the token
            // active with the caret at its front, instead of losing the
            // caret to the end of the block.
            if (c === cursorNode || (c.contains && c.contains(cursorNode))) {
              noteCursor(source.length);
            }
            source += c.getAttribute("data-leaf-wikilink-raw") || c.textContent;
            prevWasBr = false;
            continue;
          }

          var delim = self._delimiterFor(c);
          if (delim) {
            if (self._hasMarkerChildren(c)) {
              walk(c);
            } else {
              pushMarker(delim);
              walk(c);
              pushMarker(delim);
            }
            continue;
          }

          // Unknown inline tag — recurse into it without wrapping.
          walk(c);
        }

        // Element-offset cursor lands at the END (children.length).
        if (isElementCursor && cursorOffset === children.length) {
          noteCursor(source.length);
        }
      }

      walk(block);
      if (cursorAt < 0) cursorAt = source.length;
      return {
        source: source,
        markers: markers,
        cursorOffset: cursorAt
      };
    },

    // Parse a markdown source line back into a rendered block element.
    // Detects leading `#{1,6} ` for headings, `---` for HR, otherwise
    // returns a `<p>` with inline markdown parsed via the existing
    // `_buildFormattedFragment`. Broken syntax (no closing delim, weird
    // prefix like `#$ `) just stays as plain text inside a `<p>` — no
    // healing.
    _renderBlockFromSource: function (sourceText) {
      // Normalize NBSP → regular space first. Chrome auto-converts the
      // typed trailing space inside a contenteditable to U+00A0, which
      // otherwise breaks the heading regex (the literal ` ` doesn't
      // match NBSP) and drops the rendered block back to a plain `<p>`.
      var text = sourceText.replace(/\u00a0/g, " ");
      var tagName = "p";

      // A block that is exactly one preserved custom tag exits to its
      // atomic chip \u2014 the same pretty form a full reload produces \u2014 instead
      // of sitting as raw text until someone refreshes the page. The
      // toolbar's insert_markdown path already built the chip at insert
      // time; a HAND-TYPED <Hero ... /> deserves the same the moment the
      // cursor leaves the line.
      var chipHtml = this._maybeAtomicChip(text);
      if (chipHtml) {
        var chipHost = document.createElement("p");
        chipHost.innerHTML = chipHtml;
        return chipHost;
      }

      // Heading: leading `#`s (1-6, no 7th) followed by a required space
      // or tab. Kept in lockstep with `_scanSource` so the live-edit
      // display and the rendered form agree. `##h` exits to `<p>##h</p>`,
      // matching what the server actually stores.
      var headingMatch = text.match(/^(#{1,6})(?!#)[ \t](.*)$/);
      if (headingMatch) {
        tagName = "h" + headingMatch[1].length;
        text = headingMatch[2];
      }

      // Horizontal rule (only when the source block is *exactly* the
      // dashes). Otherwise treat as plain text.
      if (sourceText.match(/^-{3,}$/)) {
        return document.createElement("hr");
      }

      var block = document.createElement(tagName);
      var nodes = this._buildFormattedFragment(text);
      for (var i = 0; i < nodes.length; i++) block.appendChild(nodes[i]);

      if (!block.firstChild) {
        block.innerHTML = "<br>";
      }
      return block;
    },

    _addDelimitersTo: function (el) {
      var delim = this._delimiterFor(el);
      if (!delim) return;

      // One span per char so the cursor stops at each char boundary when
      // arrow-keying. Spans are editable plain text — no contenteditable=
      // false (which made cursor stepping unreliable across browsers).
      for (var i = delim.length - 1; i >= 0; i--) {
        var leading = document.createElement("span");
        leading.className = "leaf-syntax-decoration";
        leading.setAttribute("data-leaf-syntax-decoration", "");
        leading.textContent = delim[i];
        el.insertBefore(leading, el.firstChild);
      }
      for (var j = 0; j < delim.length; j++) {
        var trailing = document.createElement("span");
        trailing.className = "leaf-syntax-decoration";
        trailing.setAttribute("data-leaf-syntax-decoration", "");
        trailing.textContent = delim[j];
        el.appendChild(trailing);
      }
    },

    _removeDelimitersFrom: function (el) {
      if (!el) return;
      var spans = el.querySelectorAll(".leaf-syntax-decoration");
      for (var k = 0; k < spans.length; k++) {
        var s = spans[k];
        if (s.parentNode) s.parentNode.removeChild(s);
      }
    },

    // -- Sticky toolbar --

    _getStickyTopOffset: function () {
      var maxBottom = 0;
      var candidates = document.querySelectorAll(
        "header, nav, [data-navbar], .navbar"
      );
      for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        var style = window.getComputedStyle(el);
        var pos = style.position;
        if (
          (pos === "fixed" || pos === "sticky") &&
          parseInt(style.top, 10) <= 0
        ) {
          var bottom = el.getBoundingClientRect().bottom;
          if (bottom > maxBottom) maxBottom = bottom;
        }
      }
      return maxBottom;
    },

    _setupStickyToolbar: function () {
      var self = this;
      this._stickyToolbarEl = this.el.querySelector("[data-visual-toolbar]");
      if (!this._stickyToolbarEl) return;

      // Create placeholder to prevent layout shift when toolbar becomes fixed
      this._stickyPlaceholder = document.createElement("div");
      this._stickyPlaceholder.className = "leaf-toolbar-placeholder";
      this._stickyPlaceholder.style.display = "none";
      this._stickyToolbarEl.parentNode.insertBefore(
        this._stickyPlaceholder,
        this._stickyToolbarEl
      );

      this._stickyScrollHandler = function () {
        var toolbar = self._stickyToolbarEl;
        var placeholder = self._stickyPlaceholder;
        var editorRect = self.el.getBoundingClientRect();
        var toolbarHeight = toolbar.offsetHeight;
        var topOffset = self._getStickyTopOffset();

        // Use placeholder position as the toolbar's natural position when sticky
        var isSticky = toolbar.classList.contains("leaf-toolbar-sticky");
        var refRect = isSticky
          ? placeholder.getBoundingClientRect()
          : toolbar.getBoundingClientRect();

        if (
          refRect.top < topOffset &&
          editorRect.bottom > toolbarHeight + topOffset
        ) {
          if (!isSticky) {
            placeholder.style.height = toolbarHeight + "px";
            placeholder.style.display = "block";
            toolbar.style.width = self.el.offsetWidth + "px";
            toolbar.style.top = topOffset + "px";
            toolbar.classList.add("leaf-toolbar-sticky");
          } else {
            // Update width and top offset on resize
            toolbar.style.width = self.el.offsetWidth + "px";
            toolbar.style.top = topOffset + "px";
          }
        } else {
          if (isSticky) {
            toolbar.classList.remove("leaf-toolbar-sticky");
            toolbar.style.width = "";
            toolbar.style.top = "";
            placeholder.style.display = "none";
          }
        }
      };

      window.addEventListener("scroll", this._stickyScrollHandler, {
        passive: true,
      });
      window.addEventListener("resize", this._stickyScrollHandler, {
        passive: true,
      });
    },

    _cleanupStickyToolbar: function () {
      if (this._stickyScrollHandler) {
        window.removeEventListener("scroll", this._stickyScrollHandler);
        window.removeEventListener("resize", this._stickyScrollHandler);
        this._stickyScrollHandler = null;
      }
      if (this._stickyPlaceholder && this._stickyPlaceholder.parentNode) {
        this._stickyPlaceholder.parentNode.removeChild(this._stickyPlaceholder);
        this._stickyPlaceholder = null;
      }
      if (this._stickyToolbarEl) {
        this._stickyToolbarEl.classList.remove("leaf-toolbar-sticky");
        this._stickyToolbarEl.style.width = "";
        this._stickyToolbarEl.style.top = "";
        this._stickyToolbarEl = null;
      }
    },

    // -- Fullscreen toggle --
    //
    // Uses the browser Fullscreen API (`element.requestFullscreen`) so the
    // host fills the screen at OS level — browser chrome (tabs, address
    // bar, OS task bar) hides, exactly like Fresco's nav button. Escape
    // and OS-level exit gestures are handled natively by the browser.
    //
    // Source of truth is `document.fullscreenElement`. The `fullscreenchange`
    // listener (attached in `mounted`) reflects browser state into the
    // `data-leaf-fullscreen='true'` attribute, which drives the CSS that
    // flexes the inner toolbar/body/footer layout so the editor body
    // absorbs the new height. F11, Escape, the button, and any external
    // `exitFullscreen()` call all keep our UI in sync because they all
    // route through the same change event.

    _toggleFullscreen: function () {
      if (this._fullscreenElement() === this.el) {
        this._exitFullscreen();
      } else {
        this._requestFullscreen(this.el);
      }
    },

    // Fullscreen API wrappers with Safari webkit prefix fallbacks. The
    // request is a user-gesture-only API; click handlers always satisfy
    // that, so a rejection in practice means the document or iframe
    // disallows it. We swallow errors silently — falling back to the
    // old CSS-pin path is possible but not warranted yet.
    _requestFullscreen: function (el) {
      var fn = el.requestFullscreen || el.webkitRequestFullscreen ||
        el.mozRequestFullScreen || el.msRequestFullscreen;
      if (fn) { try { return fn.call(el); } catch (_) { } }
    },

    _exitFullscreen: function () {
      var fn = document.exitFullscreen || document.webkitExitFullscreen ||
        document.mozCancelFullScreen || document.msExitFullscreen;
      if (fn) { try { return fn.call(document); } catch (_) { } }
    },

    _fullscreenElement: function () {
      return document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement || null;
    },

    // Reflect `document.fullscreenElement` into our DOM. Called from the
    // `fullscreenchange` listener attached in `mounted`.
    _syncFullscreenState: function () {
      var on = this._fullscreenElement() === this.el;
      var btn = this.el.querySelector("[data-leaf-fullscreen-btn]");
      var enterIcon = btn && btn.querySelector("[data-leaf-fullscreen-enter]");
      var exitIcon = btn && btn.querySelector("[data-leaf-fullscreen-exit]");

      if (on) {
        this.el.setAttribute("data-leaf-fullscreen", "true");
        if (btn) btn.title = "Exit fullscreen";
        if (enterIcon) enterIcon.style.display = "none";
        if (exitIcon) exitIcon.style.display = "";
      } else {
        this.el.removeAttribute("data-leaf-fullscreen");
        if (btn) btn.title = "Toggle fullscreen";
        if (enterIcon) enterIcon.style.display = "";
        if (exitIcon) exitIcon.style.display = "none";
      }
    },

    // =====================================================================
    // Undo / redo history
    // =====================================================================
    //
    // Leaf owns its history rather than delegating to the browser's, because
    // the native stack cannot survive how this editor works:
    //
    //   * Every mode switch, every markdown→visual sync from the server and
    //     every `set_content` replaces the surface's content wholesale.
    //     Assigning `innerHTML` (or `value`) discards the native stack
    //     entirely — the same trap `replaceRange` was written to avoid on the
    //     textarea side, never addressed on the contenteditable side.
    //   * `document.execCommand("undo")` only knows about edits execCommand
    //     itself made. Leaf edits the DOM directly in many places (hybrid
    //     source blocks, atomic tags, select-all delete), and none of that was
    //     ever undoable.
    //   * The textarea and the contenteditable keep SEPARATE native stacks, so
    //     undo silently meant different things per tab and could never cross a
    //     mode switch.
    //
    // The model is one array of snapshots plus a cursor, which is what makes
    // redo fall out for free: undo walks the cursor back, redo walks it
    // forward, and a fresh edit truncates whatever was ahead.
    //
    // A snapshot stores the RAW content of whichever surface was active, so
    // restoring is a straight assignment with no conversion — markdown does
    // not have to round-trip through the server to undo a keystroke. The mode
    // rides along, so undoing across a mode switch returns you to the mode the
    // edit happened in, which is the only honest answer: the content of that
    // step only exists in that representation.

    _HISTORY_LIMIT: 200,

    _historyInit: function () {
      this._history = [];
      this._historyIndex = -1;
      this._historyTimer = null;
      // Set while restoring so the input events a restore fires cannot be
      // captured as new edits — that would append the state we just undid and
      // make undo appear to do nothing.
      this._historyRestoring = false;
      this._historyCaptureNow("init");
    },

    // Content of the surface the given mode edits. One place that knows the
    // mapping, so capture and restore cannot disagree about it.
    _historySurface: function (mode) {
      mode = mode || this._mode;

      if (mode === "markdown") return this._getMarkdownTextarea();
      if (mode === "html") return this._getHtmlTextarea();
      return this._visualEl;
    },

    _historyRead: function (mode) {
      var el = this._historySurface(mode);
      if (!el) return null;
      return mode === "markdown" || mode === "html" ? el.value : el.innerHTML;
    },

    // Debounced: a burst of typing collapses into one entry, so undo steps
    // through edits a person would recognise rather than one character at a
    // time.
    _historyCapture: function (reason) {
      if (this._historyRestoring || this._readonly) return;

      var self = this;
      if (this._historyTimer) clearTimeout(this._historyTimer);
      this._historyTimer = setTimeout(function () {
        self._historyCaptureNow(reason);
      }, 350);
    },

    // Immediate capture. Used before anything structural — a toolbar action, a
    // select-all delete, a mode switch — so the step before it is on the stack
    // even if the debounce has not fired yet.
    _historyCaptureNow: function (reason) {
      if (this._historyRestoring || this._readonly) return;
      if (this._historyTimer) {
        clearTimeout(this._historyTimer);
        this._historyTimer = null;
      }
      if (!this._history) return;

      var content = this._historyRead(this._mode);
      if (content === null) return;

      var top = this._history[this._historyIndex];
      // Nothing changed — a toolbar action that was a no-op, or a second
      // capture for the same keystroke. Refresh the selection so undo still
      // returns the caret to where it most recently was.
      if (top && top.mode === this._mode && top.content === content) {
        top.selection = this._historySelection();
        return;
      }

      // A new edit invalidates any redo future.
      if (this._historyIndex < this._history.length - 1) {
        this._history.length = this._historyIndex + 1;
      }

      this._history.push({
        mode: this._mode,
        content: content,
        selection: this._historySelection(),
        reason: reason || "edit"
      });

      if (this._history.length > this._HISTORY_LIMIT) {
        // Drop the oldest. Snapshots are whole documents, so an unbounded
        // stack in a long editing session is a real memory cost.
        this._history.shift();
      }

      this._historyIndex = this._history.length - 1;
      this._updateToolbarState();
    },

    _historyCanUndo: function () {
      return !!this._history && this._historyIndex > 0;
    },

    _historyCanRedo: function () {
      return !!this._history && this._historyIndex < this._history.length - 1;
    },

    _historyUndo: function () {
      if (this._readonly) return false;
      // Flush any pending keystrokes first, or the most recent typing would
      // be skipped over instead of undone.
      this._historyCaptureNow("pre-undo");
      if (!this._historyCanUndo()) return false;

      this._historyIndex -= 1;
      this._historyApply(this._history[this._historyIndex]);
      return true;
    },

    _historyRedo: function () {
      if (this._readonly) return false;
      if (!this._historyCanRedo()) return false;

      this._historyIndex += 1;
      this._historyApply(this._history[this._historyIndex]);
      return true;
    },

    _historyApply: function (entry) {
      if (!entry) return;

      var self = this;
      this._historyRestoring = true;

      try {
        if (entry.mode !== this._mode) this._historySetMode(entry.mode);

        var el = this._historySurface(entry.mode);
        if (el) {
          if (entry.mode === "markdown" || entry.mode === "html") {
            el.value = entry.content;
          } else {
            el.innerHTML = entry.content;
          }
          this._historyRestoreSelection(entry, el);
        }
      } finally {
        // Cleared on a later tick: replacing content fires input events
        // asynchronously in some engines, and capturing those would append the
        // state we just restored.
        setTimeout(function () {
          self._historyRestoring = false;
        }, 0);
      }

      this._historyAfterApply(entry);
      this._updateToolbarState();
    },

    // Tell the server and the rest of the editor what changed, using the same
    // paths a manual edit would, so a host LiveView sees an undo exactly as it
    // sees typing.
    _historyAfterApply: function (entry) {
      if (entry.mode === "markdown") {
        var ta = this._getMarkdownTextarea();
        if (ta) this._debouncedPushMarkdownChange(ta.value);
      } else if (entry.mode === "html") {
        var html = this._getHtmlTextarea();
        if (html && this._debouncedPushHtmlChange) {
          this._debouncedPushHtmlChange(html.value);
        }
      } else {
        this._debouncedPushVisualChange();
      }

      if (this._updateCounts) this._updateCounts();
    },

    // ---- selection ------------------------------------------------------
    //
    // A textarea offset is just a number. A contenteditable caret is stored as
    // a character offset into the element's text, NOT as a node reference:
    // restoring replaces every node, so any node held from before is detached
    // by the time we would use it.

    _historySelection: function () {
      var mode = this._mode;

      if (mode === "markdown" || mode === "html") {
        var ta = this._historySurface(mode);
        if (!ta) return null;
        return { start: ta.selectionStart, end: ta.selectionEnd };
      }

      if (!this._visualEl) return null;

      var sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return null;

      var range = sel.getRangeAt(0);
      if (!this._visualEl.contains(range.startContainer)) return null;

      return {
        start: this._historyTextOffset(range.startContainer, range.startOffset),
        end: this._historyTextOffset(range.endContainer, range.endOffset)
      };
    },

    // Characters of text before (container, offset) within the editor.
    //
    // A Range does this correctly for both kinds of caret position: one inside
    // a text node, and one ON an element with a child index — which is where
    // the caret sits on any empty block. The previous version walked text
    // nodes and, when the container was an element the walk could not find,
    // called ITSELF with the same element: unconditional infinite recursion.
    // It threw "too much recursion" out of the capture that runs before every
    // toolbar action, so on an empty row the action never ran at all.
    _historyTextOffset: function (container, offset) {
      if (!container || !this._visualEl) return 0;

      try {
        var range = document.createRange();
        range.setStart(this._visualEl, 0);
        range.setEnd(container, offset);

        return range.toString().length;
      } catch (e) {
        // Container outside the editor, or an offset the node cannot take.
        return 0;
      }
    },

    _historyRestoreSelection: function (entry, el) {
      var sel = entry.selection;
      if (!sel) return;

      if (entry.mode === "markdown" || entry.mode === "html") {
        try {
          el.focus({ preventScroll: true });
          el.setSelectionRange(sel.start, sel.end);
        } catch (e) {
          /* a detached or hidden textarea — nothing to place */
        }
        return;
      }

      var start = this._historyNodeAt(sel.start);
      var end = sel.end === sel.start ? start : this._historyNodeAt(sel.end);
      if (!start) return;

      try {
        var range = document.createRange();
        range.setStart(start.node, start.offset);
        range.setEnd((end || start).node, (end || start).offset);

        var selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        this._visualEl.focus({ preventScroll: true });
      } catch (e) {
        /* content shifted under us — leave the caret where the browser put it */
      }
    },

    // Inverse of _historyTextOffset: character offset back to a text node.
    _historyNodeAt: function (offset) {
      if (!this._visualEl) return null;

      var walker = document.createTreeWalker(
        this._visualEl,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );

      var count = 0;
      var node;
      var last = null;

      while ((node = walker.nextNode())) {
        var len = node.nodeValue.length;
        if (count + len >= offset) return { node: node, offset: offset - count };
        count += len;
        last = node;
      }

      if (last) return { node: last, offset: last.nodeValue.length };
      return { node: this._visualEl, offset: 0 };
    },

    // Undo can cross a mode switch, so applying an entry may have to change
    // mode first. Driving the existing tab click reuses everything that hangs
    // off a mode change — content sync, visibility, tab styling, the
    // `mode_changed` push — instead of half-reimplementing it here.
    _historySetMode: function (mode) {
      var tab = this.el.querySelector('[data-mode-tab="' + mode + '"]');

      if (tab) {
        tab.click();
        return;
      }

      // No switcher rendered (a single-mode editor, or a compact menu that is
      // closed). Fall back to the minimum that makes the surface usable.
      this._mode = mode;
      if (this._applyModeVisibility) this._applyModeVisibility(mode);
    },

    // A wholly new document — a host calling set_content. The previous
    // document's steps are not reachable from it, so keeping them would let
    // undo resurrect content the host has replaced.
    _historyReset: function () {
      this._history = [];
      this._historyIndex = -1;
      this._historyRestoring = false;
      this._historyCaptureNow("reset");
    },

    // Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z, Ctrl+Y. Shared by every surface so the
    // shortcut behaves the same in all four modes.
    _historyKeydown: function (e) {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return false;

      var key = (e.key || "").toLowerCase();

      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        this._historyUndo();
        return true;
      }

      if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        this._historyRedo();
        return true;
      }

      return false;
    },

    _execToolbarAction: function (action) {
      // Fullscreen is a view-only toggle; let it run regardless of
      // readonly state or current mode so users can expand the viewer
      // even when editing is disabled.
      if (action === "fullscreen") {
        this._toggleFullscreen();
        return;
      }

      // Export / copy are read-only operations — allowed in any mode, even
      // when editing is disabled.
      if (action === "copyMarkdown") {
        this._copyText(this._currentMarkdown());
        return;
      }
      if (action === "copyHtml") {
        this._copyText(this._currentHtml());
        return;
      }
      if (action === "downloadMarkdown") {
        this._downloadMarkdown();
        return;
      }

      if (this._readonly) return;

      // Snapshot before the action so it is undoable as one step, even if
      // the typing debounce has not fired yet.
      if (action !== "undo" && action !== "redo") {
        this._historyCaptureNow("toolbar:" + action);
      }

      if (this._mode === "markdown") {
        this._execMarkdownToolbarAction(action);
        if (action !== "undo" && action !== "redo") this._historyCapture("toolbar");
        return;
      }

      if (!this._visualEl) return;
      this._visualEl.focus({ preventScroll: true });

      // Snapshot existing formatting wrappers before the action so we can
      // diff against the post-action set to find the newly inserted one.
      // More reliable than guessing from post-action selection state.
      var preSnapshot = this._snapshotFormattingElements();

      switch (action) {
        case "bold":
          if (!this._isInsideHeading()) document.execCommand("bold", false, null);
          break;
        case "italic":
          document.execCommand("italic", false, null);
          break;
        case "strike":
          document.execCommand("strikeThrough", false, null);
          break;
        case "superscript":
          document.execCommand("superscript", false, null);
          break;
        case "subscript":
          document.execCommand("subscript", false, null);
          break;
        case "code":
          this._wrapSelectionWith("code");
          break;
        case "spoiler":
          this._toggleSpoiler();
          break;
        case "heading1":
          this._toggleHeading("h1");
          break;
        case "heading2":
          this._toggleHeading("h2");
          break;
        case "heading3":
          this._toggleHeading("h3");
          break;
        case "heading4":
          this._toggleHeading("h4");
          break;
        case "heading5":
          this._toggleHeading("h5");
          break;
        case "heading6":
          this._toggleHeading("h6");
          break;
        case "bulletList":
          // Built by hand where execCommand has nothing to act on — an empty
          // line, an editor nobody has clicked into. The native command keeps
          // the cases it is better at: selections, toggling, kind-switching.
          if (!this._insertList("ul")) {
            document.execCommand("insertUnorderedList", false, null);
          }
          break;
        case "orderedList":
          if (!this._insertList("ol")) {
            document.execCommand("insertOrderedList", false, null);
          }
          break;
        case "indent":
          // execCommand("indent") on a list item produces invalid DOM
          // (nested list as sibling of the li, or a bare <p> inside the
          // list) that used to lose the item on serialization. Build the
          // valid li > list nesting ourselves; non-list blocks keep the
          // native behavior.
          if (!this._indentListItem()) {
            document.execCommand("indent", false, null);
          }
          break;
        case "outdent":
          if (!this._outdentListItem()) {
            document.execCommand("outdent", false, null);
          }
          break;
        case "blockquote":
          this._toggleBlockquote();
          break;
        case "codeBlock":
          this._insertCodeBlock();
          break;
        case "detailsBlock":
          this._insertDetailsBlock();
          break;
        case "taskList":
          this._insertTaskList();
          break;
        case "callout":
          this._insertCallout("note");
          break;
        case "horizontalRule":
          this._insertHorizontalRule();
          break;
        case "table":
          this._insertTable();
          break;
        case "tableAddRow":
          this._tableAddRow();
          break;
        case "tableRemoveRow":
          this._tableRemoveRow();
          break;
        case "tableAddCol":
          this._tableAddCol();
          break;
        case "tableRemoveCol":
          this._tableRemoveCol();
          break;
        case "tableAlignLeft":
          this._tableSetAlign("left");
          break;
        case "tableAlignCenter":
          this._tableSetAlign("center");
          break;
        case "tableAlignRight":
          this._tableSetAlign("right");
          break;
        case "tableToggleHeader":
          this._tableToggleHeader();
          break;
        case "link":
          if (!this._denyLinks) this._insertLink();
          break;
        case "emoji":
          this._openEmojiPicker();
          return; // skip updateToolbarState/push — picker handles it
        case "symbols":
          this._openSymbolPicker();
          return;
        case "insert-image":
          if (this._denyImages) break;
          if (this._hasUpload) {
            this.pushEventTo(this.el, "insert_request", {
              editor_id: this._editorId,
              type: "image",
            });
          } else {
            this._openImageUrlDialog();
            return;
          }
          break;
        case "insert-image-upload":
          if (this._denyImages) break;
          this.pushEventTo(this.el, "insert_request", {
            editor_id: this._editorId,
            type: "image",
          });
          break;
        case "insert-image-url":
          if (!this._denyImages) {
            this._openImageUrlDialog();
            return;
          }
          break;
        case "insert-video":
          if (!this._denyVideo) {
            this.pushEventTo(this.el, "insert_request", {
              editor_id: this._editorId,
              type: "video",
            });
          }
          break;
        case "undo":
          this._historyUndo();
          return;
        case "redo":
          this._historyRedo();
          return;
        case "removeFormat":
          document.execCommand("removeFormat", false, null);
          document.execCommand("formatBlock", false, "p");
          break;
      }

      this._updateToolbarState();
      this._debouncedPushVisualChange();
      if (!this._refreshHybridSourceBlockAfterToolbarAction()) {
        this._deferredSyntaxRefresh(preSnapshot);
      }
    },

    _execMarkdownToolbarAction: function (action) {
      var gid = this._editorId.replace(/-/g, "_") + "_markdown";
      var fmt = window["markdownFormat_" + gid];
      var pfx = window["markdownLinePrefix_" + gid];
      var lnk = window["markdownLink_" + gid];
      var ins = window["markdownEditorInsert_" + gid];

      switch (action) {
        case "bold": if (fmt) fmt("**", "**"); break;
        case "italic": if (fmt) fmt("*", "*"); break;
        case "strike": if (fmt) fmt("~~", "~~"); break;
        case "superscript": if (fmt) fmt("<sup>", "</sup>"); break;
        case "subscript": if (fmt) fmt("<sub>", "</sub>"); break;
        case "code": if (fmt) fmt("`", "`"); break;
        case "spoiler": if (fmt) fmt("||", "||"); break;
        case "heading1": if (pfx) pfx("# "); break;
        case "heading2": if (pfx) pfx("## "); break;
        case "heading3": if (pfx) pfx("### "); break;
        case "heading4": if (pfx) pfx("#### "); break;
        case "heading5": if (pfx) pfx("##### "); break;
        case "heading6": if (pfx) pfx("###### "); break;
        case "bulletList": if (pfx) pfx("- "); break;
        case "orderedList": if (pfx) pfx("1. "); break;
        case "indent": { var ind = window["markdownIndent_" + gid]; if (ind) ind("indent"); break; }
        case "outdent": { var ind = window["markdownIndent_" + gid]; if (ind) ind("outdent"); break; }
        case "blockquote": if (pfx) pfx("> "); break;
        case "codeBlock": if (fmt) fmt("```\n", "\n```"); break;
        case "horizontalRule": if (ins) ins("\n---\n"); break;
        case "detailsBlock": if (ins) ins("\n<details>\n<summary>Summary</summary>\n\nContent\n\n</details>\n"); break;
        case "taskList": if (pfx) pfx("- [ ] "); break;
        case "callout": if (ins) ins("\n> [!NOTE]\n> \n"); break;
        case "table": if (ins) ins("\n| Header 1 | Header 2 |\n| --- | --- |\n| Cell 1 | Cell 2 |\n| Cell 3 | Cell 4 |\n"); break;
        case "link":
          if (lnk && !this._denyLinks) {
            this._openTextPrompt({
              label: "Link URL",
              value: "https://",
              anchor: this._toolbarAnchor("link"),
              onSubmit: function (url) {
                url = url.trim();
                if (url) lnk(url);
              },
            });
          }
          break;
        case "emoji": this._openEmojiPicker(); break;
        case "symbols": this._openSymbolPicker(); break;
        case "insert-image":
          if (this._denyImages) break;
          if (this._hasUpload) {
            this.pushEventTo(this.el, "insert_request", { editor_id: this._editorId, type: "image" });
          } else {
            this._openImageUrlDialog();
          }
          break;
        case "insert-image-upload":
          if (this._denyImages) break;
          this.pushEventTo(this.el, "insert_request", { editor_id: this._editorId, type: "image" });
          break;
        case "insert-image-url":
          if (!this._denyImages) this._openImageUrlDialog();
          break;
        case "insert-video":
          if (!this._denyVideo) this.pushEventTo(this.el, "insert_request", { editor_id: this._editorId, type: "video" });
          break;
        case "removeFormat": break;
        case "undo":
          this._historyUndo();
          break;
        case "redo":
          this._historyRedo();
          break;
      }
    },

    _toggleHeading: function (tag) {
      // Hybrid: the active block's truth is its SOURCE TEXT. formatBlock
      // restyled the DOM while the text still read as a paragraph —
      // pretty until the next exit re-rendered the block from its text
      // and silently undid the click (typing "## " by hand worked; the
      // toolbar button didn't). Toggle the prefix in the text instead,
      // which is exactly the path typing takes.
      if (
        this._mode === "hybrid" &&
        this._sourceBlock &&
        this._sourceBlock.isConnected &&
        this._getCurrentBlock() === this._sourceBlock
      ) {
        this._toggleSourceHeadingPrefix(tag);
        return;
      }

      var block = this._getCurrentBlock();
      if (block && block.tagName && block.tagName.toLowerCase() === tag) {
        document.execCommand("formatBlock", false, "p");
      } else {
        document.execCommand("formatBlock", false, tag);
      }
    },

    // Rewrite the active source block's heading prefix: `## ` on for the
    // requested level, off when it already has that level, swapped when it
    // has another. The rebuild then restyles the block live, the same way
    // it does when the marker is typed.
    _toggleSourceHeadingPrefix: function (tag) {
      var block = this._sourceBlock;
      var sel = window.getSelection();
      var cursorOffset = 0;
      if (
        sel.rangeCount &&
        block.contains(sel.getRangeAt(0).startContainer)
      ) {
        cursorOffset = this._textOffsetInBlock(
          block,
          sel.getRangeAt(0).startContainer,
          sel.getRangeAt(0).startOffset
        );
      }

      var sourceText = (block.textContent || "")
        .replace(/\u00a0/g, " ")
        .replace(/[\u200B\uFEFF]/g, "");
      var existing = sourceText.match(/^(#{1,6})(?!#)[ \t]/);
      var level = parseInt(tag.slice(1), 10) || 1;
      var body = existing ? sourceText.slice(existing[0].length) : sourceText;
      var prefix =
        existing && existing[1].length === level
          ? ""
          : "######".slice(0, level) + " ";
      var newSource = prefix + body;

      var newCursor = Math.max(
        prefix.length,
        Math.min(
          cursorOffset + (newSource.length - sourceText.length),
          newSource.length
        )
      );

      this._syntaxMutating = true;
      try {
        while (block.firstChild) block.removeChild(block.firstChild);
        if (newSource.length === 0) {
          block.innerHTML = "<br>";
        } else {
          block.appendChild(document.createTextNode(newSource));
        }
        var range = document.createRange();
        if (block.firstChild && block.firstChild.nodeType === Node.TEXT_NODE) {
          range.setStart(block.firstChild, newCursor);
        } else {
          range.setStart(block, 0);
        }
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      } finally {
        this._syntaxMutating = false;
      }

      this._lastSourceStateKey = null;
      this._activeMatchKey = null;
      this._refreshSourceBlock();
      this._debouncedPushVisualChange();
      this._updateCounts();
    },

    _toggleBlockquote: function () {
      var block = this._getCurrentBlock();
      if (
        block &&
        block.tagName &&
        block.tagName.toLowerCase() === "blockquote"
      ) {
        document.execCommand("formatBlock", false, "p");
      } else {
        document.execCommand("formatBlock", false, "blockquote");
      }
    },

    // Insert a code block (one click, no prompt — same as before). The
    // language is set afterwards via the hover overlay's language button,
    // stored as `data-language`, and round-trips as a ```lang fence.
    // Nest the caret's list item under its previous sibling, producing
    // valid `li > ol/ul > li` structure. Returns false when the caret
    // isn't in an indentable list item (caller falls back to execCommand).
    _indentListItem: function () {
      var block = this._getCurrentBlock();
      if (!block || !block.tagName || block.tagName.toLowerCase() !== "li") {
        return false;
      }
      var list = block.parentElement;
      if (!list || !/^(ul|ol)$/i.test(list.tagName)) return false;

      var prev = block.previousElementSibling;
      while (prev && prev.tagName.toLowerCase() !== "li") {
        prev = prev.previousElementSibling;
      }
      if (!prev) return false; // first item — nothing to nest under

      var listTag = list.tagName.toLowerCase();
      var nested = null;
      var last = prev.lastElementChild;
      if (last && last.tagName.toLowerCase() === listTag) {
        nested = last;
      } else {
        nested = document.createElement(listTag);
        prev.appendChild(nested);
      }

      var saved = this._captureEditorRange();
      nested.appendChild(block);
      this._savedRange = saved;
      this._restoreSavedRange();
      this._debouncedPushVisualChange();
      return true;
    },

    // Inverse of _indentListItem: lift a nested item back into the outer
    // list, right after the item that hosted it.
    _outdentListItem: function () {
      var block = this._getCurrentBlock();
      if (!block || !block.tagName || block.tagName.toLowerCase() !== "li") {
        return false;
      }
      var list = block.parentElement;
      if (!list || !/^(ul|ol)$/i.test(list.tagName)) return false;
      var hostLi = list.parentElement;
      if (!hostLi || hostLi.tagName.toLowerCase() !== "li") return false;
      var outerList = hostLi.parentElement;
      if (!outerList || !/^(ul|ol)$/i.test(outerList.tagName)) return false;

      var saved = this._captureEditorRange();
      outerList.insertBefore(block, hostLi.nextSibling);
      if (list.children.length === 0) {
        list.parentNode.removeChild(list);
      }
      this._savedRange = saved;
      this._restoreSavedRange();
      this._debouncedPushVisualChange();
      return true;
    },

    _insertCodeBlock: function () {
      // formatBlock inside a list item converts only the item's text and
      // strands the item chrome (e.g. a task checkbox) in the list.
      // Convert the whole item instead: move its text into a <pre> placed
      // after the list (replacing the list when it was the only item).
      var block = this._getCurrentBlock();
      if (block && block.tagName && block.tagName.toLowerCase() === "li") {
        var list = block.parentElement;
        if (list && list.parentNode) {
          var pre = document.createElement("pre");
          // Take the item's text minus its chrome: the task checkbox and
          // any hybrid source-mode marker span ("- [ ] " as literal text).
          var liClone = block.cloneNode(true);
          var chrome = liClone.querySelectorAll(
            ".leaf-task-box, .leaf-list-marker"
          );
          for (var ci = 0; ci < chrome.length; ci++) {
            chrome[ci].parentNode.removeChild(chrome[ci]);
          }
          pre.textContent = liClone.textContent.replace(/​/g, "");
          if (list.children.length === 1) {
            list.parentNode.replaceChild(pre, list);
          } else {
            list.parentNode.insertBefore(pre, list.nextSibling);
            list.removeChild(block);
          }
          this._placeCaretIn(pre);
          this._debouncedPushVisualChange();
          return;
        }
      }
      document.execCommand("formatBlock", false, "pre");
    },

    // -- Task lists (GFM checkboxes) --

    // Typing diagnostics, armed from the console with
    // `window.LEAF_DEBUG_TYPING = true`. Near-zero cost until armed.
    //
    // The signal is the trio: a keydown with no beforeinput after it means
    // the command never started (something prevented it, or focus/selection
    // was not where it looked); a beforeinput with no input means the engine
    // started the edit and abandoned it — and the mutation count says
    // whether script moved the DOM under it. No [leaf-typing] lines at all
    // while typing means the editor does not have focus.
    // Selection-driven work — source mode following the caret, the row
    // mirror — must never mutate the DOM in the middle of an editing
    // command: engines abandon the insertion when script moves the DOM
    // under them. But a fixed one-frame deferral is too blunt the other
    // way: the marker auto-format rebuild landed BETWEEN keystrokes and
    // re-seated the caret, so fast typing interleaved out of order. So the
    // window is tracked exactly — beforeinput opens it, input closes it —
    // and deferred work runs synchronously when idle, or immediately after
    // the command that blocked it.
    _afterEditingCommand: function (fn) {
      if (!this._typingCommandOpen) {
        fn();
        return;
      }

      if (!this._afterCommandQueue) this._afterCommandQueue = [];
      this._afterCommandQueue.push(fn);

      // Backstop for a command that dies without its input event — the
      // aborted edits this machinery exists to avoid causing.
      var self = this;
      setTimeout(function () {
        self._flushAfterCommand();
      }, 60);
    },

    _flushAfterCommand: function () {
      this._typingCommandOpen = false;
      var queue = this._afterCommandQueue;
      if (!queue || !queue.length) return;
      this._afterCommandQueue = [];
      for (var i = 0; i < queue.length; i++) queue[i]();
    },

    _setupCommandWindow: function () {
      if (!this._visualEl) return;
      var self = this;

      this._visualEl.addEventListener("beforeinput", function (e) {
        self._typingCommandOpen = true;

        // Typing over a selection that swallowed a task box. The box is
        // contenteditable=false, and some engines start such an insertText
        // and abandon it — the reporter's panel read "NO INPUT FOLLOWED".
        // Double-click on the first word anchors the range at the row
        // boundary, before the box; drags anchor in text, which is why
        // dragging always worked.
        //
        // Only insertText, and the edit is performed BY HAND — deleteContents
        // on the shrunk range, a text node, the caret after it. The previous
        // cure ran execCommand from inside the cancelled command's dispatch,
        // nesting one editing command inside another: characters landed in
        // neighbouring rows, and intercepting Backspace along the way broke
        // select-all-delete. Deletes and Enter stay native; engines handle
        // those over non-editable islands themselves.
        if (e.inputType === "insertText" && self._selectionHoldsTaskBox()) {
          e.preventDefault();
          self._typingCommandOpen = false;

          // Two intents, told apart by what was selected. The WHOLE row —
          // marker and all, the triple-click shape — means the row itself is
          // being replaced: the item leaves its list and the typed text
          // stands as a plain line, the way Obsidian treats a selected
          // marker as just more text. Anything less keeps the chrome and
          // replaces only the label.
          if (self._replaceWholeListRow(e.data || "")) {
            self._taskLog("takeover: whole row replaced");
            return;
          }

          self._taskLog("takeover: insertText over box-holding range");
          self._shrinkSelectionPastTaskBoxes();
          self._taskLog("takeover: after shrink");

          try {
            var sel2 = window.getSelection();
            if (sel2 && sel2.rangeCount) {
              var editRange = sel2.getRangeAt(0);
              editRange.deleteContents();

              var inserted = document.createTextNode(e.data || "");
              editRange.insertNode(inserted);

              var after = document.createRange();
              after.setStart(inserted, inserted.length);
              after.collapse(true);
              sel2.removeAllRanges();
              sel2.addRange(after);

              self._visualEl.dispatchEvent(new Event("input", { bubbles: true }));
            }
          } catch (err) {
            /* if even this cannot edit, there is nothing left to try */
          }
          return;
        }
      });

      this._visualEl.addEventListener("input", function () {
        self._flushAfterCommand();
      });
    },

    _setupTypingDiagnostics: function () {
      if (!this._visualEl) return;
      var self = this;

      // data-debug-typing arms the diagnostics without the console AND
      // mirrors the trace into a panel on the page — asking a reporter to
      // open devtools mid-bug loses the moment; a panel they can read and
      // paste from does not.
      if (this.el && this.el.dataset && this.el.dataset.debugTyping === "true") {
        window.LEAF_DEBUG_TYPING = true;
        this._typingPanel = document.createElement("pre");
        this._typingPanel.setAttribute("data-leaf-typing-panel", "");
        // pointer-events:none — the panel floats over the page, and a
        // click meant for a row underneath must reach that row, not the
        // panel. It swallowed such clicks once, and a swallowed click looks
        // exactly like the bug being reported. The copy button is the one
        // part that stays clickable.
        this._typingPanel.style.cssText =
          "position:fixed;bottom:0;right:0;max-width:46rem;max-height:14rem;" +
          "overflow:auto;background:#111;color:#9fe870;font-size:11px;" +
          "padding:6px 8px;z-index:9999;opacity:0.92;margin:0;" +
          "pointer-events:none;";
        this._typingPanel.textContent =
          "[leaf] typing trace — reproduce, then press copy\n";
        var copyBtn = document.createElement("button");
        copyBtn.type = "button";
        copyBtn.textContent = "copy";
        copyBtn.style.cssText =
          "position:fixed;bottom:2px;right:2px;z-index:10000;font-size:10px;" +
          "padding:1px 6px;pointer-events:auto;opacity:0.92;";
        copyBtn.addEventListener("click", function () {
          try {
            navigator.clipboard.writeText(self._typingPanel.textContent);
            copyBtn.textContent = "copied";
            setTimeout(function () {
              copyBtn.textContent = "copy";
            }, 1200);
          } catch (err) {
            copyBtn.textContent = "select it manually";
            self._typingPanel.style.pointerEvents = "auto";
          }
        });
        document.body.appendChild(this._typingPanel);
        document.body.appendChild(copyBtn);
      }

      var log = function (label, details) {
        if (!window.LEAF_DEBUG_TYPING) return;
        try {
          var line = "[leaf-typing] " + label + " | " + details;
          console.log(line);
          if (self._typingPanel) {
            self._typingPanel.textContent += line + "\n";
            self._typingPanel.scrollTop = self._typingPanel.scrollHeight;
          }
        } catch (err) {
          /* consoles can be strange places */
        }
      };

      var name = function (n) {
        if (!n) return "?";
        if (n.nodeType === 3) {
          return "#text(" + JSON.stringify((n.nodeValue || "").slice(0, 24)) + ")";
        }
        var cls = n.className ? "." + String(n.className).split(" ").join(".") : "";
        return (n.tagName || n.nodeName || "?") + cls;
      };

      var describeSelection = function () {
        var sel = window.getSelection();
        if (!sel || !sel.rangeCount) return "no selection";
        var r = sel.getRangeAt(0);
        var head = sel.isCollapsed ? "caret " : "range ";
        var out = head + name(r.startContainer) + "@" + r.startOffset;
        if (!sel.isCollapsed) out += " -> " + name(r.endContainer) + "@" + r.endOffset;
        return out;
      };

      this._visualEl.addEventListener("keydown", function (e) {
        var boxFact = "";
        try {
          var s2 = window.getSelection();
          if (s2 && s2.rangeCount && !s2.isCollapsed) {
            var r2 = s2.getRangeAt(0);
            var box2 = self._visualEl.querySelector(".leaf-task-box");
            if (box2) {
              var br2 = document.createRange();
              br2.selectNode(box2);
              boxFact =
                " | boxInRange=" +
                (r2.compareBoundaryPoints(Range.START_TO_START, br2) <= 0 &&
                  r2.compareBoundaryPoints(Range.END_TO_START, br2) < 0);
            }
          }
        } catch (err2) {
          boxFact = " | boxInRange=?";
        }
        log(
          "keydown '" + e.key + "'",
          describeSelection() + boxFact + " | focus=" + name(document.activeElement)
        );
      });

      this._visualEl.addEventListener("beforeinput", function (e) {
        if (self._typingDiagObserver) self._typingDiagObserver.takeRecords();
        self._typingCommandOpen = true;
        log(
          "beforeinput " + e.inputType,
          describeSelection() + (e.defaultPrevented ? " | ALREADY PREVENTED" : "")
        );

        // A beforeinput that never reaches its input is the abort we are
        // hunting. Flagged a beat later so the pairing is visible even when
        // the engine dies silently in between.
        setTimeout(function () {
          if (self._typingCommandOpen) {
            self._typingCommandOpen = false;
            log("NO INPUT FOLLOWED", "the engine abandoned the edit");
          }
        }, 60);
      });

      this._visualEl.addEventListener("input", function (e) {
        var records = self._typingDiagObserver
          ? self._typingDiagObserver.takeRecords()
          : [];
        self._typingCommandOpen = false;
        log(
          "input " + (e.inputType || "?"),
          describeSelection() + " | mutations=" + records.length
        );
      });

      if (typeof MutationObserver === "function") {
        this._typingDiagObserver = new MutationObserver(function () {});
        this._typingDiagObserver.observe(this._visualEl, {
          subtree: true,
          childList: true,
          attributes: true,
          characterData: true
        });
      }
    },

    _setupTaskLists: function () {
      if (!this._visualEl) return;

      this._setupCommandWindow();
      this._setupTypingDiagnostics();
      this._visualEl.addEventListener("mousedown", this._onTaskMouseDown.bind(this));

      // The gestures resolve on mouseup, and a drag can end anywhere — off
      // the row, off the editor — so the up listener is document-wide.
      this._onTaskMouseUpBound = this._onTaskMouseUp.bind(this);
      document.addEventListener("mouseup", this._onTaskMouseUpBound);

      // Reveal covered rows' markers while they are inside the selection.
      // DEFERRED, never synchronous: typing over a selection makes the
      // browser delete it and insert the key in one editing command, with a
      // selectionchange fired in the middle — and a DOM mutation from inside
      // that event makes engines abandon the insertion. Marked rows got
      // unmarked mid-keystroke, and the keystroke died with it: select a
      // word, type, and nothing happened.
      this._onTaskSelectionChange = this._scheduleTaskSelectionMirror.bind(this);
      document.addEventListener("selectionchange", this._onTaskSelectionChange);
    },

    // mousedown handles only what must beat the browser to the caret: the
    // box toggle. Everything else used to be decided here too — and
    // preventDefault on mousedown is how you make a row unselectable, since
    // a drag-selection that starts in the gutter dies before it begins. The
    // gutter and stranded-caret gestures wait for mouseup and only fire when
    // no drag happened.
    _taskLog: function (label, extra) {
      if (!window.LEAF_DEBUG_TYPING) return;
      try {
        var sel = window.getSelection();
        var selDesc = !sel || !sel.rangeCount
          ? "none"
          : (sel.isCollapsed ? "caret" : "range:" + JSON.stringify(String(sel).slice(0, 30)));
        var line = "[leaf-mouse] " + label + " | sel=" + selDesc + (extra ? " | " + extra : "");
        console.log(line);
        if (this._typingPanel) {
          this._typingPanel.textContent += line + "\n";
          this._typingPanel.scrollTop = this._typingPanel.scrollHeight;
        }
      } catch (err) {
        /* consoles */
      }
    },

    // When the selection covers a list row's ENTIRE text — marker included —
    // replace the row itself: the item leaves its list and the typed text
    // stands as a plain paragraph. Any list row: "- [ ]", "- ", "3. " are
    // one rule. Returns false when the selection is anything less, leaving
    // the label-only path to handle it.
    _replaceWholeListRow: function (text) {
      var sel = window.getSelection();
      if (!sel || !sel.rangeCount || sel.isCollapsed) return false;

      var range = sel.getRangeAt(0);
      var host = range.commonAncestorContainer;
      var li = host.nodeType === 1 ? host.closest && host.closest("li") : null;
      if (!li && host.parentNode && host.parentNode.closest) {
        li = host.parentNode.closest("li");
      }
      if (!li && host.nodeType === 1) {
        // A row-level range (li itself, or its list) — the triple-click shape.
        var lis = host.querySelectorAll ? host.querySelectorAll("li") : [];
        if (lis.length === 1) li = lis[0];
      }
      if (!li || !this._visualEl.contains(li)) return false;

      var scrub = function (t) {
        return (t || "").replace(/[\u200B\uFEFF\s\u00a0]/g, "");
      };
      if (scrub(range.toString()) !== scrub(li.textContent)) return false;

      // Text equality is not enough: on a lone-word row the label IS all the
      // text, and a double-clicked word must stay a label replacement. The
      // row is only "wholly selected" when the selection structurally holds
      // its chrome — the box, or the marker span the reveal made visible.
      var box = li.querySelector(".leaf-task-box");
      var marker = li.querySelector(".leaf-source-marker.leaf-list-marker");
      var holdsChrome =
        (box &&
          (this._rangeContainsNode(range, box) || box.contains(range.startContainer))) ||
        (marker &&
          (this._rangeContainsNode(range, marker) || marker.contains(range.startContainer)));
      if (!holdsChrome) return false;

      var list = li.parentNode;
      if (!list || !list.parentNode) return false;

      var paragraph = document.createElement("p");
      var inserted = document.createTextNode(text);
      paragraph.appendChild(inserted);
      if (text === "") paragraph.appendChild(document.createElement("br"));

      this._forgetSourceBlock(li);

      if (list.children.length === 1) {
        list.parentNode.replaceChild(paragraph, list);
      } else if (!li.previousElementSibling) {
        list.parentNode.insertBefore(paragraph, list);
        list.removeChild(li);
      } else if (!li.nextElementSibling) {
        list.parentNode.insertBefore(paragraph, list.nextSibling);
        list.removeChild(li);
      } else {
        // Mid-list: the rows below move to a second list after the paragraph.
        // A divided numbered list restarts at 1 — two separated lists are two
        // lists, and rejoining them (the merge pass) restores the numbering.
        var tail = document.createElement(list.tagName.toLowerCase());

        while (li.nextElementSibling) tail.appendChild(li.nextElementSibling);
        list.removeChild(li);
        list.parentNode.insertBefore(paragraph, list.nextSibling);
        paragraph.parentNode.insertBefore(tail, paragraph.nextSibling);
      }

      var caret = document.createRange();
      caret.setStart(inserted, inserted.length);
      caret.collapse(true);
      sel.removeAllRanges();
      sel.addRange(caret);

      this._visualEl.dispatchEvent(new Event("input", { bubbles: true }));
      return true;
    },

    // Whether the current selection's range holds a task box — the shape
    // some engines refuse to edit over.
    _selectionHoldsTaskBox: function () {
      var sel = window.getSelection();
      if (!sel || !sel.rangeCount || sel.isCollapsed) return false;

      var range = sel.getRangeAt(0);
      if (!this._visualEl || !this._visualEl.contains(range.commonAncestorContainer)) {
        return false;
      }

      // The box, and the row's list-marker span — the revealed "- [ ] " a
      // covered row shows. NOT inline source markers (the ** around bold):
      // those are body content a replacement may legitimately eat.
      var chrome = this._visualEl.querySelectorAll(
        ".leaf-task-box, .leaf-source-marker.leaf-list-marker"
      );

      for (var i = 0; i < chrome.length; i++) {
        if (
          this._rangeContainsNode(range, chrome[i]) ||
          chrome[i].contains(range.startContainer)
        ) {
          return true;
        }
      }

      return false;
    },

    // Containment spelled with comparePoint, whose answer is unambiguous.
    // The compareBoundaryPoints spelling this replaces read a box ENTIRELY
    // AFTER the selection as contained: the takeover fired for a selection
    // in one row because the NEXT row had a box, the shrink moved the range
    // start past that next row's box — start after end, a collapsed caret in
    // the wrong row — and the typed characters followed it there.
    _rangeContainsNode: function (range, node) {
      var parent = node.parentNode;
      if (!parent) return false;

      var index = Array.prototype.indexOf.call(parent.childNodes, node);

      try {
        return (
          range.comparePoint(parent, index) === 0 &&
          range.comparePoint(parent, index + 1) === 0
        );
      } catch (err) {
        return false;
      }
    },

    // Move a selection's boundaries off any task box they contain or sit
    // before, without changing which TEXT is selected. Boundaries at element
    // level (li@0, before the box) select the same characters as boundaries
    // in the text — but only the text-level form is one every engine will
    // let an edit replace.
    _shrinkSelectionPastTaskBoxes: function () {
      var sel = window.getSelection();
      if (!sel || !sel.rangeCount || sel.isCollapsed) return;

      var range = sel.getRangeAt(0);
      if (!this._visualEl || !this._visualEl.contains(range.commonAncestorContainer)) {
        return;
      }

      var chrome = this._visualEl.querySelectorAll(
        ".leaf-task-box, .leaf-source-marker.leaf-list-marker"
      );
      var adjusted = false;
      var newRange = range.cloneRange();

      // Document order matters: the box precedes the marker span, and moving
      // the start past the box leaves the marker contained for the next
      // pass of the same loop. A start INSIDE a chrome element — a
      // double-click that grabbed the revealed "- [ ] " — steps out the same
      // way.
      for (var i = 0; i < chrome.length; i++) {
        var piece = chrome[i];

        if (
          this._rangeContainsNode(newRange, piece) ||
          piece.contains(newRange.startContainer)
        ) {
          newRange.setStartAfter(piece);
          adjusted = true;
        }
      }

      if (!adjusted) return;

      // Anchor the start inside the first TEXT after the box when there is
      // one, so the boundary is unambiguous to the engine.
      var startNode = newRange.startContainer;
      if (startNode.nodeType !== 3) {
        var probe = startNode.childNodes[newRange.startOffset];
        if (probe && probe.nodeType === 3) newRange.setStart(probe, 0);
      }

      this._taskLog("selection shrunk past task box");
      sel.removeAllRanges();
      sel.addRange(newRange);
    },

    _onTaskMouseDown: function (e) {
      this._taskLog("mousedown detail=" + e.detail, "target=" + (e.target.className || e.target.nodeName));
      if (this._readonly) return;
      var li = e.target.closest && e.target.closest("li.leaf-task");
      if (!li) return;
      var box = li.querySelector(".leaf-task-box");
      if (!box) return;

      // Click on the box → toggle.
      if (e.target.closest(".leaf-task-box")) {
        e.preventDefault();
        e.stopPropagation();
        li.setAttribute(
          "data-checked",
          li.getAttribute("data-checked") === "true" ? "false" : "true"
        );
        this._debouncedPushVisualChange();
        return;
      }

      this._taskGesture = { li: li, x: e.clientX, y: e.clientY, detail: e.detail };
    },

    _onTaskMouseUp: function (e) {
      var gesture = this._taskGesture;
      this._taskGesture = null;
      this._taskLog("mouseup detail=" + e.detail, "gesture=" + (gesture ? "yes(detail=" + gesture.detail + ")" : "no"));
      if (!gesture || this._readonly) return;

      var li = gesture.li;
      if (!li.isConnected) return;
      var box = li.querySelector(".leaf-task-box");
      if (!box) return;

      // A double or triple click is a selection gesture by definition — and
      // in some browsers the word-selection is not committed yet at the
      // second mouseup, so "is anything selected?" answers no at exactly the
      // wrong moment. The corrector then collapsed the fresh selection to
      // after the box: the highlight still painted, but typing went to a
      // caret nobody could see. Keyboard-made selections replaced fine,
      // mouse-made ones died — and only on the first word, the one inside
      // the box's stranding zone.
      if (gesture.detail > 1 || e.detail > 1) {
        this._taskLog("gesture: stand down (multi-click)");
        return;
      }

      // Any real movement, or any selection, means this was a drag — the
      // person was selecting, and selecting must win.
      if (Math.abs(e.clientX - gesture.x) + Math.abs(e.clientY - gesture.y) > 4) return;
      var sel = window.getSelection();
      if (sel && sel.rangeCount && !sel.isCollapsed) return;

      // Click in the gutter LEFT of the box: open the raw `- [ ] ` marker
      // for editing — the one deliberate way in, since the marker hides
      // behind the checkbox everywhere else.
      var boxRect = box.getBoundingClientRect();
      if (boxRect.width > 0 && e.clientX < boxRect.left) {
        this._taskLog("gesture: gutter reveal");
        this._visualEl.focus({ preventScroll: true });
        this._revealTaskMarker(li);
        return;
      }

      // Caret left stranded on/behind the box, where nothing is typeable?
      // Move it to the label.
      var pos = this._caretFromPoint(e.clientX, e.clientY);
      if (!pos) return;
      var stranded =
        pos.node === box ||
        box.contains(pos.node) ||
        (pos.node === li && pos.offset === 0);
      if (!stranded) return;

      this._taskLog("gesture: STRANDED CORRECTION — collapsing to after the box");
      this._visualEl.focus({ preventScroll: true });
      var r = document.createRange();
      var after = box.nextSibling;
      if (after && after.nodeType === Node.TEXT_NODE) r.setStart(after, 0);
      else r.setStartAfter(box);
      r.collapse(true);
      sel.removeAllRanges();
      sel.addRange(r);
    },

    // One deferred run per burst of selection changes. requestAnimationFrame
    // lands after the editing command that triggered the change has finished
    // with the DOM, which is the entire point.
    _scheduleTaskSelectionMirror: function () {
      if (this._taskMirrorScheduled) return;
      this._taskMirrorScheduled = true;

      var self = this;

      this._afterEditingCommand(function () {
        self._taskMirrorScheduled = false;
        self._mirrorTaskSelection();
      });
    },

    // Mark every list row whose text the selection covers entirely, carrying
    // the literal marker it would show in source form, so CSS can reveal
    // "- " / "3. " / "- [x] " under the sweep the way Obsidian does.
    _mirrorTaskSelection: function () {
      if (!this._visualEl) return;
      this._taskLog("mirror run");

      var marked = this._visualEl.querySelectorAll("li.leaf-row-in-selection");
      var covered = [];
      var sel = window.getSelection();

      if (
        (this._mode === "visual" || this._mode === "hybrid") &&
        sel &&
        sel.rangeCount &&
        !sel.isCollapsed
      ) {
        var range = sel.getRangeAt(0);
        if (this._visualEl.contains(range.commonAncestorContainer)) {
          covered = this._coveredListItems(range);
        }
      }

      var keep = [];
      for (var j2 = 0; j2 < covered.length; j2++) keep.push(covered[j2].li);

      for (var i = 0; i < marked.length; i++) {
        // Still covered: leave it exactly as it is. A drag extending across
        // a marked row fires selectionchange constantly, and rewriting the
        // same class is churn with no message.
        if (keep.indexOf(marked[i]) !== -1) continue;
        marked[i].classList.remove("leaf-row-in-selection");
        marked[i].removeAttribute("data-leaf-selmarker");
        // A source-mode row had its real marker revealed below; hand it
        // back. The caret-driven refresh re-reveals it if the caret is on
        // the marker — during a live selection that refresh is frozen, so
        // this flip is ours to undo.
        if (marked[i].hasAttribute("data-leaf-source")) {
          marked[i].classList.remove("leaf-marker-active");
        }
      }
      for (var j = 0; j < covered.length; j++) {
        var row = covered[j].li;
        if (!row.classList.contains("leaf-row-in-selection")) {
          row.classList.add("leaf-row-in-selection");
        }

        if (row.hasAttribute("data-leaf-source")) {
          // A row already in source form carries its real marker as hidden
          // span text. A class flip shows it — no rebuild, so the selection
          // survives — and since that text sits inside the selected range,
          // the native highlight paints it: the genuine Obsidian article.
          row.classList.add("leaf-marker-active");
        } else if (row.getAttribute("data-leaf-selmarker") !== covered[j].marker) {
          row.setAttribute("data-leaf-selmarker", covered[j].marker);
        }
      }
    },

    // Put a task item into source mode with its `- [ ] ` marker revealed and
    // the caret at the marker's start — the editing entrance the gutter click
    // opens. Everywhere else the marker stays hidden behind the checkbox.
    _revealTaskMarker: function (li) {
      if (!li || !this._visualEl) return false;

      // Get the caret into the item first: source mode follows the caret.
      var seed = this._firstTextDescendant(li);
      var range = document.createRange();

      if (seed) range.setStart(seed, 0);
      else range.setStart(li, li.childNodes.length);
      range.collapse(true);

      var sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);

      var sourceBlock =
        li.hasAttribute && li.hasAttribute("data-leaf-source")
          ? li
          : this._enterSourceMode(li);
      if (!sourceBlock) return false;

      var marker = sourceBlock.querySelector(".leaf-list-marker");
      var markerText = marker && marker.firstChild;
      if (!markerText || markerText.nodeType !== Node.TEXT_NODE) return false;

      // Caret onto the marker's first character: cursorOffset 0 is strictly
      // inside the marker, so the refresh reveals it.
      var reveal = document.createRange();
      reveal.setStart(markerText, 0);
      reveal.collapse(true);
      sel.removeAllRanges();
      sel.addRange(reveal);

      this._refreshSourceBlock();
      return true;
    },

    // Resolve the caret position the browser would pick for a viewport
    // point, across the two standard APIs. Returns { node, offset } or null.
    _caretFromPoint: function (x, y) {
      if (document.caretRangeFromPoint) {
        var rng = document.caretRangeFromPoint(x, y);
        return rng ? { node: rng.startContainer, offset: rng.startOffset } : null;
      }
      if (document.caretPositionFromPoint) {
        var p = document.caretPositionFromPoint(x, y);
        return p ? { node: p.offsetNode, offset: p.offset } : null;
      }
      return null;
    },

    // Insert `el` as a top-level block AFTER the block holding the caret.
    // execCommand("insertHTML") splits the current paragraph at the caret
    // (mid-word) and Chrome additionally mangles structures like
    // <details><summary> during the split — explicit DOM insertion avoids
    // both. An empty current paragraph is replaced instead of kept.
    _insertBlockAfterCurrent: function (el) {
      var block = this._getCurrentBlock();
      while (
        block &&
        block.parentElement &&
        block.parentElement !== this._visualEl
      ) {
        block = block.parentElement;
      }
      if (block && block.parentElement === this._visualEl) {
        var isEmptyPara =
          block.tagName &&
          block.tagName.toLowerCase() === "p" &&
          block.textContent.replace(/[​ \s]/g, "") === "";
        if (isEmptyPara) {
          block.parentNode.replaceChild(el, block);
        } else {
          block.parentNode.insertBefore(el, block.nextSibling);
        }
      } else {
        this._visualEl.appendChild(el);
      }
      return el;
    },

    _placeCaretIn: function (el, selectContents) {
      var range = document.createRange();
      range.selectNodeContents(el);
      if (!selectContents) range.collapse(false);
      var sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    },

    // The bullet and numbered list buttons, for where execCommand cannot go.
    //
    // execCommand("insert*List") needs a caret on content to act on: on a
    // fresh editor nobody has clicked into, or an empty line, it does nothing
    // — the button appeared dead, which is the same complaint the task-list
    // button drew before it was taught to build its item itself. This is that
    // lesson applied to the other two buttons.
    //
    // Deliberately narrow. With text actually selected, or with the caret
    // already inside a list, the native command is the better tool — it
    // converts multi-line selections in one gesture, toggles an item back out
    // of its list, and switches a list between kinds. Returning false hands
    // those cases to it.
    _insertList: function (tag) {
      if (!this._visualEl) return false;

      // Read the selection BEFORE focusing: focus can collapse it, and a
      // collapsed selection here reads as "nothing selected" — sending the
      // multi-line case down the single-item path.
      var sel = window.getSelection();

      if (
        sel &&
        sel.rangeCount > 0 &&
        !sel.getRangeAt(0).collapsed &&
        this._visualEl.contains(sel.getRangeAt(0).startContainer)
      ) {
        return false;
      }

      this._visualEl.focus();

      var block = this._getCurrentBlock();

      if (block && block.closest && block.closest("li, ul, ol")) return false;

      var li = document.createElement("li");
      var list = document.createElement(tag);
      list.appendChild(li);

      // Convert the current line rather than splitting it — the same shape
      // the task-list button settled on.
      var isPlainBlock =
        block &&
        block.parentElement === this._visualEl &&
        /^(p|h[1-6]|div)$/i.test(block.tagName || "");

      if (isPlainBlock) {
        // Only carry the line's children over when there is text among them.
        // An empty line's lone <br> must NOT come: a <br> blocks the
        // placeholder (it is on that helper's skip list), leaving an item
        // with no text — which hybrid's empty-line tidying then deletes the
        // moment the caret moves on. Created and gone before anyone typed.
        if (/\S/.test(block.textContent || "")) {
          while (block.firstChild) li.appendChild(block.firstChild);
        }

        block.parentNode.replaceChild(list, block);
        this._forgetSourceBlock(block);
      } else {
        this._insertBlockAfterCurrent(list);
      }

      // An empty line converts to an empty item; the placeholder is where the
      // caret sits and what the tidying rules recognise as deliberate.
      this._ensureListItemPlaceholder(li);

      this._placeCaretIn(li);
      this._debouncedPushVisualChange();
      return true;
    },

    _insertTaskList: function () {
      if (!this._visualEl) return;
      this._visualEl.focus();

      var li = document.createElement("li");
      li.className = "leaf-task";
      li.setAttribute("data-checked", "false");
      var box = document.createElement("span");
      box.className = "leaf-task-box";
      box.setAttribute("contenteditable", "false");
      li.appendChild(box);

      var ul = document.createElement("ul");
      ul.appendChild(li);

      // Convert the current line into a task item — same behavior as the
      // bullet/numbered list buttons — instead of splitting the paragraph
      // at the caret and inserting an empty item between the halves.
      var block = this._getCurrentBlock();
      var isPlainBlock =
        block &&
        block.parentElement === this._visualEl &&
        /^(p|h[1-6]|div)$/i.test(block.tagName || "");

      if (isPlainBlock) {
        while (block.firstChild) li.appendChild(block.firstChild);
        block.parentNode.replaceChild(ul, block);
        this._forgetSourceBlock(block);
      } else {
        this._insertBlockAfterCurrent(ul);
      }

      // Converting an EMPTY line moves nothing, so the item would be left with
      // its checkbox and no text node — nowhere for the caret, and nothing to
      // type into. Both branches go through the same helper so the button
      // behaves the same on a blank line as on one with text.
      this._ensureListItemPlaceholder(li);

      this._placeCaretIn(li);
      this._debouncedPushVisualChange();
    },

    // -- Callouts / admonitions --

    _insertCallout: function (type) {
      type = type || "note";
      var title = type.charAt(0).toUpperCase() + type.slice(1);
      var bq = document.createElement("blockquote");
      bq.className = "leaf-callout leaf-callout-" + type;
      bq.setAttribute("data-callout", type);
      var titleP = document.createElement("p");
      titleP.className = "leaf-callout-title";
      titleP.setAttribute("contenteditable", "false");
      titleP.textContent = title;
      var bodyP = document.createElement("p");
      bodyP.appendChild(document.createTextNode("​"));
      bq.appendChild(titleP);
      bq.appendChild(bodyP);

      this._insertBlockAfterCurrent(bq);
      if (!bq.nextSibling) {
        var tail = document.createElement("p");
        tail.appendChild(document.createElement("br"));
        bq.parentNode.appendChild(tail);
      }
      this._placeCaretIn(bodyP);
      this._debouncedPushVisualChange();
    },

    // Insert a collapsible <details> block with an editable summary + body.
    _insertDetailsBlock: function () {
      if (!this._visualEl) return;
      var details = document.createElement("details");
      details.setAttribute("open", "");
      var summary = document.createElement("summary");
      summary.textContent = "Summary";
      var body = document.createElement("p");
      body.appendChild(document.createElement("br"));
      details.appendChild(summary);
      details.appendChild(body);

      this._insertBlockAfterCurrent(details);
      if (!details.nextSibling) {
        var tail = document.createElement("p");
        tail.appendChild(document.createElement("br"));
        details.parentNode.appendChild(tail);
      }
      // Select the placeholder summary so typing renames it immediately.
      this._placeCaretIn(summary, true);
      this._debouncedPushVisualChange();
    },

    // -- Emoji Picker --

    _emojiCategories: [
      { name: "Smileys", emojis: ["😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "🙃", "😉", "😊", "😇", "🥰", "😍", "🤩", "😘", "😗", "😚", "😙", "🥲", "😋", "😛", "😜", "🤪", "😝", "🤑", "🤗", "🤭", "🤫", "🤔", "🫡", "🤐", "🤨", "😐", "😑", "😶", "🫥", "😏", "😒", "🙄", "😬", "🤥", "😌", "😔", "😪", "🤤", "😴", "😷", "🤒", "🤕", "🤢", "🤮", "🥴", "😵", "🤯", "🥳", "🥸", "😎", "🤓", "🧐"] },
      { name: "Gestures", emojis: ["👋", "🤚", "🖐", "✋", "🖖", "🫱", "🫲", "🫳", "🫴", "👌", "🤌", "🤏", "✌️", "🤞", "🫰", "🤟", "🤘", "🤙", "👈", "👉", "👆", "🖕", "👇", "☝️", "🫵", "👍", "👎", "✊", "👊", "🤛", "🤜", "👏", "🙌", "🫶", "👐", "🤲", "🤝", "🙏"] },
      { name: "Hearts", emojis: ["❤️", "🧡", "💛", "💚", "💙", "💜", "🖤", "🤍", "🤎", "💔", "❤️‍🔥", "❤️‍🩹", "❣️", "💕", "💞", "💓", "💗", "💖", "💘", "💝", "💟"] },
      { name: "Animals", emojis: ["🐶", "🐱", "🐭", "🐹", "🐰", "🦊", "🐻", "🐼", "🐻‍❄️", "🐨", "🐯", "🦁", "🐮", "🐷", "🐸", "🐵", "🐔", "🐧", "🐦", "🐤", "🦆", "🦅", "🦉", "🦇", "🐺", "🐗", "🐴", "🦄", "🐝", "🐛", "🦋", "🐌", "🐞"] },
      { name: "Food", emojis: ["🍎", "🍐", "🍊", "🍋", "🍌", "🍉", "🍇", "🍓", "🫐", "🍈", "🍒", "🍑", "🥭", "🍍", "🥥", "🥝", "🍅", "🥑", "🍕", "🍔", "🍟", "🌭", "🍿", "🧁", "🍰", "🎂", "🍩", "🍪", "🍫", "🍬", "☕", "🍵", "🥤", "🍺", "🍷"] },
      { name: "Travel", emojis: ["🚗", "🚕", "🚌", "🏎", "🚑", "🚒", "✈️", "🚀", "🛸", "🚁", "⛵", "🚢", "🏠", "🏢", "🏥", "🏫", "⛪", "🕌", "🗼", "🗽", "⛲", "🌋", "🏔", "🏖", "🏕"] },
      { name: "Objects", emojis: ["⌚", "📱", "💻", "⌨️", "🖥", "🖨", "🖱", "💾", "💿", "📷", "📹", "🎥", "📺", "📻", "🎙", "⏰", "🔋", "🔌", "💡", "🔦", "🕯", "💰", "💳", "💎", "🔧", "🔨", "🔩", "⚙️", "📎", "📌", "✂️", "🔑", "🗝", "🔒", "🔓"] },
      { name: "Symbols", emojis: ["✅", "❌", "❓", "❗", "💯", "🔥", "⭐", "🌟", "✨", "💫", "💥", "💢", "💤", "🎵", "🎶", "🔔", "🔕", "📣", "💬", "💭", "🏁", "🚩", "🎯", "♻️", "⚠️", "🚫", "❎", "✳️", "❇️", "🔴", "🟠", "🟡", "🟢", "🔵", "🟣", "⚫", "⚪"] }
    ],

    // Search keywords per emoji (space-separated, lowercase). Covers the
    // common/searchable ones; emojis absent here are still browsable by
    // category and still match when their category name matches the query.
    _emojiKeywords: {
      "😀":"grinning happy smile","😃":"happy smile joy","😄":"happy smile laugh",
      "😁":"grin smile happy","😆":"laugh haha","😅":"sweat laugh nervous",
      "🤣":"rofl laugh haha funny","😂":"joy laugh cry funny tears","🙂":"slight smile",
      "🙃":"upside down silly","😉":"wink","😊":"blush smile happy","😇":"angel innocent halo",
      "🥰":"love hearts adore","😍":"love heart eyes","🤩":"star struck wow excited",
      "😘":"kiss love","😋":"yum tasty tongue","😛":"tongue silly","😜":"wink tongue silly",
      "🤪":"crazy zany silly","🤑":"money mouth rich","🤗":"hug","🤔":"thinking hmm",
      "🤐":"zipper quiet secret","😐":"neutral meh","😑":"expressionless meh","😶":"no mouth quiet",
      "😏":"smirk","😒":"unamused meh","🙄":"eye roll","😬":"grimace awkward","😴":"sleep tired zzz",
      "😷":"mask sick","🤒":"sick thermometer ill","🤕":"hurt injured bandage","🤢":"sick nausea gross",
      "🤮":"vomit sick puke","🥴":"woozy drunk dizzy","😵":"dizzy dead knockout","🤯":"mind blown explode",
      "🥳":"party celebrate birthday","😎":"cool sunglasses","🤓":"nerd geek glasses","🧐":"monocle inspect",
      "👋":"wave hello hi bye","✋":"hand stop raised","👌":"ok perfect","✌️":"peace victory",
      "🤞":"fingers crossed luck","🤟":"love you hand","🤘":"rock horns","🤙":"call me shaka",
      "👈":"point left","👉":"point right","👆":"point up","👇":"point down","☝️":"point up one",
      "👍":"thumbs up like yes good approve","👎":"thumbs down dislike no bad","✊":"fist raised power",
      "👊":"fist bump punch","👏":"clap applause","🙌":"raise hands celebrate praise","🙏":"pray thanks please",
      "🤝":"handshake deal agree","❤️":"heart love red","🧡":"orange heart","💛":"yellow heart",
      "💚":"green heart","💙":"blue heart","💜":"purple heart","🖤":"black heart","🤍":"white heart",
      "💔":"broken heart","💕":"hearts love","💖":"sparkle heart love","💗":"growing heart love",
      "🐶":"dog puppy","🐱":"cat kitten","🐭":"mouse","🐹":"hamster","🐰":"rabbit bunny","🦊":"fox",
      "🐻":"bear","🐼":"panda","🐨":"koala","🐯":"tiger","🦁":"lion","🐮":"cow","🐷":"pig","🐸":"frog",
      "🐵":"monkey","🐔":"chicken","🐧":"penguin","🐦":"bird","🦆":"duck","🦅":"eagle","🦉":"owl",
      "🦄":"unicorn","🐝":"bee","🦋":"butterfly","🐌":"snail","🐞":"ladybug",
      "🍎":"apple red fruit","🍐":"pear","🍊":"orange tangerine","🍋":"lemon","🍌":"banana",
      "🍉":"watermelon","🍇":"grapes","🍓":"strawberry","🍑":"peach","🥭":"mango","🍍":"pineapple",
      "🥥":"coconut","🥝":"kiwi","🍅":"tomato","🥑":"avocado","🍕":"pizza","🍔":"burger hamburger",
      "🍟":"fries chips","🌭":"hot dog","🍿":"popcorn","🧁":"cupcake","🍰":"cake slice","🎂":"birthday cake",
      "🍩":"donut doughnut","🍪":"cookie","🍫":"chocolate","🍬":"candy sweet","☕":"coffee tea hot",
      "🍵":"tea green","🥤":"soda drink cup","🍺":"beer","🍷":"wine",
      "🚗":"car auto","🚕":"taxi cab","🚌":"bus","🚑":"ambulance","🚒":"fire truck","✈️":"plane flight travel",
      "🚀":"rocket launch space","🛸":"ufo alien","🚁":"helicopter","⛵":"sailboat boat","🚢":"ship",
      "🏠":"house home","🏢":"office building","🏥":"hospital","🏫":"school","⛪":"church","🗼":"tower",
      "🗽":"statue liberty","🌋":"volcano","🏔":"mountain","🏖":"beach","🏕":"camping tent",
      "⌚":"watch time","📱":"phone mobile","💻":"laptop computer","⌨️":"keyboard","🖥":"desktop monitor",
      "🖨":"printer","📷":"camera photo","📹":"video camera","🎥":"movie film","📺":"tv television",
      "⏰":"alarm clock time","🔋":"battery","🔌":"plug power","💡":"idea light bulb","🔦":"flashlight torch",
      "💰":"money bag cash","💳":"credit card","💎":"diamond gem","🔧":"wrench tool","🔨":"hammer tool",
      "⚙️":"gear settings cog","📎":"paperclip attach","📌":"pin","✂️":"scissors cut","🔑":"key",
      "🔒":"lock locked secure","🔓":"unlock open",
      "✅":"check tick done yes correct","❌":"cross x no wrong cancel","❓":"question","❗":"exclamation",
      "💯":"hundred percent perfect","🔥":"fire hot lit flame","⭐":"star","🌟":"star glowing","✨":"sparkles shiny",
      "💥":"explosion boom","💤":"sleep zzz","🎵":"music note","🎶":"music notes","🔔":"bell notification",
      "💬":"speech bubble chat comment","💭":"thought bubble","🚩":"flag red","🎯":"target dart bullseye",
      "♻️":"recycle","⚠️":"warning caution","🚫":"no prohibited forbidden","🔴":"red circle","🟠":"orange circle",
      "🟡":"yellow circle","🟢":"green circle","🔵":"blue circle","🟣":"purple circle","⚫":"black circle","⚪":"white circle"
    },

    // Minimal inline replacement for window.prompt (which blocks the whole
    // page and can't be styled): a small floating dialog with one text
    // input and Cancel/OK. Enter/OK submit (the raw value, possibly "");
    // Escape/Cancel/outside-click dismiss without calling onSubmit.
    // opts: { label, value, placeholder, anchor, multiline, confirmLabel,
    //         width, onSubmit(value), onCancel() }
    // `multiline` swaps the input for a monospace textarea (Enter inserts a
    // newline; Cmd/Ctrl+Enter submits) — the atomic-block source editor
    // needs room and real line breaks.
    _openTextPrompt: function (opts) {
      var self = this;
      this._closeTextPrompt();
      var multiline = opts.multiline === true;
      var width = opts.width || (multiline ? 380 : 280);

      var dialog = document.createElement("div");
      dialog.className = "leaf-text-prompt";
      dialog.setAttribute("contenteditable", "false");
      dialog.style.cssText =
        "position:absolute;z-index:60;background:var(--color-base-200,#e5e7eb);color:var(--color-base-content,#1f2937);border:1px solid var(--color-base-300,#d1d5db);border-radius:0.5rem;box-shadow:0 4px 16px rgba(0,0,0,0.12),0 1px 4px rgba(0,0,0,0.08);padding:0.5rem;width:" +
        width + "px;";

      var label = document.createElement("div");
      label.textContent = opts.label || "";
      label.style.cssText =
        "font-size:0.72rem;font-weight:600;margin-bottom:0.3rem;opacity:0.8;";
      dialog.appendChild(label);

      var input;
      if (multiline) {
        input = document.createElement("textarea");
        input.rows = 4;
        input.className = "textarea textarea-xs textarea-bordered w-full";
        input.style.cssText =
          "font:0.75rem/1.5 ui-monospace,SFMono-Regular,monospace;width:100%;resize:vertical;";
      } else {
        input = document.createElement("input");
        input.type = "text";
        input.className = "input input-xs input-bordered w-full";
        input.style.cssText = "font-size:0.8rem;width:100%;";
      }
      input.value = opts.value || "";
      input.placeholder = opts.placeholder || "";
      dialog.appendChild(input);

      if (multiline) {
        var hint = document.createElement("div");
        hint.textContent = "⌘/Ctrl+Enter to save · Esc to cancel";
        hint.style.cssText = "font-size:0.65rem;opacity:0.55;margin-top:0.25rem;";
        dialog.appendChild(hint);
      }

      var row = document.createElement("div");
      row.style.cssText =
        "display:flex;justify-content:flex-end;gap:0.375rem;margin-top:0.45rem;";
      var cancel = document.createElement("button");
      cancel.type = "button";
      cancel.textContent = "Cancel";
      cancel.className = "btn btn-xs";
      var ok = document.createElement("button");
      ok.type = "button";
      ok.textContent = opts.confirmLabel || "OK";
      ok.className = "btn btn-xs btn-primary";
      row.appendChild(cancel);
      row.appendChild(ok);
      dialog.appendChild(row);

      var finished = false;
      var finish = function (submit) {
        if (finished) return;
        finished = true;
        var value = input.value;
        self._closeTextPrompt();
        if (submit) {
          if (opts.onSubmit) opts.onSubmit(value);
        } else if (opts.onCancel) {
          opts.onCancel();
        }
      };
      ok.addEventListener("click", function (e) {
        e.preventDefault();
        finish(true);
      });
      cancel.addEventListener("click", function (e) {
        e.preventDefault();
        finish(false);
      });
      input.addEventListener("keydown", function (e) {
        if (e.key === "Enter") {
          // In a multiline prompt a bare Enter is a newline — only the
          // modifier submits.
          if (multiline && !(e.metaKey || e.ctrlKey)) return;
          e.preventDefault();
          e.stopPropagation();
          finish(true);
        } else if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          finish(false);
        }
      });
      dialog.addEventListener("mousedown", function (e) {
        e.stopPropagation();
      });

      // Anchor under the triggering control (same pattern as the emoji /
      // symbol pickers); default to the toolbar's left edge.
      var host = this.el.querySelector("[data-visual-toolbar]") || this.el;
      host.style.position = "relative";
      var hostRect = host.getBoundingClientRect();
      var left = 8;
      var top = hostRect.height + 4;
      if (opts.anchor && opts.anchor.getBoundingClientRect) {
        var r = opts.anchor.getBoundingClientRect();
        if (r.width || r.height) {
          left = Math.max(
            0,
            Math.min(r.left - hostRect.left, hostRect.width - 288)
          );
          top = r.bottom - hostRect.top + 4;
        }
      }
      dialog.style.left = left + "px";
      dialog.style.top = top + "px";
      host.appendChild(dialog);
      this._textPrompt = dialog;

      this._textPromptCloser = function (e) {
        if (!dialog.contains(e.target)) finish(false);
      };
      setTimeout(function () {
        if (self._textPrompt === dialog) {
          document.addEventListener("mousedown", self._textPromptCloser);
        }
      }, 0);
      setTimeout(function () {
        input.focus();
        input.select();
      }, 0);
    },

    _closeTextPrompt: function () {
      if (this._textPromptCloser) {
        document.removeEventListener("mousedown", this._textPromptCloser);
        this._textPromptCloser = null;
      }
      if (this._textPrompt && this._textPrompt.parentNode) {
        this._textPrompt.parentNode.removeChild(this._textPrompt);
      }
      this._textPrompt = null;
    },

    // Preferred anchor for _openTextPrompt: the visible toolbar button
    // for `action` (there are inline + overflow copies; hidden ones have
    // no box to anchor to).
    _toolbarAnchor: function (action) {
      var btns = this.el.querySelectorAll(
        '[data-toolbar-action="' + action + '"]'
      );
      for (var i = 0; i < btns.length; i++) {
        if (btns[i].offsetParent !== null) return btns[i];
      }
      return btns[0] || null;
    },

    _openEmojiPicker: function () {
      var self = this;

      // Close if already open
      if (this._emojiPicker) {
        this._closeEmojiPicker();
        return;
      }

      // Save selection so we can restore it after picking — but only a
      // selection that actually lives inside the visual editor. A stale
      // range from another surface (markdown textarea select-all, a
      // previous document state) restored blindly makes the insert
      // REPLACE whatever that range happened to cover — up to the whole
      // document.
      this._savedRange = this._captureEditorRange();

      var btn = this.el.querySelector('[data-toolbar-action="emoji"]');
      if (!btn) return;

      var picker = document.createElement("div");
      picker.className = "leaf-emoji-picker";
      picker.style.cssText = "position:absolute;z-index:50;background:var(--color-base-200, #e5e7eb);color:var(--color-base-content, #1f2937);border:1px solid var(--color-base-300, #d1d5db);border-radius:0.5rem;box-shadow:0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08);padding:0.5rem;width:320px;max-height:360px;display:flex;flex-direction:column;";

      // Search input
      var searchWrap = document.createElement("div");
      searchWrap.style.cssText = "margin-bottom:0.375rem;";
      var searchInput = document.createElement("input");
      searchInput.type = "text";
      searchInput.placeholder = "Search emoji...";
      searchInput.className = "input input-xs input-bordered w-full";
      searchInput.style.cssText = "font-size:0.8rem;";
      searchWrap.appendChild(searchInput);
      picker.appendChild(searchWrap);

      // Category tabs
      var tabsWrap = document.createElement("div");
      tabsWrap.style.cssText = "display:flex;gap:2px;margin-bottom:0.375rem;overflow-x:auto;flex-shrink:0;padding:0.25rem 0.125rem;border-bottom:1px solid var(--color-base-300, #d1d5db);";
      picker.appendChild(tabsWrap);

      // Grid container
      var gridWrap = document.createElement("div");
      gridWrap.style.cssText = "overflow-y:auto;flex:1;";
      picker.appendChild(gridWrap);

      var categories = this._emojiCategories;
      var activeCategory = 0;

      function renderGrid(emojis) {
        gridWrap.innerHTML = "";
        var grid = document.createElement("div");
        grid.style.cssText = "display:grid;grid-template-columns:repeat(8,1fr);gap:2px;";
        emojis.forEach(function (emoji) {
          var span = document.createElement("span");
          span.textContent = emoji;
          span.style.cssText = "cursor:pointer;font-size:1.25rem;text-align:center;padding:3px;border-radius:4px;line-height:1;";
          span.addEventListener("mouseover", function () { span.style.background = "color-mix(in oklab, var(--color-base-content, #1f2937) 8%, transparent)"; });
          span.addEventListener("mouseout", function () { span.style.background = ""; });
          span.addEventListener("click", function (e) {
            e.preventDefault();
            e.stopPropagation();
            self._insertEmoji(emoji);
          });
          grid.appendChild(span);
        });
        gridWrap.appendChild(grid);
      }

      function renderTabs() {
        tabsWrap.innerHTML = "";
        categories.forEach(function (cat, i) {
          var tab = document.createElement("button");
          tab.type = "button";
          tab.textContent = cat.emojis[0];
          tab.title = cat.name;
          tab.style.cssText = "cursor:pointer;font-size:1.125rem;padding:4px 6px;border-radius:6px;border:none;background:" + (i === activeCategory ? "color-mix(in oklab, var(--color-base-content, #1f2937) 12%, transparent)" : "none") + ";line-height:1;transition:background 0.1s;";
          tab.addEventListener("click", function (e) {
            e.preventDefault();
            e.stopPropagation();
            activeCategory = i;
            searchInput.value = "";
            renderTabs();
            renderGrid(categories[i].emojis);
          });
          tabsWrap.appendChild(tab);
        });
      }

      // Search filtering: match the query against per-emoji keywords and
      // the category name. Dedupe across categories.
      searchInput.addEventListener("input", function () {
        var q = searchInput.value.toLowerCase().trim();
        if (!q) {
          tabsWrap.style.display = "";
          renderTabs();
          renderGrid(categories[activeCategory].emojis);
          return;
        }
        tabsWrap.style.display = "none";
        var matches = [];
        var seen = {};
        categories.forEach(function (cat) {
          var catMatch = cat.name.toLowerCase().indexOf(q) !== -1;
          cat.emojis.forEach(function (em) {
            if (seen[em]) return;
            var kw = self._emojiKeywords[em] || "";
            if (catMatch || kw.indexOf(q) !== -1) {
              matches.push(em);
              seen[em] = true;
            }
          });
        });
        if (matches.length === 0) {
          gridWrap.innerHTML =
            '<div style="padding:1rem;text-align:center;opacity:0.6;font-size:0.8rem;">No emoji found</div>';
        } else {
          renderGrid(matches);
        }
      });

      // Prevent picker clicks from stealing editor focus
      picker.addEventListener("mousedown", function (e) {
        e.preventDefault();
      });

      renderTabs();
      renderGrid(categories[0].emojis);

      // Position below the emoji button
      var rect = btn.getBoundingClientRect();
      var toolbarRect = btn.closest("[data-visual-toolbar]").getBoundingClientRect();
      picker.style.left = Math.max(0, rect.left - toolbarRect.left) + "px";
      picker.style.top = (rect.bottom - toolbarRect.top + 4) + "px";

      btn.closest("[data-visual-toolbar]").style.position = "relative";
      btn.closest("[data-visual-toolbar]").appendChild(picker);
      this._emojiPicker = picker;

      // Close on outside click
      var closeHandler = function (e) {
        if (!picker.contains(e.target) && e.target !== btn) {
          self._closeEmojiPicker();
        }
      };
      setTimeout(function () {
        document.addEventListener("click", closeHandler);
      }, 0);
      this._emojiCloseHandler = closeHandler;

      searchInput.focus();
    },

    _closeEmojiPicker: function () {
      if (this._emojiPicker) {
        this._emojiPicker.remove();
        this._emojiPicker = null;
      }
      if (this._emojiCloseHandler) {
        document.removeEventListener("click", this._emojiCloseHandler);
        this._emojiCloseHandler = null;
      }
    },

    // -- Special characters / date-time picker (#31) --

    _symbols: [
      "—","–","…","©","®","™","°","§","¶","•","·","«","»","“","”","‘","’",
      "×","÷","±","≈","≠","≤","≥","→","←","↑","↓","⇒","∞","µ","½","¼","¾",
      "€","£","¥","¢","✓","✗","★","☆","♦","→","№","℃","℉","✔","✘","≡",
    ],

    _openSymbolPicker: function () {
      var self = this;
      if (this._symbolPicker) {
        this._closeSymbolPicker();
        return;
      }

      this._savedRange = this._captureEditorRange();

      var anchor =
        this.el.querySelector("[data-insert-more-trigger]") ||
        this.el.querySelector('[data-toolbar-action="symbols"]');
      var toolbar = this.el.querySelector("[data-visual-toolbar]");
      if (!anchor || !toolbar) return;

      var picker = document.createElement("div");
      picker.style.cssText =
        "position:absolute;z-index:50;background:var(--color-base-200,#e5e7eb);color:var(--color-base-content,#1f2937);border:1px solid var(--color-base-300,#d1d5db);border-radius:0.5rem;box-shadow:0 4px 16px rgba(0,0,0,0.12),0 1px 4px rgba(0,0,0,0.08);padding:0.5rem;width:300px;";

      // Date / time insert row.
      var dtRow = document.createElement("div");
      dtRow.style.cssText = "display:flex;gap:4px;margin-bottom:0.4rem;";
      [
        ["Insert date", function () { return new Date().toLocaleDateString(); }],
        ["Insert time", function () { return new Date().toLocaleTimeString(); }],
      ].forEach(function (d) {
        var b = document.createElement("button");
        b.type = "button";
        b.textContent = d[0];
        b.style.cssText =
          "flex:1;font-size:0.72rem;padding:0.25rem;border:1px solid var(--color-base-300,#d1d5db);border-radius:0.3rem;cursor:pointer;background:none;color:inherit;";
        b.addEventListener("mousedown", function (e) { e.preventDefault(); });
        b.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          self._insertEmoji(d[1]());
          self._closeSymbolPicker();
        });
        dtRow.appendChild(b);
      });
      picker.appendChild(dtRow);

      // Symbol grid.
      var grid = document.createElement("div");
      grid.style.cssText =
        "display:grid;grid-template-columns:repeat(10,1fr);gap:2px;";
      this._symbols.forEach(function (s) {
        var span = document.createElement("span");
        span.textContent = s;
        span.style.cssText =
          "cursor:pointer;text-align:center;padding:4px;border-radius:4px;font-size:1rem;line-height:1;";
        span.addEventListener("mouseover", function () {
          span.style.background =
            "color-mix(in oklab, var(--color-base-content,#1f2937) 8%, transparent)";
        });
        span.addEventListener("mouseout", function () {
          span.style.background = "";
        });
        span.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          self._insertEmoji(s);
          self._closeSymbolPicker();
        });
        grid.appendChild(span);
      });
      picker.appendChild(grid);

      picker.addEventListener("mousedown", function (e) {
        e.preventDefault();
      });

      var rect = anchor.getBoundingClientRect();
      var tbRect = toolbar.getBoundingClientRect();
      toolbar.style.position = "relative";
      picker.style.left =
        Math.max(0, Math.min(rect.left - tbRect.left, tbRect.width - 300)) + "px";
      picker.style.top = rect.bottom - tbRect.top + 4 + "px";
      toolbar.appendChild(picker);
      this._symbolPicker = picker;

      var closeHandler = function (e) {
        if (!picker.contains(e.target)) self._closeSymbolPicker();
      };
      setTimeout(function () {
        document.addEventListener("click", closeHandler);
      }, 0);
      this._symbolCloseHandler = closeHandler;
    },

    _closeSymbolPicker: function () {
      if (this._symbolPicker) {
        this._symbolPicker.remove();
        this._symbolPicker = null;
      }
      if (this._symbolCloseHandler) {
        document.removeEventListener("click", this._symbolCloseHandler);
        this._symbolCloseHandler = null;
      }
    },

    _insertEmoji: function (emoji) {
      if (this._mode === "markdown") {
        // Insert into markdown textarea
        var gid = this._editorId.replace(/-/g, "_") + "_markdown";
        var ins = window["markdownEditorInsert_" + gid];
        if (ins) ins(emoji);
        return;
      }

      // Visual mode: restore saved selection and insert
      if (this._visualEl) {
        this._visualEl.focus();
        this._restoreSavedRange();
        document.execCommand("insertText", false, emoji);
        this._debouncedPushVisualChange();
      }
    },

    // Clone the current selection range, but only when it lives inside
    // the visual editor — anything else is a stale/foreign range that
    // must not be restored later (restoring it makes the next insert
    // replace whatever it covered).
    _captureEditorRange: function () {
      if (!this._visualEl) return null;
      var sel = window.getSelection();
      if (!sel.rangeCount) return null;
      var range = sel.getRangeAt(0);
      if (!this._visualEl.contains(range.commonAncestorContainer)) {
        return null;
      }
      return range.cloneRange();
    },

    // Richer capture for flows that must survive a hybrid source-mode
    // re-render (opening a dialog blurs the editor, the decorated block
    // re-renders, and a cloned Range silently collapses even though its
    // container stays connected). Alongside the range we remember the
    // selected text + block so the selection can be re-found by content.
    _captureEditorSelection: function () {
      var range = this._captureEditorRange();
      if (!range) return null;
      var sel = window.getSelection();
      return {
        range: range,
        text: sel.isCollapsed ? "" : sel.toString(),
        block: this._getCurrentBlock(),
      };
    },

    // Restore a _captureEditorSelection snapshot. Prefer the cloned range;
    // when it comes back collapsed/dead but text was selected, re-find
    // that text (nearest occurrence in the original block, then anywhere
    // in the editor). Returns true when a non-collapsed selection was
    // wanted and re-established.
    _restoreEditorSelection: function (snap) {
      if (!snap) return false;
      this._savedRange = snap.range;
      this._restoreSavedRange();
      if (!snap.text) return true;
      var sel = window.getSelection();
      if (sel.rangeCount && !sel.isCollapsed && sel.toString() === snap.text) {
        return true;
      }
      var scopes = [];
      if (snap.block && snap.block.isConnected) scopes.push(snap.block);
      scopes.push(this._visualEl);
      for (var s = 0; s < scopes.length; s++) {
        var walker = document.createTreeWalker(
          scopes[s],
          NodeFilter.SHOW_TEXT
        );
        var n;
        while ((n = walker.nextNode())) {
          var i = n.textContent.indexOf(snap.text);
          if (i >= 0) {
            var r = document.createRange();
            r.setStart(n, i);
            r.setEnd(n, i + snap.text.length);
            sel.removeAllRanges();
            sel.addRange(r);
            return true;
          }
        }
      }
      return false;
    },

    // Restore a range captured by _captureEditorRange if it is still
    // valid; otherwise fall back to a collapsed caret at the end of the
    // editor so inserts append instead of replacing stale selections.
    _restoreSavedRange: function () {
      var range = this._savedRange;
      this._savedRange = null;
      var sel = window.getSelection();
      if (
        range &&
        range.commonAncestorContainer &&
        range.commonAncestorContainer.isConnected &&
        this._visualEl.contains(range.commonAncestorContainer)
      ) {
        sel.removeAllRanges();
        sel.addRange(range);
        return;
      }
      if (sel.rangeCount && this._visualEl.contains(sel.anchorNode)) {
        return; // current caret is already in the editor — keep it
      }
      var fallback = document.createRange();
      fallback.selectNodeContents(this._visualEl);
      fallback.collapse(false);
      sel.removeAllRanges();
      sel.addRange(fallback);
    },

    _isInsideHeading: function () {
      var block = this._getCurrentBlock();
      if (!block || !block.tagName) return false;
      return /^h[1-6]$/i.test(block.tagName);
    },

    // True when the current selection spans all of the visual editor's
    // content — the cmd+A state. Compared on range boundaries: text
    // comparison is unreliable because non-editable chrome (chip labels,
    // callout titles) renders in textContent but not always in
    // selection.toString().
    _selectionCoversAllContent: function () {
      if (!this._visualEl) return false;
      var sel = window.getSelection();
      if (!sel.rangeCount || sel.isCollapsed) return false;
      var range = sel.getRangeAt(0);
      if (!this._visualEl.contains(range.commonAncestorContainer)) {
        return false;
      }
      var full = document.createRange();
      full.selectNodeContents(this._visualEl);
      try {
        return (
          range.compareBoundaryPoints(Range.START_TO_START, full) <= 0 &&
          range.compareBoundaryPoints(Range.END_TO_END, full) >= 0
        );
      } catch (_e) {
        return false;
      }
    },

    _getCurrentBlock: function () {
      var sel = window.getSelection();
      if (!sel.rangeCount) return null;
      var node = sel.anchorNode;
      while (node && node !== this._visualEl) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          var display = window.getComputedStyle(node).display;
          if (display === "block" || display === "list-item") {
            return node;
          }
        }
        node = node.parentNode;
      }
      return null;
    },

    _insertLink: function () {
      var self = this;
      var selection = window.getSelection();
      var currentHref = "";

      if (selection.rangeCount > 0) {
        var node = selection.anchorNode;
        while (node && node !== this._visualEl) {
          if (node.tagName && node.tagName.toLowerCase() === "a") {
            currentHref = node.getAttribute("href") || "";
            break;
          }
          node = node.parentNode;
        }
      }

      // The popover steals focus (unlike the modal prompt), so capture
      // the selection now and restore it before applying the command.
      var saved = this._captureEditorSelection();

      this._openTextPrompt({
        label: "Link URL",
        value: currentHref || "https://",
        anchor: this._toolbarAnchor("link"),
        onSubmit: function (url) {
          url = url.trim();
          self._visualEl.focus({ preventScroll: true });
          self._restoreEditorSelection(saved);
          if (url === "") {
            document.execCommand("unlink", false, null);
          } else {
            document.execCommand("createLink", false, url);
          }
          if (self._mode === "hybrid") self._refreshSourceBlock();
          self._debouncedPushVisualChange();
        },
      });
    },

    _wrapSelectionWith: function (tagName) {
      var selection = window.getSelection();
      if (!selection.rangeCount) return;

      var range = selection.getRangeAt(0);
      var selectedText = range.toString();

      if (selectedText.length === 0) return;

      var parent = range.commonAncestorContainer;
      if (parent.nodeType === Node.TEXT_NODE) parent = parent.parentElement;
      if (
        parent &&
        parent.tagName &&
        parent.tagName.toLowerCase() === tagName
      ) {
        var text = document.createTextNode(parent.textContent);
        parent.parentNode.replaceChild(text, parent);
        return;
      }

      try {
        var el = document.createElement(tagName);
        range.surroundContents(el);
      } catch (_e) {
        var escaped = selectedText
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
        document.execCommand(
          "insertHTML",
          false,
          "<" + tagName + ">" + escaped + "</" + tagName + ">"
        );
      }
    },

    _spoilerAncestor: function (node) {
      while (node && node !== this._visualEl) {
        if (
          node.nodeType === Node.ELEMENT_NODE &&
          node.classList &&
          node.classList.contains("leaf-spoiler")
        ) {
          return node;
        }
        node = node.parentNode;
      }
      return null;
    },

    // Like _inlineFormattingAncestor but also catches the boundary case:
    // when the cursor is between a plain text node and a formatting sibling,
    // Chrome puts the anchor in the parent — so the standard ancestor walk
    // misses the formatting until the cursor steps one character in.
    _activeFormattingForCursor: function (sel) {
      if (!sel || !sel.rangeCount) return null;

      var inside = this._inlineFormattingAncestor(sel.anchorNode);
      if (inside) return inside;

      var range = sel.getRangeAt(0);
      var node = range.endContainer;
      var offset = range.endOffset;

      if (node.nodeType === Node.TEXT_NODE) {
        if (offset === node.textContent.length && node.nextSibling) {
          var nextAncestor = this._inlineFormattingAncestor(node.nextSibling);
          if (nextAncestor) return nextAncestor;
        }
        if (offset === 0 && node.previousSibling) {
          var prevAncestor = this._inlineFormattingAncestor(
            node.previousSibling,
          );
          if (prevAncestor) return prevAncestor;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        var children = node.childNodes;
        var afterChild = children[offset];
        var beforeChild = children[offset - 1];
        if (afterChild) {
          var afterAncestor = this._inlineFormattingAncestor(afterChild);
          if (afterAncestor) return afterAncestor;
        }
        if (beforeChild) {
          var beforeAncestor = this._inlineFormattingAncestor(beforeChild);
          if (beforeAncestor) return beforeAncestor;
        }
      }

      return null;
    },

    // Closest inline-formatting wrapper around `node` that the user might
    // want to escape from when arrowing past its boundary or pressing Enter
    // inside it. Includes the spoiler span as a class match.
    _inlineFormattingAncestor: function (node) {
      var tags = [
        "b",
        "strong",
        "i",
        "em",
        "s",
        "del",
        "strike",
        "code",
        "u",
        "sub",
        "sup",
        "mark",
        "a",
      ];
      while (node && node !== this._visualEl) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          var tag = node.tagName ? node.tagName.toLowerCase() : "";
          if (tags.indexOf(tag) !== -1) return node;
          if (
            node.classList &&
            node.classList.contains("leaf-spoiler")
          ) {
            return node;
          }
        }
        node = node.parentNode;
      }
      return null;
    },

    _toggleSpoiler: function () {
      var sel = window.getSelection();
      if (!sel.rangeCount) return;
      var range = sel.getRangeAt(0);

      // If cursor / selection is inside an existing spoiler, unwrap it.
      var existing = this._spoilerAncestor(range.commonAncestorContainer);
      if (existing) {
        var text = document.createTextNode(existing.textContent);
        existing.parentNode.replaceChild(text, existing);
        return;
      }

      var selected = range.toString();
      if (!selected.length) return;

      try {
        var span = document.createElement("span");
        span.className = "leaf-spoiler";
        range.surroundContents(span);
      } catch (_e) {
        var escaped = selected
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
        document.execCommand(
          "insertHTML",
          false,
          '<span class="leaf-spoiler">' + escaped + "</span>"
        );
      }
    },

    _insertTable: function () {
      var html =
        "<table><thead><tr><th>Header 1</th><th>Header 2</th></tr></thead>" +
        "<tbody><tr><td>Cell 1</td><td>Cell 2</td></tr>" +
        "<tr><td>Cell 3</td><td>Cell 4</td></tr></tbody></table><p><br></p>";
      document.execCommand("insertHTML", false, html);
    },

    _getTableContext: function () {
      var sel = window.getSelection();
      if (!sel.rangeCount) return null;
      var node = sel.anchorNode;
      if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
      var cell = node ? node.closest("td, th") : null;
      if (!cell) return null;
      var row = cell.parentElement;
      var table = cell.closest("table");
      if (!table || !this._visualEl.contains(table)) return null;
      var colIndex = Array.prototype.indexOf.call(row.children, cell);
      return { table: table, row: row, cell: cell, colIndex: colIndex };
    },

    _tableAddRow: function () {
      var ctx = this._getTableContext();
      if (!ctx) return;
      var cols = ctx.row.children.length;
      var newRow = document.createElement("tr");
      for (var i = 0; i < cols; i++) {
        var td = document.createElement("td");
        td.innerHTML = "<br>";
        newRow.appendChild(td);
      }
      // Insert after current row; if in thead, append to tbody instead
      if (ctx.row.parentElement.tagName.toLowerCase() === "thead") {
        var tbody = ctx.table.querySelector("tbody");
        if (!tbody) {
          tbody = document.createElement("tbody");
          ctx.table.appendChild(tbody);
        }
        tbody.insertBefore(newRow, tbody.firstChild);
      } else {
        ctx.row.parentNode.insertBefore(newRow, ctx.row.nextSibling);
      }
    },

    // Align the current column (markdown alignment is per-column, encoded in
    // the header row, so we stamp every cell and let the serializer read the
    // header). Pass "" to clear.
    _tableSetAlign: function (align) {
      var ctx = this._getTableContext();
      if (!ctx) return;
      var rows = ctx.table.querySelectorAll("tr");
      for (var i = 0; i < rows.length; i++) {
        var cells = rows[i].querySelectorAll("th, td");
        var cell = cells[ctx.colIndex];
        if (!cell) continue;
        if (align) {
          cell.setAttribute("data-align", align);
          cell.style.textAlign = align;
        } else {
          cell.removeAttribute("data-align");
          cell.style.textAlign = "";
        }
      }
      this._debouncedPushVisualChange();
    },

    // Toggle the first row between header (<th>) and body (<td>) cells.
    // Markdown always treats row 0 as the header, so this is the
    // round-trip-safe interpretation of a header toggle.
    _tableToggleHeader: function () {
      var ctx = this._getTableContext();
      if (!ctx) return;
      var firstRow = ctx.table.querySelector("tr");
      if (!firstRow) return;
      var isHeader = !!firstRow.querySelector("th");
      var cells = firstRow.querySelectorAll("th, td");
      for (var i = 0; i < cells.length; i++) {
        var old = cells[i];
        var tag = isHeader ? "td" : "th";
        var repl = document.createElement(tag);
        repl.innerHTML = old.innerHTML;
        if (old.getAttribute("data-align")) {
          repl.setAttribute("data-align", old.getAttribute("data-align"));
          repl.style.textAlign = old.style.textAlign;
        }
        old.parentNode.replaceChild(repl, old);
      }
      this._debouncedPushVisualChange();
    },

    // Parse pasted TSV/CSV text into a table. Returns true if it handled the
    // paste. Tabs win as the delimiter when present (spreadsheet copy);
    // otherwise commas. A single cell with no delimiter is left to normal
    // paste handling.
    _maybePasteTabular: function (text) {
      if (!text) return false;
      var rawLines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
      // Drop a trailing empty line from the clipboard.
      while (rawLines.length && rawLines[rawLines.length - 1] === "") {
        rawLines.pop();
      }
      if (rawLines.length === 0) return false;

      // Tabs are an unambiguous tabular signal (you don't type them in
      // prose), so a single tab anywhere triggers the table. Commas are
      // NOT — to avoid hijacking normal multi-line prose that happens to
      // contain commas, the CSV path requires every line to split into the
      // SAME number of fields and at least 3 columns (≥2 commas per line),
      // which prose almost never does consistently.
      var delim = text.indexOf("\t") !== -1 ? "\t" : null;
      var grid;
      var cols;
      if (delim) {
        grid = rawLines.map(function (ln) {
          return ln.split(delim);
        });
        cols = grid.reduce(function (m, r) {
          return Math.max(m, r.length);
        }, 0);
        if (cols < 2) return false;
      } else {
        if (rawLines.length < 2) return false;
        grid = rawLines.map(function (ln) {
          return ln.split(",");
        });
        var first = grid[0].length;
        if (first < 3) return false;
        var consistent = grid.every(function (r) {
          return r.length === first;
        });
        if (!consistent) return false;
        cols = first;
      }

      function esc(s) {
        return (s || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;");
      }
      var html = "<table><thead><tr>";
      for (var c = 0; c < cols; c++) {
        html += "<th>" + (esc(grid[0][c]) || "<br>") + "</th>";
      }
      html += "</tr></thead><tbody>";
      for (var r = 1; r < grid.length; r++) {
        html += "<tr>";
        for (var c2 = 0; c2 < cols; c2++) {
          html += "<td>" + (esc(grid[r][c2]) || "<br>") + "</td>";
        }
        html += "</tr>";
      }
      html += "</tbody></table><p><br></p>";
      document.execCommand("insertHTML", false, html);
      this._debouncedPushVisualChange();
      return true;
    },

    _tableRemoveRow: function () {
      var ctx = this._getTableContext();
      if (!ctx) return;
      var allRows = ctx.table.querySelectorAll("tr");
      if (allRows.length <= 1) {
        // Last row — remove the entire table
        ctx.table.parentNode.removeChild(ctx.table);
        return;
      }
      // Don't allow removing the header row if it's the only one in thead
      if (ctx.row.parentElement.tagName.toLowerCase() === "thead") return;
      ctx.row.parentNode.removeChild(ctx.row);
    },

    _tableAddCol: function () {
      var ctx = this._getTableContext();
      if (!ctx) return;
      var rows = ctx.table.querySelectorAll("tr");
      var insertAt = ctx.colIndex + 1;
      for (var i = 0; i < rows.length; i++) {
        var isHeader = rows[i].parentElement.tagName.toLowerCase() === "thead";
        var newCell = document.createElement(isHeader ? "th" : "td");
        newCell.innerHTML = "<br>";
        var cells = rows[i].children;
        if (insertAt < cells.length) {
          rows[i].insertBefore(newCell, cells[insertAt]);
        } else {
          rows[i].appendChild(newCell);
        }
      }
    },

    _tableRemoveCol: function () {
      var ctx = this._getTableContext();
      if (!ctx) return;
      var rows = ctx.table.querySelectorAll("tr");
      var colCount = rows[0] ? rows[0].children.length : 0;
      if (colCount <= 1) {
        // Last column — remove the entire table
        ctx.table.parentNode.removeChild(ctx.table);
        return;
      }
      for (var i = 0; i < rows.length; i++) {
        var cell = rows[i].children[ctx.colIndex];
        if (cell) rows[i].removeChild(cell);
      }
    },

    _updateToolbarState: function () {
      var self = this;
      var block = this._getCurrentBlock();
      var blockTag =
        block && block.tagName ? block.tagName.toLowerCase() : "";

      var buttons = this.el.querySelectorAll("[data-toolbar-action]");
      buttons.forEach(function (btn) {
        var action = btn.dataset.toolbarAction;
        var active = false;

        // Undo/redo are the only toolbar entries whose availability is knowable
        // ahead of the click, and the only ones that are frequently a no-op.
        // Reflecting it is what makes the stack legible: the button greys out
        // at the bottom of the history instead of silently doing nothing.
        if (action === "undo" || action === "redo") {
          var enabled =
            action === "undo" ? self._historyCanUndo() : self._historyCanRedo();

          btn.disabled = !enabled;
          btn.classList.toggle("btn-disabled", !enabled);
          btn.setAttribute("aria-disabled", enabled ? "false" : "true");
          return;
        }

        switch (action) {
          case "bold":
            // Heading tags compute to font-weight:700 in our editor CSS, so
            // queryCommandState("bold") reports true for headings even
            // without an explicit <b>/<strong>. Probe for the actual element
            // ancestor instead so the bold button only lights up for real
            // bold spans.
            active = self._isInsideTag("b") || self._isInsideTag("strong");
            break;
          case "italic":
            active = document.queryCommandState("italic");
            break;
          case "strike":
            active = document.queryCommandState("strikeThrough");
            break;
          case "superscript":
            active = document.queryCommandState("superscript");
            break;
          case "subscript":
            active = document.queryCommandState("subscript");
            break;
          case "orderedList":
            active = document.queryCommandState("insertOrderedList");
            break;
          case "bulletList":
            active = document.queryCommandState("insertUnorderedList");
            break;
          case "heading1":
            active = blockTag === "h1";
            break;
          case "heading2":
            active = blockTag === "h2";
            break;
          case "heading3":
            active = blockTag === "h3";
            break;
          case "heading4":
            active = blockTag === "h4";
            break;
          case "blockquote":
            active = blockTag === "blockquote";
            break;
          case "codeBlock":
            active = blockTag === "pre";
            break;
          case "link":
            active = self._isInsideTag("a");
            break;
          case "code":
            active = self._isInsideTag("code");
            break;
          case "spoiler":
            var sel = window.getSelection();
            active =
              sel.rangeCount > 0 &&
              !!self._spoilerAncestor(sel.anchorNode);
            break;
        }

        if (active) {
          btn.classList.add("btn-active");
        } else {
          btn.classList.remove("btn-active");
        }
      });

      // Reflect the current heading level on the heading dropdown trigger so
      // users can see at a glance whether the cursor sits in a heading and
      // which level it is — instead of having the bold button mislead them
      // by lighting up for headings.
      var headingTrigger = this.el.querySelector("[data-heading-trigger]");
      if (headingTrigger) {
        var label = headingTrigger.querySelector("[data-heading-trigger-label]");
        var headingMatch = /^h([1-6])$/.exec(blockTag);
        if (headingMatch) {
          if (label) label.textContent = "H" + headingMatch[1];
          headingTrigger.classList.add("btn-active");
        } else {
          if (label) label.textContent = "H";
          headingTrigger.classList.remove("btn-active");
        }
      }
    },

    _isInsideTag: function (tagName) {
      var sel = window.getSelection();
      if (!sel.rangeCount) return false;
      var node = sel.anchorNode;
      while (node && node !== this._visualEl) {
        if (
          node.tagName &&
          node.tagName.toLowerCase() === tagName
        ) {
          return true;
        }
        node = node.parentNode;
      }
      return false;
    },

    // -- Selection toolbar (touch only) --
    //
    // A floating bubble that appears above any non-collapsed selection
    // inside the visual / hybrid editor on touch devices. Replaces the
    // need to reach for the (often-offscreen) top toolbar on a phone.
    // Four buttons: Bold, Italic, Code, Link — the most common inline
    // formats. Block-level stuff is reachable via the auto-format
    // prefixes (`# `, `- `, `> `, etc.).

    _setupSelectionToolbar: function () {
      if (!this._visualEl) return;

      var self = this;
      this._selectionToolbarEl = null;
      this._onSelectionForToolbar = function () {
        self._updateSelectionToolbar();
      };
      document.addEventListener("selectionchange", this._onSelectionForToolbar);
      this._onScrollForSelectionToolbar = function () {
        // Selection rect moves with the scroll; reposition rather
        // than dismiss so the toolbar tracks the selection.
        self._updateSelectionToolbar();
      };
      window.addEventListener("scroll", this._onScrollForSelectionToolbar, true);
    },

    // The selection toolbar is a "narrow editor" affordance — once
    // the top toolbar has slimmed down (container width ≤ 640px) we
    // surface the bubble so inline formats are one tap away. On a
    // wide layout the full toolbar is right there, so the bubble
    // would just be visual noise. Editor `id` host carries the
    // `container-type` so we measure its actual rendered width.
    _selectionToolbarEnabled: function () {
      if (!this.el) return false;
      // Opt-in layouts always show the bubble on selection; setup is only
      // wired for floating/both, so this guard just confirms intent.
      return (
        this._toolbarLayout === "floating" || this._toolbarLayout === "both"
      );
    },

    _teardownSelectionToolbar: function () {
      if (this._onSelectionForToolbar) {
        document.removeEventListener(
          "selectionchange",
          this._onSelectionForToolbar
        );
        this._onSelectionForToolbar = null;
      }
      if (this._onScrollForSelectionToolbar) {
        window.removeEventListener(
          "scroll",
          this._onScrollForSelectionToolbar,
          true
        );
        this._onScrollForSelectionToolbar = null;
      }
      this._dismissSelectionToolbar();
    },

    _updateSelectionToolbar: function () {
      var sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        this._dismissSelectionToolbar();
        return;
      }
      if (this._mode !== "visual" && this._mode !== "hybrid") {
        this._dismissSelectionToolbar();
        return;
      }
      if (!this._selectionToolbarEnabled()) {
        this._dismissSelectionToolbar();
        return;
      }
      var range = sel.getRangeAt(0);
      if (!this._visualEl || !this._visualEl.contains(range.startContainer)) {
        this._dismissSelectionToolbar();
        return;
      }
      this._showSelectionToolbar(range);
    },

    _showSelectionToolbar: function (range) {
      if (!this._selectionToolbarEl) {
        this._selectionToolbarEl = this._buildSelectionToolbar();
        document.body.appendChild(this._selectionToolbarEl);
      }
      // Sync the active-state of each button to whatever formats the
      // current selection already has, so the user can see what's on.
      this._syncSelectionToolbarState();
      var tb = this._selectionToolbarEl;
      var rect = range.getBoundingClientRect();
      if (!rect.width && !rect.height) {
        // Collapsed-after-selection-change edge case.
        this._dismissSelectionToolbar();
        return;
      }
      // Make sure the toolbar has dimensions before we measure.
      tb.style.visibility = "hidden";
      tb.style.left = "0px";
      tb.style.top = "0px";
      var tbRect = tb.getBoundingClientRect();
      var pageScrollX = window.scrollX || window.pageXOffset || 0;
      var pageScrollY = window.scrollY || window.pageYOffset || 0;
      // Center horizontally over the selection, clamped to viewport.
      var left = rect.left + rect.width / 2 - tbRect.width / 2;
      var minLeft = 8;
      var maxLeft = window.innerWidth - tbRect.width - 8;
      if (left < minLeft) left = minLeft;
      if (left > maxLeft) left = maxLeft;
      // Above selection by default; flip below if there's no room.
      var top = rect.top - tbRect.height - 8;
      if (top < 8) {
        top = rect.bottom + 8;
      }
      tb.style.left = left + pageScrollX + "px";
      tb.style.top = top + pageScrollY + "px";
      tb.style.visibility = "visible";
    },

    _dismissSelectionToolbar: function () {
      if (this._selectionToolbarEl) {
        this._selectionToolbarEl.remove();
        this._selectionToolbarEl = null;
      }
    },

    _buildSelectionToolbar: function () {
      var self = this;
      var tb = document.createElement("div");
      tb.className = "leaf-selection-toolbar";

      function makeBtn(action, label, svg) {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.setAttribute("aria-label", label);
        btn.setAttribute("data-selection-action", action);
        btn.innerHTML = svg;
        // mousedown / touchstart prevent default so the selection
        // doesn't collapse before the click handler can fire on the
        // still-active selection.
        btn.addEventListener("mousedown", function (e) {
          e.preventDefault();
        });
        btn.addEventListener("touchstart", function (e) {
          e.preventDefault();
        }, { passive: false });
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          self._execSelectionAction(action);
        });
        tb.appendChild(btn);
        return btn;
      }

      makeBtn(
        "bold",
        "Bold",
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M6.5 3.5h4.25a3.25 3.25 0 0 1 1.83 5.94 3.5 3.5 0 0 1-1.83 6.56H6.5a.5.5 0 0 1-.5-.5V4a.5.5 0 0 1 .5-.5Zm1.5 5.25h2.75a1.75 1.75 0 1 0 0-3.5H8v3.5Zm0 5.75h3a2 2 0 1 0 0-4H8v4Z"/></svg>'
      );
      makeBtn(
        "italic",
        "Italic",
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M8.5 4h7a.5.5 0 0 1 0 1h-2.6l-2.4 10h2a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h2.6l2.4-10h-2a.5.5 0 0 1 0-1Z"/></svg>'
      );
      makeBtn(
        "code",
        "Inline code",
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M7.78 6.22a.75.75 0 0 1 0 1.06L5.06 10l2.72 2.72a.75.75 0 1 1-1.06 1.06l-3.25-3.25a.75.75 0 0 1 0-1.06l3.25-3.25a.75.75 0 0 1 1.06 0Zm4.44 0a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1 0 1.06l-3.25 3.25a.75.75 0 1 1-1.06-1.06L14.94 10l-2.72-2.72a.75.75 0 0 1 0-1.06Z"/></svg>'
      );
      makeBtn(
        "link",
        "Link",
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M11.13 7.05a.75.75 0 0 1 1.06 0 3.75 3.75 0 0 1 0 5.3l-2.5 2.5a3.75 3.75 0 1 1-5.3-5.3l1.1-1.1a.75.75 0 1 1 1.06 1.06l-1.1 1.1a2.25 2.25 0 1 0 3.18 3.18l2.5-2.5a2.25 2.25 0 0 0 0-3.18.75.75 0 0 1 0-1.06Zm-2.26 5.9a.75.75 0 0 1-1.06 0 3.75 3.75 0 0 1 0-5.3l2.5-2.5a3.75 3.75 0 1 1 5.3 5.3l-1.1 1.1a.75.75 0 1 1-1.06-1.06l1.1-1.1a2.25 2.25 0 1 0-3.18-3.18l-2.5 2.5a2.25 2.25 0 0 0 0 3.18.75.75 0 0 1 0 1.06Z"/></svg>'
      );

      return tb;
    },

    _syncSelectionToolbarState: function () {
      if (!this._selectionToolbarEl) return;
      var tb = this._selectionToolbarEl;
      try {
        var bold = document.queryCommandState("bold");
        var italic = document.queryCommandState("italic");
        tb.querySelectorAll("[data-selection-action]").forEach(function (btn) {
          var action = btn.dataset.selectionAction;
          var active = false;
          if (action === "bold") active = bold;
          else if (action === "italic") active = italic;
          // Code / link state checks involve ancestor walks — skip for
          // now; they still fire toggles via execCommand.
          btn.classList.toggle("is-active", active);
        });
      } catch (_e) {
        /* queryCommandState can throw on some browsers — best effort */
      }
    },

    _execSelectionAction: function (action) {
      var self = this;
      if (action === "bold") {
        document.execCommand("bold", false, null);
      } else if (action === "italic") {
        document.execCommand("italic", false, null);
      } else if (action === "code") {
        this._wrapSelectionWith("code");
      } else if (action === "link") {
        var saved = this._captureEditorSelection();
        this._openTextPrompt({
          label: "Link URL",
          value: "https://",
          anchor: this._toolbarAnchor("link"),
          onSubmit: function (url) {
            url = url.trim();
            if (url === "") return;
            self._visualEl.focus({ preventScroll: true });
            self._restoreEditorSelection(saved);
            document.execCommand("createLink", false, url);
            self._syncSelectionToolbarState();
            self._visualEl.dispatchEvent(new Event("input", { bubbles: true }));
          },
        });
        return;
      }
      // Refresh the on/off state for the buttons after the toggle.
      this._syncSelectionToolbarState();
      // Make sure the editor sees the mutation.
      if (this._visualEl) {
        this._visualEl.dispatchEvent(new Event("input", { bubbles: true }));
      }
    },

    // -- Link popover --

    _setupLinkPopover: function () {
      if (!this._visualEl) return;
      var self = this;

      this._linkPopoverEl = null;
      this._linkPopoverAnchor = null;
      this._imagePopoverEl = null;
      this._imagePopoverTarget = null;

      this._visualEl.addEventListener("click", function (e) {
        if (self._readonly) return;

        var target = e.target;

        // Check if clicked on an <img>
        if (target.tagName && target.tagName.toLowerCase() === "img") {
          e.preventDefault();
          self._dismissLinkPopover();
          self._showImagePopover(target);
          return;
        }

        // Walk up from click target to find an <a> inside the editor
        var node = target;
        var anchor = null;
        while (node && node !== self._visualEl) {
          if (node.tagName && node.tagName.toLowerCase() === "a") {
            anchor = node;
            break;
          }
          node = node.parentNode;
        }

        if (anchor) {
          // Link popover is a visual-mode affordance only. In hybrid
          // mode the user edits links via the markdown source (cursor
          // entering the paragraph swaps it to source-mode, exposing
          // `[text](url)`). We still `preventDefault` so a click on
          // the `<a>` doesn't navigate away — the click is only
          // meant to move the caret into the link for editing.
          if (self._mode !== "visual") {
            if (!e.metaKey && !e.ctrlKey) {
              e.preventDefault();
            }
            self._dismissLinkPopover();
            self._dismissImagePopover();
            return;
          }
          e.preventDefault();
          self._dismissImagePopover();
          self._showLinkPopover(anchor);
        } else {
          self._dismissLinkPopover();
          self._dismissImagePopover();
        }
      });

      // Dismiss when clicking outside the editor + popovers
      this._onDocClickForPopover = function (e) {
        if (self._linkPopoverEl && !self._linkPopoverEl.contains(e.target) &&
          !self._visualEl.contains(e.target)) {
          self._dismissLinkPopover();
        }
        if (self._imagePopoverEl && !self._imagePopoverEl.contains(e.target) &&
          !self._visualEl.contains(e.target)) {
          self._dismissImagePopover();
        }
      };
      document.addEventListener("mousedown", this._onDocClickForPopover);
    },

    _showLinkPopover: function (anchorEl) {
      this._dismissLinkPopover();
      this._linkPopoverAnchor = anchorEl;

      var href = anchorEl.getAttribute("href") || "";
      var self = this;

      // Build popover
      var pop = document.createElement("div");
      pop.className = "leaf-link-popover";

      // Link icon
      var linkIcon = document.createElement("span");
      linkIcon.style.cssText = "display:flex;align-items:center;opacity:0.5;flex-shrink:0;";
      linkIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" width="13" height="13"><path fill-rule="evenodd" d="M8.914 6.025a.75.75 0 0 1 1.06 0 3.5 3.5 0 0 1 0 4.95l-2 2a3.5 3.5 0 0 1-5.396-4.402.75.75 0 0 1 1.251.827 2 2 0 0 0 3.085 2.514l2-2a2 2 0 0 0 0-2.828.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"/><path fill-rule="evenodd" d="M7.086 9.975a.75.75 0 0 1-1.06 0 3.5 3.5 0 0 1 0-4.95l2-2a3.5 3.5 0 0 1 5.396 4.402.75.75 0 0 1-1.251-.827 2 2 0 0 0-3.085-2.514l-2 2a2 2 0 0 0 0 2.828.75.75 0 0 1 0 1.06Z" clip-rule="evenodd"/></svg>';
      pop.appendChild(linkIcon);

      // URL display/link
      var urlLink = document.createElement("a");
      urlLink.href = href;
      urlLink.target = "_blank";
      urlLink.rel = "noopener";
      urlLink.textContent = href || "(no url)";
      urlLink.title = href;
      pop.appendChild(urlLink);

      // Actions group (pill within pill)
      var actions = document.createElement("span");
      actions.className = "leaf-popover-actions";

      // Edit button
      var editBtn = document.createElement("button");
      editBtn.type = "button";
      editBtn.title = "Edit link";
      editBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" width="13" height="13"><path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L3.05 10.476a.75.75 0 0 0-.188.335l-.95 3.507a.75.75 0 0 0 .92.92l3.507-.95a.75.75 0 0 0 .335-.188l7.963-7.963a1.75 1.75 0 0 0 0-2.475l-.149-.149ZM11.72 3.22a.25.25 0 0 1 .354 0l.149.149a.25.25 0 0 1 0 .354L5.106 10.84l-1.575.427.427-1.575 7.11-7.11.652-.362Z"/></svg>';
      editBtn.addEventListener("mousedown", function (e) { e.preventDefault(); });
      editBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        self._openTextPrompt({
          label: "Edit link URL",
          value: href,
          anchor: editBtn,
          onSubmit: function (newUrl) {
            newUrl = newUrl.trim();
            if (newUrl === "") {
              self._unwrapLink(anchorEl);
              self._dismissLinkPopover();
            } else {
              anchorEl.setAttribute("href", newUrl);
              urlLink.href = newUrl;
              urlLink.textContent = newUrl;
              urlLink.title = newUrl;
              href = newUrl;
            }
            self._debouncedPushVisualChange();
          },
        });
      });
      actions.appendChild(editBtn);

      // Divider inside actions
      var d1 = document.createElement("span");
      d1.className = "leaf-popover-divider";
      actions.appendChild(d1);

      // Remove button
      var removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.title = "Remove link";
      removeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" width="13" height="13"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L6.94 8l-1.72 1.72a.75.75 0 1 0 1.06 1.06L8 9.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L9.06 8l1.72-1.72a.75.75 0 0 0-1.06-1.06L8 6.94 6.28 5.22Z"/></svg>';
      removeBtn.addEventListener("mousedown", function (e) { e.preventDefault(); });
      removeBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        self._unwrapLink(anchorEl);
        self._dismissLinkPopover();
        self._debouncedPushVisualChange();
      });
      actions.appendChild(removeBtn);

      pop.appendChild(actions);

      // Position below the anchor element
      this.el.style.position = "relative";
      var editorRect = this.el.getBoundingClientRect();
      var anchorRect = anchorEl.getBoundingClientRect();

      pop.style.left = (anchorRect.left - editorRect.left) + "px";
      pop.style.top = (anchorRect.bottom - editorRect.top + 8) + "px";

      this.el.appendChild(pop);
      this._linkPopoverEl = pop;
    },

    _dismissLinkPopover: function () {
      if (this._linkPopoverEl) {
        this._linkPopoverEl.remove();
        this._linkPopoverEl = null;
        this._linkPopoverAnchor = null;
      }
    },

    _unwrapLink: function (anchorEl) {
      // Replace the <a> with its text content
      var parent = anchorEl.parentNode;
      while (anchorEl.firstChild) {
        parent.insertBefore(anchorEl.firstChild, anchorEl);
      }
      parent.removeChild(anchorEl);
    },

    // -- Image URL dialog --

    _openImageUrlDialog: function () {
      this._dismissImageUrlDialog();
      var self = this;

      // Save selection so we can restore it before inserting
      var savedRange = null;
      if (this._mode === "visual" && this._visualEl) {
        var sel = window.getSelection();
        if (sel.rangeCount > 0) {
          savedRange = sel.getRangeAt(0).cloneRange();
        }
      }

      var dialog = document.createElement("div");
      dialog.className = "leaf-image-url-dialog";

      var urlLabel = document.createElement("label");
      urlLabel.textContent = "Image URL";
      dialog.appendChild(urlLabel);

      var urlInput = document.createElement("input");
      urlInput.type = "text";
      urlInput.placeholder = "https://example.com/image.jpg";
      urlInput.addEventListener("mousedown", function (e) { e.stopPropagation(); });
      dialog.appendChild(urlInput);

      var altLabel = document.createElement("label");
      altLabel.textContent = "Alt text (optional)";
      dialog.appendChild(altLabel);

      var altInput = document.createElement("input");
      altInput.type = "text";
      altInput.placeholder = "Describe the image";
      altInput.addEventListener("mousedown", function (e) { e.stopPropagation(); });
      dialog.appendChild(altInput);

      var actions = document.createElement("div");
      actions.className = "leaf-image-url-actions";

      var cancelBtn = document.createElement("button");
      cancelBtn.type = "button";
      cancelBtn.className = "leaf-image-url-cancel";
      cancelBtn.textContent = "Cancel";
      cancelBtn.addEventListener("click", function () {
        self._dismissImageUrlDialog();
      });
      actions.appendChild(cancelBtn);

      var insertBtn = document.createElement("button");
      insertBtn.type = "button";
      insertBtn.className = "leaf-image-url-insert";
      insertBtn.textContent = "Insert";
      insertBtn.addEventListener("click", function () {
        var url = urlInput.value.trim();
        if (!url) return;
        var alt = altInput.value.trim();
        self._dismissImageUrlDialog();
        self._insertImageByUrl(url, alt, savedRange);
      });
      actions.appendChild(insertBtn);
      dialog.appendChild(actions);

      // Enter key in inputs triggers insert
      var handleKey = function (e) {
        if (e.key === "Enter") {
          e.preventDefault();
          insertBtn.click();
        } else if (e.key === "Escape") {
          e.preventDefault();
          self._dismissImageUrlDialog();
        }
      };
      urlInput.addEventListener("keydown", handleKey);
      altInput.addEventListener("keydown", handleKey);

      // Position below the image button using fixed positioning on body
      var splitBtn = this.el.querySelector("[data-image-split-btn]");
      if (splitBtn) {
        var btnRect = splitBtn.getBoundingClientRect();
        dialog.style.left = btnRect.left + "px";
        dialog.style.top = (btnRect.bottom + 4) + "px";
      }

      // Backdrop
      var backdrop = document.createElement("div");
      backdrop.className = "leaf-image-url-backdrop";
      backdrop.addEventListener("click", function () {
        self._dismissImageUrlDialog();
      });
      document.body.appendChild(backdrop);
      this._imageUrlBackdrop = backdrop;
      this.pushEventTo(this.el, "media_ui_opened", { editor_id: this._editorId });
      document.body.appendChild(dialog);
      this._imageUrlDialog = dialog;
      urlInput.focus();
    },

    _dismissImageUrlDialog: function () {
      if (this._imageUrlDialog) {
        this._imageUrlDialog.remove();
        this._imageUrlDialog = null;
      }
      if (this._imageUrlBackdrop) {
        this._imageUrlBackdrop.remove();
        this._imageUrlBackdrop = null;
      }
      this.pushEventTo(this.el, "media_ui_closed", { editor_id: this._editorId });
    },


    _insertImageByUrl: function (url, alt, savedRange) {
      var escapedAlt = (alt || "").replace(/"/g, "&quot;");

      if (this._mode === "visual" && this._visualEl) {
        this._visualEl.focus();
        // Restore saved selection
        if (savedRange) {
          var sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(savedRange);
        }
        var imgHtml = '<img src="' + url + '" alt="' + escapedAlt + '" draggable="true" />';
        document.execCommand("insertHTML", false, imgHtml);
        this._debouncedPushVisualChange();
      } else if (this._mode === "markdown") {
        var gid = this._editorId.replace(/-/g, "_") + "_markdown";
        var ins = window["markdownEditorInsert_" + gid];
        if (ins) {
          var md = "![" + (alt || "") + "](" + url + ")";
          ins(md);
        }
      }
    },

    // -- Image selection + resize + popover --

    // Alignment lives on the <figure> when the image is captioned, else
    // directly on the <img>; both serialize as a data-align attribute.
    _imageAlignTarget: function (imgEl) {
      var fig = imgEl.closest && imgEl.closest("figure");
      return fig || imgEl;
    },

    _setImageAlign: function (imgEl, align) {
      var t = this._imageAlignTarget(imgEl);
      if (align) t.setAttribute("data-align", align);
      else t.removeAttribute("data-align");
    },

    _imageCaption: function (imgEl) {
      var fig = imgEl.closest && imgEl.closest("figure");
      if (!fig) return "";
      var cap = fig.querySelector("figcaption");
      return cap ? cap.textContent : "";
    },

    // Set (or clear) a caption, wrapping/unwrapping a <figure> as needed and
    // carrying any alignment across the boundary.
    _setImageCaption: function (imgEl, text) {
      text = text || "";
      var fig = imgEl.closest && imgEl.closest("figure");
      if (text.trim()) {
        if (!fig) {
          fig = document.createElement("figure");
          var align = imgEl.getAttribute("data-align");
          if (align) {
            fig.setAttribute("data-align", align);
            imgEl.removeAttribute("data-align");
          }
          if (imgEl.parentNode) {
            imgEl.parentNode.insertBefore(fig, imgEl);
            fig.appendChild(imgEl);
          }
        }
        var cap = fig.querySelector("figcaption");
        if (!cap) {
          cap = document.createElement("figcaption");
          fig.appendChild(cap);
        }
        cap.textContent = text;
      } else if (fig) {
        var falign = fig.getAttribute("data-align");
        if (falign) imgEl.setAttribute("data-align", falign);
        if (fig.parentNode) fig.parentNode.insertBefore(imgEl, fig);
        fig.remove();
      }
      this._debouncedPushVisualChange();
    },

    _showImagePopover: function (imgEl) {
      this._dismissImagePopover(true);
      this._imagePopoverTarget = imgEl;

      var src = imgEl.getAttribute("src") || "";
      var alt = imgEl.getAttribute("alt") || "";
      var self = this;

      // Mark image as selected (outline via CSS)
      imgEl.classList.add("leaf-img-selected");

      // Create resize handles
      this._resizeHandles = [];
      var corners = ["nw", "ne", "sw", "se"];
      corners.forEach(function (corner) {
        var handle = document.createElement("div");
        handle.className = "leaf-resize-handle leaf-resize-handle--" + corner;
        handle.setAttribute("data-corner", corner);
        handle.addEventListener("mousedown", function (e) {
          e.preventDefault();
          e.stopPropagation();
          self._startResize(e, imgEl, corner);
        });
        self.el.appendChild(handle);
        self._resizeHandles.push(handle);
      });

      this._positionResizeHandles(imgEl);

      // Build popover
      var pop = document.createElement("div");
      pop.className = "leaf-link-popover";

      // Image icon
      var imgIcon = document.createElement("span");
      imgIcon.style.cssText = "display:flex;align-items:center;opacity:0.5;flex-shrink:0;";
      imgIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" width="13" height="13"><path fill-rule="evenodd" d="M2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm10.5 5.707L10.354 7.56a.5.5 0 0 0-.708 0L6.5 10.707 5.354 9.56a.5.5 0 0 0-.708 0L3.5 10.707V4a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v5.707ZM11 6a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" clip-rule="evenodd"/></svg>';
      pop.appendChild(imgIcon);

      // Alt text input
      var altInput = document.createElement("input");
      altInput.type = "text";
      altInput.value = alt;
      altInput.placeholder = "Alt text...";
      altInput.title = "Image alt text";
      altInput.style.cssText = [
        "background: color-mix(in oklab, var(--color-base-content, #1f2937) 8%, transparent);",
        "border: none; border-radius: 0.25rem; padding: 0.2rem 0.4rem;",
        "font-size: 0.8125rem; color: inherit; outline: none; width: 160px;",
      ].join("");
      altInput.addEventListener("mousedown", function (e) { e.stopPropagation(); });
      altInput.addEventListener("input", function () {
        imgEl.setAttribute("alt", altInput.value);
      });
      altInput.addEventListener("keydown", function (e) {
        if (e.key === "Escape") {
          self._dismissImagePopover();
        }
      });
      pop.appendChild(altInput);

      // Caption input — wraps the image in a <figure><figcaption> when set.
      var capInput = document.createElement("input");
      capInput.type = "text";
      capInput.value = self._imageCaption(imgEl);
      capInput.placeholder = "Caption...";
      capInput.title = "Image caption";
      capInput.style.cssText = [
        "background: color-mix(in oklab, var(--color-base-content, #1f2937) 8%, transparent);",
        "border: none; border-radius: 0.25rem; padding: 0.2rem 0.4rem;",
        "font-size: 0.8125rem; color: inherit; outline: none; width: 130px;",
      ].join("");
      capInput.addEventListener("mousedown", function (e) { e.stopPropagation(); });
      capInput.addEventListener("input", function () {
        self._setImageCaption(imgEl, capInput.value);
      });
      capInput.addEventListener("keydown", function (e) {
        if (e.key === "Escape") self._dismissImagePopover();
      });
      pop.appendChild(capInput);

      // Alignment buttons (left / center / right; click active to clear).
      var alignWrap = document.createElement("span");
      alignWrap.style.cssText = "display:inline-flex;gap:2px;";
      [["left", "L"], ["center", "C"], ["right", "R"]].forEach(function (a) {
        var ab = document.createElement("button");
        ab.type = "button";
        ab.title = "Align " + a[0];
        ab.textContent = a[1];
        ab.style.cssText =
          "border:none;background:none;cursor:pointer;color:inherit;font-size:0.7rem;font-weight:700;padding:0.125rem 0.3rem;border-radius:0.25rem;";
        ab.addEventListener("mousedown", function (e) { e.preventDefault(); });
        ab.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          var t = self._imageAlignTarget(imgEl);
          var cur = t.getAttribute("data-align");
          self._setImageAlign(imgEl, cur === a[0] ? "" : a[0]);
          self._debouncedPushVisualChange();
        });
        alignWrap.appendChild(ab);
      });
      pop.appendChild(alignWrap);

      // Actions group
      var actions = document.createElement("span");
      actions.className = "leaf-popover-actions";

      // Open in new tab button
      var openBtn = document.createElement("button");
      openBtn.type = "button";
      openBtn.title = "Open image";
      openBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" width="13" height="13"><path d="M8.22 2.97a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06l2.97-2.97H3.75a.75.75 0 0 1 0-1.5h7.44L8.22 4.03a.75.75 0 0 1 0-1.06Z"/></svg>';
      openBtn.addEventListener("mousedown", function (e) { e.preventDefault(); });
      openBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (src) window.open(src, "_blank");
      });
      actions.appendChild(openBtn);

      // Edit src button
      var editSrcBtn = document.createElement("button");
      editSrcBtn.type = "button";
      editSrcBtn.title = "Edit image URL";
      editSrcBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" width="13" height="13"><path d="M13.488 2.513a1.75 1.75 0 0 0-2.475 0L3.05 10.476a.75.75 0 0 0-.188.335l-.95 3.507a.75.75 0 0 0 .92.92l3.507-.95a.75.75 0 0 0 .335-.188l7.963-7.963a1.75 1.75 0 0 0 0-2.475l-.149-.149ZM11.72 3.22a.25.25 0 0 1 .354 0l.149.149a.25.25 0 0 1 0 .354L5.106 10.84l-1.575.427.427-1.575 7.11-7.11.652-.362Z"/></svg>';
      editSrcBtn.addEventListener("mousedown", function (e) { e.preventDefault(); });
      editSrcBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        self._openTextPrompt({
          label: "Image URL",
          value: src,
          anchor: editSrcBtn,
          onSubmit: function (newSrc) {
            newSrc = newSrc.trim();
            if (newSrc) {
              imgEl.setAttribute("src", newSrc);
              src = newSrc;
              self._debouncedPushVisualChange();
            }
            self._showImagePopover(imgEl);
          },
          onCancel: function () {
            self._showImagePopover(imgEl);
          },
        });
      });
      actions.appendChild(editSrcBtn);

      // Divider
      var d1 = document.createElement("span");
      d1.className = "leaf-popover-divider";
      actions.appendChild(d1);

      // Remove button
      var removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.title = "Remove image";
      removeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" width="13" height="13"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L6.94 8l-1.72 1.72a.75.75 0 1 0 1.06 1.06L8 9.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L9.06 8l1.72-1.72a.75.75 0 0 0-1.06-1.06L8 6.94 6.28 5.22Z"/></svg>';
      removeBtn.addEventListener("mousedown", function (e) { e.preventDefault(); });
      removeBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        imgEl.remove();
        self._dismissImagePopover();
        self._debouncedPushVisualChange();
      });
      actions.appendChild(removeBtn);

      pop.appendChild(actions);

      // Position below the image
      this.el.style.position = "relative";
      var editorRect = this.el.getBoundingClientRect();
      var imgRect = imgEl.getBoundingClientRect();

      pop.style.left = (imgRect.left - editorRect.left) + "px";
      pop.style.top = (imgRect.bottom - editorRect.top + 8) + "px";

      this.el.appendChild(pop);
      this._imagePopoverEl = pop;

      // Focus the alt input
      setTimeout(function () { altInput.focus(); altInput.select(); }, 50);
    },

    _positionResizeHandles: function (imgEl) {
      if (!this._resizeHandles || !imgEl) return;
      var editorRect = this.el.getBoundingClientRect();
      var imgRect = imgEl.getBoundingClientRect();
      var ox = imgRect.left - editorRect.left;
      var oy = imgRect.top - editorRect.top;
      var w = imgRect.width;
      var h = imgRect.height;
      var hs = 5; // half handle size

      var positions = {
        nw: { left: ox - hs, top: oy - hs },
        ne: { left: ox + w - hs, top: oy - hs },
        sw: { left: ox - hs, top: oy + h - hs },
        se: { left: ox + w - hs, top: oy + h - hs },
      };

      this._resizeHandles.forEach(function (handle) {
        var corner = handle.getAttribute("data-corner");
        var pos = positions[corner];
        handle.style.left = pos.left + "px";
        handle.style.top = pos.top + "px";
      });
    },

    _startResize: function (e, imgEl, corner) {
      var self = this;
      var startX = e.clientX;
      var startY = e.clientY;
      var startW = imgEl.offsetWidth;
      var startH = imgEl.offsetHeight;
      var aspect = startW / startH;

      function onMove(ev) {
        ev.preventDefault();
        var dx = ev.clientX - startX;
        var dy = ev.clientY - startY;
        var newW;

        // Use the axis with more movement, maintain aspect ratio
        if (corner === "se") {
          newW = Math.max(50, startW + dx);
        } else if (corner === "sw") {
          newW = Math.max(50, startW - dx);
        } else if (corner === "ne") {
          newW = Math.max(50, startW + dx);
        } else { // nw
          newW = Math.max(50, startW - dx);
        }

        var newH = newW / aspect;
        imgEl.style.width = newW + "px";
        imgEl.style.height = newH + "px";
        imgEl.setAttribute("width", Math.round(newW));
        imgEl.setAttribute("height", Math.round(newH));

        self._positionResizeHandles(imgEl);

        // Reposition popover below the image
        if (self._imagePopoverEl) {
          var editorRect = self.el.getBoundingClientRect();
          var imgRect = imgEl.getBoundingClientRect();
          self._imagePopoverEl.style.left = (imgRect.left - editorRect.left) + "px";
          self._imagePopoverEl.style.top = (imgRect.bottom - editorRect.top + 8) + "px";
        }
      }

      function onUp() {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      }

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },

    // -- Block & image drag-and-drop reordering --

    _setupImageDragAndDrop: function () {
      if (!this._visualEl) return;
      var self = this;

      this._dragIndicator = null;
      this._dragSourceBlock = null;
      this._dragDropTarget = null;
      this._dragHandle = null;
      this._dragHandleBlock = null;

      // Ensure existing images are draggable
      var imgs = this._visualEl.querySelectorAll("img");
      for (var i = 0; i < imgs.length; i++) {
        imgs[i].setAttribute("draggable", "true");
      }

      // Watch for new images and mark them draggable
      this._imgObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType !== Node.ELEMENT_NODE) return;
            if (node.tagName && node.tagName.toLowerCase() === "img") {
              node.setAttribute("draggable", "true");
            }
            var childImgs = node.querySelectorAll && node.querySelectorAll("img");
            if (childImgs) {
              for (var j = 0; j < childImgs.length; j++) {
                childImgs[j].setAttribute("draggable", "true");
              }
            }
          });
        });
      });
      this._imgObserver.observe(this._visualEl, { childList: true, subtree: true });

      // -- Drag handle (grip icon) for block elements --

      var handle = this._visualWrapper.querySelector("[data-drag-handle]");
      this._dragHandle = handle;

      // Show handle on mousemove over blocks
      this._visualEl.addEventListener("mousemove", function (e) {
        if (self._readonly || self._dragSourceBlock) return;
        var block = self._getHoveredBlock(e.target);
        if (!block) return;
        // Always update if stale (e.g. after innerHTML replacement) or different block
        if (block !== self._dragHandleBlock || !self._dragHandleBlock.parentNode) {
          self._dragHandleBlock = block;
          self._positionDragHandle(block);
        }
      });

      // Show handle when hovering in the left margin area (outside content but inside wrapper)
      this._visualWrapper.addEventListener("mousemove", function (e) {
        if (self._readonly || self._dragSourceBlock) return;

        // Skip if mouse is over a content block - let the visualEl handler deal with it
        var hoveredBlock = self._getHoveredBlock(e.target);
        if (hoveredBlock) return;

        // Skip if mouse is over the drag handle itself
        if (e.target.closest("[data-drag-handle]")) return;

        // Find the block nearest to the mouse cursor vertically
        var wrapperRect = self._visualWrapper.getBoundingClientRect();
        var mouseY = e.clientY - wrapperRect.top;
        var nearestBlock = self._findBlockAtY(mouseY);

        if (nearestBlock && nearestBlock !== self._dragHandleBlock) {
          self._dragHandleBlock = nearestBlock;
          self._positionDragHandle(nearestBlock);
        }
      });

      // Hide handle when mouse leaves the wrapper, unless going to the handle or a block is selected
      this._visualWrapper.addEventListener("mouseleave", function (e) {
        // Don't hide if mouse is moving to the drag handle
        if (e.relatedTarget && e.relatedTarget.matches("[data-drag-handle]")) return;

        if (!self._dragSourceBlock && !self._imagePopoverTarget) {
          self._dragHandleBlock = null;
          self._dragHandle.style.display = "none";
        }
      });

      // -- Handle mousedown (block drag via mouse, delegated so it survives morphdom) --
      this._visualWrapper.addEventListener("mousedown", function (e) {
        if (!e.target.closest("[data-drag-handle]")) return;
        var block = self._dragHandleBlock;
        if (!block || self._readonly) return;
        e.preventDefault();
        e.stopPropagation();

        self._dismissImagePopover();
        self._dismissLinkPopover();

        var startX = e.clientX;
        var startY = e.clientY;
        var moved = false;

        self._dragSourceBlock = block;
        block.classList.add("leaf-dragging");
        self._dragHandle.style.cursor = "grabbing";

        self._createDropIndicator();

        function onMouseMove(ev) {
          ev.preventDefault();
          if (
            !moved &&
            (Math.abs(ev.clientY - startY) > 4 ||
              Math.abs(ev.clientX - startX) > 4)
          ) {
            moved = true;
          }
          var target = self._findDropTarget(ev.clientY);
          if (target) {
            self._dragDropTarget = target;
            self._positionDropIndicator(target);
          }
        }

        function onMouseUp(ev) {
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
          self._dragHandle.style.cursor = "";

          // A click (no real drag) opens the block menu instead of moving.
          if (!moved) {
            self._cleanupDrag();
            self._openBlockMenu(block);
            return;
          }

          if (self._dragSourceBlock && self._dragDropTarget) {
            var sourceEl = self._dragSourceBlock;
            var targetEl = self._dragDropTarget.element;
            var position = self._dragDropTarget.position;

            var refNext = targetEl.nextSibling;
            var refParent = targetEl.parentNode;
            var targetIsSource = (sourceEl === targetEl);

            sourceEl.remove();

            if (targetIsSource) {
              if (refNext && refNext.parentNode) {
                refParent.insertBefore(sourceEl, refNext);
              } else if (refParent) {
                refParent.appendChild(sourceEl);
              }
            } else if (!targetEl.parentNode) {
              if (refNext && refNext.parentNode) {
                refParent.insertBefore(sourceEl, refNext);
              } else if (refParent) {
                refParent.appendChild(sourceEl);
              }
            } else if (position === "before") {
              targetEl.parentNode.insertBefore(sourceEl, targetEl);
            } else {
              if (targetEl.nextSibling) {
                targetEl.parentNode.insertBefore(sourceEl, targetEl.nextSibling);
              } else {
                targetEl.parentNode.appendChild(sourceEl);
              }
            }

            self._debouncedPushVisualChange();
          }

          self._cleanupDrag();
        }

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      });

      // -- Image native dragstart --
      this._visualEl.addEventListener("dragstart", function (e) {
        var img = e.target;
        if (!img || img.tagName.toLowerCase() !== "img") return;
        if (self._readonly) { e.preventDefault(); return; }

        // Don't drag if popover is open (resize handles active)
        if (self._imagePopoverTarget === img) {
          e.preventDefault();
          return;
        }

        self._dismissImagePopover();
        self._dismissLinkPopover();

        // For images, the drag source is the image itself (or its parent block)
        var block = self._getContainingBlock(img);
        self._dragSourceBlock = block || img;
        (block || img).classList.add("leaf-dragging");

        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", "leaf-image-drag");

        self._createDropIndicator();
      });

      // -- Image native drag: dragover/drop/dragleave/dragend --
      this._visualEl.addEventListener("dragover", function (e) {
        if (!self._dragSourceBlock) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";

        var target = self._findDropTarget(e.clientY);
        if (target) {
          self._dragDropTarget = target;
          self._positionDropIndicator(target);
        }
      });

      this._visualEl.addEventListener("dragleave", function (e) {
        if (!self._visualEl.contains(e.relatedTarget)) {
          if (self._dragIndicator) self._dragIndicator.style.display = "none";
          self._dragDropTarget = null;
        }
      });

      this._visualEl.addEventListener("drop", function (e) {
        e.preventDefault();
        if (!self._dragSourceBlock || !self._dragDropTarget) {
          self._cleanupDrag();
          return;
        }

        var sourceEl = self._dragSourceBlock;
        var targetEl = self._dragDropTarget.element;
        var position = self._dragDropTarget.position;

        var refNext = targetEl.nextSibling;
        var refParent = targetEl.parentNode;
        var targetIsSource = (sourceEl === targetEl);

        sourceEl.remove();

        if (targetIsSource) {
          if (refNext && refNext.parentNode) {
            refParent.insertBefore(sourceEl, refNext);
          } else if (refParent) {
            refParent.appendChild(sourceEl);
          }
        } else if (!targetEl.parentNode) {
          if (refNext && refNext.parentNode) {
            refParent.insertBefore(sourceEl, refNext);
          } else if (refParent) {
            refParent.appendChild(sourceEl);
          }
        } else if (position === "before") {
          targetEl.parentNode.insertBefore(sourceEl, targetEl);
        } else {
          if (targetEl.nextSibling) {
            targetEl.parentNode.insertBefore(sourceEl, targetEl.nextSibling);
          } else {
            targetEl.parentNode.appendChild(sourceEl);
          }
        }

        self._cleanupDrag();
        self._debouncedPushVisualChange();
      });

      this._visualEl.addEventListener("dragend", function () {
        self._cleanupDrag();
      });
    },

    _getHoveredBlock: function (target) {
      // Walk up from target to find the direct child of _visualEl
      var node = target;
      while (node && node.parentNode !== this._visualEl) {
        node = node.parentNode;
      }
      if (!node || node.nodeType !== Node.ELEMENT_NODE) return null;
      if (!this._isBlockTag(node)) return null;
      return node;
    },

    _getContainingBlock: function (node) {
      // Walk up from node to find the direct child of _visualEl
      var current = node;
      while (current && current.parentNode !== this._visualEl) {
        current = current.parentNode;
      }
      return current;
    },

    _isBlockTag: function (el) {
      var BLOCK_TAGS = {
        p: true, h1: true, h2: true, h3: true, h4: true, h5: true, h6: true,
        blockquote: true, pre: true, ul: true, ol: true, hr: true, img: true,
        div: true, figure: true, table: true
      };
      return el && el.tagName && BLOCK_TAGS[el.tagName.toLowerCase()];
    },

    _findBlockAtY: function (y) {
      var children = this._visualEl.childNodes;
      var best = null;
      var bestDist = Infinity;
      var wrapperRect = this._visualWrapper.getBoundingClientRect();

      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        if (child.nodeType !== Node.ELEMENT_NODE) continue;
        if (!this._isBlockTag(child)) continue;

        var rect = child.getBoundingClientRect();
        var marginTop = parseFloat(window.getComputedStyle(child).marginTop) || 0;
        var blockTop = rect.top - wrapperRect.top - marginTop;
        var blockBottom = rect.bottom - wrapperRect.top;

        // If mouse is within the block's range (including margin), it's a direct hit
        if (y >= blockTop && y <= blockBottom) {
          return child;
        }

        // Otherwise find nearest by distance to closest edge
        var dist = y < blockTop ? blockTop - y : y - blockBottom;
        if (dist < bestDist) {
          bestDist = dist;
          best = child;
        }
      }

      return best;
    },

    _positionDragHandle: function (block) {
      if (!this._dragHandle) return;
      var wrapperRect = this._visualWrapper.getBoundingClientRect();
      var blockRect = block.getBoundingClientRect();
      var handleHeight = this._dragHandle.offsetHeight || 28;

      // For tall blocks (paragraphs, headings, lists) align the handle to
      // the block's top so it sits next to the first line. For short
      // blocks like a hybrid-mode `<hr>` (~18px), top-aligning puts the
      // handle above the visible content; vertically center it instead so
      // it lines up with the rule.
      var top = blockRect.top - wrapperRect.top;
      if (blockRect.height < handleHeight) {
        top += (blockRect.height - handleHeight) / 2;
      }
      var left = blockRect.left - wrapperRect.left - 30;

      this._dragHandle.style.top = top + "px";
      this._dragHandle.style.left = Math.max(0, left) + "px";
      this._dragHandle.style.display = "flex";
    },

    _createDropIndicator: function () {
      if (this._dragIndicator) this._dragIndicator.remove();
      var indicator = document.createElement("div");
      indicator.className = "leaf-drop-indicator";
      indicator.style.display = "none";
      this._visualWrapper.appendChild(indicator);
      this._dragIndicator = indicator;
    },

    // -- Code block tools (language label + copy button) --

    _setupCodeBlockTools: function () {
      if (!this._visualEl || !this._visualWrapper) return;
      var self = this;
      this._visualEl.addEventListener("mouseover", function (e) {
        var pre = e.target.closest && e.target.closest("pre");
        if (pre && self._visualEl.contains(pre)) self._showCodeTools(pre);
      });
      this._visualEl.addEventListener("mouseleave", function () {
        self._hideCodeTools();
      });
    },

    _ensureCodeTools: function () {
      // A LiveView DOM patch can wipe or detach the cached bar (leaving a
      // ghost element with no buttons) — validate before reusing it.
      if (
        this._codeTools &&
        this._codeTools.isConnected &&
        this._codeTools.querySelectorAll("button").length >= 2
      ) {
        return this._codeTools;
      }
      if (this._codeTools && this._codeTools.parentNode) {
        this._codeTools.parentNode.removeChild(this._codeTools);
      }
      this._codeTools = null;
      this._codeToolsPre = null;
      var self = this;
      var bar = document.createElement("div");
      bar.className = "leaf-code-tools";
      bar.setAttribute("contenteditable", "false");
      bar.style.cssText =
        "position:absolute;z-index:20;display:none;align-items:center;gap:0.375rem;padding:0.125rem 0.25rem;border-radius:0.375rem;background:color-mix(in oklab, var(--color-base-content,#1f2937) 8%, transparent);font-size:0.7rem;user-select:none;";

      var label = document.createElement("button");
      label.type = "button";
      label.title = "Set language";
      label.style.cssText =
        "border:none;background:none;cursor:pointer;color:inherit;opacity:0.7;text-transform:uppercase;letter-spacing:0.03em;font-size:0.7rem;padding:0;";
      label.addEventListener("mousedown", function (e) {
        e.preventDefault();
      });
      label.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!self._codeToolsPre) return;
        // _codeToolsPre is cleared when the pointer leaves the block —
        // capture the target <pre> now, before the popover opens.
        var pre = self._codeToolsPre;
        var cur = pre.getAttribute("data-language") || "";
        self._openTextPrompt({
          label: "Code language",
          value: cur,
          placeholder: "e.g. javascript",
          anchor: label,
          onSubmit: function (lang) {
            lang = lang.trim();
            if (!pre.isConnected) return;
            if (lang) pre.setAttribute("data-language", lang);
            else pre.removeAttribute("data-language");
            label.textContent = lang || "code";
            self._debouncedPushVisualChange();
          },
        });
      });
      bar.appendChild(label);

      var copy = document.createElement("button");
      copy.type = "button";
      copy.textContent = "Copy";
      copy.style.cssText =
        "border:none;background:none;cursor:pointer;color:inherit;font-size:0.7rem;padding:0.125rem 0.25rem;border-radius:0.25rem;";
      copy.addEventListener("mousedown", function (e) {
        e.preventDefault();
      });
      copy.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (self._codeToolsPre) {
          self._copyText(self._codeToolsPre.textContent || "");
          copy.textContent = "Copied";
          setTimeout(function () {
            copy.textContent = "Copy";
          }, 1200);
        }
      });
      bar.appendChild(copy);

      // Don't let hovering the bar trigger the editor's mouseleave hide.
      bar.addEventListener("mouseover", function (e) {
        e.stopPropagation();
      });

      this._visualWrapper.appendChild(bar);
      this._codeToolsLabel = label;
      this._codeTools = bar;
      return bar;
    },

    _showCodeTools: function (pre) {
      var bar = this._ensureCodeTools();
      this._codeToolsPre = pre;

      var lang = pre.getAttribute("data-language") || "";
      if (!lang) {
        var c = pre.querySelector("code");
        if (c) {
          var m = c.className.match(/language-(\S+)/);
          if (m) lang = m[1];
        }
      }
      this._codeToolsLabel.textContent = lang || "code";

      var wrapperRect = this._visualWrapper.getBoundingClientRect();
      var preRect = pre.getBoundingClientRect();
      this._visualWrapper.style.position = "relative";
      bar.style.display = "flex";
      bar.style.top = preRect.top - wrapperRect.top + 4 + "px";
      bar.style.left =
        Math.max(
          0,
          preRect.right - wrapperRect.left - bar.offsetWidth - 8
        ) + "px";
    },

    _hideCodeTools: function () {
      if (this._codeTools) this._codeTools.style.display = "none";
      this._codeToolsPre = null;
    },

    _copyText: function (text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(function () {});
        return;
      }
      // Fallback for non-secure contexts.
      var ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch (e) {}
      document.body.removeChild(ta);
    },

    _findDropTarget: function (clientY) {
      var children = this._visualEl.childNodes;
      var blocks = [];

      for (var i = 0; i < children.length; i++) {
        var child = children[i];
        if (child.nodeType !== Node.ELEMENT_NODE) continue;
        if (this._isBlockTag(child)) {
          blocks.push(child);
        }
      }

      if (blocks.length === 0) return null;

      var best = null;
      var bestDist = Infinity;

      for (var j = 0; j < blocks.length; j++) {
        var block = blocks[j];
        // Skip the block being dragged
        if (block === this._dragSourceBlock) continue;

        var rect = block.getBoundingClientRect();

        var distTop = Math.abs(clientY - rect.top);
        if (distTop < bestDist) {
          bestDist = distTop;
          best = { element: block, position: "before" };
        }

        var distBottom = Math.abs(clientY - rect.bottom);
        if (distBottom < bestDist) {
          bestDist = distBottom;
          best = { element: block, position: "after" };
        }
      }

      // Edge: above first non-source block
      for (var k = 0; k < blocks.length; k++) {
        if (blocks[k] !== this._dragSourceBlock) {
          var firstRect = blocks[k].getBoundingClientRect();
          if (clientY < firstRect.top) {
            return this._normalizeDropTarget({
              element: blocks[k],
              position: "before",
            });
          }
          break;
        }
      }

      // Edge: below last non-source block
      for (var l = blocks.length - 1; l >= 0; l--) {
        if (blocks[l] !== this._dragSourceBlock) {
          var lastRect = blocks[l].getBoundingClientRect();
          if (clientY > lastRect.bottom) {
            return this._normalizeDropTarget({
              element: blocks[l],
              position: "after",
            });
          }
          break;
        }
      }

      return this._normalizeDropTarget(best);
    },

    // A drop slot immediately adjacent to the dragged block ("before its
    // next sibling" / "after its previous sibling") is a no-op — the
    // block would land exactly where it already is. Rendering the
    // indicator there reads as a pending move that then does nothing on
    // release. Normalize those slots to the source block itself, so the
    // line stays parked on the block's current position until the
    // pointer genuinely crosses into another block's territory.
    _normalizeDropTarget: function (target) {
      var source = this._dragSourceBlock;
      if (!target || !source || target.element === source) return target;

      var next = source.nextSibling;
      while (next && next.nodeType !== Node.ELEMENT_NODE) {
        next = next.nextSibling;
      }
      var prev = source.previousSibling;
      while (prev && prev.nodeType !== Node.ELEMENT_NODE) {
        prev = prev.previousSibling;
      }

      if (
        (target.position === "before" && target.element === next) ||
        (target.position === "after" && target.element === prev)
      ) {
        return { element: source, position: "before" };
      }
      return target;
    },

    _positionDropIndicator: function (target) {
      if (!this._dragIndicator || !target) return;

      var wrapperRect = this._visualWrapper.getBoundingClientRect();
      var blockRect = target.element.getBoundingClientRect();
      var y;

      if (target.position === "before") {
        y = blockRect.top - wrapperRect.top - 2;
      } else {
        y = blockRect.bottom - wrapperRect.top + 1;
      }

      y = Math.max(0, Math.min(y, this._visualWrapper.offsetHeight));

      this._dragIndicator.style.top = y + "px";
      this._dragIndicator.style.display = "block";
    },

    _cleanupDrag: function () {
      var movedBlock = this._dragSourceBlock;
      if (movedBlock) {
        movedBlock.classList.remove("leaf-dragging");
        this._dragSourceBlock = null;
      }
      if (this._dragIndicator) {
        this._dragIndicator.remove();
        this._dragIndicator = null;
      }
      this._dragDropTarget = null;

      // Re-show handle on the block that was just moved
      if (movedBlock && movedBlock.parentNode === this._visualEl) {
        this._dragHandleBlock = movedBlock;
        this._positionDragHandle(movedBlock);
      } else {
        this._dragHandleBlock = null;
        if (this._dragHandle) {
          this._dragHandle.style.display = "none";
        }
      }
    },

    // -- Block handle menu (convert type / duplicate / delete) --

    _openBlockMenu: function (block) {
      this._closeBlockMenu();
      if (!block || !this._dragHandle || !this._visualWrapper) return;
      var self = this;

      var menu = document.createElement("div");
      menu.className = "leaf-block-menu";
      menu.style.cssText =
        "position:absolute;z-index:60;background:var(--color-base-100,#fff);color:var(--color-base-content,#1f2937);border:1px solid var(--color-base-300,#d1d5db);border-radius:0.5rem;box-shadow:0 6px 20px rgba(0,0,0,0.18),0 1px 4px rgba(0,0,0,0.10);padding:0.25rem;min-width:160px;font-size:0.8rem;";

      var items = [
        { label: "Text", fn: function () { self._convertBlock(block, "p"); } },
        { label: "Heading 1", fn: function () { self._convertBlock(block, "h1"); } },
        { label: "Heading 2", fn: function () { self._convertBlock(block, "h2"); } },
        { label: "Heading 3", fn: function () { self._convertBlock(block, "h3"); } },
        { label: "Quote", fn: function () { self._convertBlock(block, "blockquote"); } },
        { label: "Code block", fn: function () { self._convertBlock(block, "pre"); } },
        { divider: true },
        { label: "Duplicate", fn: function () { self._duplicateBlock(block); } },
        { label: "Delete", danger: true, fn: function () { self._deleteBlock(block); } },
      ];

      items.forEach(function (item) {
        if (item.divider) {
          var hr = document.createElement("div");
          hr.style.cssText =
            "height:1px;background:var(--color-base-300,#d1d5db);margin:0.25rem 0;";
          menu.appendChild(hr);
          return;
        }
        var b = document.createElement("button");
        b.type = "button";
        b.textContent = item.label;
        b.style.cssText =
          "display:block;width:100%;text-align:left;padding:0.3rem 0.5rem;border:none;background:none;border-radius:0.3rem;cursor:pointer;color:" +
          (item.danger ? "var(--color-error,#dc2626)" : "inherit") +
          ";";
        b.addEventListener("mouseover", function () {
          b.style.background =
            "color-mix(in oklab, var(--color-base-content,#1f2937) 8%, transparent)";
        });
        b.addEventListener("mouseout", function () {
          b.style.background = "";
        });
        b.addEventListener("mousedown", function (e) {
          e.preventDefault();
        });
        b.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          item.fn();
          self._closeBlockMenu();
          self._debouncedPushVisualChange();
          self._updateCounts();
        });
        menu.appendChild(b);
      });

      menu.addEventListener("mousedown", function (e) {
        e.preventDefault();
      });

      // Position just right of the drag handle.
      var hRect = this._dragHandle.getBoundingClientRect();
      var wRect = this._visualWrapper.getBoundingClientRect();
      this._visualWrapper.style.position = "relative";
      menu.style.left = Math.max(0, hRect.right - wRect.left + 2) + "px";
      menu.style.top = Math.max(0, hRect.top - wRect.top) + "px";
      this._visualWrapper.appendChild(menu);
      this._blockMenu = menu;

      var closeHandler = function (e) {
        if (!menu.contains(e.target)) self._closeBlockMenu();
      };
      setTimeout(function () {
        document.addEventListener("mousedown", closeHandler);
      }, 0);
      this._blockMenuCloseHandler = closeHandler;
    },

    _closeBlockMenu: function () {
      if (this._blockMenu) {
        this._blockMenu.remove();
        this._blockMenu = null;
      }
      if (this._blockMenuCloseHandler) {
        document.removeEventListener("mousedown", this._blockMenuCloseHandler);
        this._blockMenuCloseHandler = null;
      }
    },

    // Replace a block element with a new tag, carrying its content across.
    _convertBlock: function (block, tag) {
      if (!block || !block.parentNode) return;
      var cur = block.tagName.toLowerCase();
      if (cur === tag) return;

      var el;
      if (tag === "blockquote") {
        el = document.createElement("blockquote");
        var p = document.createElement("p");
        p.innerHTML = cur === "pre" ? block.textContent : block.innerHTML;
        el.appendChild(p);
      } else if (tag === "pre") {
        el = document.createElement("pre");
        var code = document.createElement("code");
        code.textContent = block.textContent || "";
        el.appendChild(code);
      } else {
        el = document.createElement(tag);
        if (cur === "blockquote") {
          // unwrap inner <p> wrappers
          el.innerHTML = block.innerHTML.replace(/<\/?p[^>]*>/gi, "");
        } else if (cur === "pre") {
          el.textContent = block.textContent || "";
        } else {
          el.innerHTML = block.innerHTML;
        }
      }
      if (el.innerHTML.trim() === "") el.innerHTML = "<br>";
      block.parentNode.replaceChild(el, block);
      this._dragHandleBlock = null;
    },

    _duplicateBlock: function (block) {
      if (!block || !block.parentNode) return;
      var clone = block.cloneNode(true);
      clone.classList.remove("leaf-dragging");
      if (block.nextSibling) {
        block.parentNode.insertBefore(clone, block.nextSibling);
      } else {
        block.parentNode.appendChild(clone);
      }
    },

    _deleteBlock: function (block) {
      if (!block || !block.parentNode) return;
      block.remove();
      this._dragHandleBlock = null;
      if (this._dragHandle) this._dragHandle.style.display = "none";
      // Never leave the editor with no editable block.
      if (this._visualEl && this._visualEl.innerHTML.trim() === "") {
        this._visualEl.innerHTML = "<p><br></p>";
      }
    },

    _dismissImagePopover: function (skipPush) {
      // Opening the popover moves focus into its alt-text input. Dismissing
      // it has to hand focus back, or the caret is left nowhere: keystrokes
      // go to a detached input and typing appears to do nothing. Only
      // reclaim focus if it is still inside the popover we are removing —
      // a click that landed elsewhere in the document keeps its target.
      var reclaimFocus =
        this._imagePopoverEl &&
        this._imagePopoverEl.contains(document.activeElement);
      var caretTarget = this._imagePopoverTarget;

      // Remove selection class
      if (this._imagePopoverTarget) {
        this._imagePopoverTarget.classList.remove("leaf-img-selected");
      }
      // Remove resize handles
      if (this._resizeHandles) {
        this._resizeHandles.forEach(function (h) { h.remove(); });
        this._resizeHandles = null;
      }
      // Remove popover
      if (this._imagePopoverEl) {
        this._imagePopoverEl.remove();
        this._imagePopoverEl = null;
      }
      if (this._imagePopoverTarget) {
        this._imagePopoverTarget = null;
        if (!skipPush) this._debouncedPushVisualChange();
      }

      if (reclaimFocus && this._visualEl && !this._readonly) {
        this._visualEl.focus();
        this._placeCaretAfterNode(caretTarget);
      }
    },

    // Collapse the selection immediately after `node` (typically the image
    // that was being edited), so typing continues where the writer was
    // rather than at some remembered offset.
    _placeCaretAfterNode: function (node) {
      if (!node || !node.parentNode || !this._visualEl) return;
      if (!this._visualEl.contains(node)) return;
      try {
        var range = document.createRange();
        range.setStartAfter(node);
        range.collapse(true);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (_e) {
        // A detached or otherwise unrangeable node — focus alone is enough.
      }
    },

    // -- Commands from parent --

    _handleCommand: function (payload) {
      switch (payload.action) {
        case "insert_image":
          if (this._visualEl && payload.url) {
            this._visualEl.focus();
            var imgHtml =
              '<img src="' +
              payload.url +
              '" alt="' +
              (payload.alt || "").replace(/"/g, "&quot;") +
              '" draggable="true" />';
            document.execCommand("insertHTML", false, imgHtml);
            this._debouncedPushVisualChange();
          }
          break;

        case "set_content":
          // A new document: its predecessor's steps are not reachable from it,
          // so keeping them would let undo resurrect replaced content.
          this._historyReset();
          var content = payload.content || "";
          var html = payload.html || "<p><br></p>";

          if (this._visualEl) {
            this._visualEl.innerHTML = html;
            this._normalizeRenderedHtml(this._visualEl);
            this._ensureListItemPlaceholders(this._visualEl);
          }

          var markdownTextarea = this._getMarkdownTextarea();
          if (markdownTextarea) {
            markdownTextarea.value = content;
          }

          var htmlTextarea = this._getHtmlTextarea();
          if (htmlTextarea) {
            htmlTextarea.value = html;
          }

          this._sourceBlock = null;
          this._dragHandleBlock = null;

          // Re-baseline the collaboration state. The document was replaced
          // wholesale — by a host resync, a version switch, a reload — and the
          // next edit must be measured from what is here now. Diffing against
          // the old baseline would emit one enormous splice describing a
          // change nobody made, which the room would refuse, resyncing us
          // again: a loop that never settles.
          if (this._collabOperations) {
            this._lastSentMarkdown = this._currentMarkdown();
            this._lastVisibleText =
              this._mode === "markdown" || this._mode === "html"
                ? this._lastSentMarkdown
                : this._visibleText(this._visualEl);
            // Anything still unacknowledged described the document that was
            // just replaced. Rebasing incoming edits over splices from a
            // document that no longer exists places them at offsets that mean
            // nothing here.
            this._pending = [];
            this._needsResync = false;
          }

          this._syncFormInput(content);
          this._updateCounts();
          // Re-baseline the dirty snapshot: replacing the content
          // programmatically (a version switch, a collaborative sync, a
          // reload) is not a user edit, and leaving the old baseline in
          // place makes `protect_navigation` prompt about unsaved work
          // the writer never did. `_setupHostEvents` re-baselines after
          // setup for exactly this reason; this is the same situation
          // arriving later. `mark_saved: false` opts out.
          if (payload.mark_saved !== false) {
            this._savedContent = this._currentMarkdown();
          }
          break;

        case "set_mode":
          if (payload.mode && payload.mode !== this._mode) {
            if (!this._isModeAllowed(payload.mode)) break;
            var tab = this.el.querySelector(
              '[data-mode-tab="' + payload.mode + '"]'
            );
            if (tab) tab.click();
          }
          break;

        case "insert_markdown":
          var text = payload.text || "";
          if (this._mode === "markdown") {
            var insMd = this._getMarkdownTextarea();
            if (insMd) {
              this._insertAtTextarea(insMd, text);
              this._debouncedPushMarkdownChange(insMd.value);
              this._updateCounts();
            }
          } else if (this._mode === "html") {
            var insHtml = this._getHtmlTextarea();
            if (insHtml) {
              this._insertAtTextarea(insHtml, text);
              this._debouncedPushHtmlChange(insHtml.value);
              this._updateCounts();
            }
          } else if (this._visualEl) {
            this._visualEl.focus();
            var chip = this._maybeAtomicChip(text);
            if (chip) {
              // A preserved custom tag — drop it in as a finished chip.
              document.execCommand("insertHTML", false, chip);
            } else {
              document.execCommand("insertText", false, text);
              if (this._mode === "hybrid") this._refreshSourceBlock();
            }
            this._debouncedPushVisualChange();
            this._updateCounts();
          }
          break;

        case "flush":
          this._flushPending(payload.ref);
          break;

        case "mark_saved":
          this._savedContent = this._currentMarkdown();
          break;
      }
    },
  };
})();
